// Ultimate Card Game (UCG) - Procedural Unit Sprite Generation
//
// Extracted from js/engine/renderer.js (was its opening ~2000 lines) - pure
// data/canvas-baking functions with no dependency on Renderer instance
// state, so moving them here doesn't change behavior at all, only where the
// code lives. getSpriteGrid() defines each unit's pixel-art grid once per
// (unitType, race); buildSpriteBitmap() bakes a resolved grid (team colors
// substituted, team-mirrored) to an offscreen canvas exactly once per
// (unitType, race, team) so Renderer.renderUnit() can blit it with a single
// drawImage() instead of redrawing every pixel every frame - see
// buildSpriteBitmap()'s own comment below for the full reasoning.
//
// dampedSpringRecoil() lives here too since every call site is a weapon-
// recoil animation driving one of these baked sprites (Renderer.renderUnit()
// below).

import { isHDEnabled, setHDEnabled, isUHDEnabled, setUHDEnabled, isPinkBeretV2Enabled, setPinkBeretV2Enabled, hasHDSprite, getHDSpriteGrid } from './hdSprites.js';
export { isHDEnabled, setHDEnabled, isUHDEnabled, setUHDEnabled, isPinkBeretV2Enabled, setPinkBeretV2Enabled, hasHDSprite, getHDSpriteGrid };

const globalSpriteCache = {};

/**
 * Shared body builder for Apostate Loaf (Mushroom cap, cyan gills, dark torso, and suspended crystal head)
 * Used across Idle, Walk1-4, and Front sprites.
 */
function _buildApostateBody(grid) {
  // 1. Spore sparkles floating around cap (y: 0 to 4)
  grid[0][11] = '#c084fc'; grid[0][12] = '#c084fc';
  grid[1][7] = '#e879f9'; grid[1][16] = '#e879f9';
  grid[2][5] = '#38bdf8'; grid[2][18] = '#38bdf8';
  grid[4][3] = '#c084fc'; grid[4][20] = '#c084fc';

  // 2. Wide Segmented Mushroom Cap Dome (y: 1 to 5)
  // Test anchor: grid[1][11] === '#6e594d'
  grid[1][9]  = '#382e29'; grid[1][10] = '#504040'; grid[1][11] = '#6e594d'; grid[1][12] = '#856a5d'; grid[1][13] = '#6e594d'; grid[1][14] = '#504040'; grid[1][15] = '#382e29';
  grid[2][6]  = '#241e1a'; grid[2][7]  = '#382e29'; grid[2][8]  = '#504040'; grid[2][9]  = '#6e594d'; grid[2][10] = '#856a5d';
  grid[2][11] = '#6e594d'; grid[2][12] = '#856a5d'; grid[2][13] = '#6e594d'; grid[2][14] = '#856a5d'; grid[2][15] = '#6e594d';
  grid[2][16] = '#504040'; grid[2][17] = '#382e29'; grid[2][18] = '#241e1a';

  grid[3][4]  = '#1a1512'; grid[3][5]  = '#2e2520'; grid[3][6]  = '#382e29'; grid[3][7]  = '#504040'; grid[3][8]  = '#6e594d';
  grid[3][9]  = '#856a5d'; grid[3][10] = '#382e29'; grid[3][11] = '#856a5d'; grid[3][12] = '#856a5d'; grid[3][13] = '#382e29';
  grid[3][14] = '#6e594d'; grid[3][15] = '#856a5d'; grid[3][16] = '#504040'; grid[3][17] = '#382e29'; grid[3][18] = '#2e2520'; grid[3][19] = '#1a1512';

  grid[4][3]  = '#1a1512'; grid[4][4]  = '#241e1a'; grid[4][5]  = '#382e29'; grid[4][6]  = '#504040'; grid[4][7]  = '#6e594d';
  grid[4][8]  = '#504040'; grid[4][9]  = '#382e29'; grid[4][10] = '#504040'; grid[4][11] = '#6e594d'; grid[4][12] = '#6e594d';
  grid[4][13] = '#504040'; grid[4][14] = '#382e29'; grid[4][15] = '#504040'; grid[4][16] = '#6e594d'; grid[4][17] = '#504040';
  grid[4][18] = '#382e29'; grid[4][19] = '#241e1a'; grid[4][20] = '#1a1512';

  grid[5][3]  = '#151210'; grid[5][4]  = '#1a1512'; grid[5][5]  = '#241e1a'; grid[5][6]  = '#382e29'; grid[5][7]  = '#241e1a';
  grid[5][8]  = '#382e29'; grid[5][9]  = '#1a1512'; grid[5][10] = 'TEAM';   grid[5][11] = 'TEAM';   grid[5][12] = '#1a1512';
  grid[5][13] = '#382e29'; grid[5][14] = '#241e1a'; grid[5][15] = '#382e29'; grid[5][16] = '#241e1a'; grid[5][17] = '#1a1512';

  // 3. Bioluminescent Gills (Row 6)
  // Test anchor: grid[6][8] === '#00f0ff'
  grid[6][4]  = '#0891b2'; grid[6][5]  = '#06b6d4'; grid[6][6]  = '#22d3ee'; grid[6][7]  = '#06b6d4';
  grid[6][8]  = '#00f0ff'; grid[6][9]  = '#22d3ee'; grid[6][10] = '#00f0ff'; grid[6][11] = '#06b6d4';
  grid[6][12] = '#06b6d4'; grid[6][13] = '#00f0ff'; grid[6][14] = '#22d3ee'; grid[6][15] = '#00f0ff';
  grid[6][16] = '#06b6d4'; grid[6][17] = '#22d3ee'; grid[6][18] = '#06b6d4'; grid[6][19] = '#0891b2';

  // 4. THE PROMINENT CHITIN TORSO / "STAMM" (Rows 7 to 11)
  // Directly matches the green-circled ribbed trunk & V-carapace between gills and leg roots!
  // Row 7: Upper Chitin Collar (Deep shadow under gills, ribbed neck band)
  grid[7][7]  = '#1a1512'; grid[7][8]  = '#2e2520'; grid[7][9]  = '#4a3d35'; grid[7][10] = '#6e594d';
  grid[7][11] = '#5a4035'; grid[7][12] = '#5a4035'; // Central furrow
  grid[7][13] = '#6e594d'; grid[7][14] = '#4a3d35'; grid[7][15] = '#2e2520'; grid[7][16] = '#1a1512';

  // Row 8: Upper Ribbed Carapace Plate (V-crest highlight in center, chevron rib ridges)
  grid[8][7]  = '#241e1a'; grid[8][8]  = '#4a3d35'; grid[8][9]  = '#7a5848'; grid[8][10] = '#5a4035';
  grid[8][11] = '#9a6e58'; grid[8][12] = '#9a6e58'; // V-carapace apex highlight!
  grid[8][13] = '#5a4035'; grid[8][14] = '#7a5848'; grid[8][15] = '#4a3d35'; grid[8][16] = '#241e1a';

  // Row 9: Mid-Torso Trunk Segment (Horizontal dividing groove, lateral ribs & V-plate)
  grid[9][7]  = '#1a1512'; grid[9][8]  = '#5a4035'; grid[9][9]  = '#3b2d26'; grid[9][10] = '#856a5d';
  grid[9][11] = '#856a5d'; grid[9][12] = '#856a5d'; // Shield mid-plate
  grid[9][13] = '#856a5d'; grid[9][14] = '#3b2d26'; grid[9][15] = '#5a4035'; grid[9][16] = '#1a1512';

  // Row 10: Lower Carapace V-Taper (Where front legs emerge, V-keel points down)
  grid[10][8]  = '#241e1a'; grid[10][9]  = '#3b2d26'; grid[10][10] = '#5a4035';
  grid[10][11] = '#7a5848'; grid[10][12] = '#7a5848'; // V-shield bottom tip
  grid[10][13] = '#5a4035'; grid[10][14] = '#3b2d26'; grid[10][15] = '#241e1a';

  // Row 11: Pelvic Trunk Skirt & Bio-Spine Emergence
  grid[11][9]  = '#1a1512'; grid[11][10] = '#3b2d26'; // Chitin flanks
  grid[11][11] = '#06b6d4'; grid[11][12] = '#06b6d4'; // Bio-Spine root descending from torso
  grid[11][13] = '#3b2d26'; grid[11][14] = '#1a1512'; // Chitin flanks

  // 5. SEGMENTED BIO-SPINE & SUSPENDED CRYSTAL HEAD (Rows 12 to 16)
  // Row 12: Slender Cyan Vertebrae with lateral rib spurs
  grid[12][10] = '#00f0ff'; // Left rib spur
  grid[12][11] = '#22d3ee'; grid[12][12] = '#22d3ee'; // Spine cord center
  grid[12][13] = '#00f0ff'; // Right rib spur

  // Row 13: Crystal Head Brow & Blazing Optic Eye (CRITICAL test anchor grid[13][11] === '#ffffff')
  grid[13][10] = '#0284c7';
  grid[13][11] = '#ffffff'; grid[13][12] = '#00f0ff'; // Radiant Eye Core
  grid[13][13] = '#0284c7';

  // Row 14: Crystalline Cheeks & Temporal Facets
  grid[14][10] = '#0f172a'; grid[14][11] = '#0284c7'; grid[14][12] = '#0284c7'; grid[14][13] = '#0f172a';

  // Row 15: Chin Taper
  grid[15][11] = '#00f0ff'; grid[15][12] = '#06b6d4';

  // Row 16: Chin Mandible Root
  grid[16][11] = '#0891b2'; grid[16][12] = '#0891b2';
  // Rows 17-23 beneath head are open clearance so legs extend noticeably longer than head!
}

/**
 * Shared body builder for Forgotten Loaf (Compact Glass Sphere with Glowing Cyan Bio-Fluid & Sul Rumpf Chassis)
 * Used across Idle and Walk1-4 sprites.
 */
function _buildForgottenLoafBody(grid) {
  // 1. Compact Glass Sphere with Glowing Cyan Bio-Fluid (y: 3-8, cols 8-15)
  // Row 3: Specular glass dome arc
  grid[3][10] = '#ffffff'; grid[3][11] = '#ffffff'; grid[3][12] = '#bae6fd'; grid[3][13] = '#7dd3fc';

  // Row 4: Upper meniscus & light reflection
  grid[4][9]  = '#ffffff'; grid[4][10] = '#e0f2fe'; grid[4][11] = '#00f0ff'; grid[4][12] = '#38bdf8';
  grid[4][13] = '#0ea5e9'; grid[4][14] = '#7dd3fc';

  // Row 5: Deep glowing bioluminescent fluid core
  grid[5][8]  = '#38bdf8'; grid[5][9]  = '#00f0ff'; grid[5][10] = '#ffffff'; grid[5][11] = '#00e5ff';
  grid[5][12] = '#0284c7'; grid[5][13] = '#0ea5e9'; grid[5][14] = '#38bdf8'; grid[5][15] = '#0369a1';

  // Row 6: Dense fluid with micro-bubble highlight
  grid[6][8]  = '#0284c7'; grid[6][9]  = '#00e5ff'; grid[6][10] = '#38bdf8'; grid[6][11] = '#00f0ff';
  grid[6][12] = '#0284c7'; grid[6][13] = '#0369a1'; grid[6][14] = '#0284c7'; grid[6][15] = '#075985';

  // Row 7: Tank lower bowl curvature
  grid[7][9]  = '#0369a1'; grid[7][10] = '#0284c7'; grid[7][11] = '#38bdf8'; grid[7][12] = '#0284c7';
  grid[7][13] = '#0369a1'; grid[7][14] = '#075985';

  // Row 8: Base of sphere nestling into cradle collar
  grid[8][10] = '#075985'; grid[8][11] = '#0369a1'; grid[8][12] = '#0284c7'; grid[8][13] = '#075985';

  // 2. Sul Rumpf (Segmented Chitin Carapace Torso / Chassis, y: 8-12)
  // Row 8: Retaining collar brackets clasping the sphere
  grid[8][8]   = '#522519'; grid[8][9]   = '#78351f';
  grid[8][14]  = '#78351f'; grid[8][15]  = '#522519';

  // Row 9: Upper thoracic carapace & dorsal chitin plates
  grid[9][7]   = '#3d1d14'; grid[9][8]   = '#522519'; grid[9][9]   = '#78351f'; grid[9][10]  = '#9a3412';
  grid[9][11]  = '#78351f'; grid[9][12]  = '#78351f'; grid[9][13]  = '#9a3412'; grid[9][14]  = '#78351f';
  grid[9][15]  = '#522519'; grid[9][16]  = '#3d1d14';

  // Row 10: Mid torso with bioluminescent amber vent fissures
  grid[10][6]  = '#27130d'; grid[10][7]  = '#3d1d14'; grid[10][8]  = '#522519'; grid[10][9]  = '#ea580c';
  grid[10][10] = '#f97316'; grid[10][11] = '#78351f'; grid[10][12] = '#78351f'; grid[10][13] = '#f97316';
  grid[10][14] = '#ea580c'; grid[10][15] = '#522519'; grid[10][16] = '#3d1d14'; grid[10][17] = '#27130d';

  // Row 11: Lower carapace plates and leg coxa sockets
  grid[11][6]  = '#1c0d09'; grid[11][7]  = '#27130d'; grid[11][8]  = '#522519'; grid[11][9]  = '#78351f';
  grid[11][10] = '#9a3412'; grid[11][11] = '#9a3412'; grid[11][12] = '#9a3412'; grid[11][13] = '#9a3412';
  grid[11][14] = '#78351f'; grid[11][15] = '#522519'; grid[11][16] = '#27130d'; grid[11][17] = '#1c0d09';

  // Row 12: Ventral keel and under-hull armature
  grid[12][8]  = '#1c0d09'; grid[12][9]  = '#27130d'; grid[12][10] = '#3d1d14'; grid[12][11] = '#522519';
  grid[12][12] = '#522519'; grid[12][13] = '#3d1d14'; grid[12][14] = '#27130d'; grid[12][15] = '#1c0d09';
}

// ── THICK, LONGER ARACHNID LEGS (2-pixel cross section with highlight & shadow) ──
function _addApostateOuterRearLeg(grid, isLeft, kneeY, kneeX, elbowY, elbowX, footY, footX) {
  const coxaX = isLeft ? 6 : 17;
  const innerDir = isLeft ? 1 : -1;

  // Coxa from Torso (2px thick)
  grid[7][coxaX] = '#7a5848';
  grid[8][coxaX] = '#5a4035';
  grid[6][isLeft ? coxaX - 2 : coxaX + 2] = '#9a6e58';
  grid[7][isLeft ? coxaX - 1 : coxaX + 1] = '#7a5848';

  // High Knee Cap & Spike (2-3px solid knuckle)
  // Test anchor: grid[5][2] === '#3b322a' on left
  grid[kneeY][kneeX] = '#3b322a';
  grid[kneeY][kneeX + innerDir] = '#5a4035';
  if (kneeY > 0) {
    grid[kneeY - 1][kneeX] = '#9a6e58'; // Upward spur highlight
    grid[kneeY - 1][kneeX + innerDir] = '#3b322a';
  }

  // Femur/Tibia: Knee down to Elbow (2px wide)
  const stepY = elbowY - kneeY;
  for (let y = kneeY + 1; y <= elbowY; y++) {
    const t = (y - kneeY) / stepY;
    const x = Math.round(kneeX + (elbowX - kneeX) * t);
    grid[y][x] = (y % 2 === 0) ? '#9a6e58' : '#7a5848';
    grid[y][x + innerDir] = (y % 2 === 0) ? '#5a4035' : '#3b2d26'; // Dark underside
  }

  // Tibia/Tarsus: Elbow down to Foot (2px wide, tapering at claw)
  const dropY = footY - elbowY;
  for (let y = elbowY + 1; y <= footY; y++) {
    const t = (y - elbowY) / dropY;
    const x = Math.round(elbowX + (footX - elbowX) * t);
    grid[y][x] = (y % 2 === 0) ? '#7a5848' : '#5a4035';
    if (y < footY) {
      grid[y][x + innerDir] = (y % 2 === 0) ? '#3b2d26' : '#241a14';
    }
  }
  // Claw tip on the ground
  grid[footY][footX] = '#241a14';
}

function _addApostateMidLeg(grid, isLeft, kneeY, kneeX, elbowY, elbowX, footY, footX) {
  const coxaX = isLeft ? 8 : 15;
  const innerDir = isLeft ? 1 : -1;

  // Coxa from Torso (2px thick)
  grid[8][coxaX] = '#7a5848';
  grid[9][coxaX] = '#5a4035';
  grid[7][isLeft ? coxaX - 2 : coxaX + 2] = '#9a6e58';
  grid[8][isLeft ? coxaX - 1 : coxaX + 1] = '#7a5848';

  // Knee Cap
  grid[kneeY][kneeX] = '#4a3d35';
  grid[kneeY][kneeX + innerDir] = '#3b2d26';
  if (kneeY > 0) grid[kneeY - 1][kneeX] = '#7a5848';

  // Knee down to Elbow (2px wide)
  const stepY = elbowY - kneeY;
  for (let y = kneeY + 1; y <= elbowY; y++) {
    const t = (y - kneeY) / stepY;
    const x = Math.round(kneeX + (elbowX - kneeX) * t);
    grid[y][x] = (y % 2 === 0) ? '#9a6e58' : '#7a5848';
    grid[y][x + innerDir] = (y % 2 === 0) ? '#5a4035' : '#3b2d26';
  }

  // Elbow down to Foot (2px wide)
  const dropY = footY - elbowY;
  for (let y = elbowY + 1; y <= footY; y++) {
    const t = (y - elbowY) / dropY;
    const x = Math.round(elbowX + (footX - elbowX) * t);
    grid[y][x] = (y % 2 === 0) ? '#7a5848' : '#5a4035';
    if (y < footY) {
      grid[y][x + innerDir] = (y % 2 === 0) ? '#3b2d26' : '#241a14';
    }
  }
  grid[footY][footX] = '#241a14';
}

function _addApostateFrontLeg(grid, isLeft, kneeY, kneeX, footY, footX) {
  const coxaX = isLeft ? 8 : 15;
  const innerDir = isLeft ? 1 : -1;

  grid[9][coxaX] = '#5a4035';
  grid[10][coxaX] = '#3b2d26';
  grid[8][isLeft ? coxaX - 1 : coxaX + 1] = '#7a5848';

  // Knee
  grid[kneeY][kneeX] = '#5a4035';
  grid[kneeY][kneeX + innerDir] = '#3b2d26';

  const dropY = footY - kneeY;
  for (let y = kneeY + 1; y <= footY; y++) {
    const t = (y - kneeY) / dropY;
    const x = Math.round(kneeX + (footX - kneeX) * t);
    grid[y][x] = (y % 2 === 0) ? '#7a5848' : '#5a4035';
    if (y < footY) {
      grid[y][x + innerDir] = (y % 2 === 0) ? '#3b2d26' : '#241a14';
    }
  }
  grid[footY][footX] = '#241a14';
}

/**
 * Shared body builder for Preserver Lock (biomechanical horn feeding a
 * suspended plasma orb via cable-like nerve strands, a bony cocoon-like
 * chest shield on the front torso, armored tail plating on the rear).
 * Always the same pose - the beam FX itself (drawn separately in
 * js/engine/render/units.js off unit.beamTargetId) carries the "attacking"
 * read, so no separate charge/attack panel is needed. The legs (built
 * separately by _addPreserverLockLeg below) are what carry the walk cycle;
 * this body is composited on top of them as its own layer in units.js so it
 * can bob/roll independently of which legs are currently planted.
 */
function _buildPreserverLockBody(grid) {
  // 1. Emitter Head with Twin Lenses & Antennas (rows 0-3)
  // Radial spikes / lens flare beams (rows 0-1) matching the card artwork's blinding corona
  grid[0][8]  = '#7dd3fc';
  grid[0][10] = '#bae6fd';
  grid[0][11] = '#ffffff'; // Test anchor: bright suspended plasma core / flare
  grid[0][12] = '#bae6fd';
  grid[0][13] = '#ffffff';
  grid[0][14] = '#bae6fd';
  grid[0][16] = '#7dd3fc';

  // Row 1: Twin projector lenses & chassis rim
  grid[1][7]  = '#38bdf8';
  grid[1][8]  = '#0f172a';
  grid[1][9]  = '#e0f2fe';
  grid[1][10] = '#ffffff'; // Left lens core
  grid[1][11] = '#ffffff'; // Left lens center (Test anchor: grid[1][11] === '#ffffff')
  grid[1][12] = '#0f172a'; // Center chassis divider/housing
  grid[1][13] = '#ffffff'; // Right lens center
  grid[1][14] = '#ffffff'; // Right lens core
  grid[1][15] = '#e0f2fe';
  grid[1][16] = '#0f172a';
  grid[1][17] = '#38bdf8';

  // Row 2: Lower lens rims & mounting bracket
  grid[2][8]  = '#0284c7';
  grid[2][9]  = '#38bdf8';
  grid[2][10] = '#7dd3fc';
  grid[2][11] = '#bae6fd';
  grid[2][12] = '#0f172a';
  grid[2][13] = '#bae6fd';
  grid[2][14] = '#7dd3fc';
  grid[2][15] = '#38bdf8';
  grid[2][16] = '#0284c7';

  // Row 3: Swivel mount collar
  grid[3][10] = '#0f172a';
  grid[3][11] = '#1e293b';
  grid[3][12] = '#64748b';
  grid[3][13] = '#f8fafc';
  grid[3][14] = '#cbd5e1';

  // 2. Neck Mast: Dual-strand Conduit Cables (front/inner) + Segmented Spine (rear/outer)
  // Rows 4 to 10
  // Row 4:
  grid[4][10] = '#020617'; grid[4][11] = '#1e293b'; grid[4][12] = '#f1f5f9'; grid[4][13] = '#cbd5e1';
  // Row 5:
  grid[5][11] = '#020617'; grid[5][12] = '#1e293b'; grid[5][13] = '#e7e5e4'; grid[5][14] = '#a8a29e';
  // Row 6:
  grid[6][12] = '#020617'; grid[6][13] = '#1e293b'; grid[6][14] = '#d6d3d1'; grid[6][15] = '#78716c';
  // Row 7:
  grid[7][13] = '#020617'; grid[7][14] = '#334155'; grid[7][15] = '#d6d3d1'; grid[7][16] = '#78716c';
  // Row 8:
  grid[8][14] = '#020617'; grid[8][15] = '#334155'; grid[8][16] = '#a8a29e'; grid[8][17] = '#57534e';
  // Row 9:
  grid[9][14] = '#020617'; grid[9][15] = '#1e293b'; grid[9][16] = '#a8a29e'; grid[9][17] = '#57534e';
  // Row 10: Rooting into torso crest
  grid[10][15] = '#020617'; grid[10][16] = '#334155'; grid[10][17] = '#78716c'; grid[10][18] = '#44403c';

  // 3. Biomechanical Carapace Shell (H.R. Giger striated chitin & bone rib ridges)
  // Rising diagonally from rear-low to front-high (no false eyes on torso!)
  // Row 9: Upper dorsal carapace crest
  grid[9][8]  = '#1c1917'; grid[9][9]  = '#292524'; grid[9][10] = '#44403c'; grid[9][11] = '#78716c'; grid[9][12] = '#a8a29e'; grid[9][13] = '#d6d3d1';
  
  // Row 10: Striated ribs & shadow grooves
  grid[10][6] = '#1c1917'; grid[10][7] = '#292524'; grid[10][8] = '#d6d3d1'; grid[10][9] = '#44403c'; grid[10][10] = '#e7e5e4'; grid[10][11] = '#57534e'; grid[10][12] = '#d6d3d1'; grid[10][13] = '#44403c'; grid[10][14] = '#a8a29e';

  // Row 11: Mid carapace with chitin arches
  grid[11][5] = '#1c1917'; grid[11][6] = '#44403c'; grid[11][7] = '#a8a29e'; grid[11][8] = '#292524'; grid[11][9] = '#d6d3d1'; grid[11][10] = '#44403c'; grid[11][11] = '#e7e5e4'; grid[11][12] = '#57534e'; grid[11][13] = '#a8a29e'; grid[11][14] = '#334155'; grid[11][15] = '#1e293b'; grid[11][16] = '#0f172a';

  // Row 12: Torso plate with TEAM power fissure (grid[12][11] = 'TEAM' anchor!)
  grid[12][5] = '#292524'; grid[12][6] = '#78716c'; grid[12][7] = '#292524'; grid[12][8] = '#a8a29e'; grid[12][9] = '#475569'; grid[12][10] = '#334155'; grid[12][11] = 'TEAM'; grid[12][12] = 'TEAM'; grid[12][13] = '#334155'; grid[12][14] = '#475569'; grid[12][15] = '#1e293b'; grid[12][16] = '#0f172a';

  // Row 13: Lower torso and chest plate
  grid[13][5] = '#1c1917'; grid[13][6] = '#57534e'; grid[13][7] = '#1c1917'; grid[13][8] = '#334155'; grid[13][9] = '#475569'; grid[13][10] = 'TEAM'; grid[13][11] = 'TEAM'; grid[13][12] = 'TEAM'; grid[13][13] = '#475569'; grid[13][14] = '#334155'; grid[13][15] = '#1e293b'; grid[13][16] = '#0f172a'; grid[13][17] = '#020617';

  // Row 14: Lower torso base & hip linkage
  grid[14][5] = '#1c1917'; grid[14][6] = '#292524'; grid[14][7] = '#334155'; grid[14][8] = '#475569'; grid[14][9] = '#64748b'; grid[14][10] = '#64748b'; grid[14][11] = '#475569'; grid[14][12] = '#334155'; grid[14][13] = '#475569'; grid[14][14] = '#334155'; grid[14][15] = '#1e293b'; grid[14][16] = '#0f172a';

  // Row 15: Underside belly
  grid[15][6] = '#020617'; grid[15][7] = '#0f172a'; grid[15][8] = '#1e293b'; grid[15][9] = '#334155'; grid[15][10] = '#334155'; grid[15][11] = '#334155'; grid[15][12] = '#334155'; grid[15][13] = '#1e293b'; grid[15][14] = '#0f172a'; grid[15][15] = '#020617';

  // 4. Front Shoulder Husk (Rows 11-16, cols 17-21)
  grid[11][17] = '#78716c'; grid[11][18] = '#a8a29e'; grid[11][19] = '#57534e';
  grid[12][17] = '#44403c'; grid[12][18] = '#d6d3d1'; grid[12][19] = '#a8a29e'; grid[12][20] = '#44403c';
  grid[13][18] = '#78716c'; grid[13][19] = '#d6d3d1'; grid[13][20] = '#a8a29e'; grid[13][21] = '#3b2d26';
  grid[14][17] = '#334155'; grid[14][18] = '#a8a29e'; grid[14][19] = '#78716c'; grid[14][20] = '#241e1a';
  grid[15][18] = '#57534e'; grid[15][19] = '#3b2d26';
  grid[16][19] = '#241e1a';

  // 5. Circular Hip Joint / Power Disc on Rear Flank (Rows 13-16, cols 1-5)
  // Distinct spherical/disc mechanical joint as seen in 1_preserverlock.png
  grid[13][2] = '#0f172a'; grid[13][3] = '#334155'; grid[13][4] = '#1e293b';
  grid[14][1] = '#0f172a'; grid[14][2] = '#475569'; grid[14][3] = '#cbd5e1'; grid[14][4] = '#475569'; grid[14][5] = '#0f172a';
  grid[15][1] = '#0f172a'; grid[15][2] = '#334155'; grid[15][3] = '#94a3b8'; grid[15][4] = '#334155'; grid[15][5] = '#0f172a';
  grid[16][2] = '#020617'; grid[16][3] = '#1e293b'; grid[16][4] = '#020617';
}

// Six-legged "elephant gait" limb: hip (coxa) -> knee -> foot, 2px column
// with a yellow-black hazard knee joint (matches the armored leg joints in
// the card artwork). One shared helper for all 3 leg pairs (front/mid/rear)
// on either side - only the anchor coordinates differ per call.
//
// strideX/lift drive the walk-cycle frames (PreserverLock_Legs_WalkN): a
// swinging leg gets a forward strideX plus a positive lift (knee & foot
// pulled up, shin drawn short/absent - reads as mid-air), a grounded leg
// gets a small negative strideX as it trails under the shifting weight.
// Both are clamped to the 24x24 grid since several feet already sit right
// at the x=0/x=23 edges (see the call sites below) with no margin to spare.
function _addPreserverLockLeg(grid, isLeft, coxaY, coxaX, kneeY, kneeX, footY, footX, strideX = 0, lift = 0) {
  const innerDir = isLeft ? 1 : -1;
  const clamp = (v) => Math.max(0, Math.min(23, v));
  kneeX = clamp(kneeX + Math.round(strideX * 0.5));
  footX = clamp(footX + strideX);
  kneeY = clamp(kneeY - Math.round(lift * 0.6));
  footY = clamp(footY - lift);

  // Cool slate/anthracite chitin (not warm brown) to match the dark
  // gunmetal legs in 1_preserverlock.png.
  grid[coxaY][coxaX] = '#334155';

  const upSteps = Math.max(1, kneeY - coxaY);
  for (let y = coxaY + 1; y <= kneeY; y++) {
    const tt = (y - coxaY) / upSteps;
    const x = Math.round(coxaX + (kneeX - coxaX) * tt);
    grid[y][x] = (y % 2 === 0) ? '#475569' : '#334155';
    grid[y][x + innerDir] = (y % 2 === 0) ? '#1e293b' : '#0f172a';
  }

  // Hazard-striped knee joint
  grid[kneeY][kneeX] = '#eab308';
  grid[kneeY][kneeX + innerDir] = '#0f172a';

  const downSteps = Math.max(1, footY - kneeY);
  for (let y = kneeY + 1; y <= footY; y++) {
    const tt = (y - kneeY) / downSteps;
    const x = Math.round(kneeX + (footX - kneeX) * tt);
    grid[y][x] = (y % 2 === 0) ? '#475569' : '#1e293b';
    if (y < footY) grid[y][x + innerDir] = '#0f172a';
  }
  grid[footY][footX] = '#020617';
}

/**
 * Helper to build slender, articulated arachnid stilt legs for Forgotten Loaf:
 * Clearly differentiated TWO-SEGMENT structure:
 * 1. Oberschenkel (Upper Leg / Femur): Thicker chitin limb arching UP and OUT from the coxa socket.
 * 2. Knie-Gelenk (Knee Knuckle): Prominent amber articulated ball joint breaking the silhouette.
 * 3. Unterschenkel (Lower Leg / Shin): Slender limb plunging DOWN to ground with sharp golden talon.
 */
function _addForgottenLoafLeg(grid, isLeft, coxaY, coxaX, kneeY, kneeX, footY, footX, strideX = 0, lift = 0, isForeground = true) {
  const innerDir = isLeft ? 1 : -1;
  const clamp = (v) => Math.max(0, Math.min(23, v));
  kneeX = clamp(kneeX + Math.round(strideX * 0.5));
  footX = clamp(footX + strideX);
  kneeY = clamp(kneeY - Math.round(lift * 0.6));
  footY = clamp(footY - lift);

  const CHITIN_DARK  = isForeground ? '#3d1d14' : '#1c0d09';
  const CHITIN_MID   = isForeground ? '#78351f' : '#27130d';
  const CHITIN_LIGHT = isForeground ? '#9a3412' : '#3d1d14';
  const KNEE_AMBER   = isForeground ? '#f59e0b' : '#78351f';
  const KNEE_SPARK   = isForeground ? '#fef08a' : '#b45309';
  const TIP_COLOR    = isForeground ? '#facc15' : '#522519';

  // Coxa socket on the Sul torso chassis
  if (grid[coxaY] && grid[coxaY][coxaX] !== undefined) {
    grid[coxaY][coxaX] = CHITIN_MID;
  }

  // 1. OBERSCHENKEL (Upper Leg / Femur: Coxa -> Knee, steep arch upward and outward)
  // Substantially thicker chitin armored thigh (double-width profile)
  const upDy = kneeY - coxaY;
  const upDx = kneeX - coxaX;
  const upSteps = Math.max(1, Math.max(Math.abs(upDy), Math.abs(upDx)));
  for (let s = 1; s <= upSteps; s++) {
    const tt = s / upSteps;
    const y = clamp(Math.round(coxaY + upDy * tt));
    const x = clamp(Math.round(coxaX + upDx * tt));
    if (grid[y]) {
      grid[y][x] = (s % 2 === 0) ? CHITIN_LIGHT : CHITIN_MID;
      // Underside shading + dorsal armor plating to give thick muscular thigh volume
      if (y + 1 <= 23 && !grid[y + 1][x]) {
        grid[y + 1][x] = CHITIN_DARK;
      }
      if (isForeground && x + innerDir >= 0 && x + innerDir <= 23 && !grid[y][x + innerDir]) {
        grid[y][x + innerDir] = CHITIN_MID;
      }
    }
  }

  // 2. PROMINENT KNEE KNUCKLE (Articulated ball joint)
  if (grid[kneeY] && grid[kneeY][kneeX] !== undefined) {
    grid[kneeY][kneeX] = KNEE_AMBER;
    // Highlight facet to emphasize the joint bend
    if (isForeground && kneeX - innerDir >= 0 && kneeX - innerDir <= 23 && !grid[kneeY][kneeX - innerDir]) {
      grid[kneeY][kneeX - innerDir] = KNEE_SPARK;
    }
  }

  // 3. UNTERSCHENKEL (Lower Leg / Shin: Knee -> Foot, sharp plunge down to ground)
  const downDy = footY - kneeY;
  const downDx = footX - kneeX;
  const downSteps = Math.max(1, Math.max(Math.abs(downDy), Math.abs(downDx)));
  for (let s = 1; s <= downSteps; s++) {
    const tt = s / downSteps;
    const y = clamp(Math.round(kneeY + downDy * tt));
    const x = clamp(Math.round(kneeX + downDx * tt));
    if (grid[y]) {
      if (y === footY) {
        grid[y][x] = TIP_COLOR;
      } else {
        grid[y][x] = (s % 2 === 0) ? CHITIN_MID : CHITIN_LIGHT;
        if (isForeground && x + innerDir >= 0 && x + innerDir <= 23 && !grid[y][x + innerDir]) {
          grid[y][x + innerDir] = CHITIN_DARK;
        }
      }
    }
  }

  // 4. Ground claw talon tip
  if (grid[footY] && grid[footY][footX] !== undefined) {
    grid[footY][footX] = TIP_COLOR;
  }
}

/**
 * 𓉐 SUL MONITOR LOCK PROCEDURAL 24x24 SPRITE GENERATORS
 * Authentic recreation of 1_monitorlock.png / gold_monitorlock.png:
 * - Towering serpentine alien leviathan emerging from fractured rock/earth crater
 * - Silvery segmented exoskeleton structure with prominent dorsal carapace plates and rib spurs
 * - Deep visceral striated crimson muscle tissue beneath the armor plates
 * - 5 lateral bioluminescent turquoise "monitor" eyes embedded along the flanks
 * - Majestic fan-shaped crown ("fächerhafter Kranz am Kopf") with 6 curved silver rib-spines and cyan tips
 * - Giant multifaceted cyan compound sensor dome ("Monitor" eye) with bright specular highlights
 * - Menacing closed armored mandibles in resting pose; wide gaping predatory maw with sharp fangs in bite pose
 */
function _buildMonitorLockCrater(grid) {
  // Fractured basalt rim, earthen mound, and deep crater abyss (rows 18..23, cols 1..22)
  // Deep subterranean chasm / abyss pit (behind worm trunk)
  for (let y = 19; y <= 23; y++) {
    for (let x = 7; x <= 16; x++) {
      grid[y][x] = '#050505';
    }
  }
  for (let x = 8; x <= 15; x++) {
    grid[20][x] = '#0c0a09';
    grid[21][x] = '#020202';
    grid[22][x] = '#020202';
    grid[23][x] = '#050505';
  }

  // Left crater rim & basalt spires (cols 1..7, rows 18..23)
  grid[18][4] = '#78716c'; grid[18][5] = '#a8a29e';
  grid[19][3] = '#57534e'; grid[19][4] = '#a8a29e'; grid[19][5] = '#cbd5e1'; grid[19][6] = '#78716c';
  grid[20][2] = '#44403c'; grid[20][3] = '#78716c'; grid[20][4] = '#d6d3d1'; grid[20][5] = '#a8a29e'; grid[20][6] = '#57534e'; grid[20][7] = '#292524';
  grid[21][1] = '#292524'; grid[21][2] = '#57534e'; grid[21][3] = '#a8a29e'; grid[21][4] = '#78716c'; grid[21][5] = '#44403c'; grid[21][6] = '#292524'; grid[21][7] = '#1c1917';
  grid[22][1] = '#1c1917'; grid[22][2] = '#44403c'; grid[22][3] = '#78350f'; grid[22][4] = '#57534e'; grid[22][5] = '#292524'; grid[22][6] = '#1c1917';
  grid[23][1] = '#0c0a09'; grid[23][2] = '#1c1917'; grid[23][3] = '#292524'; grid[23][4] = '#44403c'; grid[23][5] = '#292524'; grid[23][6] = '#1c1917';

  // Right crater rim & basalt spires (cols 16..22, rows 18..23)
  grid[18][18] = '#a8a29e'; grid[18][19] = '#78716c';
  grid[19][17] = '#78716c'; grid[19][18] = '#cbd5e1'; grid[19][19] = '#a8a29e'; grid[19][20] = '#57534e';
  grid[20][16] = '#292524'; grid[20][17] = '#57534e'; grid[20][18] = '#a8a29e'; grid[20][19] = '#d6d3d1'; grid[20][20] = '#78716c'; grid[20][21] = '#44403c';
  grid[21][16] = '#1c1917'; grid[21][17] = '#292524'; grid[21][18] = '#44403c'; grid[21][19] = '#78716c'; grid[21][20] = '#a8a29e'; grid[21][21] = '#57534e'; grid[21][22] = '#292524';
  grid[22][17] = '#1c1917'; grid[22][18] = '#292524'; grid[22][19] = '#57534e'; grid[22][20] = '#78350f'; grid[22][21] = '#44403c'; grid[22][22] = '#1c1917';
  grid[23][17] = '#1c1917'; grid[23][18] = '#292524'; grid[23][19] = '#44403c'; grid[23][20] = '#292524'; grid[23][21] = '#1c1917'; grid[23][22] = '#0c0a09';

  // Base earth foundation line (row 23)
  for (let x = 2; x <= 21; x++) {
    if (!grid[23][x]) grid[23][x] = '#1c1917';
  }
}

function _buildMonitorLockCraterFront(grid) {
  // Uplifted foreground rock slabs & front earthen rim (sits IN FRONT of worm base)
  // Row 21: Sharp forward rock fangs flanking the emergence aperture
  grid[21][5] = '#78716c'; grid[21][6] = '#57534e'; grid[21][7] = '#292524';
  grid[21][16] = '#292524'; grid[21][17] = '#57534e'; grid[21][18] = '#78716c';

  // Row 22: Forward basalt shelf & cracked stone slabs overlapping the worm trunk
  grid[22][3] = '#44403c'; grid[22][4] = '#78716c'; grid[22][5] = '#a8a29e'; grid[22][6] = '#57534e';
  grid[22][7] = '#44403c'; grid[22][8] = '#292524'; grid[22][9] = '#1c1917';
  grid[22][14] = '#1c1917'; grid[22][15] = '#292524'; grid[22][16] = '#44403c';
  grid[22][17] = '#57534e'; grid[22][18] = '#a8a29e'; grid[22][19] = '#78716c'; grid[22][20] = '#44403c';

  // Row 23: Front earthen lip, bedrock teeth, and rubble mounds
  grid[23][4] = '#292524'; grid[23][5] = '#57534e'; grid[23][6] = '#78716c'; grid[23][7] = '#57534e';
  grid[23][8] = '#44403c'; grid[23][9] = '#292524'; grid[23][10] = '#1c1917'; grid[23][11] = '#292524';
  grid[23][12] = '#292524'; grid[23][13] = '#1c1917'; grid[23][14] = '#292524'; grid[23][15] = '#44403c';
  grid[23][16] = '#57534e'; grid[23][17] = '#78716c'; grid[23][18] = '#57534e'; grid[23][19] = '#292524';
}

function _buildMonitorLockBody(grid) {
  // ROUNDED CYLINDRICAL TITAN BODY (matching card artwork):
  // - Smooth tubular annelid trunk with cylindrical volume shading (no jagged saw-tooth spurs)
  // - Articulated dorsal Platin-Chrom vertebrae plates conforming to the curved back
  // - Plump striated crimson muscle flanks with longitudinal highlight
  // - Flush circular cyan monitor sensory pores embedded smoothly along the lateral line
  // - Solid rooted subterranean base anchored in rows 18-23

  // Palette constants
  const SHADOW = '#450a0a';
  const DARK_RED = '#7f1d1d';
  const MID_RED = '#991b1b';
  const CRIMSON = '#b91c1c';
  const LIGHT_RED = '#dc2626';
  const FLESH_HL = '#ef4444';

  const CHROME_DARK = '#334155';
  const CHROME_SHADOW = '#475569';
  const SLATE = '#64748b';
  const SILVER_MID = '#94a3b8';
  const SILVER_LIGHT = '#cbd5e1';
  const PLATIN = '#f1f5f9';
  const SPECULAR = '#f8fafc';

  const CYAN_CORE = '#00f0ff';
  const CYAN_DARK = '#0891b2';
  const CYAN_DEEP = '#0e7490';

  // Rows 6-8: Cervical Trunk (seamless cylindrical connection to head cols 9-16)
  grid[6][10] = CHROME_SHADOW; grid[6][11] = SILVER_LIGHT; grid[6][12] = PLATIN; grid[6][13] = SILVER_LIGHT; grid[6][14] = DARK_RED; grid[6][15] = SHADOW;

  grid[7][9] = CHROME_DARK; grid[7][10] = SILVER_LIGHT; grid[7][11] = PLATIN; grid[7][12] = SILVER_LIGHT; grid[7][13] = CRIMSON; grid[7][14] = LIGHT_RED; grid[7][15] = SHADOW;

  grid[8][8] = CHROME_SHADOW; grid[8][9] = SPECULAR; grid[8][10] = SILVER_LIGHT; grid[8][11] = CYAN_CORE; grid[8][12] = CYAN_DARK; grid[8][13] = CRIMSON; grid[8][14] = MID_RED; grid[8][15] = SHADOW;

  // Rows 9-13: Upper Thoracic Curve (smooth rounded cylinder curving gently to cols 7-15)
  grid[9][8] = CHROME_SHADOW; grid[9][9] = SILVER_LIGHT; grid[9][10] = PLATIN; grid[9][11] = SILVER_LIGHT; grid[9][12] = CRIMSON; grid[9][13] = FLESH_HL; grid[9][14] = LIGHT_RED; grid[9][15] = SHADOW;

  grid[10][7] = CHROME_DARK; grid[10][8] = SILVER_LIGHT; grid[10][9] = SPECULAR; grid[10][10] = PLATIN; grid[10][11] = SILVER_MID; grid[10][12] = CRIMSON; grid[10][13] = FLESH_HL; grid[10][14] = MID_RED; grid[10][15] = SHADOW;

  grid[11][7] = CHROME_SHADOW; grid[11][8] = SILVER_LIGHT; grid[11][9] = PLATIN; grid[11][10] = CYAN_CORE; grid[11][11] = CYAN_DARK; grid[11][12] = CRIMSON; grid[11][13] = LIGHT_RED; grid[11][14] = MID_RED; grid[11][15] = SHADOW;

  grid[12][7] = CHROME_DARK; grid[12][8] = SILVER_MID; grid[12][9] = SILVER_LIGHT; grid[12][10] = PLATIN; grid[12][11] = SILVER_LIGHT; grid[12][12] = CRIMSON; grid[12][13] = FLESH_HL; grid[12][14] = LIGHT_RED; grid[12][15] = SHADOW;

  grid[13][8] = CHROME_SHADOW; grid[13][9] = SILVER_LIGHT; grid[13][10] = PLATIN; grid[13][11] = SILVER_MID; grid[13][12] = DARK_RED; grid[13][13] = CRIMSON; grid[13][14] = FLESH_HL; grid[13][15] = LIGHT_RED; grid[13][16] = SHADOW;

  // Rows 14-17: Lower Mid-Trunk (smooth rounded tubular body cols 8-16)
  grid[14][8] = CHROME_DARK; grid[14][9] = SILVER_MID; grid[14][10] = SILVER_LIGHT; grid[14][11] = PLATIN; grid[14][12] = CYAN_CORE; grid[14][13] = CYAN_DARK; grid[14][14] = CRIMSON; grid[14][15] = LIGHT_RED; grid[14][16] = SHADOW;

  grid[15][8] = CHROME_SHADOW; grid[15][9] = SILVER_LIGHT; grid[15][10] = SPECULAR; grid[15][11] = PLATIN; grid[15][12] = SILVER_MID; grid[15][13] = CRIMSON; grid[15][14] = FLESH_HL; grid[15][15] = LIGHT_RED; grid[15][16] = SHADOW;

  grid[16][8] = CHROME_DARK; grid[16][9] = SILVER_MID; grid[16][10] = SILVER_LIGHT; grid[16][11] = PLATIN; grid[16][12] = SILVER_LIGHT; grid[16][13] = DARK_RED; grid[16][14] = CRIMSON; grid[16][15] = LIGHT_RED; grid[16][16] = SHADOW;

  grid[17][8] = CHROME_SHADOW; grid[17][9] = SILVER_LIGHT; grid[17][10] = PLATIN; grid[17][11] = CYAN_CORE; grid[17][12] = CYAN_DARK; grid[17][13] = CRIMSON; grid[17][14] = FLESH_HL; grid[17][15] = LIGHT_RED; grid[17][16] = SHADOW;

  // Rows 18-23: Solid Rooted Crater Trunk (cols 8-16 - matches crater basalt collar with zero gap)
  grid[18][8] = CHROME_SHADOW; grid[18][9] = SILVER_LIGHT; grid[18][10] = SPECULAR; grid[18][11] = SILVER_LIGHT; grid[18][12] = CYAN_CORE; grid[18][13] = CYAN_DARK; grid[18][14] = CRIMSON; grid[18][15] = LIGHT_RED; grid[18][16] = SHADOW;

  grid[19][8] = CHROME_DARK; grid[19][9] = SILVER_MID; grid[19][10] = PLATIN; grid[19][11] = SILVER_LIGHT; grid[19][12] = DARK_RED; grid[19][13] = CRIMSON; grid[19][14] = LIGHT_RED; grid[19][15] = MID_RED; grid[19][16] = SHADOW;

  grid[20][8] = CHROME_DARK; grid[20][9] = CHROME_SHADOW; grid[20][10] = SILVER_LIGHT; grid[20][11] = SPECULAR; grid[20][12] = CYAN_CORE; grid[20][13] = CYAN_DEEP; grid[20][14] = CRIMSON; grid[20][15] = LIGHT_RED; grid[20][16] = SHADOW;

  grid[21][8] = '#1e293b'; grid[21][9] = CHROME_DARK; grid[21][10] = SLATE; grid[21][11] = SILVER_LIGHT; grid[21][12] = SILVER_MID; grid[21][13] = DARK_RED; grid[21][14] = CRIMSON; grid[21][15] = DARK_RED; grid[21][16] = CHROME_DARK;

  grid[22][8] = '#0f172a'; grid[22][9] = '#1e293b'; grid[22][10] = CHROME_SHADOW; grid[22][11] = SLATE; grid[22][12] = SILVER_MID; grid[22][13] = SHADOW; grid[22][14] = DARK_RED; grid[22][15] = CRIMSON; grid[22][16] = '#1e293b';

  grid[23][8] = '#09090b'; grid[23][9] = '#0f172a'; grid[23][10] = '#1e293b'; grid[23][11] = CHROME_DARK; grid[23][12] = CHROME_SHADOW; grid[23][13] = CHROME_DARK; grid[23][14] = '#1e293b'; grid[23][15] = '#0f172a'; grid[23][16] = '#09090b';
}

function _buildMonitorLockCrown(grid) {
  // 1. MAJESTIC FAN-SHAPED EXOSKELETON CROWN / KRANZ (Rows 0-6, Cols 0-14)
  // Distinct, curved silver/bone rib-spines arching backward & upward with cyan tips
  // Polished Platin-Chrom palette with metallic specular sheen
  
  // Spine 1 (Topmost arch):
  grid[0][5] = '#00f0ff'; grid[0][6] = '#f1f5f9'; grid[0][7] = '#e2e8f0'; grid[0][8] = '#94a3b8';
  grid[1][8] = '#cbd5e1'; grid[1][9] = '#e2e8f0';

  // Spine 2 (Upper-left spreading arch):
  grid[1][2] = '#00f0ff'; grid[1][3] = '#22d3ee'; grid[1][4] = '#f1f5f9'; grid[1][5] = '#cbd5e1'; grid[1][6] = '#64748b';
  grid[2][6] = '#cbd5e1'; grid[2][7] = '#e2e8f0';

  // Spine 3 (Mid-high fan rib):
  grid[2][0] = '#00f0ff'; grid[2][1] = '#38bdf8'; grid[2][2] = '#f8fafc'; grid[2][3] = '#cbd5e1'; grid[2][4] = '#475569';
  grid[3][5] = '#94a3b8'; grid[3][6] = '#cbd5e1';

  // Spine 4 (Center horizontal fan blade):
  grid[3][0] = '#22d3ee'; grid[3][1] = '#f1f5f9'; grid[3][2] = '#cbd5e1'; grid[3][3] = '#64748b';
  grid[4][4] = '#94a3b8'; grid[4][5] = '#cbd5e1';

  // Spine 5 (Lower fan rib):
  grid[4][1] = '#00f0ff'; grid[4][2] = '#22d3ee'; grid[4][3] = '#e2e8f0'; grid[4][4] = '#cbd5e1'; grid[4][5] = '#334155';

  // Spine 6 (Occipital lower recurve horn):
  grid[5][2] = '#00f0ff'; grid[5][3] = '#cbd5e1'; grid[5][4] = '#f8fafc'; grid[5][5] = '#64748b';
}

function _buildMonitorLockHead(grid, includeCrown = true) {
  if (includeCrown) {
    _buildMonitorLockCrown(grid);
  }

  // Occipital Skull Base & Cranial Plate Carapace (cols 7-13, rows 0-6) - Polished Platin-Chrom
  grid[0][10] = '#cbd5e1'; grid[0][11] = '#e2e8f0'; grid[0][12] = '#94a3b8';
  grid[1][10] = '#f8fafc'; grid[1][11] = '#f1f5f9'; grid[1][12] = '#cbd5e1'; grid[1][13] = '#64748b';
  grid[2][8] = '#cbd5e1'; grid[2][9] = '#f8fafc'; grid[2][10] = '#f1f5f9'; grid[2][11] = '#cbd5e1'; grid[2][12] = '#475569'; grid[2][13] = '#1e293b';
  grid[3][7] = '#94a3b8'; grid[3][8] = '#e2e8f0'; grid[3][9] = '#cbd5e1'; grid[3][10] = '#475569'; grid[3][11] = '#1e293b';
  grid[4][6] = '#64748b'; grid[4][7] = '#cbd5e1'; grid[4][8] = '#94a3b8'; grid[4][9] = '#334155'; grid[4][10] = '#0f172a';
  grid[5][6] = '#334155'; grid[5][7] = '#64748b'; grid[5][8] = '#94a3b8'; grid[5][9] = '#1e293b';

  // 2. GIANT CYAN COMPOUND SENSOR EYE ("MONITOR" DOME) (Rows 1 to 7, Cols 12 to 21)
  // Row 1: Top cranial brow rim
  grid[1][14] = '#0e7490'; grid[1][15] = '#0891b2'; grid[1][16] = '#06b6d4'; grid[1][17] = '#0891b2'; grid[1][18] = '#0e7490';
  // Row 2: Upper dome curvature & bright specular cluster
  grid[2][14] = '#0891b2'; grid[2][15] = '#22d3ee'; grid[2][16] = '#ffffff'; grid[2][17] = '#cffafe'; grid[2][18] = '#22d3ee'; grid[2][19] = '#0891b2'; grid[2][20] = '#0e7490';
  // Row 3: Faceted hexagonal ommatidia grid
  grid[3][12] = '#0891b2'; grid[3][13] = '#22d3ee'; grid[3][14] = '#00f0ff'; grid[3][15] = '#ffffff'; grid[3][16] = '#22d3ee'; grid[3][17] = '#00f0ff'; grid[3][18] = '#06b6d4'; grid[3][19] = '#0891b2'; grid[3][20] = '#155e75';
  // Row 4: Glowing core sensor nodes
  grid[4][12] = '#06b6d4'; grid[4][13] = '#00f0ff'; grid[4][14] = '#22d3ee'; grid[4][15] = '#00f0ff'; grid[4][16] = '#67e8f9'; grid[4][17] = '#00f0ff'; grid[4][18] = '#0891b2'; grid[4][19] = '#0e7490'; grid[4][20] = '#155e75';
  // Row 5: Lower dome rim
  grid[5][12] = '#0891b2'; grid[5][13] = '#06b6d4'; grid[5][14] = '#00f0ff'; grid[5][15] = '#22d3ee'; grid[5][16] = '#0891b2'; grid[5][17] = '#0e7490'; grid[5][18] = '#155e75'; grid[5][19] = '#083344';
  // Row 6: Ventral optic socket bracket
  grid[6][12] = '#0e7490'; grid[6][13] = '#0891b2'; grid[6][14] = '#06b6d4'; grid[6][15] = '#0891b2'; grid[6][16] = '#0e7490'; grid[6][17] = '#155e75';

  // 3. PREDATORY MANDIBLES / JAW (Rows 6-8, Cols 10-18) - CLOSED RESTING POSE (Platin-Silber)
  grid[6][10] = '#475569'; grid[6][11] = '#94a3b8';
  grid[7][10] = '#1e293b'; grid[7][11] = '#475569'; grid[7][12] = '#cbd5e1'; grid[7][13] = '#f1f5f9'; grid[7][14] = '#cbd5e1'; grid[7][15] = '#94a3b8'; grid[7][16] = '#475569'; grid[7][17] = '#cbd5e1'; grid[7][18] = '#f8fafc';
  grid[8][11] = '#0f172a'; grid[8][12] = '#1e293b'; grid[8][13] = '#64748b'; grid[8][14] = '#cbd5e1'; grid[8][15] = '#f8fafc'; grid[8][16] = '#cbd5e1'; grid[8][17] = '#64748b'; grid[8][18] = '#1e293b';
}

// Drops the jaw wide open for the attack "bite" pose:
// Upper beak fangs, deep visceral dark red oral cavity, needle fangs,
// and a dropped lower mandible plate clamping forward toward the enemy.
function _addMonitorLockOpenJaw(grid) {
  // Clear closed mandible line at rows 7-8 and open predatory maw
  grid[7][12] = '#334155'; grid[7][13] = '#cbd5e1'; grid[7][14] = '#f1f5f9'; grid[7][15] = '#cbd5e1'; grid[7][16] = '#f1f5f9'; grid[7][17] = '#f8fafc'; grid[7][18] = '#cbd5e1'; grid[7][19] = '#f8fafc';

  // Upper main fang & cavernous gullet (Row 8)
  grid[8][10] = '#1e293b'; grid[8][11] = '#450a0a'; grid[8][12] = '#7f1d1d'; grid[8][13] = '#020617'; grid[8][14] = '#020617'; grid[8][15] = '#020617'; grid[8][16] = '#020617'; grid[8][17] = '#020617';
  grid[8][18] = '#f1f5f9';
  grid[8][19] = null; grid[8][20] = null; // Wide open front gap

  // Deep throat, bioluminescent venom spark & lower needle fang (Row 9)
  grid[9][10] = '#0f172a'; grid[9][11] = '#450a0a'; grid[9][12] = '#991b1b'; grid[9][13] = '#020617'; grid[9][14] = '#00f0ff'; grid[9][15] = '#020617'; grid[9][16] = '#020617';
  grid[9][17] = '#f8fafc'; grid[9][18] = '#ffffff';
  grid[9][19] = null; grid[9][20] = null; // Wide open front gap

  // Dropped lower mandible plate & fangs (Row 10)
  grid[10][11] = '#0f172a'; grid[10][12] = '#1e293b'; grid[10][13] = '#475569'; grid[10][14] = '#cbd5e1'; grid[10][15] = '#f1f5f9'; grid[10][16] = '#cbd5e1'; grid[10][17] = '#94a3b8'; grid[10][18] = '#cbd5e1'; grid[10][19] = '#f8fafc';

  // Lower mandible chin spur & joint hinge (Row 11)
  grid[11][13] = '#0f172a'; grid[11][14] = '#1e293b'; grid[11][15] = '#475569'; grid[11][16] = '#94a3b8'; grid[11][17] = '#cbd5e1'; grid[11][18] = '#475569';
}

function _buildMonitorLock(grid) {
  _buildMonitorLockCrater(grid);
  _buildMonitorLockBody(grid);
  _buildMonitorLockCraterFront(grid);
  _buildMonitorLockHead(grid);
}

/**
 * Shared BODY builder for Sentinal Lock (three-winged alien bat, "Spitfire").
 * SIDE PROFILE, facing RIGHT - like every other side-scrolling unit in the
 * roster (Rocketship, A9Piranha, Processing Pillar, ...), since it flies
 * nose-first toward the enemy, not face-on toward the camera. A pointed
 * ear, a rounded cranium and a snout tapering off the right edge read as an
 * animal head in profile; torso/tail/claws trail back to the left.
 *
 * Deliberately carries NO wings - the whole point of a barrel roll around
 * the flight axis, watched from the side, is that the fuselage itself never
 * visibly rotates (the nose keeps pointing forward the entire time); only
 * the wings appear to swing. See js/engine/render/units.js for how the
 * three wing panels (SentinalLock_WingPanel below) are animated live
 * on top of this static body instead of this body being rotated as a whole.
 */
function _buildSentinalLockBody(grid) {
  // 1. Pointed ear folding back over the cranium - reddish-brown, same
  // carapace family as the torso (section 6). Only the snout tip (section 4
  // below) stays lit purple - see js/engine/render/units.js's head-tip glow
  // overlay drawn on top of this static bitmap.
  grid[4][17] = '#7c2d12';
  grid[5][16] = '#431407'; grid[5][17] = '#9a3412';
  grid[6][15] = '#431407'; grid[6][16] = '#9a3412'; grid[6][17] = '#b45309';

  // 2. Rounded cranium - reddish-brown
  grid[7][15] = '#7c2d12'; grid[7][16] = '#b45309'; grid[7][17] = '#c2410c'; grid[7][18] = '#b45309';
  grid[8][14] = '#431407'; grid[8][15] = '#9a3412'; grid[8][16] = '#c2410c'; grid[8][17] = '#c2410c'; grid[8][18] = '#c2410c'; grid[8][19] = '#b45309';

  // 3. Brow + single visible eye (side profile - not a symmetric pair) -
  // reddish-brown; the orange eye itself is an accent, not part of the
  // body/wing recolor, and stays as-is.
  grid[9][14] = '#7c2d12'; grid[9][15] = '#9a3412'; grid[9][16] = '#b45309'; grid[9][17] = '#fb923c'; grid[9][18] = '#c2410c'; grid[9][19] = '#b45309'; grid[9][20] = '#9a3412';

  // 4. Snout tapering off toward the nose tip (right edge) - THE ONLY part
  // of the head that stays lila/purple (the "glimmend" glowing tip), a
  // notch brighter than the old whole-head palette so it reads as lit
  // against the surrounding rust-brown.
  grid[10][15] = '#2e1065'; grid[10][16] = '#5b21b6'; grid[10][17] = '#6d28d9'; grid[10][18] = '#7c3aed'; grid[10][19] = '#6d28d9'; grid[10][20] = '#5b21b6'; grid[10][21] = '#4c1d95';
  grid[11][14] = '#2e1065'; grid[11][15] = '#4c1d95'; grid[11][16] = '#3b0764'; grid[11][17] = '#6d28d9'; grid[11][18] = '#7c3aed'; grid[11][19] = '#8b5cf6'; grid[11][20] = '#7c3aed'; grid[11][21] = '#5b21b6'; grid[11][22] = '#e5e7eb';

  // 5. Jaw with a single fang tooth showing at the tip - reddish-brown
  // (the underside of the head, not the glowing snout tip itself)
  grid[12][13] = '#431407'; grid[12][14] = '#7c2d12'; grid[12][15] = '#431407'; grid[12][16] = '#431407'; grid[12][17] = '#7c2d12'; grid[12][18] = '#6b2410'; grid[12][19] = '#6b2410'; grid[12][20] = '#7c2d12'; grid[12][21] = '#e5e7eb';
  grid[13][13] = '#431407';

  // 6. Torso trailing back (left) from the neck, TEAM-colored chest carapace
  grid[13][7] = '#431407'; grid[13][8] = '#7c2d12'; grid[13][9] = '#9a3412'; grid[13][10] = '#7c2d12'; grid[13][11] = '#431407'; grid[13][12] = '#431407';
  grid[14][7] = '#7c2d12'; grid[14][8] = 'TEAM'; grid[14][9] = 'TEAM'; grid[14][10] = 'TEAM'; grid[14][11] = '#7c2d12';
  grid[15][8] = '#431407'; grid[15][9] = '#6b2410'; grid[15][10] = '#431407';

  // 7. Small tucked claws either side of the torso
  grid[14][5] = '#1c0a02'; grid[15][5] = '#431407';
  grid[16][8] = '#1c0a02'; grid[16][10] = '#431407';

  // 8. Thin barbed tail trailing further back and down
  grid[16][6] = '#431407'; grid[17][5] = '#7c2d12'; grid[18][4] = '#431407';
  grid[19][3] = '#7c2d12'; grid[20][2] = '#6b2410'; grid[20][1] = '#fb923c';
}

/**
 * A single reusable wing-panel builder for Sentinal Lock: a short rigid
 * strut rooted at the BOTTOM-CENTER of its own 24x24 canvas, pointing
 * straight up, ending in a sleek, swept-back fighter-jet/swallow-wing
 * membrane plate - NOT a symmetric lens/diamond. See
 * _addSentinalLockWingStrutPanel for the asymmetric leading/trailing-edge
 * shaping that gives it its swept silhouette.
 *
 * This is deliberately its OWN small sprite, drawn THREE separate times per
 * frame in js/engine/render/units.js with a live vertical ctx.scale(1, s)
 * per instance (s = cos of that wing's own rotation phase, 120° apart) -
 * simulating each wing swinging edge-on-and-back through the screen's depth
 * axis as the whole unit barrel-rolls around its own forward (flight) axis,
 * exactly as it would look from a side-scroller camera (rotation around the
 * axis pointing at the screen, like the earlier two attempts, is the WRONG
 * axis - it looks like the unit is doing a flat spin, not a barrel roll,
 * and it drags the head/fuselage around with it instead of leaving the nose
 * pointed forward). A static, non-rotated panel like this is also what the
 * combined 'SentinalLock' fallback pose (see getSpriteGrid) bakes in once,
 * undistorted, for a non-animated thumbnail.
 */
// Reddish-brown membrane, same carapace family as the torso - shared by
// both the reusable wing-panel builder below and the combined static
// fallback pose's own inline wing draw (getSpriteGrid), which duplicates
// this call with a different hub position instead of reusing this function.
const SENTINAL_LOCK_WING_STRUT_COLOR = '#57534e';
const SENTINAL_LOCK_WING_PANEL_COLORS = ['#7c2d12', '#9a3412', '#b45309'];
const SENTINAL_LOCK_WING_EDGE_COLOR = '#ea580c';

function _buildSentinalLockWingPanel(grid) {
  _addSentinalLockWingStrutPanel(grid, 22, 12, -Math.PI / 2, 2, 8, 5, SENTINAL_LOCK_WING_STRUT_COLOR, SENTINAL_LOCK_WING_PANEL_COLORS, SENTINAL_LOCK_WING_EDGE_COLOR);
}

function _buildSentinalLockRoll(rollIdx = 0) {
  const grid = Array.from({ length: 24 }, () => Array(24).fill(null));
  const bodyGrid = Array.from({ length: 24 }, () => Array(24).fill(null));
  const wingGrid = Array.from({ length: 24 }, () => Array(24).fill(null));
  _buildSentinalLockBody(bodyGrid);
  _buildSentinalLockWingPanel(wingGrid);

  const rollBase = (rollIdx / 4) * (Math.PI * 2 / 3);
  const phases = [rollBase, rollBase + (Math.PI * 2 / 3), rollBase + (Math.PI * 4 / 3)];

  // 1. Wings swung away from camera drawn behind fuselage (sin(p) <= 0)
  for (let w = 0; w < 3; w++) {
    const p = phases[w];
    if (Math.sin(p) <= 0) {
      const s = Math.cos(p);
      if (Math.abs(s) >= 0.04) {
        for (let wy = 0; wy < 24; wy++) {
          for (let wx = 0; wx < 24; wx++) {
            if (wingGrid[wy][wx]) {
              const y = 11 + Math.round((wy - 22) * s);
              const x = wx;
              if (y >= 0 && y < 24 && x >= 0 && x < 24) {
                grid[y][x] = wingGrid[wy][wx];
              }
            }
          }
        }
      }
    }
  }

  // 2. Fuselage body
  for (let y = 0; y < 24; y++) {
    for (let x = 0; x < 24; x++) {
      if (bodyGrid[y][x]) grid[y][x] = bodyGrid[y][x];
    }
  }

  // 3. Wings swung toward camera drawn in front of fuselage (sin(p) > 0)
  for (let w = 0; w < 3; w++) {
    const p = phases[w];
    if (Math.sin(p) > 0) {
      const s = Math.cos(p);
      if (Math.abs(s) >= 0.04) {
        for (let wy = 0; wy < 24; wy++) {
          for (let wx = 0; wx < 24; wx++) {
            if (wingGrid[wy][wx]) {
              const y = 11 + Math.round((wy - 22) * s);
              const x = wx;
              if (y >= 0 && y < 24 && x >= 0 && x < 24) {
                grid[y][x] = wingGrid[wy][wx];
              }
            }
          }
        }
      }
    }
  }
  return grid;
}

/**
 * One swept-back fighter-jet/swallow-wing: a short rigid strut from the
 * root, ending in an ASYMMETRIC membrane panel - a near-straight, minimal
 * leading edge (the +perp side, which points toward the body's own
 * "forward"/nose direction - see _buildSentinalLockBody's facing-RIGHT
 * profile) and a much deeper trailing edge (-perp side) that bows out wide
 * at the root and sweeps sharply back to meet the leading edge at a single
 * pointed tip. This replaces the old symmetric lens/diamond shape (equal
 * width fore and aft, blunt at both radial ends) that read as a flat static
 * kite rather than a sleek swept wing. Riveting a contained panel to a
 * strut like this - rather than tapering a membrane straight out of the
 * torso to a point - is what keeps three of these from reading as a
 * starfish.
 */
function _addSentinalLockWingStrutPanel(grid, hubY, hubX, angle, strutLen, panelLen, maxHalfWidth, strutColor, panelColors, edgeColor) {
  const dx = Math.cos(angle);
  const dy = Math.sin(angle);
  const perpX = -dy;
  const perpY = dx;
  const hubRadius = 3;

  for (let r = hubRadius; r <= hubRadius + strutLen; r++) {
    const y = Math.round(hubY + dy * r);
    const x = Math.round(hubX + dx * r);
    if (y >= 0 && y <= 23 && x >= 0 && x <= 23) grid[y][x] = strutColor;
  }

  const panelStart = hubRadius + strutLen;
  const steps = panelLen * 2;
  for (let s = 0; s <= steps; s++) {
    const t = s / steps;
    const r = panelStart + panelLen * t;
    // The chord line itself sweeps backward (-perp, toward the tail) with
    // span - fast at first (t^0.7, concave) and far enough (2.2x, up from
    // an earlier 1.3x) that the tip lands well past the root's own trailing
    // edge instead of just barely behind it - a proper swallow-tail-like
    // point trailing out behind the wing, not a wedge that only subtly
    // outsweeps the root (an earlier sin-eased version swept the centerline
    // back too slowly for even that: the wide trailing edge, being the
    // visually dominant curve, ended up sweeping forward toward the tip -
    // a swept wing's leading edge must lead the recession, not trail it).
    const sweepBack = maxHalfWidth * 2.2 * Math.pow(t, 0.7);
    const cy = hubY + dy * r - perpY * sweepBack;
    const cx = hubX + dx * r - perpX * sweepBack;
    // Leading edge (+perp, toward the nose): thin, tapering fast - reads as
    // strongly swept back (root sits slightly ahead of the chord line, tip
    // lands far behind it). Trailing edge (-perp): wide at the root,
    // tapering just fast enough to stay roughly flush with (never ahead
    // of) the sweeping chord line, so the whole silhouette recedes toward
    // the tail as it narrows to a point, never curving forward.
    const front = maxHalfWidth * 0.28 * Math.pow(1 - t, 1.8);
    const back = maxHalfWidth * 1.1 * Math.pow(1 - t, 1.3);
    const stripe = panelColors[s % panelColors.length];
    for (let w = -back; w <= front; w += 1) {
      const py = Math.round(cy + perpY * w);
      const px = Math.round(cx + perpX * w);
      if (py < 0 || py > 23 || px < 0 || px > 23) continue;
      const isOuterRim = Math.min(front - w, w + back) <= 0.8;
      grid[py][px] = isOuterRim ? edgeColor : stripe;
    }
  }
}

/**
 * 𓉐 SUL CLAIMING PILLAR: VOLCANIC CHIMNEY & BIO-SPIRE
 * Monolithic volcanic spire rooted at the territory divider:
 * - Caldera summit with molten carmine magma & vent glow
 * - Slender obsidian needle shaft with carmine arterial veins
 * - Broad muscular base with filigree root buttresses anchoring into soil
 */
function _buildClaimingPillarGrid() {
  const gridSize = 24;
  const grid = Array.from({ length: 24 }, () => Array(24).fill(null));

  const OBS_BLACK = '#09090b';
  const OBS_DARK  = '#18181b';
  const OBS_MID   = '#27272a';
  const OBS_LIGHT = '#3f3f46';
  const OBS_HIGH  = '#52525b';

  const LAVA_CORE = '#fef08a';
  const LAVA_HOT  = '#fbbf24';
  const LAVA_BRT  = '#f97316';
  const LAVA_MID  = '#ef4444';
  const LAVA_DARK = '#b91c1c';

  // Caldera Summit (Rows 1-3)
  grid[1][11] = LAVA_CORE; grid[1][12] = LAVA_HOT;
  grid[2][10] = OBS_HIGH; grid[2][11] = LAVA_BRT; grid[2][12] = LAVA_MID; grid[2][13] = OBS_LIGHT;
  grid[3][9] = OBS_DARK; grid[3][10] = OBS_MID; grid[3][11] = LAVA_DARK; grid[3][12] = LAVA_MID; grid[3][13] = OBS_MID; grid[3][14] = OBS_DARK;

  // Slender Spire Chimney (Rows 4-13)
  for (let y = 4; y <= 13; y++) {
    const left = 9;
    const right = 14;
    for (let x = left; x <= right; x++) {
      grid[y][x] = (x === left || x === right) ? OBS_DARK : (x < 12 ? OBS_MID : OBS_LIGHT);
    }
    // Woven carmine lava fissure
    const fx = 11 + ((y % 3) === 0 ? 1 : ((y % 3) === 1 ? 0 : -1));
    grid[y][fx] = (y % 2 === 0) ? LAVA_BRT : LAVA_MID;
    if (y % 3 === 0 && fx + 1 <= right - 1) grid[y][fx + 1] = LAVA_HOT;
  }

  // Broadening Bio-Volcanic Trunk (Rows 14-19)
  for (let y = 14; y <= 19; y++) {
    const span = (y - 14) + 1;
    const left = Math.max(3, 9 - span);
    const right = Math.min(20, 14 + span);
    for (let x = left; x <= right; x++) {
      grid[y][x] = (x === left || x === right) ? OBS_BLACK : (x < 11 ? OBS_DARK : (x > 15 ? OBS_LIGHT : OBS_MID));
    }
    // Branching arterial conduits
    grid[y][10] = LAVA_MID;
    grid[y][13] = LAVA_BRT;
    if (y >= 16) {
      grid[y][left + 2] = LAVA_DARK;
      grid[y][right - 2] = LAVA_DARK;
    }
  }

  // Curved 3D Circular Base & Root Buttresses (Rows 20-23)
  // Row 20: Flaring buttress roots
  for (let x = 3; x <= 20; x++) grid[20][x] = (x === 3 || x === 20) ? OBS_BLACK : OBS_DARK;
  grid[20][6] = LAVA_DARK; grid[20][11] = LAVA_MID; grid[20][17] = LAVA_DARK;

  // Row 21: Outer flank anchoring
  for (let x = 2; x <= 21; x++) grid[21][x] = (x === 2 || x === 21) ? OBS_BLACK : (x <= 5 || x >= 18 ? OBS_DARK : OBS_MID);
  grid[21][11] = LAVA_DARK; grid[21][12] = LAVA_MID;

  // Row 22: Middle circular ground contact
  for (let x = 4; x <= 19; x++) grid[22][x] = (x === 4 || x === 19) ? OBS_BLACK : OBS_DARK;

  // Row 23: Front apex curve (lip reaching forward towards camera)
  for (let x = 8; x <= 15; x++) grid[23][x] = (x === 8 || x === 15) ? OBS_BLACK : OBS_DARK;

  return { grid, gridSize };
}

/**
 * 𓉐 SUL PROCESSING PILLAR 36x36 HD NATIVE MATRIX & BRAID RIG
 * Authentic recreation of 1_processingpillar.png:
 * - Pure tentacle cascading mass without conventional torso
 * - Spherical compound ocular lantern dome with 19 light holes/pores (Rows 2..14, Cols 15..29)
 * - Dorsal organic shoulder carapace crest protecting upper mantle (Cols 11..15, Rows 7..14)
 * - 5 vertical twisted tentacle cords + void gaps (Rows 15..27) + hanging tendril tips (Rows 28..30)
 * - 4 articulated stilt legs grounding the silhouette (Rows 22..34)
 * Modes: 'idle' | 'charge' | 'attack' | 'head' | 'body'
 */
function _buildProcessingPillarGrid(mode = 'idle') {
  const gridSize = 36;
  const grid = Array.from({ length: gridSize }, () => Array(gridSize).fill(null));

  const set = (r, c, val) => {
    if (r >= 0 && r < gridSize && c >= 0 && c < gridSize) grid[r][c] = val;
  };
  const get = (r, c) => (r >= 0 && r < gridSize && c >= 0 && c < gridSize) ? grid[r][c] : null;

  // Palette: Alien Bio-Fibrous Chitin & Muscle (Faithful earthy sepia & umber tones)
  const cShadow = '#150c07';
  const cDeep   = '#26160d';
  const cDark   = '#3e2417';
  const cMid    = '#5e3925';
  const cLight  = '#845337';
  const cHigh   = '#a86e4b';
  const cPeak   = '#cc9470';
  const cVoid   = '#080402';

  // Compound Ocular Matrix (Numerous glowing white eye holes / pores with rims)
  const holeRim = '#0d1624';
  const eCore   = '#ffffff';
  const eSky    = '#7dd3fc';

  const isHeadOnly = (mode === 'head');
  const isBodyOnly = (mode === 'body');

  // =========================================================================
  // 1. NATÜRLICHE SCHULTERPANZERUNG (Dorsale Chitin-Schulterkrone, Cols 11..15, Rows 7..14)
  // =========================================================================
  if (!isHeadOnly) {
    set(7, 12, 'TEAM');
    set(8, 11, cShadow); set(8, 12, cHigh); set(8, 13, cLight);
    set(9, 11, cShadow); set(9, 12, cMid); set(9, 13, cHigh); set(9, 14, cPeak);
    for (let y = 10; y <= 14; y++) {
      set(y, 11, cShadow);
      set(y, 12, cDeep);
      set(y, 13, (y % 2 === 0) ? cDark : cMid);
      set(y, 14, (y % 2 === 0) ? cHigh : cLight);
    }
  }

  // =========================================================================
  // 2. KUGELKOPF / LANTERN DOME MIT POREN (Rows 2..14, Cols 15..29)
  // =========================================================================
  if (!isBodyOnly) {
    for (let x = 19; x <= 25; x++) set(2, x, cShadow);
    for (let x = 17; x <= 27; x++) set(3, x, (x < 20) ? cDeep : cMid);

    for (let y = 4; y <= 14; y++) {
      const xMin = (y <= 5 || y >= 13) ? 16 : 15;
      const xMax = (y <= 5 || y >= 13) ? 28 : 29;

      for (let x = xMin; x <= xMax; x++) {
        if (x === xMin || x === xMax) {
          set(y, x, cShadow);
        } else if (x <= 18) {
          set(y, x, (x === xMin + 1) ? cDeep : cDark);
        } else {
          set(y, x, ((x + y) % 2 === 0) ? cDark : cMid);
        }
      }
    }

    // Leuchtende Poren / Löcher (19 Ocular Pores across Rows 4 to 14)
    const pores = [
      { r: 4,  c: 23 }, { r: 4,  c: 26 },
      { r: 6,  c: 21 }, { r: 6,  c: 24 }, { r: 6,  c: 27 },
      { r: 8,  c: 20 }, { r: 8,  c: 23 }, { r: 8,  c: 26 }, { r: 8, c: 28 },
      { r: 10, c: 19 }, { r: 10, c: 22 }, { r: 10, c: 25 }, { r: 10, c: 28 },
      { r: 12, c: 20 }, { r: 12, c: 23 }, { r: 12, c: 26 },
      { r: 14, c: 21 }, { r: 14, c: 24 }, { r: 14, c: 27 }
    ];

    pores.forEach(p => {
      if (get(p.r, p.c - 1)) set(p.r, p.c - 1, holeRim);
      if (get(p.r, p.c + 1)) set(p.r, p.c + 1, holeRim);
      if (get(p.r - 1, p.c)) set(p.r - 1, p.c, holeRim);
      if (get(p.r + 1, p.c)) set(p.r + 1, p.c, holeRim);
      set(p.r, p.c, eCore);
    });
  }

  // =========================================================================
  // 3. REINES TENTAKEL-BÜNDEL (NULL RUMPF / KEIN TORSO, Rows 15..27)
  // =========================================================================
  if (!isHeadOnly) {
    for (let y = 15; y <= 27; y++) {
      const s1 = Math.round(13 + Math.sin(y * 0.65) * 0.7);
      set(y, s1, cShadow);
      set(y, s1 + 1, (y % 3 === 0) ? cHigh : cMid);

      const s2 = Math.round(16 + Math.sin(y * 0.7 + 1.2) * 0.8);
      set(y, s2, cDeep);
      set(y, s2 + 1, (y % 2 === 0) ? cLight : cMid);

      const s3 = Math.round(20 + Math.sin(y * 0.7 + 2.5) * 0.8);
      set(y, s3, cShadow);
      set(y, s3 + 1, ((y + 1) % 3 === 0) ? cPeak : cHigh);

      const s4 = Math.round(23 + Math.sin(y * 0.7 + 3.8) * 0.7);
      set(y, s4, cDeep);
      set(y, s4 + 1, (y % 2 === 1) ? cLight : cMid);

      const s5 = Math.round(26 + Math.sin(y * 0.7 + 5.0) * 0.7);
      set(y, s5, cShadow);
      set(y, s5 + 1, (y % 3 === 1) ? cMid : cDark);
    }

    // Frei hängende Spitzen
    const dangles = [13, 16, 20, 24, 27];
    dangles.forEach((dx, i) => {
      set(28, dx, cDark);
      if (i % 2 === 0) {
        set(29, dx, eCore);
        set(30, dx, eSky);
      } else {
        set(29, dx, cShadow);
      }
    });

    // =========================================================================
    // 4. ARTIKULIERTE STELZENBEINE (Rows 22..34)
    // =========================================================================
    // Leg 1
    set(22, 12, cShadow); set(23, 11, cDark); set(24, 10, cMid); set(25, 9, cHigh);
    set(26, 8, cLight); set(27, 8, cPeak);
    set(28, 9, cMid); set(29, 10, cDark); set(30, 11, cShadow);
    set(31, 11, cDark); set(32, 11, cShadow); set(33, 12, cVoid); set(34, 12, cVoid);

    // Leg 2
    set(24, 15, cShadow); set(25, 15, cDark); set(26, 14, cMid);
    set(27, 14, cLight); set(28, 15, cDark); set(29, 15, cMid);
    set(30, 15, cDark); set(31, 16, cShadow); set(32, 16, cVoid); set(33, 16, cVoid);

    // Leg 3
    set(24, 22, cShadow); set(25, 22, cDark); set(26, 23, cMid);
    set(27, 23, cHigh); set(28, 21, cMid); set(29, 21, cDark);
    set(30, 21, cShadow); set(31, 20, cShadow); set(32, 20, cVoid); set(33, 20, cVoid);

    // Leg 4
    set(22, 27, cShadow); set(23, 28, cDark); set(24, 29, cMid); set(25, 30, cLight);
    set(26, 31, cHigh); set(27, 31, cPeak);
    set(28, 30, cMid); set(29, 29, cDark); set(30, 28, cShadow);
    set(31, 28, cMid); set(32, 27, cDark); set(33, 27, cShadow); set(34, 27, cVoid);
  }

  return { grid, gridSize };
}

/**
 * 𓉐 SUL HARVESTING PILLAR PROCEDURAL SPRITE GENERATOR (24x24)
 * Authentic recreation of 1_harvestingpillar.png:
 * - Crystalline / white stone crags & fortress base
 * - Two soaring rainbow-scaled segmented horns ending in razor-sharp bone fingers
 * - Central convoluted emerald brain/visceral mass with glowing red compound ocular matrix
 * - Spindly arachnid legs clasping the mineral spires
 * Modes: 'idle' | 'stalk' | 'harvest' | 'harvest2'
 */
function _buildHarvestingPillarGrid(mode = 'idle') {
  // 𓉐 SUL HARVESTING PILLAR: AUTHENTIC BIO-ELDRITCH HORROR (Facing RIGHT, +X forward)
  // - Central Body: Large green brain head with deep sulci/gyri, many glowing red eyes,
  //   and squirming ventral tendrils trailing in flight.
  // - Two Flexible Arms: Covered in brilliant iridescent rainbow scales (violet, blue, cyan, green, yellow, orange, red).
  // - Hand Claws: Radiating 7 sinister spider fingers at the arm ends.
  // - 100% Free Levitation / Flying: Discards all white stone and ground spider legs.
  const gridSize = 24;
  const grid = Array.from({ length: 24 }, () => Array(24).fill(null));

  const isHarvest = (mode === 'harvest' || mode === 'harvest2');
  const isHarvest2 = (mode === 'harvest2');
  const isStalk = (mode === 'stalk');

  // Palette: Convoluted Emerald Brain
  const brainDeep     = '#052e16'; // Deepest cerebral fissures
  const brainDark     = isHarvest2 ? '#16a34a' : '#14532d'; // Sulcus shadows
  const brainMid      = isHarvest2 ? '#22c55e' : '#16a34a'; // Cortical folds
  const brainLight    = isHarvest2 ? '#86efac' : '#4ade80'; // Gyri crests
  const brainHigh     = isHarvest2 ? '#ffffff' : (isHarvest ? '#bbf7d0' : '#86efac'); // Wet highlights

  // Palette: Many Glowing Red Eyes
  const eyeDeep       = '#450a0a';
  const eyeDark       = '#7f1d1d';
  const eyeIris       = '#dc2626';
  const eyeGlow       = isHarvest2 ? '#f87171' : (isHarvest ? '#fca5a5' : '#ef4444');
  const eyeCore       = isHarvest2 ? '#ffffff' : (isHarvest ? '#ffffff' : '#fef2f2');

  // Palette: Oil-Slick Iridescent Scaled Arms
  // The rainbow band order is kept (each slot still gets its own hue), but every
  // hue is desaturated into near-black, ink-blue and umber-brown — a delicately
  // frightening dark iridescent sheen instead of bright candy-rainbow.
  const sPurpleDark   = '#0d0c19';
  const sPurple       = '#18162a';
  const sPurpleLight  = '#332d54';

  const sBlueDark     = '#131b31';
  const sBlue         = '#1c2544';
  const sBlueLight    = '#334881';

  const sCyanDark     = '#0d2029';
  const sCyan         = '#173642';
  const sCyanLight    = '#285766';

  const sGreenDark    = '#1c1611';
  const sGreen        = '#271e18';
  const sGreenLight   = '#3d2e22';

  const sYellowDark   = '#251b0f';
  const sYellow       = '#42311c';
  const sYellowLight  = '#604731';

  const sOrangeDark   = '#1c100c';
  const sOrange       = '#36251b';
  const sOrangeLight  = '#523b2b';

  const sRedDark      = '#160c0c';
  const sRed          = '#271313';
  const sRedLight     = '#391a1a';

  // Palette: Chitin Knuckles & 7 Spider Fingers (Dark sinister claws from artwork)
  const clawDark      = '#09090b';
  const clawJoint     = '#18181b';
  const clawSpine     = '#3f3f46';
  const clawTip       = '#a1a1aa';

  // Palette: Ventral bio-tendrils / tentacles
  const tDeep         = '#052e16';
  const tDark         = '#14532d';
  const tMid          = '#16a34a';

  // =========================================================================
  // 1. BACKGROUND FLEXIBLE SCALED ARM (Arm 1, Dorsal Arch)
  // Curves high up and arches forward towards the enemy
  // =========================================================================
  // Row 0: Apex crest of Background Arm — Cyan to Green scales
  grid[0][13] = sCyanDark; grid[0][14] = sCyan; grid[0][15] = sGreen; grid[0][16] = sGreenDark;

  // Row 1: Sinuous arc — Blue to Cyan on left, Green to Yellow on right
  grid[1][11] = sBlueDark; grid[1][12] = sBlue; grid[1][13] = sCyan;
  grid[1][16] = sGreen; grid[1][17] = sYellow; grid[1][18] = sYellowDark;

  // Row 2: Descending forward curve — Purple/Blue on left, Yellow/Orange on right
  grid[2][9] = sPurpleDark; grid[2][10] = sPurple; grid[2][11] = sBlue;
  grid[2][18] = sYellow; grid[2][19] = sOrange; grid[2][20] = sOrangeDark;

  // Row 3: Purple root scales & Background Wrist (Red scales + Knuckles)
  grid[3][8] = sPurpleDark; grid[3][9] = sPurple; grid[3][10] = sPurpleLight;
  grid[3][19] = sRed; grid[3][20] = clawJoint; grid[3][21] = clawDark;

  // Row 4: Spider Finger spreading claws (Background hand)
  grid[4][7] = sPurpleDark; grid[4][8] = sPurple;
  grid[4][20] = clawSpine; grid[4][21] = clawJoint; grid[4][22] = clawTip;

  // =========================================================================
  // 2. LARGE CONVOLUTED GREEN BRAIN HEAD (Dominant Central Organism)
  // Convoluted lobes, fissures (sulci) and gyri facing RIGHT (+X)
  // =========================================================================
  // Row 5: Superior cerebral cortex dome
  grid[5][8] = brainDark; grid[5][9] = brainMid; grid[5][10] = brainLight; grid[5][11] = brainDeep;
  grid[5][12] = brainMid; grid[5][13] = brainLight; grid[5][14] = brainMid; grid[5][15] = brainDark;

  // Row 6: Upper parietal lobes + Foreground Arm root
  grid[6][6] = brainDark; grid[6][7] = brainMid; grid[6][8] = brainLight; grid[6][9] = brainDeep;
  grid[6][10] = brainMid; grid[6][11] = brainHigh; grid[6][12] = brainMid; grid[6][13] = brainDark;
  // Foreground Arm root (Purple/Blue scales)
  grid[6][14] = sPurpleDark; grid[6][15] = sPurple; grid[6][16] = sPurpleLight;

  // Row 7: Upper occipital & frontal lobes + Foreground Arm blue/cyan scales
  grid[7][5] = brainDark; grid[7][6] = brainMid; grid[7][7] = brainDeep;
  grid[7][8] = brainMid; grid[7][9] = brainLight; grid[7][10] = brainHigh; grid[7][11] = brainMid;
  grid[7][12] = brainDeep; grid[7][13] = brainMid; grid[7][14] = brainDark;
  // Foreground Arm (Blue & Cyan scales)
  grid[7][15] = sBlueDark; grid[7][16] = sBlue; grid[7][17] = sCyan;

  // Row 8: Upper eyes cluster embedded in frontal lobe + Foreground Arm cyan/green scales
  grid[8][4] = brainDark; grid[8][5] = brainMid; grid[8][6] = brainLight; grid[8][7] = brainDeep;
  grid[8][8] = brainMid; grid[8][9] = brainLight; grid[8][10] = brainMid; grid[8][11] = brainDeep;
  grid[8][12] = brainMid;
  // Eye 1 (Dorsal brow eye)
  grid[8][13] = eyeDark; grid[8][14] = eyeGlow; grid[8][15] = eyeDark;
  // Foreground Arm (Cyan, Green & Yellow scales)
  grid[8][16] = sCyan; grid[8][17] = sGreen; grid[8][18] = sYellow;

  // Row 9: Eye cluster (Upper frontal eyes) + Foreground Arm orange/red scales
  grid[9][4] = brainDark; grid[9][5] = brainMid; grid[9][6] = brainDeep;
  grid[9][7] = brainMid; grid[9][8] = brainHigh; grid[9][9] = brainMid; grid[9][10] = brainDark;
  // Eye 2 (Upper frontal eye) & Eye 3 (Temporal eye)
  grid[9][11] = eyeDark; grid[9][12] = eyeGlow; grid[9][13] = eyeDark;
  grid[9][14] = brainDeep;
  grid[9][15] = eyeDark; grid[9][16] = eyeIris; grid[9][17] = eyeGlow; grid[9][18] = eyeCore;
  // Foreground Arm (Orange & Red scales reaching forward)
  grid[9][19] = sOrange; grid[9][20] = sRed;

  // Row 10: Primary Central Ocular Cluster + Foreground Hand Wrist & Knuckles
  grid[10][4] = brainDeep; grid[10][5] = brainDark; grid[10][6] = brainMid; grid[10][7] = brainLight;
  grid[10][8] = brainMid; grid[10][9] = brainDeep; grid[10][10] = brainMid;
  // Primary forward-facing ocular matrix (Eye 4 & Eye 5)
  grid[10][11] = brainDark; grid[10][12] = eyeDark; grid[10][13] = eyeGlow; grid[10][14] = eyeIris;
  grid[10][15] = eyeDark; grid[10][16] = eyeIris; grid[10][17] = eyeCore; grid[10][18] = eyeGlow;
  grid[10][19] = eyeDark;
  // Foreground Hand Knuckles
  grid[10][20] = clawJoint; grid[10][21] = clawDark; grid[10][22] = clawSpine;

  // Row 11: Main ocular stare & radiating 7 Spider Fingers (Foreground hand)
  grid[11][5] = brainDark; grid[11][6] = brainMid; grid[11][7] = brainHigh; grid[11][8] = brainMid;
  grid[11][9] = brainDark; grid[11][10] = brainMid; grid[11][11] = brainLight;
  // Eye 6 (Central dominant eye glare)
  grid[11][12] = eyeDeep; grid[11][13] = eyeDark; grid[11][14] = eyeGlow; grid[11][15] = eyeCore;
  grid[11][16] = eyeGlow; grid[11][17] = eyeIris; grid[11][18] = eyeDark;
  // Spider Fingers (Upper claws spreading forward)
  grid[11][20] = clawSpine; grid[11][21] = clawJoint; grid[11][22] = clawTip;

  // Row 12: Mid-lower brain lobes, secondary eyes & middle spider claws
  grid[12][5] = brainDeep; grid[12][6] = brainDark; grid[12][7] = brainMid; grid[12][8] = brainDeep;
  grid[12][9] = brainMid; grid[12][10] = brainLight; grid[12][11] = brainMid;
  // Eye 7 (Lower frontal eye)
  grid[12][12] = brainDark; grid[12][13] = eyeDark; grid[12][14] = eyeIris; grid[12][15] = eyeGlow;
  grid[12][16] = eyeCore; grid[12][17] = eyeGlow; grid[12][18] = eyeDark;
  // Spider Fingers (Middle claw forward reach)
  const clawReachX = isStalk ? 23 : 22;
  grid[12][20] = clawJoint; grid[12][21] = clawSpine; grid[12][clawReachX] = clawTip;

  // Row 13: Temporal cheeks, snout eye & lower spider claws
  grid[13][6] = brainDark; grid[13][7] = brainMid; grid[13][8] = brainLight; grid[13][9] = brainDeep;
  grid[13][10] = brainMid; grid[13][11] = brainHigh; grid[13][12] = brainMid;
  // Eye 8 (Snout eye)
  grid[13][13] = eyeDark; grid[13][14] = eyeGlow; grid[13][15] = eyeIris; grid[13][16] = eyeDark;
  grid[13][17] = brainDark;
  // Spider Fingers (Lower claw cluster)
  grid[13][19] = clawJoint; grid[13][20] = clawSpine; grid[13][21] = clawTip;

  // Row 14: Lower brain curvature & bio-plasma tether mouth / aperture
  grid[14][6] = brainDeep; grid[14][7] = brainDark; grid[14][8] = brainMid; grid[14][9] = brainDeep;
  grid[14][10] = brainMid; grid[14][11] = brainLight; grid[14][12] = brainDark;
  // Eye 9 (Cheek eye)
  grid[14][13] = eyeDark; grid[14][14] = eyeGlow; grid[14][15] = eyeDark;
  // Mouth sphincter for harvesting tether
  grid[14][16] = isHarvest ? '#7e22ce' : brainDark;
  grid[14][17] = isHarvest2 ? '#ffffff' : (isHarvest ? '#c084fc' : brainLight);
  // Spider Fingers (Lowest claw curling down)
  grid[14][19] = clawSpine; grid[14][20] = clawTip;

  // Row 15: Brain underside, oral sphincter & tendril roots
  grid[15][7] = brainDark; grid[15][8] = brainMid; grid[15][9] = brainDeep;
  grid[15][10] = brainDark; grid[15][11] = brainMid; grid[15][12] = brainDark;
  grid[15][13] = brainDeep;
  grid[15][14] = isHarvest ? '#581c87' : brainDark;
  grid[15][15] = isHarvest ? '#a855f7' : brainMid;
  grid[15][16] = isHarvest ? '#7e22ce' : brainDeep;

  // =========================================================================
  // 3. SQUIRMING VENTRAL BIO-TENDRILS (Trailing below brain in levitation)
  // Floating organisms: tentacles undulate backward as the brain flies forward
  // =========================================================================
  // Row 16: Bases of 4 trailing tendrils
  grid[16][7] = tDark; grid[16][8] = tMid;
  grid[16][10] = tDark; grid[16][11] = tMid;
  grid[16][13] = tDark; grid[16][14] = tMid;

  // Row 17: Tendril shafts trailing backward (-X)
  grid[17][6] = tDark; grid[17][7] = tMid;
  grid[17][9] = tDark; grid[17][10] = tMid;
  grid[17][12] = tDark; grid[17][13] = tMid;

  // Row 18: Sinuous flex
  grid[18][6] = tDeep; grid[18][7] = tDark;
  grid[18][8] = tDark; grid[18][9] = tMid;
  grid[18][11] = tDark; grid[18][12] = tMid;

  // Row 19: Lower tapering shafts
  grid[19][5] = tDark; grid[19][6] = tMid;
  grid[19][8] = tDeep; grid[19][9] = tDark;
  grid[19][11] = tDeep; grid[19][12] = tDark;

  // Row 20: Whisker filaments
  grid[20][5] = tMid;
  grid[20][7] = tDark; grid[20][8] = tMid;
  grid[20][10] = tDark;

  // Row 21: Trailing tips
  grid[21][4] = tDark;
  grid[21][7] = tMid;
  grid[21][9] = tDark;

  // Row 22: Faint filament tips
  grid[22][4] = tDeep;
  grid[22][6] = tDark;

  return { grid, gridSize };
}

function _renderHyperTeslaWalkerLegs(grid, phase) {
  // 🦂 8-Legged Mechanical Scorpion Walker Undercarriage (y: 11..19, x: 0..19)
  // Chassis mounting girder & 4 Heavy Copper Swivel Hip Bearings
  for (let x = 4; x <= 14; x++) { grid[11][x] = '#1e293b'; grid[12][x] = '#0f172a'; }
  grid[11][4] = '#d97706'; grid[12][4] = '#b45309';
  grid[11][7] = '#f59e0b'; grid[12][7] = '#d97706';
  grid[11][10] = '#f59e0b'; grid[12][10] = '#d97706';
  grid[11][13] = '#d97706'; grid[12][13] = '#b45309';

  // Alternating Tetrapod Gait Kinematics:
  // Group A (L1, R2, L3, R4) vs Group B (R1, L2, R3, L4)
  if (phase === 0) {
    // Stand / Neutral Idle Posture
    // Leg 1 (Front Left, x: 13..18)
    grid[11][14] = '#475569'; grid[11][15] = '#64748b'; grid[12][16] = '#94a3b8';
    grid[13][17] = '#64748b'; grid[14][17] = '#475569'; grid[15][17] = '#334155';
    grid[16][17] = '#1e293b'; grid[17][18] = '#475569'; grid[18][18] = '#94a3b8'; grid[19][19] = '#cbd5e1';
    // Leg 2 (Mid-Front Left, x: 10..15)
    grid[12][11] = '#475569'; grid[12][12] = '#64748b'; grid[13][13] = '#94a3b8';
    grid[14][14] = '#64748b'; grid[15][14] = '#475569'; grid[16][15] = '#334155';
    grid[17][15] = '#1e293b'; grid[18][16] = '#475569'; grid[19][16] = '#94a3b8';
    // Leg 3 (Mid-Rear Left, x: 6..10)
    grid[12][8] = '#475569'; grid[12][7] = '#64748b'; grid[13][7] = '#94a3b8';
    grid[14][6] = '#64748b'; grid[15][6] = '#475569'; grid[16][6] = '#334155';
    grid[17][7] = '#1e293b'; grid[18][7] = '#475569'; grid[19][8] = '#94a3b8';
    // Leg 4 (Rear Left, x: 0..5)
    grid[11][3] = '#475569'; grid[11][2] = '#64748b'; grid[12][1] = '#94a3b8';
    grid[13][1] = '#64748b'; grid[14][1] = '#475569'; grid[15][1] = '#334155';
    grid[16][1] = '#1e293b'; grid[17][1] = '#475569'; grid[18][0] = '#94a3b8'; grid[19][0] = '#cbd5e1';
    // Far Side Supporting Legs (R1..R4 in shadow tones)
    grid[13][15] = '#1e293b'; grid[14][16] = '#0f172a'; grid[15][16] = '#1e293b'; grid[16][16] = '#0f172a'; grid[17][16] = '#1e293b'; grid[18][17] = '#0f172a';
    grid[13][11] = '#1e293b'; grid[14][11] = '#0f172a'; grid[15][12] = '#1e293b'; grid[16][12] = '#0f172a'; grid[17][13] = '#1e293b'; grid[18][13] = '#0f172a';
    grid[13][8] = '#1e293b'; grid[14][9] = '#0f172a'; grid[15][9] = '#1e293b'; grid[16][10] = '#0f172a'; grid[17][10] = '#1e293b'; grid[18][10] = '#0f172a';
    grid[13][4] = '#1e293b'; grid[14][3] = '#0f172a'; grid[15][3] = '#1e293b'; grid[16][3] = '#0f172a'; grid[17][2] = '#1e293b'; grid[18][2] = '#0f172a';
  } else if (phase === 1) {
    // Phase 1: Group A (L1, R2, L3, R4) Reaches Forward (+X); Group B Stance Push
    grid[10][15] = '#64748b'; grid[10][16] = '#94a3b8'; grid[11][17] = '#cbd5e1'; grid[12][18] = '#94a3b8';
    grid[13][18] = '#64748b'; grid[14][18] = '#475569'; grid[15][19] = '#334155'; grid[16][19] = '#94a3b8';
    grid[12][11] = '#475569'; grid[13][12] = '#334155'; grid[14][13] = '#1e293b'; grid[15][13] = '#334155';
    grid[16][14] = '#475569'; grid[17][14] = '#94a3b8'; grid[18][14] = '#cbd5e1';
    grid[11][8] = '#64748b'; grid[12][9] = '#94a3b8'; grid[13][9] = '#64748b'; grid[14][9] = '#475569';
    grid[15][10] = '#334155'; grid[16][10] = '#475569'; grid[17][10] = '#94a3b8'; grid[18][11] = '#cbd5e1';
    grid[12][3] = '#334155'; grid[13][2] = '#1e293b'; grid[14][1] = '#334155'; grid[15][1] = '#1e293b';
    grid[16][0] = '#475569'; grid[17][0] = '#94a3b8'; grid[18][0] = '#cbd5e1';
    // Far legs
    grid[13][14] = '#1e293b'; grid[14][15] = '#0f172a'; grid[15][15] = '#1e293b'; grid[16][16] = '#0f172a';
    grid[11][12] = '#0f172a'; grid[12][13] = '#1e293b'; grid[13][13] = '#0f172a'; grid[14][14] = '#1e293b'; grid[15][14] = '#0f172a';
    grid[13][7] = '#0f172a'; grid[14][7] = '#1e293b'; grid[15][7] = '#0f172a'; grid[16][7] = '#1e293b';
    grid[11][5] = '#0f172a'; grid[12][4] = '#1e293b'; grid[13][4] = '#0f172a'; grid[14][3] = '#1e293b';
  } else if (phase === 2) {
    // Phase 2: Weight Transfer & Hydraulic Absorption (Chassis Bob)
    grid[11][15] = '#64748b'; grid[12][16] = '#94a3b8'; grid[13][17] = '#64748b'; grid[14][17] = '#475569';
    grid[15][18] = '#334155'; grid[16][18] = '#475569'; grid[17][18] = '#94a3b8'; grid[18][19] = '#cbd5e1';
    grid[11][12] = '#64748b'; grid[12][13] = '#94a3b8'; grid[13][13] = '#64748b'; grid[14][14] = '#475569';
    grid[15][14] = '#334155'; grid[16][15] = '#475569'; grid[17][15] = '#94a3b8'; grid[18][15] = '#cbd5e1';
    grid[12][8] = '#64748b'; grid[13][8] = '#94a3b8'; grid[14][8] = '#475569'; grid[15][9] = '#334155';
    grid[16][9] = '#475569'; grid[17][9] = '#94a3b8'; grid[18][10] = '#cbd5e1';
    grid[11][3] = '#64748b'; grid[12][2] = '#94a3b8'; grid[13][1] = '#64748b'; grid[14][1] = '#475569';
    grid[15][1] = '#334155'; grid[16][1] = '#475569'; grid[17][0] = '#94a3b8'; grid[18][0] = '#cbd5e1';
    // Far legs
    grid[13][15] = '#0f172a'; grid[14][16] = '#1e293b'; grid[15][16] = '#0f172a'; grid[16][16] = '#1e293b';
    grid[13][11] = '#0f172a'; grid[14][11] = '#1e293b'; grid[15][12] = '#0f172a'; grid[16][12] = '#1e293b';
    grid[13][8] = '#0f172a'; grid[14][9] = '#1e293b'; grid[15][9] = '#0f172a'; grid[16][10] = '#1e293b';
    grid[13][4] = '#0f172a'; grid[14][3] = '#1e293b'; grid[15][3] = '#0f172a'; grid[16][3] = '#1e293b';
  } else if (phase === 3) {
    // Phase 3: Group B (R1, L2, R3, L4) Reaches Forward; Group A Stance Push
    grid[12][14] = '#475569'; grid[13][15] = '#334155'; grid[14][15] = '#1e293b'; grid[15][16] = '#334155';
    grid[16][16] = '#475569'; grid[17][17] = '#94a3b8'; grid[18][17] = '#cbd5e1';
    grid[10][12] = '#64748b'; grid[10][13] = '#94a3b8'; grid[11][14] = '#cbd5e1'; grid[12][15] = '#94a3b8';
    grid[13][15] = '#64748b'; grid[14][16] = '#475569'; grid[15][16] = '#334155'; grid[16][17] = '#94a3b8';
    grid[12][7] = '#334155'; grid[13][6] = '#1e293b'; grid[14][6] = '#334155'; grid[15][6] = '#1e293b';
    grid[16][6] = '#475569'; grid[17][7] = '#94a3b8'; grid[18][7] = '#cbd5e1';
    grid[10][4] = '#64748b'; grid[11][3] = '#94a3b8'; grid[12][2] = '#64748b'; grid[13][2] = '#475569';
    grid[14][1] = '#334155'; grid[15][1] = '#475569'; grid[16][0] = '#94a3b8'; grid[17][0] = '#cbd5e1';
    // Far legs
    grid[11][16] = '#0f172a'; grid[12][17] = '#1e293b'; grid[13][17] = '#0f172a'; grid[14][18] = '#1e293b';
    grid[13][12] = '#0f172a'; grid[14][12] = '#1e293b'; grid[15][13] = '#0f172a'; grid[16][13] = '#1e293b';
    grid[11][9] = '#0f172a'; grid[12][10] = '#1e293b'; grid[13][10] = '#0f172a'; grid[14][11] = '#1e293b';
    grid[13][3] = '#0f172a'; grid[14][2] = '#1e293b'; grid[15][2] = '#0f172a'; grid[16][1] = '#1e293b';
  } else {
    // Phase 4: Passing & Return to Balance
    grid[11][14] = '#475569'; grid[12][15] = '#64748b'; grid[13][16] = '#94a3b8'; grid[14][16] = '#64748b';
    grid[15][17] = '#475569'; grid[16][17] = '#334155'; grid[17][18] = '#94a3b8'; grid[18][18] = '#cbd5e1';
    grid[11][11] = '#475569'; grid[12][12] = '#64748b'; grid[13][13] = '#94a3b8'; grid[14][13] = '#64748b';
    grid[15][14] = '#475569'; grid[16][14] = '#334155'; grid[17][15] = '#94a3b8'; grid[18][15] = '#cbd5e1';
    grid[12][7] = '#475569'; grid[13][7] = '#64748b'; grid[14][7] = '#94a3b8'; grid[15][8] = '#475569';
    grid[16][8] = '#334155'; grid[17][8] = '#475569'; grid[18][9] = '#94a3b8'; grid[19][9] = '#cbd5e1';
    grid[11][3] = '#475569'; grid[12][2] = '#64748b'; grid[13][1] = '#94a3b8'; grid[14][1] = '#475569';
    grid[15][1] = '#334155'; grid[16][1] = '#1e293b'; grid[17][0] = '#94a3b8'; grid[18][0] = '#cbd5e1';
    // Far legs
    grid[13][15] = '#1e293b'; grid[14][15] = '#0f172a'; grid[15][16] = '#1e293b'; grid[16][16] = '#0f172a';
    grid[13][11] = '#1e293b'; grid[14][11] = '#0f172a'; grid[15][12] = '#1e293b'; grid[16][12] = '#0f172a';
    grid[13][8] = '#1e293b'; grid[14][8] = '#0f172a'; grid[15][9] = '#1e293b'; grid[16][9] = '#0f172a';
    grid[13][3] = '#1e293b'; grid[14][2] = '#0f172a'; grid[15][2] = '#1e293b'; grid[16][2] = '#0f172a';
  }
}

export function getSpriteGrid(unitType, race) {
  if (!unitType || typeof unitType !== 'string') return null;
  const cacheKey = `${unitType}_${race || 'Human'}`;
  if (globalSpriteCache[cacheKey]) {
    return globalSpriteCache[cacheKey];
  }
    
    let hash = 0;
    for (let i = 0; i < unitType.length; i++) {
      hash = ((hash << 5) - hash) + unitType.charCodeAt(i);
      hash |= 0;
    }
    const seed = Math.abs(hash);

    let gridSize = 14;
    let grid = Array.from({ length: gridSize }, () => Array(gridSize).fill(null));

    // Shared palette toolkit for the structures & props redesign pass - keeps every
    // reworked building's paneling/organic shading consistent instead of each
    // case hardcoding its own one-off tones.
    const metalLight = '#94a3b8', metalMid = '#334155', metalDark = '#1e293b';
    const organicLight = '#84cc16', organicDark = '#1a2e05';
    const psiGoldLight = '#fbbf24', psiGoldMid = '#eab308', psiGoldDark = '#92400e';

    // ==========================================
    // 1. TERRAN BESPOKE 8-BIT RETRO MODELS
    // ==========================================
    if (unitType === 'Marine_Legs' || unitType === 'Marine_Legs_Stand') {
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));
      // Pelvis / Belt harness (y: 8, x: 4 to 8)
      for (let x = 4; x <= 8; x++) grid[8][x] = '#1e293b';
      grid[8][6] = '#64748b'; // Belt buckle
      // Left Leg (y: 9 to 11, x: 3 to 5)
      for (let y = 9; y <= 11; y++) {
        grid[y][3] = '#1e293b'; grid[y][4] = '#2563eb'; grid[y][5] = '#3b82f6';
      }
      grid[10][4] = '#60a5fa'; // Knee armor cap
      // Left Boot (y: 12 to 13, x: 2 to 5)
      grid[12][2] = '#1e293b'; grid[12][3] = '#334155'; grid[12][4] = '#475569'; grid[12][5] = '#1e293b';
      grid[13][2] = '#0f172a'; grid[13][3] = '#1e293b'; grid[13][4] = '#334155'; grid[13][5] = '#0f172a';

      // Right Leg (y: 9 to 11, x: 7 to 9)
      for (let y = 9; y <= 11; y++) {
        grid[y][7] = '#1e293b'; grid[y][8] = '#2563eb'; grid[y][9] = '#3b82f6';
      }
      grid[10][8] = '#60a5fa'; // Knee armor cap
      // Right Boot (y: 12 to 13, x: 6 to 9)
      grid[12][6] = '#1e293b'; grid[12][7] = '#334155'; grid[12][8] = '#475569'; grid[12][9] = '#1e293b';
      grid[13][6] = '#0f172a'; grid[13][7] = '#1e293b'; grid[13][8] = '#334155'; grid[13][9] = '#0f172a';
    }
    else if (unitType === 'Marine_Legs_Walk1') {
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));
      // Belt harness
      for (let x = 4; x <= 8; x++) grid[8][x] = '#1e293b';
      grid[8][6] = '#64748b';
      // 1. Left Stride Forward (+X, x: 6 to 10)
      grid[9][6] = '#1e293b'; grid[9][7] = '#2563eb'; grid[9][8] = '#3b82f6';
      grid[10][7] = '#2563eb'; grid[10][8] = '#60a5fa'; grid[10][9] = '#3b82f6';
      grid[11][8] = '#1e293b'; grid[11][9] = '#2563eb'; grid[11][10] = '#3b82f6';
      grid[12][8] = '#1e293b'; grid[12][9] = '#334155'; grid[12][10] = '#475569'; grid[12][11] = '#1e293b';
      grid[13][8] = '#0f172a'; grid[13][9] = '#1e293b'; grid[13][10] = '#334155'; grid[13][11] = '#0f172a';
      // 2. Right Push Leg Trailing Back (-X, x: 1 to 5)
      grid[9][4] = '#1e293b'; grid[9][5] = '#2563eb';
      grid[10][3] = '#1e293b'; grid[10][4] = '#2563eb'; grid[10][5] = '#60a5fa';
      grid[11][2] = '#0f172a'; grid[11][3] = '#1e293b'; grid[11][4] = '#334155';
      grid[12][1] = '#0f172a'; grid[12][2] = '#1e293b'; grid[12][3] = '#334155';
      grid[13][2] = '#0f172a'; grid[13][3] = '#1e293b';
    }
    else if (unitType === 'Marine_Legs_Walk2') {
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));
      // Belt harness
      for (let x = 4; x <= 8; x++) grid[8][x] = '#1e293b';
      grid[8][6] = '#64748b';
      // Left Support Leg Flat Under Hip (x: 5 to 8)
      grid[9][5] = '#1e293b'; grid[9][6] = '#2563eb'; grid[9][7] = '#3b82f6';
      grid[10][5] = '#1e293b'; grid[10][6] = '#2563eb'; grid[10][7] = '#60a5fa';
      grid[11][5] = '#1e293b'; grid[11][6] = '#2563eb'; grid[11][7] = '#3b82f6';
      grid[12][5] = '#1e293b'; grid[12][6] = '#334155'; grid[12][7] = '#475569'; grid[12][8] = '#1e293b';
      grid[13][5] = '#0f172a'; grid[13][6] = '#1e293b'; grid[13][7] = '#334155'; grid[13][8] = '#0f172a';
      // Right Leg Lifting Behind (x: 2 to 5)
      grid[9][3] = '#1e293b'; grid[9][4] = '#2563eb';
      grid[10][2] = '#1e293b'; grid[10][3] = '#60a5fa'; grid[10][4] = '#3b82f6';
      grid[11][2] = '#0f172a'; grid[11][3] = '#1e293b'; grid[11][4] = '#334155';
      grid[12][2] = '#0f172a'; grid[12][3] = '#1e293b';
    }
    else if (unitType === 'Marine_Legs_Walk3') {
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));
      // Belt harness
      for (let x = 4; x <= 8; x++) grid[8][x] = '#1e293b';
      grid[8][6] = '#64748b';
      // Left Straight Supporting Leg (x: 4 to 6)
      grid[9][4] = '#1e293b'; grid[9][5] = '#2563eb'; grid[9][6] = '#3b82f6';
      grid[10][4] = '#1e293b'; grid[10][5] = '#2563eb'; grid[10][6] = '#60a5fa';
      grid[11][4] = '#1e293b'; grid[11][5] = '#2563eb'; grid[11][6] = '#3b82f6';
      grid[12][4] = '#1e293b'; grid[12][5] = '#334155'; grid[12][6] = '#475569';
      grid[13][4] = '#0f172a'; grid[13][5] = '#1e293b'; grid[13][6] = '#334155';
      // Right Passing Leg (Lifted High Knee Forward, x: 6 to 9)
      grid[9][7] = '#1e293b'; grid[9][8] = '#2563eb';
      grid[10][7] = '#60a5fa'; grid[10][8] = '#3b82f6'; grid[10][9] = '#60a5fa';
      grid[11][7] = '#1e293b'; grid[11][8] = '#334155'; grid[11][9] = '#475569';
      grid[12][7] = '#0f172a'; grid[12][8] = '#1e293b';
    }
    else if (unitType === 'Marine_Legs_Walk4') {
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));
      // Belt harness
      for (let x = 4; x <= 8; x++) grid[8][x] = '#1e293b';
      grid[8][6] = '#64748b';
      // 1. Right Stride Forward (+X, x: 6 to 10)
      grid[9][6] = '#1e293b'; grid[9][7] = '#2563eb'; grid[9][8] = '#3b82f6';
      grid[10][7] = '#1e293b'; grid[10][8] = '#60a5fa'; grid[10][9] = '#3b82f6';
      grid[11][7] = '#1e293b'; grid[11][8] = '#2563eb'; grid[11][9] = '#3b82f6'; grid[11][10] = '#1e293b';
      grid[12][7] = '#1e293b'; grid[12][8] = '#334155'; grid[12][9] = '#475569'; grid[12][10] = '#1e293b';
      grid[13][8] = '#0f172a'; grid[13][9] = '#1e293b'; grid[13][10] = '#334155';
      // 2. Left Push Leg Trailing Back (-X, x: 1 to 5)
      grid[9][4] = '#1e293b'; grid[9][5] = '#2563eb';
      grid[10][3] = '#1e293b'; grid[10][4] = '#60a5fa'; grid[10][5] = '#2563eb';
      grid[11][2] = '#0f172a'; grid[11][3] = '#1e293b'; grid[11][4] = '#334155';
      grid[12][1] = '#0f172a'; grid[12][2] = '#1e293b'; grid[12][3] = '#334155';
      grid[13][1] = '#0f172a'; grid[13][2] = '#1e293b';
    }
    else if (unitType === 'Marine_Legs_Walk5') {
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));
      // Belt harness
      for (let x = 4; x <= 8; x++) grid[8][x] = '#1e293b';
      grid[8][6] = '#64748b';
      // Right Support Leg Flat Under Hip (x: 5 to 8)
      grid[9][5] = '#1e293b'; grid[9][6] = '#2563eb'; grid[9][7] = '#3b82f6';
      grid[10][5] = '#1e293b'; grid[10][6] = '#2563eb'; grid[10][7] = '#60a5fa';
      grid[11][5] = '#1e293b'; grid[11][6] = '#2563eb'; grid[11][7] = '#3b82f6';
      grid[12][5] = '#1e293b'; grid[12][6] = '#334155'; grid[12][7] = '#475569'; grid[12][8] = '#1e293b';
      grid[13][5] = '#0f172a'; grid[13][6] = '#1e293b'; grid[13][7] = '#334155'; grid[13][8] = '#0f172a';
      // Left Leg Lifting Behind (x: 2 to 5)
      grid[9][3] = '#1e293b'; grid[9][4] = '#2563eb';
      grid[10][2] = '#1e293b'; grid[10][3] = '#60a5fa'; grid[10][4] = '#3b82f6';
      grid[11][2] = '#0f172a'; grid[11][3] = '#1e293b'; grid[11][4] = '#334155';
      grid[12][2] = '#0f172a'; grid[12][3] = '#1e293b';
    }
    else if (unitType === 'Marine_Legs_Walk6') {
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));
      // Belt harness
      for (let x = 4; x <= 8; x++) grid[8][x] = '#1e293b';
      grid[8][6] = '#64748b';
      // Right Straight Supporting Leg (x: 4 to 6)
      grid[9][4] = '#1e293b'; grid[9][5] = '#2563eb'; grid[9][6] = '#3b82f6';
      grid[10][4] = '#1e293b'; grid[10][5] = '#2563eb'; grid[10][6] = '#60a5fa';
      grid[11][4] = '#1e293b'; grid[11][5] = '#2563eb'; grid[11][6] = '#3b82f6';
      grid[12][4] = '#1e293b'; grid[12][5] = '#334155'; grid[12][6] = '#475569';
      grid[13][4] = '#0f172a'; grid[13][5] = '#1e293b'; grid[13][6] = '#334155';
      // Left Passing Leg (Lifted High Knee Forward, x: 6 to 9)
      grid[9][7] = '#1e293b'; grid[9][8] = '#2563eb';
      grid[10][7] = '#60a5fa'; grid[10][8] = '#3b82f6'; grid[10][9] = '#60a5fa';
      grid[11][7] = '#1e293b'; grid[11][8] = '#334155'; grid[11][9] = '#475569';
      grid[12][7] = '#0f172a'; grid[12][8] = '#1e293b';
    }
    else if (unitType === 'Marine_Torso') {
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));
      // Rear life support backpack (y: 3 to 7, x: 1 to 3)
      for (let y = 3; y <= 7; y++) {
        grid[y][1] = '#0f172a'; grid[y][2] = '#1e293b'; grid[y][3] = '#334155';
      }
      grid[4][1] = '#38bdf8'; grid[5][1] = '#00f0ff'; // Cyan power cells
      // Helmet & Golden Visor (y: 1 to 4, x: 5 to 9)
      for (let y = 1; y <= 4; y++) {
        for (let x = 5; x <= 8; x++) grid[y][x] = '#2563eb';
      }
      grid[1][6] = '#60a5fa'; grid[1][7] = '#93c5fd'; // Helmet top highlight
      // Golden Visor & Headlamp
      grid[2][7] = '#facc15'; grid[2][8] = '#fde047';
      grid[3][7] = '#eab308'; grid[3][8] = '#facc15';
      grid[2][9] = '#38bdf8'; // Tactical visor HUD light
      // Chestplate & Team Pauldrons (y: 4 to 8, x: 3 to 9)
      for (let y = 4; y <= 8; y++) {
        for (let x = 4; x <= 8; x++) grid[y][x] = '#2563eb';
      }
      // Left & Right Pauldrons
      grid[4][3] = '#1e293b'; grid[5][3] = '#3b82f6'; grid[6][3] = '#1e293b';
      // Team Emblem on chest
      grid[5][5] = 'TEAM'; grid[5][6] = 'TEAM';
      grid[6][5] = 'TEAM'; grid[6][6] = 'TEAM';
      grid[4][5] = '#60a5fa'; // Armor highlight
      // Respirator vents
      grid[4][7] = '#1e293b'; grid[4][8] = '#475569';
    }
    else if (unitType === 'MarineBlack_Torso') {
      // Kampagnen-exklusiver Reskin von 'Marine_Torso' (identisches Grid/
      // Layout, siehe dort) - nur die Blau-Palette ist durch eine
      // Schwarz/Anthrazit-Palette ersetzt. Wird ausschließlich von
      // Kampagnen-Karten mit cardData.spriteRecolor === 'black' angefragt
      // (js/engine/render/units.js), nie von der regulären PvP-Marine-Karte.
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));
      // Rear life support backpack (y: 3 to 7, x: 1 to 3)
      for (let y = 3; y <= 7; y++) {
        grid[y][1] = '#0f172a'; grid[y][2] = '#1e293b'; grid[y][3] = '#334155';
      }
      grid[4][1] = '#38bdf8'; grid[5][1] = '#00f0ff'; // Cyan power cells
      // Helmet & Golden Visor (y: 1 to 4, x: 5 to 9)
      for (let y = 1; y <= 4; y++) {
        for (let x = 5; x <= 8; x++) grid[y][x] = '#18181b';
      }
      grid[1][6] = '#52525b'; grid[1][7] = '#71717a'; // Helmet top highlight
      // Golden Visor & Headlamp
      grid[2][7] = '#facc15'; grid[2][8] = '#fde047';
      grid[3][7] = '#eab308'; grid[3][8] = '#facc15';
      grid[2][9] = '#38bdf8'; // Tactical visor HUD light
      // Chestplate & Team Pauldrons (y: 4 to 8, x: 3 to 9)
      for (let y = 4; y <= 8; y++) {
        for (let x = 4; x <= 8; x++) grid[y][x] = '#18181b';
      }
      // Left & Right Pauldrons
      grid[4][3] = '#1e293b'; grid[5][3] = '#3f3f46'; grid[6][3] = '#1e293b';
      // Team Emblem on chest
      grid[5][5] = 'TEAM'; grid[5][6] = 'TEAM';
      grid[6][5] = 'TEAM'; grid[6][6] = 'TEAM';
      grid[4][5] = '#52525b'; // Armor highlight
      // Respirator vents
      grid[4][7] = '#1e293b'; grid[4][8] = '#475569';
    }
    else if (unitType === 'Marine_Gun') {
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));
      // Marine Arms & Hands holding Gauss Rifle (y: 5 to 7, x: 5 to 8)
      grid[5][5] = '#2563eb'; grid[5][6] = '#3b82f6'; // Arm sleeve
      grid[6][6] = '#1e293b'; grid[6][7] = '#334155'; // Armored gauntlet grip
      // C-14 Impaler Gauss Rifle Body (y: 5 to 8, x: 7 to 13)
      grid[6][7] = '#0f172a'; grid[6][8] = '#1e293b'; // Receiver & stock
      grid[7][8] = '#0f172a'; grid[7][9] = '#1e293b'; // Magazine box
      // Barrel & Shroud (y: 5 to 6, x: 9 to 13)
      grid[5][9] = '#475569'; grid[5][10] = '#64748b'; grid[5][11] = '#94a3b8'; grid[5][12] = '#cbd5e1';
      grid[6][9] = '#334155'; grid[6][10] = '#475569'; grid[6][11] = '#64748b'; grid[6][12] = '#94a3b8';
      // Muzzle Brake / Flash suppressor Tip
      grid[5][13] = '#cbd5e1'; grid[6][13] = '#ffffff';
    }
    else if (unitType === 'HyperMarine_Legs' || unitType === 'HyperMarine_Legs_Stand') {
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));
      // Pelvis / Belt harness (y: 8, x: 4 to 8)
      for (let x = 4; x <= 8; x++) grid[8][x] = '#1c1210';
      grid[8][6] = '#64748b'; // Belt buckle
      // Left Leg (y: 9 to 11, x: 3 to 5)
      for (let y = 9; y <= 11; y++) {
        grid[y][3] = '#1c1210'; grid[y][4] = '#b91c1c'; grid[y][5] = '#ef4444';
      }
      grid[10][4] = '#f87171'; // Knee armor cap
      // Left Boot (y: 12 to 13, x: 2 to 5)
      grid[12][2] = '#1c1210'; grid[12][3] = '#450a0a'; grid[12][4] = '#7f1d1d'; grid[12][5] = '#1c1210';
      grid[13][2] = '#120a08'; grid[13][3] = '#1c1210'; grid[13][4] = '#450a0a'; grid[13][5] = '#120a08';

      // Right Leg (y: 9 to 11, x: 7 to 9)
      for (let y = 9; y <= 11; y++) {
        grid[y][7] = '#1c1210'; grid[y][8] = '#b91c1c'; grid[y][9] = '#ef4444';
      }
      grid[10][8] = '#f87171'; // Knee armor cap
      // Right Boot (y: 12 to 13, x: 6 to 9)
      grid[12][6] = '#1c1210'; grid[12][7] = '#450a0a'; grid[12][8] = '#7f1d1d'; grid[12][9] = '#1c1210';
      grid[13][6] = '#120a08'; grid[13][7] = '#1c1210'; grid[13][8] = '#450a0a'; grid[13][9] = '#120a08';
    }
    else if (unitType === 'HyperMarine_Legs_Walk1') {
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));
      // Belt harness
      for (let x = 4; x <= 8; x++) grid[8][x] = '#1c1210';
      grid[8][6] = '#64748b';
      // 1. Left Stride Forward (+X, x: 6 to 10)
      grid[9][6] = '#1c1210'; grid[9][7] = '#b91c1c'; grid[9][8] = '#ef4444';
      grid[10][7] = '#b91c1c'; grid[10][8] = '#f87171'; grid[10][9] = '#ef4444';
      grid[11][8] = '#1c1210'; grid[11][9] = '#b91c1c'; grid[11][10] = '#ef4444';
      grid[12][8] = '#1c1210'; grid[12][9] = '#450a0a'; grid[12][10] = '#7f1d1d'; grid[12][11] = '#1c1210';
      grid[13][8] = '#120a08'; grid[13][9] = '#1c1210'; grid[13][10] = '#450a0a'; grid[13][11] = '#120a08';
      // 2. Right Push Leg Trailing Back (-X, x: 1 to 5)
      grid[9][4] = '#1c1210'; grid[9][5] = '#b91c1c';
      grid[10][3] = '#1c1210'; grid[10][4] = '#b91c1c'; grid[10][5] = '#f87171';
      grid[11][2] = '#120a08'; grid[11][3] = '#1c1210'; grid[11][4] = '#450a0a';
      grid[12][1] = '#120a08'; grid[12][2] = '#1c1210'; grid[12][3] = '#450a0a';
      grid[13][2] = '#120a08'; grid[13][3] = '#1c1210';
    }
    else if (unitType === 'HyperMarine_Legs_Walk2') {
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));
      // Belt harness
      for (let x = 4; x <= 8; x++) grid[8][x] = '#1c1210';
      grid[8][6] = '#64748b';
      // Left Support Leg Flat Under Hip (x: 5 to 8)
      grid[9][5] = '#1c1210'; grid[9][6] = '#b91c1c'; grid[9][7] = '#ef4444';
      grid[10][5] = '#1c1210'; grid[10][6] = '#b91c1c'; grid[10][7] = '#f87171';
      grid[11][5] = '#1c1210'; grid[11][6] = '#b91c1c'; grid[11][7] = '#ef4444';
      grid[12][5] = '#1c1210'; grid[12][6] = '#450a0a'; grid[12][7] = '#7f1d1d'; grid[12][8] = '#1c1210';
      grid[13][5] = '#120a08'; grid[13][6] = '#1c1210'; grid[13][7] = '#450a0a'; grid[13][8] = '#120a08';
      // Right Leg Lifting Behind (x: 2 to 5)
      grid[9][3] = '#1c1210'; grid[9][4] = '#b91c1c';
      grid[10][2] = '#1c1210'; grid[10][3] = '#f87171'; grid[10][4] = '#ef4444';
      grid[11][2] = '#120a08'; grid[11][3] = '#1c1210'; grid[11][4] = '#450a0a';
      grid[12][2] = '#120a08'; grid[12][3] = '#1c1210';
    }
    else if (unitType === 'HyperMarine_Legs_Walk3') {
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));
      // Belt harness
      for (let x = 4; x <= 8; x++) grid[8][x] = '#1c1210';
      grid[8][6] = '#64748b';
      // Left Straight Supporting Leg (x: 4 to 6)
      grid[9][4] = '#1c1210'; grid[9][5] = '#b91c1c'; grid[9][6] = '#ef4444';
      grid[10][4] = '#1c1210'; grid[10][5] = '#b91c1c'; grid[10][6] = '#f87171';
      grid[11][4] = '#1c1210'; grid[11][5] = '#b91c1c'; grid[11][6] = '#ef4444';
      grid[12][4] = '#1c1210'; grid[12][5] = '#450a0a'; grid[12][6] = '#7f1d1d';
      grid[13][4] = '#120a08'; grid[13][5] = '#1c1210'; grid[13][6] = '#450a0a';
      // Right Passing Leg (Lifted High Knee Forward, x: 6 to 9)
      grid[9][7] = '#1c1210'; grid[9][8] = '#b91c1c';
      grid[10][7] = '#f87171'; grid[10][8] = '#ef4444'; grid[10][9] = '#f87171';
      grid[11][7] = '#1c1210'; grid[11][8] = '#450a0a'; grid[11][9] = '#7f1d1d';
      grid[12][7] = '#120a08'; grid[12][8] = '#1c1210';
    }
    else if (unitType === 'HyperMarine_Legs_Walk4') {
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));
      // Belt harness
      for (let x = 4; x <= 8; x++) grid[8][x] = '#1c1210';
      grid[8][6] = '#64748b';
      // 1. Right Stride Forward (+X, x: 6 to 10)
      grid[9][6] = '#1c1210'; grid[9][7] = '#b91c1c'; grid[9][8] = '#ef4444';
      grid[10][7] = '#1c1210'; grid[10][8] = '#f87171'; grid[10][9] = '#ef4444';
      grid[11][7] = '#1c1210'; grid[11][8] = '#b91c1c'; grid[11][9] = '#ef4444'; grid[11][10] = '#1c1210';
      grid[12][7] = '#1c1210'; grid[12][8] = '#450a0a'; grid[12][9] = '#7f1d1d'; grid[12][10] = '#1c1210';
      grid[13][8] = '#120a08'; grid[13][9] = '#1c1210'; grid[13][10] = '#450a0a';
      // 2. Left Push Leg Trailing Back (-X, x: 1 to 5)
      grid[9][4] = '#1c1210'; grid[9][5] = '#b91c1c';
      grid[10][3] = '#1c1210'; grid[10][4] = '#f87171'; grid[10][5] = '#b91c1c';
      grid[11][2] = '#120a08'; grid[11][3] = '#1c1210'; grid[11][4] = '#450a0a';
      grid[12][1] = '#120a08'; grid[12][2] = '#1c1210'; grid[12][3] = '#450a0a';
      grid[13][1] = '#120a08'; grid[13][2] = '#1c1210';
    }
    else if (unitType === 'HyperMarine_Legs_Walk5') {
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));
      // Belt harness
      for (let x = 4; x <= 8; x++) grid[8][x] = '#1c1210';
      grid[8][6] = '#64748b';
      // Right Support Leg Flat Under Hip (x: 5 to 8)
      grid[9][5] = '#1c1210'; grid[9][6] = '#b91c1c'; grid[9][7] = '#ef4444';
      grid[10][5] = '#1c1210'; grid[10][6] = '#b91c1c'; grid[10][7] = '#f87171';
      grid[11][5] = '#1c1210'; grid[11][6] = '#b91c1c'; grid[11][7] = '#ef4444';
      grid[12][5] = '#1c1210'; grid[12][6] = '#450a0a'; grid[12][7] = '#7f1d1d'; grid[12][8] = '#1c1210';
      grid[13][5] = '#120a08'; grid[13][6] = '#1c1210'; grid[13][7] = '#450a0a'; grid[13][8] = '#120a08';
      // Left Leg Lifting Behind (x: 2 to 5)
      grid[9][3] = '#1c1210'; grid[9][4] = '#b91c1c';
      grid[10][2] = '#1c1210'; grid[10][3] = '#f87171'; grid[10][4] = '#ef4444';
      grid[11][2] = '#120a08'; grid[11][3] = '#1c1210'; grid[11][4] = '#450a0a';
      grid[12][2] = '#120a08'; grid[12][3] = '#1c1210';
    }
    else if (unitType === 'HyperMarine_Legs_Walk6') {
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));
      // Belt harness
      for (let x = 4; x <= 8; x++) grid[8][x] = '#1c1210';
      grid[8][6] = '#64748b';
      // Right Straight Supporting Leg (x: 4 to 6)
      grid[9][4] = '#1c1210'; grid[9][5] = '#b91c1c'; grid[9][6] = '#ef4444';
      grid[10][4] = '#1c1210'; grid[10][5] = '#b91c1c'; grid[10][6] = '#f87171';
      grid[11][4] = '#1c1210'; grid[11][5] = '#b91c1c'; grid[11][6] = '#ef4444';
      grid[12][4] = '#1c1210'; grid[12][5] = '#450a0a'; grid[12][6] = '#7f1d1d';
      grid[13][4] = '#120a08'; grid[13][5] = '#1c1210'; grid[13][6] = '#450a0a';
      // Left Passing Leg (Lifted High Knee Forward, x: 6 to 9)
      grid[9][7] = '#1c1210'; grid[9][8] = '#b91c1c';
      grid[10][7] = '#f87171'; grid[10][8] = '#ef4444'; grid[10][9] = '#f87171';
      grid[11][7] = '#1c1210'; grid[11][8] = '#450a0a'; grid[11][9] = '#7f1d1d';
      grid[12][7] = '#120a08'; grid[12][8] = '#1c1210';
    }
    else if (unitType === 'HyperMarine_Torso') {
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));
      // Rear life support backpack (y: 3 to 7, x: 1 to 3)
      for (let y = 3; y <= 7; y++) {
        grid[y][1] = '#120a08'; grid[y][2] = '#1c1210'; grid[y][3] = '#450a0a';
      }
      grid[4][1] = '#fb923c'; grid[5][1] = '#ffb454'; // Cyan power cells
      // Helmet & Golden Visor (y: 1 to 4, x: 5 to 9)
      for (let y = 1; y <= 4; y++) {
        for (let x = 5; x <= 8; x++) grid[y][x] = '#b91c1c';
      }
      grid[1][6] = '#f87171'; grid[1][7] = '#fecaca'; // Helmet top highlight
      // Golden Visor & Headlamp
      grid[2][7] = '#facc15'; grid[2][8] = '#fde047';
      grid[3][7] = '#eab308'; grid[3][8] = '#facc15';
      grid[2][9] = '#fb923c'; // Tactical visor HUD light
      // Chestplate & Team Pauldrons (y: 4 to 8, x: 3 to 9)
      for (let y = 4; y <= 8; y++) {
        for (let x = 4; x <= 8; x++) grid[y][x] = '#b91c1c';
      }
      // Left & Right Pauldrons
      grid[4][3] = '#1c1210'; grid[5][3] = '#ef4444'; grid[6][3] = '#1c1210';
      // Team Emblem on chest
      grid[5][5] = 'TEAM'; grid[5][6] = 'TEAM';
      grid[6][5] = 'TEAM'; grid[6][6] = 'TEAM';
      grid[4][5] = '#f87171'; // Armor highlight
      // Respirator vents
      grid[4][7] = '#1c1210'; grid[4][8] = '#7f1d1d';
    }
    else if (unitType === 'HyperMarine_Gun') {
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));
      // Hyper Marine Arms & Hands holding Gauss Rifle (y: 5 to 7, x: 5 to 8)
      grid[5][5] = '#b91c1c'; grid[5][6] = '#ef4444'; // Arm sleeve
      grid[6][6] = '#1c1210'; grid[6][7] = '#334155'; // Armored gauntlet grip
      // C-14 Impaler Gauss Rifle Body (y: 5 to 8, x: 7 to 13)
      grid[6][7] = '#0f172a'; grid[6][8] = '#1e293b'; // Receiver & stock
      grid[7][8] = '#0f172a'; grid[7][9] = '#1e293b'; // Magazine box
      // Barrel & Shroud (y: 5 to 6, x: 9 to 13)
      grid[5][9] = '#475569'; grid[5][10] = '#64748b'; grid[5][11] = '#94a3b8'; grid[5][12] = '#cbd5e1';
      grid[6][9] = '#334155'; grid[6][10] = '#475569'; grid[6][11] = '#64748b'; grid[6][12] = '#94a3b8';
      // Muzzle Brake / Flash suppressor Tip
      grid[5][13] = '#cbd5e1'; grid[6][13] = '#ffffff';
    }
    else if (unitType === 'HyperMarine') {
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));
      // 🌋 HYPER MARINE: Full Assembled 14x14 Model (Molten Titan Exoskeleton)
      // 1. Backpack & Helmet (y: 1 to 4, x: 1 to 9)
      for (let y = 3; y <= 7; y++) {
        grid[y][1] = '#120a08'; grid[y][2] = '#1c1210';
      }
      grid[4][1] = '#fb923c'; grid[5][1] = '#ffb454'; // Molten plasma vents
      for (let y = 1; y <= 4; y++) for (let x = 5; x <= 8; x++) grid[y][x] = '#b91c1c';
      grid[1][6] = '#f87171'; grid[1][7] = '#fecaca'; // Helmet top highlight
      grid[2][7] = '#facc15'; grid[2][8] = '#fde047'; // Golden Visor
      grid[3][7] = '#eab308'; grid[3][8] = '#facc15';
      grid[2][9] = '#fb923c'; // Tactical visor HUD light
      // 2. Torso & Arms (y: 4 to 8, x: 3 to 8)
      for (let y = 4; y <= 8; y++) for (let x = 3; x <= 8; x++) grid[y][x] = '#b91c1c';
      grid[4][3] = '#1c1210'; grid[5][3] = '#ef4444'; grid[6][3] = '#1c1210'; // Pauldron
      grid[5][5] = 'TEAM'; grid[5][6] = 'TEAM';
      grid[6][5] = 'TEAM'; grid[6][6] = 'TEAM';
      grid[4][5] = '#f87171'; // Armor highlight
      grid[4][7] = '#1c1210'; grid[4][8] = '#7f1d1d'; // Respirator vents
      // 3. Hyper C-14 Impaler Gauss Rifle
      grid[6][7] = '#0f172a'; grid[6][8] = '#1e293b'; grid[7][8] = '#0f172a'; grid[7][9] = '#1e293b';
      grid[5][9] = '#475569'; grid[5][10] = '#64748b'; grid[5][11] = '#94a3b8'; grid[5][12] = '#cbd5e1';
      grid[6][9] = '#334155'; grid[6][10] = '#475569'; grid[6][11] = '#64748b'; grid[6][12] = '#94a3b8';
      grid[5][13] = '#cbd5e1'; grid[6][13] = '#ffffff';
      // 4. Belt, Legs & Boots (y: 8 to 13)
      for (let x = 4; x <= 8; x++) grid[8][x] = '#1c1210';
      grid[8][6] = '#64748b'; // Belt buckle
      for (let y = 9; y <= 11; y++) {
        grid[y][3] = '#1c1210'; grid[y][4] = '#b91c1c'; grid[y][5] = '#ef4444';
        grid[y][7] = '#1c1210'; grid[y][8] = '#b91c1c'; grid[y][9] = '#ef4444';
      }
      grid[10][4] = '#f87171'; grid[10][8] = '#f87171'; // Knee armor caps
      grid[12][2] = '#1c1210'; grid[12][3] = '#450a0a'; grid[12][4] = '#7f1d1d';
      grid[13][2] = '#120a08'; grid[13][3] = '#1c1210'; grid[13][4] = '#450a0a';
      grid[12][6] = '#1c1210'; grid[12][7] = '#450a0a'; grid[12][8] = '#7f1d1d';
      grid[13][6] = '#120a08'; grid[13][7] = '#1c1210'; grid[13][8] = '#450a0a';
    }
    // ==========================================
    // DEVIL PATROL 8-BIT BESPOKE RETRO RIG
    // ==========================================
    else if (unitType === 'DevilPatrol_Legs' || unitType === 'DevilPatrol_Legs_Stand') {
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));
      // Belt / Heat shield harness (y: 8, x: 4 to 8)
      for (let x = 4; x <= 8; x++) grid[8][x] = '#1c1917';
      grid[8][6] = '#f97316'; // Orange thermal regulator
      // Left Leg (y: 9 to 11, x: 3 to 5)
      for (let y = 9; y <= 11; y++) {
        grid[y][3] = '#1c1917'; grid[y][4] = '#44403c'; grid[y][5] = '#78350f';
      }
      grid[10][4] = '#b45309'; // Bronze knee cap
      // Left Boot (y: 12 to 13, x: 2 to 5)
      grid[12][2] = '#1c1917'; grid[12][3] = '#292524'; grid[12][4] = '#44403c'; grid[12][5] = '#1c1917';
      grid[13][2] = '#0c0a09'; grid[13][3] = '#1c1917'; grid[13][4] = '#292524'; grid[13][5] = '#0c0a09';

      // Right Leg (y: 9 to 11, x: 7 to 9)
      for (let y = 9; y <= 11; y++) {
        grid[y][7] = '#1c1917'; grid[y][8] = '#44403c'; grid[y][9] = '#78350f';
      }
      grid[10][8] = '#b45309'; // Bronze knee cap
      // Right Boot (y: 12 to 13, x: 6 to 9)
      grid[12][6] = '#1c1917'; grid[12][7] = '#292524'; grid[12][8] = '#44403c'; grid[12][9] = '#1c1917';
      grid[13][6] = '#0c0a09'; grid[13][7] = '#1c1917'; grid[13][8] = '#292524'; grid[13][9] = '#0c0a09';
    }
    else if (unitType === 'DevilPatrol_Legs_Walk1') {
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));
      for (let x = 4; x <= 8; x++) grid[8][x] = '#1c1917';
      grid[8][6] = '#f97316';
      // 1. Left Stride Forward
      grid[9][6] = '#1c1917'; grid[9][7] = '#44403c'; grid[9][8] = '#78350f';
      grid[10][7] = '#44403c'; grid[10][8] = '#b45309'; grid[10][9] = '#78350f';
      grid[11][8] = '#1c1917'; grid[11][9] = '#44403c'; grid[11][10] = '#78350f';
      grid[12][8] = '#1c1917'; grid[12][9] = '#292524'; grid[12][10] = '#44403c'; grid[12][11] = '#1c1917';
      grid[13][8] = '#0c0a09'; grid[13][9] = '#1c1917'; grid[13][10] = '#292524'; grid[13][11] = '#0c0a09';
      // 2. Right Push Leg Trailing
      grid[9][4] = '#1c1917'; grid[9][5] = '#44403c';
      grid[10][3] = '#1c1917'; grid[10][4] = '#44403c'; grid[10][5] = '#b45309';
      grid[11][2] = '#0c0a09'; grid[11][3] = '#1c1917'; grid[11][4] = '#292524';
      grid[12][1] = '#0c0a09'; grid[12][2] = '#1c1917'; grid[12][3] = '#292524';
      grid[13][2] = '#0c0a09'; grid[13][3] = '#1c1917';
    }
    else if (unitType === 'DevilPatrol_Legs_Walk2') {
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));
      for (let x = 4; x <= 8; x++) grid[8][x] = '#1c1917';
      grid[8][6] = '#f97316';
      grid[9][5] = '#1c1917'; grid[9][6] = '#44403c'; grid[9][7] = '#78350f';
      grid[10][5] = '#1c1917'; grid[10][6] = '#44403c'; grid[10][7] = '#b45309';
      grid[11][5] = '#1c1917'; grid[11][6] = '#44403c'; grid[11][7] = '#78350f';
      grid[12][5] = '#1c1917'; grid[12][6] = '#292524'; grid[12][7] = '#44403c'; grid[12][8] = '#1c1917';
      grid[13][5] = '#0c0a09'; grid[13][6] = '#1c1917'; grid[13][7] = '#292524'; grid[13][8] = '#0c0a09';
      grid[9][3] = '#1c1917'; grid[9][4] = '#44403c';
      grid[10][2] = '#1c1917'; grid[10][3] = '#b45309'; grid[10][4] = '#78350f';
      grid[11][2] = '#0c0a09'; grid[11][3] = '#1c1917'; grid[11][4] = '#292524';
      grid[12][2] = '#0c0a09'; grid[12][3] = '#1c1917';
    }
    else if (unitType === 'DevilPatrol_Legs_Walk3') {
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));
      for (let x = 4; x <= 8; x++) grid[8][x] = '#1c1917';
      grid[8][6] = '#f97316';
      grid[9][4] = '#1c1917'; grid[9][5] = '#44403c'; grid[9][6] = '#78350f';
      grid[10][4] = '#1c1917'; grid[10][5] = '#44403c'; grid[10][6] = '#b45309';
      grid[11][4] = '#1c1917'; grid[11][5] = '#44403c'; grid[11][6] = '#78350f';
      grid[12][4] = '#1c1917'; grid[12][5] = '#292524'; grid[12][6] = '#44403c';
      grid[13][4] = '#0c0a09'; grid[13][5] = '#1c1917'; grid[13][6] = '#292524';
      grid[9][7] = '#1c1917'; grid[9][8] = '#44403c';
      grid[10][7] = '#b45309'; grid[10][8] = '#78350f'; grid[10][9] = '#b45309';
      grid[11][7] = '#1c1917'; grid[11][8] = '#292524'; grid[11][9] = '#44403c';
      grid[12][7] = '#0c0a09'; grid[12][8] = '#1c1917';
    }
    else if (unitType === 'DevilPatrol_Legs_Walk4') {
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));
      for (let x = 4; x <= 8; x++) grid[8][x] = '#1c1917';
      grid[8][6] = '#f97316';
      grid[9][6] = '#1c1917'; grid[9][7] = '#44403c'; grid[9][8] = '#78350f';
      grid[10][7] = '#1c1917'; grid[10][8] = '#b45309'; grid[10][9] = '#78350f';
      grid[11][7] = '#1c1917'; grid[11][8] = '#44403c'; grid[11][9] = '#78350f'; grid[11][10] = '#1c1917';
      grid[12][7] = '#1c1917'; grid[12][8] = '#292524'; grid[12][9] = '#44403c'; grid[12][10] = '#1c1917';
      grid[13][8] = '#0c0a09'; grid[13][9] = '#1c1917'; grid[13][10] = '#292524';
      grid[9][4] = '#1c1917'; grid[9][5] = '#44403c';
      grid[10][3] = '#1c1917'; grid[10][4] = '#b45309'; grid[10][5] = '#44403c';
      grid[11][2] = '#0c0a09'; grid[11][3] = '#1c1917'; grid[11][4] = '#292524';
      grid[12][1] = '#0c0a09'; grid[12][2] = '#1c1917'; grid[12][3] = '#292524';
      grid[13][1] = '#0c0a09'; grid[13][2] = '#1c1917';
    }
    else if (unitType === 'DevilPatrol_Legs_Walk5') {
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));
      for (let x = 4; x <= 8; x++) grid[8][x] = '#1c1917';
      grid[8][6] = '#f97316';
      grid[9][5] = '#1c1917'; grid[9][6] = '#44403c'; grid[9][7] = '#78350f';
      grid[10][5] = '#1c1917'; grid[10][6] = '#44403c'; grid[10][7] = '#b45309';
      grid[11][5] = '#1c1917'; grid[11][6] = '#44403c'; grid[11][7] = '#78350f';
      grid[12][5] = '#1c1917'; grid[12][6] = '#292524'; grid[12][7] = '#44403c'; grid[12][8] = '#1c1917';
      grid[13][5] = '#0c0a09'; grid[13][6] = '#1c1917'; grid[13][7] = '#292524'; grid[13][8] = '#0c0a09';
      grid[9][3] = '#1c1917'; grid[9][4] = '#44403c';
      grid[10][2] = '#1c1917'; grid[10][3] = '#b45309'; grid[10][4] = '#78350f';
      grid[11][2] = '#0c0a09'; grid[11][3] = '#1c1917'; grid[11][4] = '#292524';
      grid[12][2] = '#0c0a09'; grid[12][3] = '#1c1917';
    }
    else if (unitType === 'DevilPatrol_Legs_Walk6') {
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));
      for (let x = 4; x <= 8; x++) grid[8][x] = '#1c1917';
      grid[8][6] = '#f97316';
      grid[9][4] = '#1c1917'; grid[9][5] = '#44403c'; grid[9][6] = '#78350f';
      grid[10][4] = '#1c1917'; grid[10][5] = '#44403c'; grid[10][6] = '#b45309';
      grid[11][4] = '#1c1917'; grid[11][5] = '#44403c'; grid[11][6] = '#78350f';
      grid[12][4] = '#1c1917'; grid[12][5] = '#292524'; grid[12][6] = '#44403c';
      grid[13][4] = '#0c0a09'; grid[13][5] = '#1c1917'; grid[13][6] = '#292524';
      grid[9][7] = '#1c1917'; grid[9][8] = '#44403c';
      grid[10][7] = '#b45309'; grid[10][8] = '#78350f'; grid[10][9] = '#b45309';
      grid[11][7] = '#1c1917'; grid[11][8] = '#292524'; grid[11][9] = '#44403c';
      grid[12][7] = '#0c0a09'; grid[12][8] = '#1c1917';
    }
    else if (unitType === 'DevilPatrol_Torso') {
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));
      // 1. Back Fuel Tank Conduits & Heat Radiator (x: 1-2, y: 3-7)
      for (let y = 3; y <= 7; y++) { grid[y][1] = '#1c1917'; grid[y][2] = '#78350f'; }
      grid[4][1] = '#f97316'; grid[5][1] = '#b45309';
      
      // 2. Tactical Bronze Helmet (x: 4-8, y: 0-4)
      grid[0][5] = '#1c1917'; grid[0][6] = '#292524'; grid[0][7] = '#1c1917';
      for (let x = 4; x <= 8; x++) grid[1][x] = '#44403c';
      grid[1][5] = '#78350f'; grid[1][6] = '#b45309'; // Brow shroud mount
      
      // 3. Fiery Visor (from 1_devilpatrol.png - yellow-orange flame glow, y: 2-3, x: 6-9)
      grid[2][4] = '#1c1917'; grid[2][5] = '#292524';
      grid[2][6] = '#f97316'; grid[2][7] = '#facc15'; grid[2][8] = '#fef08a'; grid[2][9] = '#1c1917';
      grid[3][4] = '#1c1917'; grid[3][5] = '#44403c';
      grid[3][6] = '#ef4444'; grid[3][7] = '#f97316'; grid[3][8] = '#facc15'; grid[3][9] = '#1c1917';

      // 4. Heavy Hitzeschild Torso & Collar (y: 4-7, x: 3-8)
      for (let y = 4; y <= 7; y++) for (let x = 3; x <= 8; x++) grid[y][x] = '#292524';
      grid[4][5] = '#78350f'; grid[4][6] = '#b45309'; // Collar reinforcement
      grid[5][5] = 'TEAM'; grid[5][6] = 'TEAM';
      grid[6][5] = 'TEAM'; grid[6][6] = 'TEAM';
      grid[7][5] = '#1c1917'; grid[7][6] = '#1c1917'; // Belt join
    }
    else if (unitType === 'DevilPatrol_Gun') {
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));
      // Heavy Perdition Dual-Barrel Flamethrower with Pilot Igniter
      // Grip & Receiver (x: 7-8, y: 6-7)
      grid[6][7] = '#1c1917'; grid[6][8] = '#292524'; grid[7][8] = '#1c1917';
      // Twin Barrels & Shroud (x: 9-12, y: 5-6)
      grid[5][9] = '#334155'; grid[5][10] = '#475569'; grid[5][11] = '#64748b'; grid[5][12] = '#94a3b8';
      grid[6][9] = '#1e293b'; grid[6][10] = '#334155'; grid[6][11] = '#475569'; grid[6][12] = '#64748b';
      // Fuel tube underneath (y: 7, x: 9-11)
      grid[7][9] = '#78350f'; grid[7][10] = '#b45309'; grid[7][11] = '#f97316';
      // Pilot Igniter Tip (x: 13, y: 5-6)
      grid[5][13] = '#f97316'; grid[6][13] = '#facc15';
    }
    else if (unitType === 'DevilPatrol') {
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));
      // Composite Assembled Devil Patrol Sprite
      // 1. Back Tanks
      for (let y = 3; y <= 7; y++) { grid[y][1] = '#1c1917'; grid[y][2] = '#78350f'; }
      grid[4][1] = '#f97316'; grid[5][1] = '#b45309';
      // 2. Helmet & Visor
      grid[0][5] = '#1c1917'; grid[0][6] = '#292524'; grid[0][7] = '#1c1917';
      for (let x = 4; x <= 8; x++) grid[1][x] = '#44403c';
      grid[1][5] = '#78350f'; grid[1][6] = '#b45309';
      grid[2][4] = '#1c1917'; grid[2][5] = '#292524';
      grid[2][6] = '#f97316'; grid[2][7] = '#facc15'; grid[2][8] = '#fef08a'; grid[2][9] = '#1c1917';
      grid[3][4] = '#1c1917'; grid[3][5] = '#44403c';
      grid[3][6] = '#ef4444'; grid[3][7] = '#f97316'; grid[3][8] = '#facc15'; grid[3][9] = '#1c1917';
      // 3. Torso & Team Armor
      for (let y = 4; y <= 7; y++) for (let x = 3; x <= 8; x++) grid[y][x] = '#292524';
      grid[4][5] = '#78350f'; grid[4][6] = '#b45309';
      grid[5][5] = 'TEAM'; grid[5][6] = 'TEAM';
      grid[6][5] = 'TEAM'; grid[6][6] = 'TEAM';
      grid[7][5] = '#1c1917'; grid[7][6] = '#1c1917';
      // 4. Flamethrower Weapon
      grid[6][7] = '#1c1917'; grid[6][8] = '#292524'; grid[7][8] = '#1c1917';
      grid[5][9] = '#334155'; grid[5][10] = '#475569'; grid[5][11] = '#64748b'; grid[5][12] = '#94a3b8';
      grid[6][9] = '#1e293b'; grid[6][10] = '#334155'; grid[6][11] = '#475569'; grid[6][12] = '#64748b';
      grid[7][9] = '#78350f'; grid[7][10] = '#b45309'; grid[7][11] = '#f97316';
      grid[5][13] = '#f97316'; grid[6][13] = '#facc15';
      // 5. Legs & Boots
      for (let y = 9; y <= 11; y++) {
        grid[y][3] = '#1c1917'; grid[y][4] = '#44403c'; grid[y][5] = '#78350f';
        grid[y][7] = '#1c1917'; grid[y][8] = '#44403c'; grid[y][9] = '#78350f';
      }
      grid[10][4] = '#b45309'; grid[10][8] = '#b45309';
      grid[12][2] = '#1c1917'; grid[12][3] = '#292524'; grid[12][4] = '#44403c';
      grid[13][2] = '#0c0a09'; grid[13][3] = '#1c1917'; grid[13][4] = '#292524';
      grid[12][6] = '#1c1917'; grid[12][7] = '#292524'; grid[12][8] = '#44403c';
      grid[13][6] = '#0c0a09'; grid[13][7] = '#1c1917'; grid[13][8] = '#292524';
    }
    // ==========================================
    // HYPER DEVIL PATROL 8-BIT MODULAR AIRBORNE JETPACK RIG
    // ==========================================
    // Elite jetpack-borne heavy infantry (Fusion of 3 Devil Patrols).
    // Heavy bronze/copper heat-shielded flight armor, twin jetpack thrusters,
    // glowing multi-optic flight visor, chest turbine reactor, and a heavy
    // rotary grenade launcher fed by a massive curving ammo belt.
    // Authored on a crisp 16x16 grid based directly on 1_hyperdevilpatrol.png.
    // ==========================================
    else if (unitType === 'HyperDevilPatrol_Legs' || unitType === 'HyperDevilPatrol_Legs_Stand' || unitType.startsWith('HyperDevilPatrol_Legs_Walk')) {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // Aerodynamic Flight Leg Stance trailing slightly behind in flight with boot thruster stabilization
      const pulse = unitType.endsWith('2') || unitType.endsWith('5');
      // Thigh armor & knee guards (y: 9..11, x: 3..7)
      grid[9][3] = '#1c1917'; grid[9][4] = '#78350f'; grid[9][5] = '#b45309'; grid[9][6] = '#1c1917';
      grid[10][3] = '#292524'; grid[10][4] = '#b45309'; grid[10][5] = '#d97706'; grid[10][6] = '#78350f';
      grid[11][2] = '#1c1917'; grid[11][3] = '#78350f'; grid[11][4] = '#b45309'; grid[11][5] = '#292524';
      // Shin armor trailing rearward (y: 12..14, x: 2..5)
      grid[12][2] = '#292524'; grid[12][3] = '#44403c'; grid[12][4] = '#78350f';
      grid[13][1] = '#1c1917'; grid[13][2] = '#292524'; grid[13][3] = '#44403c';
      // Flight boots with vector stabilization micro-nozzles (y: 14..15, x: 0..3)
      grid[14][1] = '#0c0a09'; grid[14][2] = '#1c1917'; grid[14][3] = '#292524';
      grid[15][0] = pulse ? '#38bdf8' : '#0284c7'; grid[15][1] = '#1c1917'; grid[15][2] = '#0c0a09';
      if (pulse) grid[15][0] = '#7dd3fc';
    }
    else if (unitType === 'HyperDevilPatrol_Torso') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // 1. Heavy Twin Jetpack Thrusters with Fuel Tanks & Plasma Core (-X rear, y: 2..8, x: 0..3)
      for (let y = 3; y <= 7; y++) {
        grid[y][1] = '#1c1917';
        grid[y][2] = '#78350f';
      }
      grid[2][2] = '#b45309'; grid[2][3] = '#1c1917';
      grid[4][0] = '#38bdf8'; grid[4][1] = '#0284c7'; // Thruster conduit glow
      grid[5][0] = '#f97316'; grid[5][1] = '#b45309';
      grid[7][1] = '#b45309'; grid[8][1] = '#1c1917';
      grid[8][0] = '#0284c7'; grid[8][1] = '#38bdf8'; // Lower thruster nozzle

      // 2. High-Altitude Flight Helmet with Brow Shroud & Dual Sensor Visor (y: 0..4, x: 4..9)
      grid[0][5] = '#1c1917'; grid[0][6] = '#292524'; grid[0][7] = '#44403c'; grid[0][8] = '#1c1917';
      grid[1][4] = '#1c1917'; grid[1][5] = '#78350f'; grid[1][6] = '#b45309'; grid[1][7] = '#d97706'; grid[1][8] = '#292524';
      // Visor Optics (Glowing amber-crimson dual flight optics from artwork)
      grid[2][4] = '#1c1917'; grid[2][5] = '#292524';
      grid[2][6] = '#f97316'; grid[2][7] = '#facc15'; grid[2][8] = '#fef08a'; grid[2][9] = '#1c1917';
      grid[3][4] = '#1c1917'; grid[3][5] = '#44403c';
      grid[3][6] = '#ef4444'; grid[3][7] = '#f97316'; grid[3][8] = '#facc15'; grid[3][9] = '#1c1917';
      // Respirator / Chin Filter
      grid[4][5] = '#292524'; grid[4][6] = '#44403c'; grid[4][7] = '#292524'; grid[4][8] = '#1c1917';

      // 3. Heavy Bronze Torso & Chest Turbine Reactor Core (y: 5..9, x: 3..8)
      grid[5][3] = '#78350f'; grid[5][4] = '#b45309'; grid[5][5] = '#d97706'; grid[5][6] = '#b45309'; grid[5][7] = '#78350f'; grid[5][8] = '#1c1917';
      // Chest Turbine / Reactor (blue core with copper shroud)
      grid[6][3] = 'TEAM'; grid[6][4] = '#292524'; grid[6][5] = '#38bdf8'; grid[6][6] = '#0284c7'; grid[6][7] = '#292524'; grid[6][8] = 'TEAM';
      grid[7][3] = 'TEAM'; grid[7][4] = '#44403c'; grid[7][5] = '#0284c7'; grid[7][6] = '#38bdf8'; grid[7][7] = '#44403c'; grid[7][8] = 'TEAM';
      grid[8][4] = '#1c1917'; grid[8][5] = '#78350f'; grid[8][6] = '#b45309'; grid[8][7] = '#1c1917';
    }
    else if (unitType === 'HyperDevilPatrol_Gun') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // Heavy Multi-Chamber Rotary Grenade Launcher & Curving Ammunition Belt
      // Heavy Arm & Shoulder Grip (y: 5..7, x: 5..8)
      grid[5][5] = '#78350f'; grid[5][6] = '#b45309'; grid[5][7] = '#1c1917';
      grid[6][6] = '#292524'; grid[6][7] = '#44403c'; grid[6][8] = '#1c1917';

      // Rotary Drum Cylinder Magazine (y: 8..11, x: 7..11)
      grid[8][8] = '#1c1917'; grid[8][9] = '#334155'; grid[8][10] = '#475569'; grid[8][11] = '#1c1917';
      grid[9][7] = '#1c1917'; grid[9][8] = '#eab308'; grid[9][9] = '#ca8a04'; grid[9][10] = '#eab308'; grid[9][11] = '#334155';
      grid[10][7] = '#334155'; grid[10][8] = '#ca8a04'; grid[10][9] = '#1e293b'; grid[10][10] = '#ca8a04'; grid[10][11] = '#475569';
      grid[11][8] = '#1c1917'; grid[11][9] = '#334155'; grid[11][10] = '#475569'; grid[11][11] = '#1c1917';

      // Heavy Launcher Barrel & Vented Shroud (y: 6..8, x: 9..15)
      grid[6][10] = '#475569'; grid[6][11] = '#64748b'; grid[6][12] = '#94a3b8'; grid[6][13] = '#475569';
      grid[7][9] = '#334155'; grid[7][10] = '#475569'; grid[7][11] = '#64748b'; grid[7][12] = '#94a3b8'; grid[7][13] = '#cbd5e1'; grid[7][14] = '#f8fafc';
      grid[8][12] = '#334155'; grid[8][13] = '#475569'; grid[8][14] = '#64748b';
      // Muzzle Brake / Crown (x: 15, y: 6..8)
      grid[6][15] = '#1e293b'; grid[7][15] = '#f97316'; grid[8][15] = '#1e293b';
    }
    else if (unitType === 'HyperDevilPatrol') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // Assembled Full 16x16 Hyper Devil Patrol Sprite
      // 1. Jetpack Thrusters
      for (let y = 3; y <= 7; y++) { grid[y][1] = '#1c1917'; grid[y][2] = '#78350f'; }
      grid[2][2] = '#b45309'; grid[2][3] = '#1c1917';
      grid[4][0] = '#38bdf8'; grid[4][1] = '#0284c7';
      grid[5][0] = '#f97316'; grid[5][1] = '#b45309';
      grid[8][0] = '#0284c7'; grid[8][1] = '#38bdf8';

      // 2. Helmet & Visor
      grid[0][5] = '#1c1917'; grid[0][6] = '#292524'; grid[0][7] = '#44403c'; grid[0][8] = '#1c1917';
      grid[1][4] = '#1c1917'; grid[1][5] = '#78350f'; grid[1][6] = '#b45309'; grid[1][7] = '#d97706'; grid[1][8] = '#292524';
      grid[2][4] = '#1c1917'; grid[2][5] = '#292524';
      grid[2][6] = '#f97316'; grid[2][7] = '#facc15'; grid[2][8] = '#fef08a'; grid[2][9] = '#1c1917';
      grid[3][4] = '#1c1917'; grid[3][5] = '#44403c';
      grid[3][6] = '#ef4444'; grid[3][7] = '#f97316'; grid[3][8] = '#facc15'; grid[3][9] = '#1c1917';
      grid[4][5] = '#292524'; grid[4][6] = '#44403c'; grid[4][7] = '#292524'; grid[4][8] = '#1c1917';

      // 3. Torso, Team Armor & Turbine
      grid[5][3] = '#78350f'; grid[5][4] = '#b45309'; grid[5][5] = '#d97706'; grid[5][6] = '#b45309'; grid[5][7] = '#78350f'; grid[5][8] = '#1c1917';
      grid[6][3] = 'TEAM'; grid[6][4] = '#292524'; grid[6][5] = '#38bdf8'; grid[6][6] = '#0284c7'; grid[6][7] = '#292524'; grid[6][8] = 'TEAM';
      grid[7][3] = 'TEAM'; grid[7][4] = '#44403c'; grid[7][5] = '#0284c7'; grid[7][6] = '#38bdf8'; grid[7][7] = '#44403c'; grid[7][8] = 'TEAM';
      grid[8][4] = '#1c1917'; grid[8][5] = '#78350f'; grid[8][6] = '#b45309'; grid[8][7] = '#1c1917';

      // 4. Rotary Grenade Launcher
      grid[6][10] = '#475569'; grid[6][11] = '#64748b'; grid[6][12] = '#94a3b8'; grid[6][13] = '#475569';
      grid[7][9] = '#334155'; grid[7][10] = '#475569'; grid[7][11] = '#64748b'; grid[7][12] = '#94a3b8'; grid[7][13] = '#cbd5e1'; grid[7][14] = '#f8fafc';
      grid[8][8] = '#1c1917'; grid[8][9] = '#334155'; grid[8][10] = '#475569'; grid[8][11] = '#1c1917'; grid[8][12] = '#334155'; grid[8][13] = '#475569'; grid[8][14] = '#64748b';
      grid[9][7] = '#1c1917'; grid[9][8] = '#eab308'; grid[9][9] = '#ca8a04'; grid[9][10] = '#eab308'; grid[9][11] = '#334155';
      grid[10][7] = '#334155'; grid[10][8] = '#ca8a04'; grid[10][9] = '#1e293b'; grid[10][10] = '#ca8a04'; grid[10][11] = '#475569';
      grid[11][8] = '#1c1917'; grid[11][9] = '#334155'; grid[11][10] = '#475569'; grid[11][11] = '#1c1917';
      grid[6][15] = '#1e293b'; grid[7][15] = '#f97316'; grid[8][15] = '#1e293b';

      // 6. Trailing Flight Legs
      grid[9][3] = '#1c1917'; grid[9][4] = '#78350f'; grid[9][5] = '#b45309';
      grid[10][3] = '#292524'; grid[10][4] = '#b45309'; grid[10][5] = '#d97706';
      grid[11][2] = '#1c1917'; grid[11][3] = '#78350f'; grid[11][4] = '#b45309';
      grid[12][2] = '#292524'; grid[12][3] = '#44403c';
      grid[13][1] = '#1c1917'; grid[13][2] = '#292524';
      grid[14][2] = '#1c1917'; grid[15][2] = '#0c0a09';
    }
    // ==========================================
    // WHEELIE BOMBER 8-BIT BESPOKE RETRO RIG
    // ==========================================
    // High-octane wasteland combat dirt bike: multi-frame wheelie cycle
    // (chassis + engine + wheels + bombs) + wild wasteland biker rider
    // dynamically leaning, pivoting, and banking with realistic motorcycle
    // kinematics. Grid authored facing right (forward = higher x).
    // ==========================================
    else if (unitType.startsWith('WheelieBomber_Bike') && !unitType.includes('Wheelie1') && !unitType.includes('Wheelie2')) {
      let roll = 0;
      if (unitType.endsWith('_Roll2')) roll = 1;
      else if (unitType.endsWith('_Roll3')) roll = 2;
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));

      const paintSD_WheelieBomber_Wheel = (g, x0, topY, rRoll = 0) => {
        const TIRE = '#0c0a09', RIM = '#e2e8f0', HUB = '#334155', SPOKE = '#94a3b8';
        g[topY][x0 + 1] = TIRE; g[topY][x0 + 2] = TIRE;
        g[topY + 1][x0] = TIRE; g[topY + 1][x0 + 3] = TIRE;
        g[topY + 2][x0] = TIRE; g[topY + 2][x0 + 3] = TIRE;
        g[topY + 3][x0] = TIRE; g[topY + 3][x0 + 3] = TIRE;
        g[topY + 4][x0 + 1] = TIRE; g[topY + 4][x0 + 2] = TIRE;

        const r = ((rRoll % 3) + 3) % 3;
        if (r === 0) {
          g[topY + 1][x0 + 1] = SPOKE; g[topY + 1][x0 + 2] = RIM;
          g[topY + 2][x0 + 1] = RIM;   g[topY + 2][x0 + 2] = HUB;
          g[topY + 3][x0 + 1] = SPOKE; g[topY + 3][x0 + 2] = RIM;
        } else if (r === 1) {
          g[topY + 1][x0 + 1] = SPOKE; g[topY + 1][x0 + 2] = RIM;
          g[topY + 2][x0 + 1] = HUB;   g[topY + 2][x0 + 2] = RIM;
          g[topY + 3][x0 + 1] = RIM;   g[topY + 3][x0 + 2] = SPOKE;
        } else {
          g[topY + 1][x0 + 1] = RIM;   g[topY + 1][x0 + 2] = HUB;
          g[topY + 2][x0 + 1] = SPOKE; g[topY + 2][x0 + 2] = RIM;
          g[topY + 3][x0 + 1] = RIM;   g[topY + 3][x0 + 2] = SPOKE;
        }
      };

      // 1. Rear Wheel: 4x4 knobby tire, chrome rim ring & dark hub, center (2.5, 11.5)
      paintSD_WheelieBomber_Wheel(grid, 1, 9, roll);

      // 2. Front Wheel: 4x4 knobby tire, chrome rim & dark hub, center (11.5, 11.5)
      paintSD_WheelieBomber_Wheel(grid, 10, 9, roll);

      // 3. Engine Block & Transmission (Cylinder head with cooling fins, crankcase)
      grid[9][6] = '#475569'; grid[9][7] = '#334155'; grid[9][8] = '#475569';
      grid[10][5] = '#1e293b'; grid[10][6] = '#475569'; grid[10][7] = '#334155'; grid[10][8] = '#1e293b';
      grid[11][5] = '#334155'; grid[11][6] = '#1e293b'; grid[11][7] = '#475569'; grid[11][8] = '#64748b'; // Footpeg

      // 4. Swept-Back Exhaust Pipe with glowing hot tip
      grid[12][6] = '#475569'; grid[12][5] = '#64748b';
      grid[10][0] = '#ea580c'; grid[9][0] = '#f97316'; grid[10][1] = '#64748b';

      // 5. Cradle Frame, Fuel Tank (with TEAM color racing stripe) & Leather Seat
      grid[8][5] = '#334155'; grid[8][6] = '#334155'; grid[8][7] = '#475569'; grid[8][8] = '#334155';
      grid[7][4] = '#18181b'; grid[7][5] = '#27272a'; grid[8][4] = '#18181b'; // Seat
      grid[7][6] = '#64748b'; grid[7][7] = 'TEAM'; grid[7][8] = 'TEAM'; grid[7][9] = '#94a3b8'; // Tank
      grid[8][7] = 'TEAM';

      // 6. Rear Dynamite / Bomb Rack (Wheelie Bomber payload!)
      grid[6][2] = '#dc2626'; grid[6][3] = '#ef4444'; grid[6][4] = '#fbbf24'; // Bomb canisters & fuse

      // 7. Front Telescopic Fork, Headlamp & Handlebars
      grid[5][9] = '#18181b'; grid[5][10] = '#64748b'; // Handlebars
      grid[6][10] = '#94a3b8'; // Triple clamp
      grid[7][10] = '#cbd5e1'; grid[7][11] = '#94a3b8'; // Fork tube
      grid[8][11] = '#cbd5e1'; grid[8][12] = '#94a3b8'; // Fender
      grid[9][11] = '#94a3b8'; // Axle clamp
      grid[6][11] = '#475569'; grid[6][12] = '#fde047'; grid[6][13] = '#fef08a'; // Headlight
    }
    else if (unitType.startsWith('WheelieBomber_Bike_Wheelie1')) {
      // Dynamic mid-wheelie (~30° lift): front suspension extended, chassis climbing, rear tire digging in
      let roll = 0;
      if (unitType.endsWith('_Roll2')) roll = 1;
      else if (unitType.endsWith('_Roll3')) roll = 2;
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));

      const paintSD_WheelieBomber_Wheel = (g, x0, topY, rRoll = 0) => {
        const TIRE = '#0c0a09', RIM = '#e2e8f0', HUB = '#334155', SPOKE = '#94a3b8';
        g[topY][x0 + 1] = TIRE; g[topY][x0 + 2] = TIRE;
        g[topY + 1][x0] = TIRE; g[topY + 1][x0 + 3] = TIRE;
        g[topY + 2][x0] = TIRE; g[topY + 2][x0 + 3] = TIRE;
        g[topY + 3][x0] = TIRE; g[topY + 3][x0 + 3] = TIRE;
        g[topY + 4][x0 + 1] = TIRE; g[topY + 4][x0 + 2] = TIRE;

        const r = ((rRoll % 3) + 3) % 3;
        if (r === 0) {
          g[topY + 1][x0 + 1] = SPOKE; g[topY + 1][x0 + 2] = RIM;
          g[topY + 2][x0 + 1] = RIM;   g[topY + 2][x0 + 2] = HUB;
          g[topY + 3][x0 + 1] = SPOKE; g[topY + 3][x0 + 2] = RIM;
        } else if (r === 1) {
          g[topY + 1][x0 + 1] = SPOKE; g[topY + 1][x0 + 2] = RIM;
          g[topY + 2][x0 + 1] = HUB;   g[topY + 2][x0 + 2] = RIM;
          g[topY + 3][x0 + 1] = RIM;   g[topY + 3][x0 + 2] = SPOKE;
        } else {
          g[topY + 1][x0 + 1] = RIM;   g[topY + 1][x0 + 2] = HUB;
          g[topY + 2][x0 + 1] = SPOKE; g[topY + 2][x0 + 2] = RIM;
          g[topY + 3][x0 + 1] = RIM;   g[topY + 3][x0 + 2] = SPOKE;
        }
      };

      // Rear Wheel: planted pivot at (2.5, 11.5)
      paintSD_WheelieBomber_Wheel(grid, 1, 9, roll);

      // Front Wheel: lifted 4x4 wheel centered at (11.5, 7.5)
      paintSD_WheelieBomber_Wheel(grid, 10, 5, roll);

      // Diagonal Frame, Tilted Engine & Transmission
      grid[9][5] = '#334155'; grid[8][6] = '#334155'; grid[7][7] = '#475569'; grid[6][8] = '#334155';
      grid[8][5] = '#475569'; grid[8][6] = '#1e293b'; grid[7][6] = '#475569';

      // Tilted Tank, Seat & Bombs
      grid[5][6] = '#18181b'; grid[5][7] = '#64748b'; grid[5][8] = 'TEAM'; grid[5][9] = 'TEAM';
      grid[6][7] = 'TEAM';
      grid[5][3] = '#dc2626'; grid[5][4] = '#ef4444';

      // Lifted Fork & Headlamp
      grid[4][8] = '#18181b'; grid[4][9] = '#64748b'; // Handlebars
      grid[4][10] = '#94a3b8'; grid[5][10] = '#cbd5e1';
      grid[3][10] = '#475569'; grid[3][11] = '#fde047'; grid[3][12] = '#fef08a';

      // Exhaust Backfire & Dirt Roost
      grid[10][0] = '#ea580c'; grid[11][0] = '#f97316'; grid[12][0] = '#fbbf24';
      grid[13][0] = '#78350f'; grid[13][1] = '#92400e';
    }
    else if (unitType.startsWith('WheelieBomber_Bike_Wheelie2')) {
      // 12 o'Clock Peak Wheelie (~55-60°): front wheel high in the sky, rear tail throwing sparks
      let roll = 0;
      if (unitType.endsWith('_Roll2')) roll = 1;
      else if (unitType.endsWith('_Roll3')) roll = 2;
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));

      const paintSD_WheelieBomber_Wheel = (g, x0, topY, rRoll = 0) => {
        const TIRE = '#0c0a09', RIM = '#e2e8f0', HUB = '#334155', SPOKE = '#94a3b8';
        g[topY][x0 + 1] = TIRE; g[topY][x0 + 2] = TIRE;
        g[topY + 1][x0] = TIRE; g[topY + 1][x0 + 3] = TIRE;
        g[topY + 2][x0] = TIRE; g[topY + 2][x0 + 3] = TIRE;
        g[topY + 3][x0] = TIRE; g[topY + 3][x0 + 3] = TIRE;
        g[topY + 4][x0 + 1] = TIRE; g[topY + 4][x0 + 2] = TIRE;

        const r = ((rRoll % 3) + 3) % 3;
        if (r === 0) {
          g[topY + 1][x0 + 1] = SPOKE; g[topY + 1][x0 + 2] = RIM;
          g[topY + 2][x0 + 1] = RIM;   g[topY + 2][x0 + 2] = HUB;
          g[topY + 3][x0 + 1] = SPOKE; g[topY + 3][x0 + 2] = RIM;
        } else if (r === 1) {
          g[topY + 1][x0 + 1] = SPOKE; g[topY + 1][x0 + 2] = RIM;
          g[topY + 2][x0 + 1] = HUB;   g[topY + 2][x0 + 2] = RIM;
          g[topY + 3][x0 + 1] = RIM;   g[topY + 3][x0 + 2] = SPOKE;
        } else {
          g[topY + 1][x0 + 1] = RIM;   g[topY + 1][x0 + 2] = HUB;
          g[topY + 2][x0 + 1] = SPOKE; g[topY + 2][x0 + 2] = RIM;
          g[topY + 3][x0 + 1] = RIM;   g[topY + 3][x0 + 2] = SPOKE;
        }
      };

      // Rear Wheel: planted pivot at (2.5, 11.5)
      paintSD_WheelieBomber_Wheel(grid, 1, 9, roll);

      // Front Wheel: 4x4 high overhead centered at (9.5, 3.5)
      paintSD_WheelieBomber_Wheel(grid, 8, 1, roll);

      // Steep Diagonal Frame Rail & Vertical Engine
      grid[9][4] = '#334155'; grid[8][4] = '#475569'; grid[7][5] = '#1e293b'; grid[6][5] = '#475569';
      grid[5][6] = '#334155'; grid[4][7] = '#334155';

      // Steep Tank, Seat & Bomb
      grid[7][3] = '#dc2626'; grid[8][3] = '#ef4444'; grid[7][4] = '#18181b';
      grid[6][6] = '#64748b'; grid[6][7] = 'TEAM'; grid[5][7] = 'TEAM';

      // High Fork & Headlamp
      grid[3][7] = '#18181b'; grid[2][7] = '#94a3b8'; grid[3][8] = '#cbd5e1';
      grid[2][8] = '#fde047'; grid[1][8] = '#fef08a';

      // Scraped Tail Sparks & Exhaust Jet
      grid[10][0] = '#f97316'; grid[11][0] = '#fdba74';
      grid[12][0] = '#fbbf24'; grid[13][0] = '#f97316'; grid[13][1] = '#ea580c';
    }
    else if (unitType === 'WheelieBomber_Rider') {
      // Mad-Max Wasteland Biker: helmet with combat goggles, billowing crest,
      // leather vest with team insignia, arms gripping handlebars, boots on pegs.
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));

      // 1. Helmet, Glowing Visor/Goggles & Fluttering Crest/Bandana
      grid[1][6] = '#f97316'; grid[1][7] = '#fb923c'; // Crest
      grid[2][5] = '#f97316'; grid[2][6] = '#0f172a'; grid[2][7] = '#1e293b'; grid[2][8] = '#0f172a'; // Helmet
      grid[3][6] = '#0f172a'; grid[3][7] = '#38bdf8'; grid[3][8] = '#38bdf8'; // Glowing goggles!

      // 2. Torso / Armored Biker Vest (with prominent TEAM color stripe)
      grid[4][5] = '#1e293b'; grid[4][6] = 'TEAM'; grid[4][7] = 'TEAM'; grid[4][8] = '#334155';
      grid[5][5] = '#1e293b'; grid[5][6] = 'TEAM'; grid[5][7] = 'TEAM'; grid[5][8] = '#475569';
      grid[6][5] = '#18181b'; grid[6][6] = '#334155'; grid[6][7] = '#18181b'; // Belt harness

      // 3. Arms & Gloved Hands reaching forward to the handlebar
      grid[4][8] = '#475569'; grid[4][9] = '#334155'; // Shoulder/upper arm
      grid[5][9] = '#475569'; grid[5][10] = '#18181b'; // Forearm & glove grip

      // 4. Legs & Combat Boots on footpegs
      grid[6][4] = '#1e293b'; grid[6][5] = '#334155'; // Thigh
      grid[7][5] = '#1e293b'; grid[7][6] = '#475569'; // Shin
      grid[8][6] = '#18181b'; grid[8][7] = '#0f172a'; // Combat boot
    }
    else if (unitType === 'WheelieBomber' || unitType === 'WheelieBomber_Wheelie1' || unitType === 'WheelieBomber_Wheelie2') {
      // Composite (Grounded Motorcycle + Wasteland Biker), used for
      // death-shatter particle effects and single-canvas previews.
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));
      const TIRE = '#0c0a09', RIM = '#e2e8f0', HUB = '#334155', SPOKE = '#94a3b8';

      // Rear Wheel
      grid[9][2] = TIRE; grid[9][3] = TIRE;
      grid[10][1] = TIRE; grid[10][2] = SPOKE; grid[10][3] = RIM; grid[10][4] = TIRE;
      grid[11][1] = TIRE; grid[11][2] = RIM; grid[11][3] = HUB; grid[11][4] = TIRE;
      grid[12][1] = TIRE; grid[12][2] = SPOKE; grid[12][3] = RIM; grid[12][4] = TIRE;
      grid[13][2] = TIRE; grid[13][3] = TIRE;

      // Front Wheel
      grid[9][11] = TIRE; grid[9][12] = TIRE;
      grid[10][10] = TIRE; grid[10][11] = SPOKE; grid[10][12] = RIM; grid[10][13] = TIRE;
      grid[11][10] = TIRE; grid[11][11] = RIM; grid[11][12] = HUB; grid[11][13] = TIRE;
      grid[12][10] = TIRE; grid[12][11] = SPOKE; grid[12][12] = RIM; grid[12][13] = TIRE;
      grid[13][11] = TIRE; grid[13][12] = TIRE;

      // Engine & Frame
      grid[9][6] = '#475569'; grid[9][7] = '#334155'; grid[9][8] = '#475569';
      grid[10][5] = '#1e293b'; grid[10][6] = '#475569'; grid[10][7] = '#334155';
      grid[11][5] = '#334155'; grid[11][6] = '#1e293b'; grid[11][7] = '#475569'; grid[11][8] = '#64748b';
      grid[12][6] = '#475569'; grid[12][5] = '#64748b';
      grid[10][0] = '#ea580c'; grid[9][0] = '#f97316'; grid[10][1] = '#64748b';

      // Tank & Seat
      grid[8][5] = '#334155'; grid[8][6] = '#334155'; grid[8][7] = 'TEAM'; grid[8][8] = '#334155';
      grid[7][6] = '#64748b'; grid[7][7] = 'TEAM'; grid[7][8] = 'TEAM'; grid[7][9] = '#94a3b8';
      grid[6][2] = '#dc2626'; grid[6][3] = '#ef4444'; grid[6][4] = '#fbbf24'; // Bombs

      // Fork & Headlamp
      grid[7][10] = '#cbd5e1'; grid[7][11] = '#94a3b8';
      grid[8][11] = '#cbd5e1'; grid[8][12] = '#94a3b8';
      grid[6][11] = '#475569'; grid[6][12] = '#fde047'; grid[6][13] = '#fef08a';

      // Mounted Biker
      grid[1][6] = '#f97316'; grid[1][7] = '#fb923c';
      grid[2][5] = '#f97316'; grid[2][6] = '#0f172a'; grid[2][7] = '#1e293b'; grid[2][8] = '#0f172a';
      grid[3][6] = '#0f172a'; grid[3][7] = '#38bdf8'; grid[3][8] = '#38bdf8';
      grid[4][5] = '#1e293b'; grid[4][6] = 'TEAM'; grid[4][7] = 'TEAM'; grid[4][8] = '#334155';
      grid[5][5] = '#1e293b'; grid[5][6] = 'TEAM'; grid[5][7] = 'TEAM'; grid[5][9] = '#475569'; grid[5][10] = '#18181b';
      grid[6][5] = '#18181b'; grid[6][6] = '#334155';
      grid[7][5] = '#1e293b'; grid[8][6] = '#18181b';
    }
    else if (unitType === 'Marine') {
      gridSize = 14;
      grid = Array.from({ length: 14 }, () => Array(14).fill(null));
      // Full Assembled Marine Model
      // 1. Backpack & Helmet
      for (let y = 3; y <= 7; y++) { grid[y][1] = '#0f172a'; grid[y][2] = '#1e293b'; }
      grid[4][1] = '#38bdf8';
      for (let y = 1; y <= 4; y++) for (let x = 5; x <= 8; x++) grid[y][x] = '#2563eb';
      grid[1][6] = '#60a5fa';
      grid[2][7] = '#facc15'; grid[2][8] = '#fde047';
      grid[3][7] = '#eab308'; grid[3][8] = '#facc15';
      grid[2][9] = '#38bdf8';
      // 2. Torso & Arms
      for (let y = 4; y <= 8; y++) for (let x = 3; x <= 8; x++) grid[y][x] = '#2563eb';
      grid[5][5] = 'TEAM'; grid[5][6] = 'TEAM';
      grid[6][5] = 'TEAM'; grid[6][6] = 'TEAM';
      grid[4][5] = '#60a5fa';
      // 3. Gauss Rifle
      grid[6][7] = '#0f172a'; grid[6][8] = '#1e293b'; grid[7][8] = '#0f172a';
      grid[5][9] = '#475569'; grid[5][10] = '#64748b'; grid[5][11] = '#94a3b8'; grid[5][12] = '#cbd5e1';
      grid[6][9] = '#334155'; grid[6][10] = '#475569'; grid[6][11] = '#64748b'; grid[6][12] = '#94a3b8';
      grid[5][13] = '#cbd5e1'; grid[6][13] = '#ffffff';
      // 4. Legs & Boots
      for (let y = 9; y <= 11; y++) {
        grid[y][3] = '#1e293b'; grid[y][4] = '#2563eb'; grid[y][5] = '#3b82f6';
        grid[y][7] = '#1e293b'; grid[y][8] = '#2563eb'; grid[y][9] = '#3b82f6';
      }
      grid[10][4] = '#60a5fa'; grid[10][8] = '#60a5fa';
      grid[12][2] = '#1e293b'; grid[12][3] = '#334155'; grid[12][4] = '#475569';
      grid[13][2] = '#0f172a'; grid[13][3] = '#1e293b'; grid[13][4] = '#334155';
      grid[12][6] = '#1e293b'; grid[12][7] = '#334155'; grid[12][8] = '#475569';
      grid[13][6] = '#0f172a'; grid[13][7] = '#1e293b'; grid[13][8] = '#334155';
    }
    else if (unitType === 'Shade_Legs' || unitType === 'Shade_Legs_Stand') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // 🌑 Shade Legs Stand: Pelvis, Belt, Stealth Greaves & Tattered Cape Tail
      grid[9][5] = '#0f172a'; grid[9][6] = '#1e293b'; grid[9][7] = '#1e293b'; grid[9][8] = '#0f172a';
      grid[10][4] = '#0f172a'; grid[10][5] = '#020617'; grid[10][6] = '#334155'; grid[10][7] = '#334155'; grid[10][8] = '#020617';
      // Left leg
      grid[11][4] = '#020617'; grid[11][5] = '#0f172a'; grid[11][6] = '#1e293b';
      grid[12][4] = '#020617'; grid[12][5] = '#1e293b';
      grid[13][3] = '#020617'; grid[13][4] = '#0f172a'; grid[13][5] = '#1e293b';
      grid[14][3] = '#020617'; grid[14][4] = '#0f172a';
      grid[15][3] = '#020617'; grid[15][4] = '#090d16';
      // Right leg
      grid[11][7] = '#1e293b'; grid[11][8] = '#0f172a';
      grid[12][7] = '#334155'; grid[12][8] = '#1e293b';
      grid[13][7] = '#1e293b'; grid[13][8] = '#0f172a';
      grid[14][7] = '#0f172a'; grid[14][8] = '#1e293b'; grid[14][9] = '#020617';
      grid[15][7] = '#020617'; grid[15][8] = '#0f172a'; grid[15][9] = '#020617';
      // Tattered cape tail behind legs (-X)
      grid[10][1] = '#020617'; grid[10][2] = '#090d16'; grid[10][3] = '#1e293b';
      grid[11][0] = '#020617'; grid[11][1] = '#090d16'; grid[11][2] = '#0f172a';
      grid[12][0] = '#020617'; grid[12][1] = '#090d16';
      grid[13][0] = '#020617'; grid[13][1] = '#090d16';
      grid[14][0] = '#020617';
    }
    else if (unitType === 'Shade_Legs_Walk1') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // Belt harness
      grid[9][5] = '#0f172a'; grid[9][6] = '#1e293b'; grid[9][7] = '#1e293b'; grid[9][8] = '#0f172a';
      grid[10][4] = '#0f172a'; grid[10][5] = '#020617'; grid[10][6] = '#334155'; grid[10][7] = '#334155'; grid[10][8] = '#020617';
      // 1. Left Stride Forward (+X)
      grid[11][7] = '#0f172a'; grid[11][8] = '#1e293b'; grid[11][9] = '#334155';
      grid[12][8] = '#1e293b'; grid[12][9] = '#475569'; grid[12][10] = '#1e293b';
      grid[13][9] = '#0f172a'; grid[13][10] = '#1e293b'; grid[13][11] = '#334155';
      grid[14][9] = '#020617'; grid[14][10] = '#0f172a'; grid[14][11] = '#1e293b';
      grid[15][9] = '#020617'; grid[15][10] = '#090d16';
      // 2. Right Push Leg Trailing Back (-X)
      grid[11][4] = '#0f172a'; grid[11][5] = '#1e293b';
      grid[12][3] = '#020617'; grid[12][4] = '#0f172a'; grid[12][5] = '#334155';
      grid[13][2] = '#020617'; grid[13][3] = '#0f172a'; grid[13][4] = '#1e293b';
      grid[14][1] = '#020617'; grid[14][2] = '#090d16'; grid[14][3] = '#0f172a';
      grid[15][2] = '#020617';
      // Cape billow
      grid[10][1] = '#020617'; grid[10][2] = '#090d16'; grid[10][3] = '#1e293b';
      grid[11][0] = '#020617'; grid[11][1] = '#090d16'; grid[11][2] = '#0f172a';
      grid[12][0] = '#020617'; grid[12][1] = '#090d16';
      grid[13][0] = '#020617';
    }
    else if (unitType === 'Shade_Legs_Walk2') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // Belt harness
      grid[9][5] = '#0f172a'; grid[9][6] = '#1e293b'; grid[9][7] = '#1e293b'; grid[9][8] = '#0f172a';
      grid[10][4] = '#0f172a'; grid[10][5] = '#020617'; grid[10][6] = '#334155'; grid[10][7] = '#334155'; grid[10][8] = '#020617';
      // Left Support Leg Flat Under Hip
      grid[11][6] = '#020617'; grid[11][7] = '#0f172a'; grid[11][8] = '#1e293b';
      grid[12][6] = '#020617'; grid[12][7] = '#1e293b'; grid[12][8] = '#334155';
      grid[13][6] = '#020617'; grid[13][7] = '#0f172a'; grid[13][8] = '#1e293b';
      grid[14][6] = '#020617'; grid[14][7] = '#0f172a'; grid[14][8] = '#1e293b';
      grid[15][6] = '#020617'; grid[15][7] = '#090d16'; grid[15][8] = '#020617';
      // Right Leg Lifting Behind
      grid[11][3] = '#0f172a'; grid[11][4] = '#1e293b';
      grid[12][2] = '#020617'; grid[12][3] = '#0f172a'; grid[12][4] = '#334155';
      grid[13][2] = '#020617'; grid[13][3] = '#0f172a';
      // Cape billow
      grid[10][1] = '#020617'; grid[10][2] = '#090d16'; grid[10][3] = '#1e293b';
      grid[11][0] = '#020617'; grid[11][1] = '#090d16'; grid[11][2] = '#0f172a';
      grid[12][0] = '#020617'; grid[12][1] = '#020617';
    }
    else if (unitType === 'Shade_Legs_Walk3') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // Belt harness
      grid[9][5] = '#0f172a'; grid[9][6] = '#1e293b'; grid[9][7] = '#1e293b'; grid[9][8] = '#0f172a';
      grid[10][4] = '#0f172a'; grid[10][5] = '#020617'; grid[10][6] = '#334155'; grid[10][7] = '#334155'; grid[10][8] = '#020617';
      // Left Straight Supporting Leg
      grid[11][5] = '#020617'; grid[11][6] = '#0f172a'; grid[11][7] = '#1e293b';
      grid[12][5] = '#020617'; grid[12][6] = '#1e293b'; grid[12][7] = '#334155';
      grid[13][5] = '#020617'; grid[13][6] = '#0f172a'; grid[13][7] = '#1e293b';
      grid[14][5] = '#020617'; grid[14][6] = '#0f172a'; grid[14][7] = '#1e293b';
      grid[15][5] = '#020617'; grid[15][6] = '#090d16'; grid[15][7] = '#020617';
      // Right Passing Leg (Lifted High Knee Forward)
      grid[11][8] = '#0f172a'; grid[11][9] = '#1e293b'; grid[11][10] = '#334155';
      grid[12][8] = '#1e293b'; grid[12][9] = '#475569'; grid[12][10] = '#334155';
      grid[13][8] = '#0f172a'; grid[13][9] = '#1e293b';
      // Cape billow
      grid[10][1] = '#020617'; grid[10][2] = '#090d16'; grid[10][3] = '#1e293b';
      grid[11][0] = '#020617'; grid[11][1] = '#090d16'; grid[11][2] = '#0f172a';
      grid[12][0] = '#020617'; grid[12][1] = '#020617';
    }
    else if (unitType === 'Shade_Legs_Walk4') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // Belt harness
      grid[9][5] = '#0f172a'; grid[9][6] = '#1e293b'; grid[9][7] = '#1e293b'; grid[9][8] = '#0f172a';
      grid[10][4] = '#0f172a'; grid[10][5] = '#020617'; grid[10][6] = '#334155'; grid[10][7] = '#334155'; grid[10][8] = '#020617';
      // 1. Right Stride Forward (+X)
      grid[11][7] = '#0f172a'; grid[11][8] = '#1e293b'; grid[11][9] = '#334155';
      grid[12][8] = '#1e293b'; grid[12][9] = '#475569'; grid[12][10] = '#1e293b';
      grid[13][9] = '#0f172a'; grid[13][10] = '#1e293b'; grid[13][11] = '#334155';
      grid[14][9] = '#020617'; grid[14][10] = '#0f172a'; grid[14][11] = '#1e293b';
      grid[15][9] = '#020617'; grid[15][10] = '#090d16';
      // 2. Left Push Leg Trailing Back (-X)
      grid[11][4] = '#0f172a'; grid[11][5] = '#1e293b';
      grid[12][3] = '#020617'; grid[12][4] = '#0f172a'; grid[12][5] = '#334155';
      grid[13][2] = '#020617'; grid[13][3] = '#0f172a'; grid[13][4] = '#1e293b';
      grid[14][1] = '#020617'; grid[14][2] = '#090d16'; grid[14][3] = '#0f172a';
      grid[15][2] = '#020617';
      // Cape billow
      grid[10][1] = '#020617'; grid[10][2] = '#090d16'; grid[10][3] = '#1e293b';
      grid[11][0] = '#020617'; grid[11][1] = '#090d16'; grid[11][2] = '#0f172a';
      grid[12][0] = '#020617'; grid[12][1] = '#090d16';
      grid[13][0] = '#020617';
    }
    else if (unitType === 'Shade_Legs_Walk5') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // Belt harness
      grid[9][5] = '#0f172a'; grid[9][6] = '#1e293b'; grid[9][7] = '#1e293b'; grid[9][8] = '#0f172a';
      grid[10][4] = '#0f172a'; grid[10][5] = '#020617'; grid[10][6] = '#334155'; grid[10][7] = '#334155'; grid[10][8] = '#020617';
      // Right Support Leg Flat Under Hip
      grid[11][6] = '#020617'; grid[11][7] = '#0f172a'; grid[11][8] = '#1e293b';
      grid[12][6] = '#020617'; grid[12][7] = '#1e293b'; grid[12][8] = '#334155';
      grid[13][6] = '#020617'; grid[13][7] = '#0f172a'; grid[13][8] = '#1e293b';
      grid[14][6] = '#020617'; grid[14][7] = '#0f172a'; grid[14][8] = '#1e293b';
      grid[15][6] = '#020617'; grid[15][7] = '#090d16'; grid[15][8] = '#020617';
      // Left Leg Lifting Behind
      grid[11][3] = '#0f172a'; grid[11][4] = '#1e293b';
      grid[12][2] = '#020617'; grid[12][3] = '#0f172a'; grid[12][4] = '#334155';
      grid[13][2] = '#020617'; grid[13][3] = '#0f172a';
      // Cape billow
      grid[10][1] = '#020617'; grid[10][2] = '#090d16'; grid[10][3] = '#1e293b';
      grid[11][0] = '#020617'; grid[11][1] = '#090d16'; grid[11][2] = '#0f172a';
      grid[12][0] = '#020617'; grid[12][1] = '#020617';
    }
    else if (unitType === 'Shade_Legs_Walk6') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // Belt harness
      grid[9][5] = '#0f172a'; grid[9][6] = '#1e293b'; grid[9][7] = '#1e293b'; grid[9][8] = '#0f172a';
      grid[10][4] = '#0f172a'; grid[10][5] = '#020617'; grid[10][6] = '#334155'; grid[10][7] = '#334155'; grid[10][8] = '#020617';
      // Right Straight Supporting Leg
      grid[11][5] = '#020617'; grid[11][6] = '#0f172a'; grid[11][7] = '#1e293b';
      grid[12][5] = '#020617'; grid[12][6] = '#1e293b'; grid[12][7] = '#334155';
      grid[13][5] = '#020617'; grid[13][6] = '#0f172a'; grid[13][7] = '#1e293b';
      grid[14][5] = '#020617'; grid[14][6] = '#0f172a'; grid[14][7] = '#1e293b';
      grid[15][5] = '#020617'; grid[15][6] = '#090d16'; grid[15][7] = '#020617';
      // Left Passing Leg (Lifted High Knee Forward)
      grid[11][8] = '#0f172a'; grid[11][9] = '#1e293b'; grid[11][10] = '#334155';
      grid[12][8] = '#1e293b'; grid[12][9] = '#475569'; grid[12][10] = '#334155';
      grid[13][8] = '#0f172a'; grid[13][9] = '#1e293b';
      // Cape billow
      grid[10][1] = '#020617'; grid[10][2] = '#090d16'; grid[10][3] = '#1e293b';
      grid[11][0] = '#020617'; grid[11][1] = '#090d16'; grid[11][2] = '#0f172a';
      grid[12][0] = '#020617'; grid[12][1] = '#020617';
    }
    else if (unitType === 'Shade_Torso') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // 🌑 Shade Torso: Ragged Billowing Tattered Cloak, Hooded Cowl, Glowing Red Visor & Tactical Carbon Armor

      // 1. Tattered Shadow Cape Upper (-X rear)
      grid[2][3] = '#090d16'; grid[2][4] = '#1e293b';
      grid[3][2] = '#020617'; grid[3][3] = '#090d16'; grid[3][4] = '#1e293b';
      grid[4][1] = '#020617'; grid[4][2] = '#090d16'; grid[4][3] = '#1e293b'; grid[4][4] = '#334155';
      grid[5][0] = '#020617'; grid[5][1] = '#090d16'; grid[5][2] = '#0f172a'; grid[5][3] = '#1e293b'; grid[5][4] = '#1e293b';
      grid[6][0] = '#020617'; grid[6][1] = '#090d16'; grid[6][2] = '#0f172a'; grid[6][3] = '#1e293b';
      grid[7][1] = '#020617'; grid[7][2] = '#090d16'; grid[7][3] = '#1e293b';
      grid[8][1] = '#020617'; grid[8][2] = '#090d16'; grid[8][3] = '#1e293b';
      grid[9][2] = '#020617'; grid[9][3] = '#090d16';

      // 2. Hooded Cowl & Head (y: 1..5, x: 5..9)
      grid[1][6] = '#020617'; grid[1][7] = '#0f172a'; grid[1][8] = '#020617';
      grid[2][5] = '#020617'; grid[2][6] = '#0f172a'; grid[2][7] = '#1e293b'; grid[2][8] = '#0f172a'; grid[2][9] = '#020617';
      grid[3][5] = '#0f172a'; grid[3][6] = '#020617';
      // Glowing Red Visor / Optics (x: 7, 8)
      grid[3][7] = '#ef4444'; grid[3][8] = '#ff0033'; grid[3][9] = '#020617';
      // Respirator / Tactical Mask
      grid[4][5] = '#0f172a'; grid[4][6] = '#1e293b'; grid[4][7] = '#334155'; grid[4][8] = '#1e293b'; grid[4][9] = '#0f172a';
      grid[5][6] = '#1e293b'; grid[5][7] = '#475569'; grid[5][8] = '#1e293b';

      // 3. Torso & Tactical Armor (y: 6..9, x: 4..8)
      grid[6][4] = '#0f172a'; grid[6][5] = 'TEAM'; grid[6][6] = '#1e293b'; grid[6][7] = '#334155'; grid[6][8] = '#1e293b';
      grid[7][4] = '#0f172a'; grid[7][5] = 'TEAM'; grid[7][6] = '#334155'; grid[7][7] = '#475569'; grid[7][8] = '#1e293b';
      grid[8][5] = '#0f172a'; grid[8][6] = '#1e293b'; grid[8][7] = '#334155'; grid[8][8] = '#1e293b';
      grid[9][5] = '#0f172a'; grid[9][6] = '#1e293b'; grid[9][7] = '#1e293b'; grid[9][8] = '#0f172a';
    }
    else if (unitType === 'Shade_Gun') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // 🌑 Shade Gun & Arms: Suppressed Sniper Rifle with High-Power Scope & Foregrip
      // Operative Arms & Gloves (y: 6..7, x: 4..7)
      grid[6][4] = '#1e293b'; grid[6][5] = '#334155';
      grid[7][5] = '#0f172a'; grid[7][6] = '#1e293b'; grid[7][7] = '#334155';
      // Scope with red glint (y: 5, x: 9..12)
      grid[5][9] = '#0f172a'; grid[5][10] = '#334155'; grid[5][11] = '#475569'; grid[5][12] = '#ff0033';
      // Receiver, Grip & Magazine
      grid[7][8] = '#1e293b'; grid[7][9] = '#475569'; grid[7][10] = '#334155';
      grid[8][9] = '#0f172a';
      // Barrel & Handguard
      grid[7][11] = '#334155'; grid[7][12] = '#475569'; grid[7][13] = '#64748b'; grid[7][14] = '#94a3b8';
      // Suppressor Muzzle
      grid[6][15] = '#0f172a'; grid[7][15] = '#1e293b'; grid[8][15] = '#0f172a';
      // Folded Bipod
      grid[8][13] = '#0f172a'; grid[8][14] = '#1e293b';
    }
    else if (unitType === 'Shade') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // 🌑 SHADE: Assembled Full 16x16 Model Fallback
      grid[2][3] = '#090d16'; grid[2][4] = '#1e293b';
      grid[3][2] = '#020617'; grid[3][3] = '#090d16'; grid[3][4] = '#1e293b';
      grid[4][1] = '#020617'; grid[4][2] = '#090d16'; grid[4][3] = '#1e293b'; grid[4][4] = '#334155';
      grid[5][0] = '#020617'; grid[5][1] = '#090d16'; grid[5][2] = '#0f172a'; grid[5][3] = '#1e293b'; grid[5][4] = '#1e293b';
      grid[6][0] = '#020617'; grid[6][1] = '#090d16'; grid[6][2] = '#0f172a'; grid[6][3] = '#1e293b';
      grid[7][1] = '#020617'; grid[7][2] = '#090d16'; grid[7][3] = '#1e293b';
      grid[8][1] = '#020617'; grid[8][2] = '#090d16'; grid[8][3] = '#1e293b';
      grid[9][2] = '#020617'; grid[9][3] = '#090d16';
      grid[10][1] = '#020617'; grid[10][2] = '#090d16'; grid[10][3] = '#1e293b';
      grid[11][0] = '#020617'; grid[11][1] = '#090d16'; grid[11][2] = '#0f172a';
      grid[12][0] = '#020617'; grid[12][1] = '#090d16';
      grid[13][0] = '#020617'; grid[13][1] = '#090d16';
      grid[14][0] = '#020617';
      grid[1][6] = '#020617'; grid[1][7] = '#0f172a'; grid[1][8] = '#020617';
      grid[2][5] = '#020617'; grid[2][6] = '#0f172a'; grid[2][7] = '#1e293b'; grid[2][8] = '#0f172a'; grid[2][9] = '#020617';
      grid[3][5] = '#0f172a'; grid[3][6] = '#020617';
      grid[3][7] = '#ef4444'; grid[3][8] = '#ff0033'; grid[3][9] = '#020617';
      grid[4][5] = '#0f172a'; grid[4][6] = '#1e293b'; grid[4][7] = '#334155'; grid[4][8] = '#1e293b'; grid[4][9] = '#0f172a';
      grid[5][6] = '#1e293b'; grid[5][7] = '#475569'; grid[5][8] = '#1e293b';
      grid[6][4] = '#0f172a'; grid[6][5] = 'TEAM'; grid[6][6] = '#1e293b'; grid[6][7] = '#334155'; grid[6][8] = '#1e293b';
      grid[7][4] = '#0f172a'; grid[7][5] = 'TEAM'; grid[7][6] = '#334155'; grid[7][7] = '#475569'; grid[7][8] = '#1e293b';
      grid[8][5] = '#0f172a'; grid[8][6] = '#1e293b'; grid[8][7] = '#334155'; grid[8][8] = '#1e293b';
      grid[9][5] = '#0f172a'; grid[9][6] = '#1e293b'; grid[9][7] = '#1e293b'; grid[9][8] = '#0f172a';
      grid[10][4] = '#0f172a'; grid[10][5] = '#020617'; grid[10][6] = '#334155'; grid[10][7] = '#334155'; grid[10][8] = '#020617';
      grid[11][4] = '#020617'; grid[11][5] = '#0f172a'; grid[11][6] = '#1e293b';
      grid[12][4] = '#020617'; grid[12][5] = '#1e293b';
      grid[13][3] = '#020617'; grid[13][4] = '#0f172a'; grid[13][5] = '#1e293b';
      grid[14][3] = '#020617'; grid[14][4] = '#0f172a';
      grid[15][3] = '#020617'; grid[15][4] = '#090d16';
      grid[11][7] = '#1e293b'; grid[11][8] = '#0f172a';
      grid[12][7] = '#334155'; grid[12][8] = '#1e293b';
      grid[13][7] = '#1e293b'; grid[13][8] = '#0f172a';
      grid[14][7] = '#0f172a'; grid[14][8] = '#1e293b'; grid[14][9] = '#020617';
      grid[15][7] = '#020617'; grid[15][8] = '#0f172a'; grid[15][9] = '#020617';
      grid[5][9] = '#0f172a'; grid[5][10] = '#334155'; grid[5][11] = '#475569'; grid[5][12] = '#ff0033';
      grid[7][9] = '#475569'; grid[7][10] = '#334155';
      grid[8][9] = '#0f172a';
      grid[7][11] = '#334155'; grid[7][12] = '#475569'; grid[7][13] = '#64748b'; grid[7][14] = '#94a3b8';
      grid[6][15] = '#0f172a'; grid[7][15] = '#1e293b'; grid[8][15] = '#0f172a';
      grid[8][13] = '#0f172a'; grid[8][14] = '#1e293b';
    }
    else if (unitType === 'HyperShade_Legs' || unitType === 'HyperShade_Legs_Stand') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // 🦅 Hyper Shade Legs Stand: Carbon Greaves, Crimson Power Harness & Shadow Cape Tail
      grid[9][5] = '#0f172a'; grid[9][6] = '#1e293b'; grid[9][7] = '#1e293b'; grid[9][8] = '#0f172a';
      grid[10][4] = '#0f172a'; grid[10][5] = '#020617'; grid[10][6] = '#ef4444'; grid[10][7] = '#ef4444'; grid[10][8] = '#020617';
      // Left leg with crimson nano-conduits
      grid[11][4] = '#020617'; grid[11][5] = '#0f172a'; grid[11][6] = '#dc2626';
      grid[12][4] = '#020617'; grid[12][5] = '#1e293b';
      grid[13][3] = '#020617'; grid[13][4] = '#0f172a'; grid[13][5] = '#ef4444';
      grid[14][3] = '#020617'; grid[14][4] = '#0f172a';
      grid[15][3] = '#020617'; grid[15][4] = '#090d16';
      // Right leg with crimson nano-conduits
      grid[11][7] = '#dc2626'; grid[11][8] = '#0f172a';
      grid[12][7] = '#334155'; grid[12][8] = '#1e293b';
      grid[13][7] = '#ef4444'; grid[13][8] = '#0f172a';
      grid[14][7] = '#0f172a'; grid[14][8] = '#1e293b'; grid[14][9] = '#020617';
      grid[15][7] = '#020617'; grid[15][8] = '#0f172a'; grid[15][9] = '#020617';
      // Tattered shadow-wing cape tail behind legs (-X)
      grid[10][0] = '#dc2626'; grid[10][1] = '#020617'; grid[10][2] = '#090d16'; grid[10][3] = '#1e293b';
      grid[11][0] = '#020617'; grid[11][1] = '#090d16'; grid[11][2] = '#0f172a';
      grid[12][0] = '#020617'; grid[12][1] = '#090d16';
      grid[13][0] = '#020617'; grid[13][1] = '#090d16';
      grid[14][0] = '#020617';
    }
    else if (unitType === 'HyperShade_Legs_Walk1') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // Belt harness with crimson reactor core
      grid[9][5] = '#0f172a'; grid[9][6] = '#1e293b'; grid[9][7] = '#1e293b'; grid[9][8] = '#0f172a';
      grid[10][4] = '#0f172a'; grid[10][5] = '#020617'; grid[10][6] = '#ef4444'; grid[10][7] = '#ef4444'; grid[10][8] = '#020617';
      // 1. Left Stride Forward (+X)
      grid[11][7] = '#0f172a'; grid[11][8] = '#dc2626'; grid[11][9] = '#334155';
      grid[12][8] = '#1e293b'; grid[12][9] = '#ef4444'; grid[12][10] = '#1e293b';
      grid[13][9] = '#0f172a'; grid[13][10] = '#1e293b'; grid[13][11] = '#334155';
      grid[14][9] = '#020617'; grid[14][10] = '#0f172a'; grid[14][11] = '#1e293b';
      grid[15][9] = '#020617'; grid[15][10] = '#090d16';
      // 2. Right Push Leg Trailing Back (-X)
      grid[11][4] = '#0f172a'; grid[11][5] = '#1e293b';
      grid[12][3] = '#020617'; grid[12][4] = '#0f172a'; grid[12][5] = '#ef4444';
      grid[13][2] = '#020617'; grid[13][3] = '#0f172a'; grid[13][4] = '#1e293b';
      grid[14][1] = '#020617'; grid[14][2] = '#090d16'; grid[14][3] = '#0f172a';
      grid[15][2] = '#020617';
      // Shadow cape billow
      grid[10][0] = '#dc2626'; grid[10][1] = '#020617'; grid[10][2] = '#090d16'; grid[10][3] = '#1e293b';
      grid[11][0] = '#020617'; grid[11][1] = '#090d16'; grid[11][2] = '#0f172a';
      grid[12][0] = '#020617'; grid[12][1] = '#090d16';
      grid[13][0] = '#020617';
    }
    else if (unitType === 'HyperShade_Legs_Walk2') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      grid[9][5] = '#0f172a'; grid[9][6] = '#1e293b'; grid[9][7] = '#1e293b'; grid[9][8] = '#0f172a';
      grid[10][4] = '#0f172a'; grid[10][5] = '#020617'; grid[10][6] = '#ef4444'; grid[10][7] = '#ef4444'; grid[10][8] = '#020617';
      // Left Support Leg Flat Under Hip
      grid[11][6] = '#020617'; grid[11][7] = '#0f172a'; grid[11][8] = '#dc2626';
      grid[12][6] = '#020617'; grid[12][7] = '#1e293b'; grid[12][8] = '#ef4444';
      grid[13][6] = '#020617'; grid[13][7] = '#0f172a'; grid[13][8] = '#1e293b';
      grid[14][6] = '#020617'; grid[14][7] = '#0f172a'; grid[14][8] = '#1e293b';
      grid[15][6] = '#020617'; grid[15][7] = '#090d16'; grid[15][8] = '#020617';
      // Right Leg Lifting Behind
      grid[11][3] = '#0f172a'; grid[11][4] = '#1e293b';
      grid[12][2] = '#020617'; grid[12][3] = '#0f172a'; grid[12][4] = '#ef4444';
      grid[13][2] = '#020617'; grid[13][3] = '#0f172a';
      // Shadow cape billow
      grid[10][0] = '#dc2626'; grid[10][1] = '#020617'; grid[10][2] = '#090d16'; grid[10][3] = '#1e293b';
      grid[11][0] = '#020617'; grid[11][1] = '#090d16'; grid[11][2] = '#0f172a';
      grid[12][0] = '#020617'; grid[12][1] = '#020617';
    }
    else if (unitType === 'HyperShade_Legs_Walk3') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      grid[9][5] = '#0f172a'; grid[9][6] = '#1e293b'; grid[9][7] = '#1e293b'; grid[9][8] = '#0f172a';
      grid[10][4] = '#0f172a'; grid[10][5] = '#020617'; grid[10][6] = '#ef4444'; grid[10][7] = '#ef4444'; grid[10][8] = '#020617';
      // Left Straight Supporting Leg
      grid[11][5] = '#020617'; grid[11][6] = '#0f172a'; grid[11][7] = '#dc2626';
      grid[12][5] = '#020617'; grid[12][6] = '#1e293b'; grid[12][7] = '#ef4444';
      grid[13][5] = '#020617'; grid[13][6] = '#0f172a'; grid[13][7] = '#1e293b';
      grid[14][5] = '#020617'; grid[14][6] = '#0f172a'; grid[14][7] = '#1e293b';
      grid[15][5] = '#020617'; grid[15][6] = '#090d16'; grid[15][7] = '#020617';
      // Right Passing Leg (Lifted High Knee Forward)
      grid[11][8] = '#0f172a'; grid[11][9] = '#dc2626'; grid[11][10] = '#334155';
      grid[12][8] = '#1e293b'; grid[12][9] = '#ef4444'; grid[12][10] = '#334155';
      grid[13][8] = '#0f172a'; grid[13][9] = '#1e293b';
      // Shadow cape billow
      grid[10][0] = '#dc2626'; grid[10][1] = '#020617'; grid[10][2] = '#090d16'; grid[10][3] = '#1e293b';
      grid[11][0] = '#020617'; grid[11][1] = '#090d16'; grid[11][2] = '#0f172a';
      grid[12][0] = '#020617'; grid[12][1] = '#020617';
    }
    else if (unitType === 'HyperShade_Legs_Walk4') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      grid[9][5] = '#0f172a'; grid[9][6] = '#1e293b'; grid[9][7] = '#1e293b'; grid[9][8] = '#0f172a';
      grid[10][4] = '#0f172a'; grid[10][5] = '#020617'; grid[10][6] = '#ef4444'; grid[10][7] = '#ef4444'; grid[10][8] = '#020617';
      // 1. Right Stride Forward (+X)
      grid[11][7] = '#0f172a'; grid[11][8] = '#dc2626'; grid[11][9] = '#334155';
      grid[12][8] = '#1e293b'; grid[12][9] = '#ef4444'; grid[12][10] = '#1e293b';
      grid[13][9] = '#0f172a'; grid[13][10] = '#1e293b'; grid[13][11] = '#334155';
      grid[14][9] = '#020617'; grid[14][10] = '#0f172a'; grid[14][11] = '#1e293b';
      grid[15][9] = '#020617'; grid[15][10] = '#090d16';
      // 2. Left Push Leg Trailing Back (-X)
      grid[11][4] = '#0f172a'; grid[11][5] = '#1e293b';
      grid[12][3] = '#020617'; grid[12][4] = '#0f172a'; grid[12][5] = '#ef4444';
      grid[13][2] = '#020617'; grid[13][3] = '#0f172a'; grid[13][4] = '#1e293b';
      grid[14][1] = '#020617'; grid[14][2] = '#090d16'; grid[14][3] = '#0f172a';
      grid[15][2] = '#020617';
      // Shadow cape billow
      grid[10][0] = '#dc2626'; grid[10][1] = '#020617'; grid[10][2] = '#090d16'; grid[10][3] = '#1e293b';
      grid[11][0] = '#020617'; grid[11][1] = '#090d16'; grid[11][2] = '#0f172a';
      grid[12][0] = '#020617'; grid[12][1] = '#090d16';
      grid[13][0] = '#020617';
    }
    else if (unitType === 'HyperShade_Legs_Walk5') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      grid[9][5] = '#0f172a'; grid[9][6] = '#1e293b'; grid[9][7] = '#1e293b'; grid[9][8] = '#0f172a';
      grid[10][4] = '#0f172a'; grid[10][5] = '#020617'; grid[10][6] = '#ef4444'; grid[10][7] = '#ef4444'; grid[10][8] = '#020617';
      // Right Support Leg Flat Under Hip
      grid[11][6] = '#020617'; grid[11][7] = '#0f172a'; grid[11][8] = '#dc2626';
      grid[12][6] = '#020617'; grid[12][7] = '#1e293b'; grid[12][8] = '#ef4444';
      grid[13][6] = '#020617'; grid[13][7] = '#0f172a'; grid[13][8] = '#1e293b';
      grid[14][6] = '#020617'; grid[14][7] = '#0f172a'; grid[14][8] = '#1e293b';
      grid[15][6] = '#020617'; grid[15][7] = '#090d16'; grid[15][8] = '#020617';
      // Left Leg Lifting Behind
      grid[11][3] = '#0f172a'; grid[11][4] = '#1e293b';
      grid[12][2] = '#020617'; grid[12][3] = '#0f172a'; grid[12][4] = '#ef4444';
      grid[13][2] = '#020617'; grid[13][3] = '#0f172a';
      // Shadow cape billow
      grid[10][0] = '#dc2626'; grid[10][1] = '#020617'; grid[10][2] = '#090d16'; grid[10][3] = '#1e293b';
      grid[11][0] = '#020617'; grid[11][1] = '#090d16'; grid[11][2] = '#0f172a';
      grid[12][0] = '#020617'; grid[12][1] = '#020617';
    }
    else if (unitType === 'HyperShade_Legs_Walk6') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      grid[9][5] = '#0f172a'; grid[9][6] = '#1e293b'; grid[9][7] = '#1e293b'; grid[9][8] = '#0f172a';
      grid[10][4] = '#0f172a'; grid[10][5] = '#020617'; grid[10][6] = '#ef4444'; grid[10][7] = '#ef4444'; grid[10][8] = '#020617';
      // Right Straight Supporting Leg
      grid[11][5] = '#020617'; grid[11][6] = '#0f172a'; grid[11][7] = '#dc2626';
      grid[12][5] = '#020617'; grid[12][6] = '#1e293b'; grid[12][7] = '#ef4444';
      grid[13][5] = '#020617'; grid[13][6] = '#0f172a'; grid[13][7] = '#1e293b';
      grid[14][5] = '#020617'; grid[14][6] = '#0f172a'; grid[14][7] = '#1e293b';
      grid[15][5] = '#020617'; grid[15][6] = '#090d16'; grid[15][7] = '#020617';
      // Left Passing Leg (Lifted High Knee Forward)
      grid[11][8] = '#0f172a'; grid[11][9] = '#dc2626'; grid[11][10] = '#334155';
      grid[12][8] = '#1e293b'; grid[12][9] = '#ef4444'; grid[12][10] = '#334155';
      grid[13][8] = '#0f172a'; grid[13][9] = '#1e293b';
      // Shadow cape billow
      grid[10][0] = '#dc2626'; grid[10][1] = '#020617'; grid[10][2] = '#090d16'; grid[10][3] = '#1e293b';
      grid[11][0] = '#020617'; grid[11][1] = '#090d16'; grid[11][2] = '#0f172a';
      grid[12][0] = '#020617'; grid[12][1] = '#020617';
    }
    else if (unitType === 'HyperShade_Torso') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // 🦅 Hyper Shade Torso: Massive Billowing Shadow-Wing Cowl, Multi-Optic Crimson Lenses & Hyper Nanotech Armor
      // 1. Spreading Shadow-Wing Tendrils & Cloak (-X rear & top)
      grid[0][2] = '#dc2626'; grid[0][3] = '#020617';
      grid[1][1] = '#ef4444'; grid[1][2] = '#090d16'; grid[1][3] = '#0f172a'; grid[1][4] = '#1e293b';
      grid[2][0] = '#dc2626'; grid[2][1] = '#020617'; grid[2][2] = '#090d16'; grid[2][3] = '#090d16'; grid[2][4] = '#1e293b';
      grid[3][0] = '#020617'; grid[3][1] = '#090d16'; grid[3][2] = '#020617'; grid[3][3] = '#090d16'; grid[3][4] = '#1e293b';
      grid[4][0] = '#020617'; grid[4][1] = '#dc2626'; grid[4][2] = '#090d16'; grid[4][3] = '#1e293b'; grid[4][4] = '#334155';
      grid[5][0] = '#020617'; grid[5][1] = '#090d16'; grid[5][2] = '#0f172a'; grid[5][3] = '#1e293b'; grid[5][4] = '#1e293b';
      grid[6][0] = '#dc2626'; grid[6][1] = '#020617'; grid[6][2] = '#0f172a'; grid[6][3] = '#1e293b';
      grid[7][0] = '#020617'; grid[7][1] = '#020617'; grid[7][2] = '#090d16'; grid[7][3] = '#1e293b';
      grid[8][1] = '#020617'; grid[8][2] = '#090d16'; grid[8][3] = '#1e293b';
      grid[9][2] = '#020617'; grid[9][3] = '#090d16';

      // 2. Hooded Cowl & Multi-Optic Crimson Sensor Cluster (y: 1..5, x: 5..9)
      grid[1][6] = '#020617'; grid[1][7] = '#0f172a'; grid[1][8] = '#020617';
      grid[2][5] = '#020617'; grid[2][6] = '#0f172a'; grid[2][7] = '#ef4444'; grid[2][8] = '#dc2626'; grid[2][9] = '#020617';
      grid[3][5] = '#0f172a'; grid[3][6] = '#020617';
      // Multi-Optic Sensor Lenses (Crimson Tri-Lenses)
      grid[3][7] = '#ff0033'; grid[3][8] = '#ff0033'; grid[3][9] = '#ef4444';
      // Tactical Respirator & Jaw Visor
      grid[4][5] = '#0f172a'; grid[4][6] = '#1e293b'; grid[4][7] = '#dc2626'; grid[4][8] = '#1e293b'; grid[4][9] = '#0f172a';
      grid[5][6] = '#1e293b'; grid[5][7] = '#475569'; grid[5][8] = '#1e293b';

      // 3. Torso & Hyper-Nanotech Armor (y: 6..9, x: 4..8)
      grid[6][4] = '#0f172a'; grid[6][5] = 'TEAM'; grid[6][6] = '#dc2626'; grid[6][7] = '#ef4444'; grid[6][8] = '#1e293b';
      grid[7][4] = '#0f172a'; grid[7][5] = 'TEAM'; grid[7][6] = '#ef4444'; grid[7][7] = '#475569'; grid[7][8] = '#1e293b';
      grid[8][5] = '#0f172a'; grid[8][6] = '#1e293b'; grid[8][7] = '#dc2626'; grid[8][8] = '#1e293b';
      grid[9][5] = '#0f172a'; grid[9][6] = '#1e293b'; grid[9][7] = '#1e293b'; grid[9][8] = '#0f172a';
    }
    else if (unitType === 'HyperShade_Gun') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // 🦅 Hyper Shade Heavy Anti-Materiel Suppressed Sniper Rifle with Thermal Vents & Laser Optic
      // Operative Arms & Cybernetic Gloves (y: 6..7, x: 4..7)
      grid[6][4] = '#1e293b'; grid[6][5] = '#dc2626';
      grid[7][5] = '#0f172a'; grid[7][6] = '#1e293b'; grid[7][7] = '#ef4444';
      // Multi-Lens Tactical Scope with Red Glint (y: 5, x: 9..12)
      grid[5][9] = '#0f172a'; grid[5][10] = '#ef4444'; grid[5][11] = '#ff0033'; grid[5][12] = '#f87171';
      // Receiver, Grip, & Mag with Crimson Power Channel
      grid[7][8] = '#1e293b'; grid[7][9] = '#dc2626'; grid[7][10] = '#ef4444';
      grid[8][9] = '#0f172a';
      // Extended Rail Barrel & Thermal Shroud
      grid[7][11] = '#475569'; grid[7][12] = '#dc2626'; grid[7][13] = '#64748b'; grid[7][14] = '#94a3b8';
      // Heavy Suppressor Muzzle
      grid[6][15] = '#0f172a'; grid[7][15] = '#ef4444'; grid[8][15] = '#0f172a';
      // Folded Heavy Bipod
      grid[8][13] = '#0f172a'; grid[8][14] = '#1e293b';
    }
    else if (unitType === 'HyperShade') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // 🦅 HYPER SHADE: Assembled Full 16x16 Model Fallback
      grid[0][2] = '#dc2626'; grid[0][3] = '#020617';
      grid[1][1] = '#ef4444'; grid[1][2] = '#090d16'; grid[1][3] = '#0f172a'; grid[1][4] = '#1e293b';
      grid[2][0] = '#dc2626'; grid[2][1] = '#020617'; grid[2][2] = '#090d16'; grid[2][3] = '#090d16'; grid[2][4] = '#1e293b';
      grid[3][0] = '#020617'; grid[3][1] = '#090d16'; grid[3][2] = '#020617'; grid[3][3] = '#090d16'; grid[3][4] = '#1e293b';
      grid[4][0] = '#020617'; grid[4][1] = '#dc2626'; grid[4][2] = '#090d16'; grid[4][3] = '#1e293b'; grid[4][4] = '#334155';
      grid[5][0] = '#020617'; grid[5][1] = '#090d16'; grid[5][2] = '#0f172a'; grid[5][3] = '#1e293b'; grid[5][4] = '#1e293b';
      grid[6][0] = '#dc2626'; grid[6][1] = '#020617'; grid[6][2] = '#0f172a'; grid[6][3] = '#1e293b';
      grid[7][0] = '#020617'; grid[7][1] = '#020617'; grid[7][2] = '#090d16'; grid[7][3] = '#1e293b';
      grid[8][1] = '#020617'; grid[8][2] = '#090d16'; grid[8][3] = '#1e293b';
      grid[9][2] = '#020617'; grid[9][3] = '#090d16';
      grid[10][0] = '#dc2626'; grid[10][1] = '#020617'; grid[10][2] = '#090d16'; grid[10][3] = '#1e293b';
      grid[11][0] = '#020617'; grid[11][1] = '#090d16'; grid[11][2] = '#0f172a';
      grid[12][0] = '#020617'; grid[12][1] = '#090d16';
      grid[13][0] = '#020617'; grid[13][1] = '#090d16';
      grid[14][0] = '#020617';
      grid[1][6] = '#020617'; grid[1][7] = '#0f172a'; grid[1][8] = '#020617';
      grid[2][5] = '#020617'; grid[2][6] = '#0f172a'; grid[2][7] = '#ef4444'; grid[2][8] = '#dc2626'; grid[2][9] = '#020617';
      grid[3][5] = '#0f172a'; grid[3][6] = '#020617';
      grid[3][7] = '#ff0033'; grid[3][8] = '#ff0033'; grid[3][9] = '#ef4444';
      grid[4][5] = '#0f172a'; grid[4][6] = '#1e293b'; grid[4][7] = '#dc2626'; grid[4][8] = '#1e293b'; grid[4][9] = '#0f172a';
      grid[5][6] = '#1e293b'; grid[5][7] = '#475569'; grid[5][8] = '#1e293b';
      grid[6][4] = '#0f172a'; grid[6][5] = 'TEAM'; grid[6][6] = '#dc2626'; grid[6][7] = '#ef4444'; grid[6][8] = '#1e293b';
      grid[7][4] = '#0f172a'; grid[7][5] = 'TEAM'; grid[7][6] = '#ef4444'; grid[7][7] = '#475569'; grid[7][8] = '#1e293b';
      grid[8][5] = '#0f172a'; grid[8][6] = '#1e293b'; grid[8][7] = '#dc2626'; grid[8][8] = '#1e293b';
      grid[9][5] = '#0f172a'; grid[9][6] = '#1e293b'; grid[9][7] = '#1e293b'; grid[9][8] = '#0f172a';
      grid[10][4] = '#0f172a'; grid[10][5] = '#020617'; grid[10][6] = '#ef4444'; grid[10][7] = '#ef4444'; grid[10][8] = '#020617';
      grid[11][4] = '#020617'; grid[11][5] = '#0f172a'; grid[11][6] = '#dc2626';
      grid[12][4] = '#020617'; grid[12][5] = '#1e293b';
      grid[13][3] = '#020617'; grid[13][4] = '#0f172a'; grid[13][5] = '#ef4444';
      grid[14][3] = '#020617'; grid[14][4] = '#0f172a';
      grid[15][3] = '#020617'; grid[15][4] = '#090d16';
      grid[11][7] = '#dc2626'; grid[11][8] = '#0f172a';
      grid[12][7] = '#334155'; grid[12][8] = '#1e293b';
      grid[13][7] = '#ef4444'; grid[13][8] = '#0f172a';
      grid[14][7] = '#0f172a'; grid[14][8] = '#1e293b'; grid[14][9] = '#020617';
      grid[15][7] = '#020617'; grid[15][8] = '#0f172a'; grid[15][9] = '#020617';
      grid[5][9] = '#0f172a'; grid[5][10] = '#ef4444'; grid[5][11] = '#ff0033'; grid[5][12] = '#f87171';
      grid[7][9] = '#dc2626'; grid[7][10] = '#ef4444';
      grid[8][9] = '#0f172a';
      grid[7][11] = '#475569'; grid[7][12] = '#dc2626'; grid[7][13] = '#64748b'; grid[7][14] = '#94a3b8';
      grid[6][15] = '#0f172a'; grid[7][15] = '#ef4444'; grid[8][15] = '#0f172a';
      grid[8][13] = '#0f172a'; grid[8][14] = '#1e293b';
    }
    else if (             unitType === 'Panzer_Chassis_Roll1' ||
             unitType === 'Panzer_Chassis_Roll2' ||
             unitType === 'Panzer_Chassis_Roll3' ||
             unitType === 'Panzer_Chassis' ||
             unitType === 'Panzer_Chassis_Roll1' ||
             unitType === 'Panzer_Chassis_Roll2' ||
             unitType === 'Panzer_Chassis_Roll3' ||
             unitType === 'HyperPanzer_Chassis' ||
             unitType === 'HyperPanzer_Chassis_Stand' ||
             unitType === 'HyperPanzer_Chassis_Roll1' ||
             unitType === 'HyperPanzer_Chassis_Roll2' ||
             unitType === 'HyperPanzer_Chassis_Roll3' ||
             unitType === 'Hyper_Panzer_Chassis' ||
             unitType === 'Hyper_Panzer_Chassis_Stand' ||
             unitType === 'Hyper_Panzer_Chassis_Roll1' ||
             unitType === 'Hyper_Panzer_Chassis_Roll2' ||
             unitType === 'Hyper_Panzer_Chassis_Roll3') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));

      let rollFrame = 0;
      if (unitType === 'Panzer_Chassis_Roll2' || unitType === 'Panzer_Chassis_Roll2' || unitType === 'HyperPanzer_Chassis_Roll2' || unitType === 'Hyper_Panzer_Chassis_Roll2') rollFrame = 1;
      else if (unitType === 'Panzer_Chassis_Roll3' || unitType === 'Panzer_Chassis_Roll3' || unitType === 'HyperPanzer_Chassis_Roll3' || unitType === 'Hyper_Panzer_Chassis_Roll3') rollFrame = 2;

      // Heavy Caterpillar Tracks & Bogie Wheels (y: 11 to 15, x: 1 to 14)
      for (let y = 11; y <= 15; y++) {
        for (let x = 1; x <= 14; x++) {
          grid[y][x] = (y === 11 || y === 15 || x === 1 || x === 14) ? '#0f172a' : '#1e293b';
        }
      }
      // Animated track cleats / grousers on top and bottom tracks
      for (let x = 2; x <= 13; x++) {
        if ((x + rollFrame) % 3 === 0) {
          grid[11][x] = '#334155';
          grid[15][x] = '#334155';
        }
      }
      // 4 Road wheels & bogies (Laufrollen & Nabendeckel) with 3-phase rotation
      for (const wx of [2, 5, 8, 11]) {
        if (rollFrame === 0) {
          grid[12][wx] = '#64748b'; grid[12][wx + 1] = '#94a3b8';
          grid[13][wx] = '#cbd5e1'; grid[13][wx + 1] = '#475569';
          grid[14][wx] = '#334155'; grid[14][wx + 1] = '#0f172a';
        } else if (rollFrame === 1) {
          grid[12][wx] = '#94a3b8'; grid[12][wx + 1] = '#cbd5e1';
          grid[13][wx] = '#475569'; grid[13][wx + 1] = '#64748b';
          grid[14][wx] = '#0f172a'; grid[14][wx + 1] = '#334155';
        } else {
          grid[12][wx] = '#cbd5e1'; grid[12][wx + 1] = '#475569';
          grid[13][wx] = '#94a3b8'; grid[13][wx + 1] = '#0f172a';
          grid[14][wx] = '#64748b'; grid[14][wx + 1] = '#334155';
        }
      }
      // Armored Lower Hull & Glacis Plate (y: 7 to 10, x: 2 to 13)
      for (let y = 7; y <= 10; y++) {
        for (let x = 2; x <= 12; x++) {
          grid[y][x] = (y === 7 || x === 12) ? '#64748b' : ((x === 2 || y === 10) ? '#1e293b' : '#334155');
        }
      }
      // Sloped front nose & driver cyan optic block
      grid[7][11] = '#38bdf8'; grid[7][12] = '#00f0ff';
      grid[8][13] = '#475569'; grid[9][13] = '#334155';
      // Side skirt team badge
      grid[8][5] = 'TEAM'; grid[8][6] = 'TEAM';
      grid[9][5] = 'TEAM'; grid[9][6] = 'TEAM';
      // Rear engine exhaust glow
      grid[8][1] = '#f97316'; grid[9][1] = '#78350f';
      // Turret mounting ring (Drehkranz)
      grid[6][5] = '#0f172a'; grid[6][6] = '#475569'; grid[6][7] = '#475569'; grid[6][8] = '#0f172a';
    }
    else if (unitType === 'Panzer_Turret' ||
             unitType.startsWith('HyperPanzer_Turret') || unitType.startsWith('Hyper_Panzer_Turret')) {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // Armored Turret Housing (y: 4 to 8, x: 3 to 9)
      for (let y = 4; y <= 8; y++) {
        for (let x = 3; x <= 9; x++) {
          grid[y][x] = (y === 4) ? '#64748b' : ((x === 3 || y === 8) ? '#1e293b' : '#475569');
        }
      }
      // Commander cupola & optic sensor
      grid[3][4] = '#94a3b8'; grid[3][5] = '#cbd5e1';
      grid[4][5] = '#facc15'; grid[4][6] = '#ef4444';
      // Rear bustle / ammo magazine overhang
      grid[5][1] = '#1e293b'; grid[5][2] = '#334155';
      grid[6][1] = '#1e293b'; grid[6][2] = '#334155';
      grid[7][2] = '#0f172a';
      // Turret team insignia
      grid[6][4] = 'TEAM'; grid[6][5] = 'TEAM';
      grid[7][4] = 'TEAM'; grid[7][5] = 'TEAM';
      // Heavy 120mm Shock Cannon Mantlet, Barrel & Muzzle Brake
      grid[5][9] = '#1e293b'; grid[6][9] = '#334155'; grid[7][9] = '#334155'; grid[8][9] = '#1e293b';
      for (let x = 10; x <= 14; x++) {
        grid[6][x] = (x % 2 === 0) ? '#cbd5e1' : '#94a3b8';
        grid[7][x] = '#475569';
      }
      grid[5][14] = '#334155'; grid[5][15] = '#64748b';
      grid[6][15] = '#f1f5f9'; grid[7][15] = '#f1f5f9';
      grid[8][14] = '#334155'; grid[8][15] = '#64748b';
    }
    else if (unitType === 'Panzer' || unitType === 'HyperPanzer' || unitType === 'Hyper_Panzer') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // Full Assembled Model
      // 1. Treads & Wheels
      for (let y = 11; y <= 15; y++) {
        for (let x = 1; x <= 14; x++) {
          grid[y][x] = (y === 11 || y === 15 || x === 1 || x === 14) ? '#0f172a' : '#1e293b';
        }
      }
      for (const wx of [2, 5, 8, 11]) {
        grid[12][wx] = '#64748b'; grid[12][wx + 1] = '#94a3b8';
        grid[13][wx] = '#cbd5e1'; grid[13][wx + 1] = '#475569';
        grid[14][wx] = '#334155'; grid[14][wx + 1] = '#0f172a';
      }
      // 2. Hull
      for (let y = 8; y <= 10; y++) {
        for (let x = 2; x <= 12; x++) {
          grid[y][x] = (y === 8 || x === 12) ? '#64748b' : ((x === 2 || y === 10) ? '#1e293b' : '#334155');
        }
      }
      grid[8][11] = '#38bdf8'; grid[8][12] = '#00f0ff';
      grid[9][5] = 'TEAM'; grid[9][6] = 'TEAM';
      grid[8][1] = '#f97316'; grid[9][1] = '#78350f';
      // 3. Turret & 120mm Gun
      for (let y = 4; y <= 7; y++) {
        for (let x = 3; x <= 9; x++) {
          grid[y][x] = (y === 4) ? '#64748b' : ((x === 3 || y === 7) ? '#1e293b' : '#475569');
        }
      }
      grid[3][4] = '#94a3b8'; grid[3][5] = '#cbd5e1';
      grid[4][5] = '#facc15'; grid[4][6] = '#ef4444';
      grid[5][1] = '#1e293b'; grid[5][2] = '#334155';
      grid[6][1] = '#1e293b'; grid[6][2] = '#334155';
      grid[6][4] = 'TEAM'; grid[6][5] = 'TEAM';
      grid[5][9] = '#1e293b'; grid[6][9] = '#334155';
      for (let x = 10; x <= 14; x++) {
        grid[6][x] = (x % 2 === 0) ? '#cbd5e1' : '#94a3b8';
        grid[7][x] = '#475569';
      }
      grid[5][14] = '#334155'; grid[5][15] = '#64748b';
      grid[6][15] = '#f1f5f9'; grid[7][15] = '#f1f5f9';
      grid[8][14] = '#334155'; grid[8][15] = '#64748b';
    }
    else if (unitType === 'TeslaWalker_Legs' || unitType === 'TeslaWalker_Legs_Stand') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // Mechanical Spider Chassis Mount & Hip Pivot (y: 8 to 9, x: 5 to 10)
      for (let x = 5; x <= 10; x++) { grid[8][x] = '#1e293b'; grid[9][x] = '#0f172a'; }
      grid[8][7] = '#475569'; grid[8][8] = '#64748b';
      // 1. Front-Left Leg (x: 0 to 4, y: 8 to 15)
      grid[8][4] = '#334155'; grid[9][3] = '#475569'; grid[9][2] = '#64748b'; grid[8][1] = '#94a3b8'; // Knee arch
      grid[10][1] = '#475569'; grid[11][1] = '#334155'; grid[12][1] = '#1e293b'; grid[13][1] = '#334155'; // Shin
      grid[14][0] = '#475569'; grid[14][1] = '#94a3b8'; grid[15][0] = '#0f172a'; grid[15][1] = '#cbd5e1'; // Claw foot
      // 2. Front-Right Leg (x: 11 to 15, y: 8 to 15)
      grid[8][11] = '#334155'; grid[9][12] = '#475569'; grid[9][13] = '#64748b'; grid[8][14] = '#94a3b8'; // Knee arch
      grid[10][14] = '#475569'; grid[11][14] = '#334155'; grid[12][14] = '#1e293b'; grid[13][14] = '#334155'; // Shin
      grid[14][14] = '#94a3b8'; grid[14][15] = '#475569'; grid[15][14] = '#cbd5e1'; grid[15][15] = '#0f172a'; // Claw foot
      // 3. Rear-Left Leg (x: 4 to 6, y: 10 to 15)
      grid[10][5] = '#1e293b'; grid[11][4] = '#334155'; grid[12][4] = '#475569'; grid[13][4] = '#334155';
      grid[14][4] = '#64748b'; grid[15][4] = '#0f172a'; grid[15][5] = '#94a3b8';
      // 4. Rear-Right Leg (x: 9 to 11, y: 10 to 15)
      grid[10][10] = '#1e293b'; grid[11][11] = '#334155'; grid[12][11] = '#475569'; grid[13][11] = '#334155';
      grid[14][11] = '#64748b'; grid[15][10] = '#94a3b8'; grid[15][11] = '#0f172a';
    }
    else if (unitType === 'TeslaWalker_Legs_Walk1') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      for (let x = 5; x <= 10; x++) { grid[8][x] = '#1e293b'; grid[9][x] = '#0f172a'; }
      grid[8][7] = '#475569'; grid[8][8] = '#64748b';
      // Phase 1: Front-Right Stride Extended Forward (+X), Rear-Left Stride Forward
      grid[8][4] = '#334155'; grid[9][3] = '#475569'; grid[9][2] = '#334155';
      grid[10][2] = '#1e293b'; grid[11][1] = '#334155'; grid[12][1] = '#1e293b'; grid[13][0] = '#0f172a';
      grid[14][0] = '#475569'; grid[15][0] = '#94a3b8';
      grid[8][11] = '#334155'; grid[8][12] = '#475569'; grid[7][13] = '#94a3b8'; grid[7][14] = '#cbd5e1';
      grid[8][14] = '#64748b'; grid[9][15] = '#475569'; grid[10][15] = '#334155'; grid[11][15] = '#1e293b';
      grid[12][15] = '#475569'; grid[13][15] = '#94a3b8'; grid[14][15] = '#cbd5e1';
      grid[10][5] = '#1e293b'; grid[10][6] = '#475569'; grid[11][6] = '#64748b'; grid[12][6] = '#475569';
      grid[13][6] = '#334155'; grid[14][6] = '#94a3b8'; grid[15][6] = '#cbd5e1';
      grid[10][10] = '#1e293b'; grid[11][10] = '#334155'; grid[12][9] = '#1e293b'; grid[13][9] = '#334155';
      grid[14][9] = '#64748b'; grid[15][9] = '#0f172a';
    }
    else if (unitType === 'TeslaWalker_Legs_Walk2') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      for (let x = 5; x <= 10; x++) { grid[8][x] = '#1e293b'; grid[9][x] = '#0f172a'; }
      grid[8][7] = '#475569'; grid[8][8] = '#64748b';
      // Phase 2: Passing & Weight Transition
      grid[8][4] = '#334155'; grid[9][3] = '#475569'; grid[9][2] = '#64748b';
      grid[10][2] = '#475569'; grid[11][2] = '#334155'; grid[12][2] = '#1e293b'; grid[13][2] = '#334155';
      grid[14][1] = '#475569'; grid[14][2] = '#94a3b8'; grid[15][1] = '#0f172a'; grid[15][2] = '#cbd5e1';
      grid[8][11] = '#334155'; grid[9][12] = '#475569'; grid[9][13] = '#64748b';
      grid[10][13] = '#475569'; grid[11][13] = '#334155'; grid[12][13] = '#1e293b'; grid[13][13] = '#334155';
      grid[14][13] = '#94a3b8'; grid[14][14] = '#475569'; grid[15][13] = '#cbd5e1'; grid[15][14] = '#0f172a';
      grid[10][5] = '#1e293b'; grid[11][5] = '#334155'; grid[12][5] = '#475569'; grid[13][5] = '#334155';
      grid[14][5] = '#64748b'; grid[15][5] = '#0f172a';
      grid[10][10] = '#1e293b'; grid[11][10] = '#334155'; grid[12][10] = '#475569'; grid[13][10] = '#334155';
      grid[14][10] = '#64748b'; grid[15][10] = '#0f172a';
    }
    else if (unitType === 'TeslaWalker_Legs_Walk3') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      for (let x = 5; x <= 10; x++) { grid[8][x] = '#1e293b'; grid[9][x] = '#0f172a'; }
      grid[8][7] = '#475569'; grid[8][8] = '#64748b';
      // Phase 3: Front-Left Stride Extended Forward, Rear-Right Stride Forward
      grid[8][4] = '#334155'; grid[8][3] = '#475569'; grid[7][2] = '#94a3b8'; grid[7][1] = '#cbd5e1';
      grid[8][1] = '#64748b'; grid[9][0] = '#475569'; grid[10][0] = '#334155'; grid[11][0] = '#1e293b';
      grid[12][0] = '#475569'; grid[13][0] = '#94a3b8'; grid[14][0] = '#cbd5e1';
      grid[8][11] = '#334155'; grid[9][12] = '#475569'; grid[9][13] = '#334155';
      grid[10][13] = '#1e293b'; grid[11][14] = '#334155'; grid[12][14] = '#1e293b'; grid[13][15] = '#0f172a';
      grid[14][15] = '#475569'; grid[15][15] = '#94a3b8';
      grid[10][5] = '#1e293b'; grid[11][5] = '#334155'; grid[12][6] = '#1e293b'; grid[13][6] = '#334155';
      grid[14][6] = '#64748b'; grid[15][6] = '#0f172a';
      grid[10][10] = '#1e293b'; grid[10][9] = '#475569'; grid[11][9] = '#64748b'; grid[12][9] = '#475569';
      grid[13][9] = '#334155'; grid[14][9] = '#94a3b8'; grid[15][9] = '#cbd5e1';
    }
    else if (unitType === 'TeslaWalker_Legs_Walk4') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      for (let x = 5; x <= 10; x++) { grid[8][x] = '#1e293b'; grid[9][x] = '#0f172a'; }
      grid[8][7] = '#475569'; grid[8][8] = '#64748b';
      // Phase 4: Passing & Return to Center
      grid[8][4] = '#334155'; grid[9][3] = '#475569'; grid[9][2] = '#64748b'; grid[8][1] = '#94a3b8';
      grid[10][1] = '#475569'; grid[11][1] = '#334155'; grid[12][1] = '#1e293b'; grid[13][1] = '#334155';
      grid[14][0] = '#475569'; grid[14][1] = '#94a3b8'; grid[15][0] = '#0f172a'; grid[15][1] = '#cbd5e1';
      grid[8][11] = '#334155'; grid[9][12] = '#475569'; grid[9][13] = '#64748b'; grid[8][14] = '#94a3b8';
      grid[10][14] = '#475569'; grid[11][14] = '#334155'; grid[12][14] = '#1e293b'; grid[13][14] = '#334155';
      grid[14][14] = '#94a3b8'; grid[14][15] = '#475569'; grid[15][14] = '#cbd5e1'; grid[15][15] = '#0f172a';
      grid[10][5] = '#1e293b'; grid[11][4] = '#334155'; grid[12][4] = '#475569'; grid[13][4] = '#334155';
      grid[14][4] = '#64748b'; grid[15][4] = '#0f172a';
      grid[10][10] = '#1e293b'; grid[11][11] = '#334155'; grid[12][11] = '#475569'; grid[13][11] = '#334155';
      grid[14][11] = '#64748b'; grid[15][11] = '#0f172a';
    }
    else if (unitType === 'TeslaWalker_Chassis') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // Robotic Core Body Hull (y: 7 to 11, x: 4 to 11)
      for (let y = 7; y <= 11; y++) {
        for (let x = 4; x <= 11; x++) {
          grid[y][x] = (y === 7 || x === 11) ? '#64748b' : ((x === 4 || y === 11) ? '#0f172a' : '#334155');
        }
      }
      // Top Armor Bevel & Capacitor Housing
      grid[6][5] = '#1e293b'; grid[6][6] = '#475569'; grid[6][7] = '#64748b'; grid[6][8] = '#64748b'; grid[6][9] = '#475569'; grid[6][10] = '#1e293b';
      // Team Insignia Decals
      grid[8][6] = 'TEAM'; grid[8][7] = 'TEAM';
      grid[9][6] = 'TEAM'; grid[9][7] = 'TEAM';
      // Forward High-Tech Sensor Optic Eye (Cyan Visor)
      grid[8][10] = '#38bdf8'; grid[8][11] = '#00f0ff';
      grid[9][10] = '#0284c7'; grid[9][11] = '#38bdf8';
      // Power Cell Capacitors
      grid[7][4] = '#38bdf8'; grid[7][5] = '#00f0ff';
      grid[10][4] = '#0f172a'; grid[10][5] = '#1e293b';
    }
    else if (unitType === 'TeslaWalker_Tesla') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // Tesla Coil Base Mounting Pivot (y: 6, x: 6 to 9)
      grid[6][6] = '#0f172a'; grid[6][7] = '#334155'; grid[6][8] = '#334155'; grid[6][9] = '#0f172a';
      // Dielectric Insulating Rings (y: 3 to 5, x: 6 to 9)
      grid[5][5] = '#1e293b'; grid[5][6] = '#475569'; grid[5][7] = '#d97706'; grid[5][8] = '#b45309'; grid[5][9] = '#475569'; grid[5][10] = '#1e293b';
      grid[4][5] = '#1e293b'; grid[4][6] = '#64748b'; grid[4][7] = '#f59e0b'; grid[4][8] = '#d97706'; grid[4][9] = '#64748b'; grid[4][10] = '#1e293b';
      grid[3][6] = '#334155'; grid[3][7] = '#475569'; grid[3][8] = '#475569'; grid[3][9] = '#334155';
      // Spherical Toroid Capacitor / Tesla Emitter Sphere (y: 1 to 2, x: 6 to 9)
      grid[2][6] = '#0284c7'; grid[2][7] = '#38bdf8'; grid[2][8] = '#00f0ff'; grid[2][9] = '#0284c7';
      grid[1][6] = '#38bdf8'; grid[1][7] = '#00f0ff'; grid[1][8] = '#ffffff'; grid[1][9] = '#38bdf8';
      // Discharge Electrodes / Prongs (Top)
      grid[0][5] = '#00f0ff'; grid[0][10] = '#00f0ff';
      grid[1][5] = '#94a3b8'; grid[1][10] = '#94a3b8';
    }
    else if (unitType === 'TeslaWalker' || unitType === 'Tesla_Walker') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // Full Assembled 16x16 Model: Legs + Chassis + Tesla Device
      // 1. Spider Legs (y: 8 to 15)
      grid[8][4] = '#334155'; grid[9][3] = '#475569'; grid[9][2] = '#64748b'; grid[8][1] = '#94a3b8';
      grid[10][1] = '#475569'; grid[11][1] = '#334155'; grid[12][1] = '#1e293b'; grid[13][1] = '#334155';
      grid[14][0] = '#475569'; grid[14][1] = '#94a3b8'; grid[15][0] = '#0f172a'; grid[15][1] = '#cbd5e1';
      grid[8][11] = '#334155'; grid[9][12] = '#475569'; grid[9][13] = '#64748b'; grid[8][14] = '#94a3b8';
      grid[10][14] = '#475569'; grid[11][14] = '#334155'; grid[12][14] = '#1e293b'; grid[13][14] = '#334155';
      grid[14][14] = '#94a3b8'; grid[14][15] = '#475569'; grid[15][14] = '#cbd5e1'; grid[15][15] = '#0f172a';
      grid[10][5] = '#1e293b'; grid[11][4] = '#334155'; grid[12][4] = '#475569'; grid[13][4] = '#334155';
      grid[14][4] = '#64748b'; grid[15][4] = '#0f172a'; grid[15][5] = '#94a3b8';
      grid[10][10] = '#1e293b'; grid[11][11] = '#334155'; grid[12][11] = '#475569'; grid[13][11] = '#334155';
      grid[14][11] = '#64748b'; grid[15][10] = '#94a3b8'; grid[15][11] = '#0f172a';
      // 2. Chassis Hull (y: 7 to 11, x: 4 to 11)
      for (let y = 7; y <= 11; y++) {
        for (let x = 4; x <= 11; x++) {
          grid[y][x] = (y === 7 || x === 11) ? '#64748b' : ((x === 4 || y === 11) ? '#0f172a' : '#334155');
        }
      }
      grid[8][6] = 'TEAM'; grid[8][7] = 'TEAM';
      grid[9][6] = 'TEAM'; grid[9][7] = 'TEAM';
      grid[8][10] = '#38bdf8'; grid[8][11] = '#00f0ff';
      grid[9][10] = '#0284c7'; grid[9][11] = '#38bdf8';
      grid[7][4] = '#38bdf8'; grid[7][5] = '#00f0ff';
      // 3. Tesla Device (y: 0 to 6)
      grid[6][6] = '#0f172a'; grid[6][7] = '#334155'; grid[6][8] = '#334155'; grid[6][9] = '#0f172a';
      grid[5][5] = '#1e293b'; grid[5][6] = '#475569'; grid[5][7] = '#d97706'; grid[5][8] = '#b45309'; grid[5][9] = '#475569'; grid[5][10] = '#1e293b';
      grid[4][5] = '#1e293b'; grid[4][6] = '#64748b'; grid[4][7] = '#f59e0b'; grid[4][8] = '#d97706'; grid[4][9] = '#64748b'; grid[4][10] = '#1e293b';
      grid[3][6] = '#334155'; grid[3][7] = '#475569'; grid[3][8] = '#475569'; grid[3][9] = '#334155';
      grid[2][6] = '#0284c7'; grid[2][7] = '#38bdf8'; grid[2][8] = '#00f0ff'; grid[2][9] = '#0284c7';
      grid[1][6] = '#38bdf8'; grid[1][7] = '#00f0ff'; grid[1][8] = '#ffffff'; grid[1][9] = '#38bdf8';
      grid[0][5] = '#00f0ff'; grid[0][10] = '#00f0ff';
      grid[1][5] = '#94a3b8'; grid[1][10] = '#94a3b8';
    }
    else if (unitType === 'HyperTeslaWalker_Legs' ||
             unitType === 'HyperTeslaWalker_Legs_Stand' ||
             unitType === 'HyperTeslaWalker_Legs_Walk1' ||
             unitType === 'HyperTeslaWalker_Legs_Walk2' ||
             unitType === 'HyperTeslaWalker_Legs_Walk3' ||
             unitType === 'HyperTeslaWalker_Legs_Walk4') {
      gridSize = 20;
      grid = Array.from({ length: 20 }, () => Array(20).fill(null));
      let phase = 0;
      if (unitType === 'HyperTeslaWalker_Legs_Walk1') phase = 1;
      else if (unitType === 'HyperTeslaWalker_Legs_Walk2') phase = 2;
      else if (unitType === 'HyperTeslaWalker_Legs_Walk3') phase = 3;
      else if (unitType === 'HyperTeslaWalker_Legs_Walk4') phase = 4;
      _renderHyperTeslaWalkerLegs(grid, phase);
    }
    else if (unitType === 'HyperTeslaWalker_Chassis') {
      gridSize = 20;
      grid = Array.from({ length: 20 }, () => Array(20).fill(null));
      // 🛡️ Heavy Armored Scorpion Walker Hull & Cockpit (Facing +X)
      // 1. Armored Carapace Body Plates (x: 4..15, y: 7..13)
      for (let y = 8; y <= 12; y++) {
        for (let x = 4; x <= 14; x++) {
          grid[y][x] = (y === 8 || x === 14) ? '#64748b' : ((x === 4 || y === 12) ? '#0f172a' : '#334155');
        }
      }
      grid[7][6] = '#1e293b'; grid[7][7] = '#475569'; grid[7][8] = '#64748b'; grid[7][9] = '#64748b';
      grid[7][10] = '#475569'; grid[7][11] = '#475569'; grid[7][12] = '#1e293b';

      // 2. High Copper Piping / Hydraulic Conduits (Artwork: Relevant Copper Elements)
      grid[6][6] = '#b45309'; grid[6][7] = '#d97706'; grid[6][8] = '#f59e0b'; grid[6][9] = '#d97706';
      grid[6][10] = '#b45309'; grid[7][13] = '#d97706'; grid[8][13] = '#f59e0b';
      // Exposed side copper gear cluster & mechanical valves
      grid[9][5] = '#d97706'; grid[9][6] = '#f59e0b'; grid[10][5] = '#b45309'; grid[10][6] = '#d97706';
      grid[11][5] = '#d97706';

      // 3. Central Forward Optical Lens / High-Tech Sensor Eye (x: 13..15, y: 8..10)
      grid[8][13] = '#1e293b'; grid[8][14] = '#0284c7'; grid[8][15] = '#38bdf8';
      grid[9][13] = '#0284c7'; grid[9][14] = '#00f0ff'; grid[9][15] = '#ffffff';
      grid[10][13] = '#0f172a'; grid[10][14] = '#0284c7'; grid[10][15] = '#38bdf8';

      // 4. Pilot Operator Cockpit Pod (Right Arm / Viewer's front-right pod, x: 12..16, y: 11..15)
      grid[11][13] = '#1e293b'; grid[11][14] = '#334155'; grid[11][15] = '#475569'; grid[11][16] = '#1e293b';
      grid[12][13] = '#38bdf8'; grid[12][14] = '#00f0ff'; grid[12][15] = '#0284c7'; grid[12][16] = '#334155'; // Glass canopy
      grid[13][13] = '#0284c7'; grid[13][14] = '#caa152'; grid[13][15] = '#0284c7'; grid[13][16] = '#1e293b'; // Pilot silhouette
      grid[14][13] = '#1e293b'; grid[14][14] = '#334155'; grid[14][15] = '#475569'; grid[14][16] = '#0f172a';

      // 5. Team Insignia Decals
      grid[9][9] = 'TEAM'; grid[9][10] = 'TEAM';
      grid[10][9] = 'TEAM'; grid[10][10] = 'TEAM';
    }
    else if (unitType === 'HyperTeslaWalker_Tail') {
      gridSize = 20;
      grid = Array.from({ length: 20 }, () => Array(20).fill(null));
      // ⚡ Arched Segmented Scorpion Tail & Copper-Wound Tesla Coil Emitter (y: 0..12, x: 1..17)
      // 1. Tail Root & Swivel Base (x: 2..5, y: 8..11)
      grid[10][3] = '#0f172a'; grid[10][4] = '#334155'; grid[10][5] = '#1e293b';
      grid[9][2] = '#1e293b'; grid[9][3] = '#d97706'; grid[9][4] = '#f59e0b'; grid[9][5] = '#b45309'; // Copper hinge
      grid[8][2] = '#334155'; grid[8][3] = '#475569'; grid[8][4] = '#334155';

      // 2. Curving Armored Tail Segments (x: 1..6, y: 2..7)
      grid[7][1] = '#1e293b'; grid[7][2] = '#475569'; grid[7][3] = '#64748b'; grid[7][4] = '#d97706';
      grid[6][1] = '#334155'; grid[6][2] = '#64748b'; grid[6][3] = '#94a3b8';
      grid[5][1] = '#1e293b'; grid[5][2] = '#475569'; grid[5][3] = '#64748b'; grid[5][4] = '#d97706';
      grid[4][2] = '#334155'; grid[4][3] = '#64748b'; grid[4][4] = '#94a3b8';
      grid[3][3] = '#1e293b'; grid[3][4] = '#475569'; grid[3][5] = '#64748b';
      grid[2][4] = '#334155'; grid[2][5] = '#64748b'; grid[2][6] = '#94a3b8'; grid[2][7] = '#cbd5e1';

      // 3. Massive Glowing Copper-Wound Tesla Coil (x: 7..14, y: 0..4)
      // Rear Ceramic Dielectric Insulator Collar
      grid[1][7] = '#1e293b'; grid[2][7] = '#475569'; grid[3][7] = '#475569'; grid[4][7] = '#334155';
      // Twin Angled Copper Electrodes / Antennae (x: 6..9, y: 0)
      grid[0][6] = '#d97706'; grid[0][7] = '#f59e0b'; grid[0][8] = '#cbd5e1'; grid[0][9] = '#f59e0b';
      grid[1][8] = '#d97706'; grid[1][9] = '#f59e0b';

      // Dense Copper Wire Windings (x: 8..13, y: 1..4) — Core Artwork Element
      for (let x = 8; x <= 13; x++) {
        grid[1][x] = (x % 2 === 0) ? '#f59e0b' : '#fbbf24'; // Bright copper wire gleam
        grid[2][x] = (x % 2 === 0) ? '#d97706' : '#f59e0b'; // Core wire winding
        grid[3][x] = (x % 2 === 0) ? '#b45309' : '#d97706'; // Shaded copper underbelly
        grid[4][x] = (x % 2 === 0) ? '#78350f' : '#b45309'; // Lower copper coil mass
      }

      // Front Ceramic Insulator Collar (x: 14, y: 1..4)
      grid[1][14] = '#475569'; grid[2][14] = '#64748b'; grid[3][14] = '#64748b'; grid[4][14] = '#334155';

      // 4. Emitter Cone & Forward Needle Stinger (x: 15..19, y: 1..4)
      grid[1][15] = '#0284c7'; grid[2][15] = '#38bdf8'; grid[3][15] = '#0284c7';
      grid[2][16] = '#00f0ff'; grid[2][17] = '#ffffff'; grid[3][17] = '#38bdf8';
      grid[2][18] = '#00f0ff'; grid[2][19] = '#ffffff'; // Discharge needle tip
    }
    else if (unitType === 'HyperTeslaWalker_Claw' ||
             unitType === 'HyperTeslaWalker_Claw_Grab' ||
             unitType === 'HyperTeslaWalker_Claw_Lift') {
      gridSize = 20;
      grid = Array.from({ length: 20 }, () => Array(20).fill(null));
      // ✂️ Left Hydraulic Cleaver Pincer Arm (Nahkampf-Exekutions-Greifer)
      const isGrab = (unitType === 'HyperTeslaWalker_Claw_Grab');
      const isLift = (unitType === 'HyperTeslaWalker_Claw_Lift');

      // Heavy Hydraulic Shoulder Pivot & Dual Cylinders (x: 12..14, y: 12..16)
      grid[13][12] = '#0f172a'; grid[13][13] = '#1e293b'; grid[13][14] = '#334155';
      grid[14][12] = '#334155'; grid[14][13] = '#d97706'; grid[14][14] = '#f59e0b'; // Copper fluid conduit
      grid[15][12] = '#1e293b'; grid[15][13] = '#475569'; grid[15][14] = '#64748b';

      if (isLift) {
        // Lifted upward pose (clamped shut, holding victim in air)
        grid[9][14] = '#64748b'; grid[9][15] = '#94a3b8'; grid[9][16] = '#cbd5e1';
        grid[10][13] = '#475569'; grid[10][14] = '#64748b'; grid[10][15] = '#94a3b8';
        grid[11][13] = '#d97706'; grid[11][14] = '#f59e0b';
        grid[8][15] = '#cbd5e1'; grid[8][16] = '#ffffff';
      } else if (isGrab) {
        // Snapping jaws wide open around victim
        grid[11][15] = '#64748b'; grid[11][16] = '#94a3b8'; grid[10][17] = '#cbd5e1'; grid[10][18] = '#ffffff';
        grid[15][15] = '#64748b'; grid[15][16] = '#94a3b8'; grid[16][17] = '#cbd5e1'; grid[16][18] = '#ffffff';
        grid[12][14] = '#d97706'; grid[13][14] = '#f59e0b'; grid[14][14] = '#d97706';
      } else {
        // Neutral ready stance
        grid[13][15] = '#475569'; grid[13][16] = '#64748b'; grid[12][17] = '#94a3b8'; grid[12][18] = '#cbd5e1';
        grid[15][15] = '#475569'; grid[15][16] = '#64748b'; grid[16][17] = '#94a3b8'; grid[16][18] = '#cbd5e1';
        grid[14][14] = '#d97706'; grid[14][15] = '#f59e0b';
      }
    }
    else if (unitType === 'HyperTeslaWalker' || unitType === 'Hyper_Tesla_Walker' ||
             unitType === 'HyperTeslaWalker_Stand' ||
             unitType === 'HyperTeslaWalker_Walk1' ||
             unitType === 'HyperTeslaWalker_Walk2' ||
             unitType === 'HyperTeslaWalker_Walk3' ||
             unitType === 'HyperTeslaWalker_Walk4') {
      gridSize = 20;
      grid = Array.from({ length: 20 }, () => Array(20).fill(null));
      let phase = 0;
      if (unitType === 'HyperTeslaWalker_Walk1') phase = 1;
      else if (unitType === 'HyperTeslaWalker_Walk2') phase = 2;
      else if (unitType === 'HyperTeslaWalker_Walk3') phase = 3;
      else if (unitType === 'HyperTeslaWalker_Walk4') phase = 4;

      // 🦂 Assembled 20x20 Composite Model (Legs + Chassis + Tail + Claw)
      // 1. Legs (Phase 0: Neutral Stand, Phase 1..4: Alternating Tetrapod Walk)
      _renderHyperTeslaWalkerLegs(grid, phase);

      // 2. Chassis & Hull (x: 4..14, y: 7..12)
      for (let y = 8; y <= 12; y++) {
        for (let x = 4; x <= 14; x++) {
          grid[y][x] = (y === 8 || x === 14) ? '#64748b' : ((x === 4 || y === 12) ? '#0f172a' : '#334155');
        }
      }
      grid[6][6] = '#b45309'; grid[6][7] = '#d97706'; grid[6][8] = '#f59e0b'; grid[6][9] = '#d97706'; grid[6][10] = '#b45309';
      grid[8][13] = '#1e293b'; grid[8][14] = '#0284c7'; grid[8][15] = '#38bdf8';
      grid[9][13] = '#0284c7'; grid[9][14] = '#00f0ff'; grid[9][15] = '#ffffff';
      grid[10][13] = '#0f172a'; grid[10][14] = '#0284c7'; grid[10][15] = '#38bdf8';
      // Cockpit
      grid[12][13] = '#38bdf8'; grid[12][14] = '#00f0ff'; grid[12][15] = '#0284c7';
      grid[13][13] = '#0284c7'; grid[13][14] = '#caa152'; grid[13][15] = '#0284c7';
      // Team Insignia
      grid[9][9] = 'TEAM'; grid[9][10] = 'TEAM'; grid[10][9] = 'TEAM'; grid[10][10] = 'TEAM';

      // 3. Arched Stinger Tail with Copper Wire Coil (Enlarged)
      grid[9][2] = '#1e293b'; grid[9][3] = '#d97706'; grid[9][4] = '#f59e0b';
      grid[7][1] = '#1e293b'; grid[7][2] = '#475569'; grid[7][3] = '#64748b'; grid[7][4] = '#d97706';
      grid[5][1] = '#1e293b'; grid[5][2] = '#475569'; grid[5][3] = '#64748b'; grid[5][4] = '#d97706';
      grid[3][3] = '#1e293b'; grid[3][4] = '#475569'; grid[3][5] = '#64748b';
      grid[2][4] = '#334155'; grid[2][5] = '#64748b'; grid[2][6] = '#94a3b8';
      grid[0][6] = '#d97706'; grid[0][7] = '#f59e0b'; grid[0][8] = '#cbd5e1'; grid[0][9] = '#f59e0b';
      for (let x = 8; x <= 13; x++) {
        grid[1][x] = (x % 2 === 0) ? '#f59e0b' : '#fbbf24';
        grid[2][x] = (x % 2 === 0) ? '#d97706' : '#f59e0b';
        grid[3][x] = (x % 2 === 0) ? '#b45309' : '#d97706';
        grid[4][x] = (x % 2 === 0) ? '#78350f' : '#b45309';
      }
      grid[1][14] = '#475569'; grid[2][14] = '#64748b';
      grid[2][15] = '#38bdf8'; grid[2][16] = '#00f0ff'; grid[2][17] = '#ffffff'; grid[2][18] = '#00f0ff'; grid[2][19] = '#ffffff';

      // 4. Hydraulic Left Cleaver Claw
      grid[14][13] = '#d97706'; grid[14][14] = '#f59e0b';
      grid[13][15] = '#475569'; grid[13][16] = '#64748b'; grid[12][17] = '#94a3b8'; grid[12][18] = '#cbd5e1';
      grid[15][15] = '#475569'; grid[15][16] = '#64748b'; grid[16][17] = '#94a3b8'; grid[16][18] = '#cbd5e1';
    }
    else if (unitType === 'Rocketship') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // 🚀 Rocketship: Sleek high-speed aerospace missile carrier
      // 1. Delta Wing Planes (Top & Bottom swept-back aerodynamic wings)
      for (let y = 2; y <= 5; y++) {
        for (let x = 3; x <= 11 - y; x++) {
          grid[y][x] = (y === 2 || x === 3) ? '#1e293b' : '#475569';
        }
      }
      grid[2][4] = '#64748b'; grid[2][5] = '#94a3b8'; grid[3][4] = '#64748b';
      for (let y = 10; y <= 13; y++) {
        for (let x = 3; x <= y - 2; x++) {
          grid[y][x] = (y === 13 || x === 3) ? '#1e293b' : '#475569';
        }
      }
      grid[13][4] = '#64748b'; grid[13][5] = '#94a3b8'; grid[12][4] = '#64748b';

      // 2. Central Titanium Fuselage (y: 6 to 9, x: 2 to 14)
      for (let y = 6; y <= 9; y++) {
        for (let x = 2; x <= 13; x++) {
          grid[y][x] = (y === 6) ? '#64748b' : ((y === 9 || x === 2) ? '#1e293b' : '#334155');
        }
      }
      // Aerodynamic Needle Nose Cone (+X forward)
      grid[7][14] = '#cbd5e1'; grid[8][14] = '#94a3b8';
      grid[7][15] = '#f1f5f9';

      // Glossy Cyan Cockpit Canopy Visor (x: 9 to 12, y: 7 to 8)
      grid[6][9] = '#38bdf8'; grid[6][10] = '#00f0ff'; grid[6][11] = '#38bdf8';
      grid[7][9] = '#0284c7'; grid[7][10] = '#38bdf8'; grid[7][11] = '#00f0ff'; grid[7][12] = '#e0f2fe';
      grid[8][10] = '#0284c7'; grid[8][11] = '#38bdf8';

      // Team Insignia Decal on dorsal spine
      grid[7][5] = 'TEAM'; grid[7][6] = 'TEAM';
      grid[8][5] = 'TEAM'; grid[8][6] = 'TEAM';

      // 3. Side Missile Battery Pods with Red Warhead Tips
      grid[3][6] = '#0f172a'; grid[3][7] = '#334155'; grid[3][8] = '#cbd5e1'; grid[3][9] = '#cbd5e1';
      grid[3][10] = '#ef4444'; grid[3][11] = '#facc15';
      grid[12][6] = '#0f172a'; grid[12][7] = '#334155'; grid[12][8] = '#cbd5e1'; grid[12][9] = '#cbd5e1';
      grid[12][10] = '#ef4444'; grid[12][11] = '#facc15';

      // 4. Twin Rocket Thrusters & Afterburner Nozzles (-X rear)
      grid[6][1] = '#f97316'; grid[6][0] = '#fef08a';
      grid[9][1] = '#f97316'; grid[9][0] = '#fef08a';
    }
    else if (unitType === 'A9Piranha' || unitType === 'A9Piranha_Hull') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // 🚁 A9 Piranha Heavy Attack Helicopter — Side Profile Perspective
      // Facing right (+X forward, nose at right; -X rear, tail at left; top at y: 0; skids at bottom)

      // 1. Tail Boom, Stabilizers & Tail Rotor (-X rear: x: 0..6)
      // Vertical swept tail fin (x: 1..2, y: 2..6)
      grid[2][1] = '#475569'; grid[2][2] = '#64748b';
      grid[3][1] = '#334155'; grid[3][2] = '#475569';
      grid[4][1] = '#1e293b'; grid[4][2] = '#334155';
      grid[5][1] = '#1e293b'; grid[5][2] = '#334155';
      grid[6][1] = '#0f172a'; grid[6][2] = '#1e293b';

      // Tail rotor hub & spinning blade at rear fin tip (x: 0, y: 1..5)
      grid[1][0] = '#cbd5e1'; grid[2][0] = '#94a3b8';
      grid[3][0] = '#facc15'; // yellow hazard tip
      grid[4][0] = '#94a3b8'; grid[5][0] = '#cbd5e1';

      // Horizontal tail stabilizer (x: 2..4, y: 6)
      grid[6][3] = '#475569'; grid[6][4] = '#334155';

      // Tail boom extending from fin to cabin (y: 7, x: 2..6)
      for (let x = 2; x <= 6; x++) {
        grid[7][x] = (x % 2 === 0) ? '#0f172a' : '#334155';
      }
      grid[8][3] = '#1e293b'; grid[8][4] = '#1e293b'; grid[8][5] = '#334155';

      // 2. Engine Cowling / Turbine Doghouse & Rotor Mast (y: 2..5, x: 6..10)
      grid[2][8] = '#0f172a'; grid[2][9] = '#94a3b8'; // Mast collar hub
      grid[3][8] = '#334155'; grid[3][9] = '#475569';
      grid[4][6] = '#0f172a'; grid[4][7] = '#1e293b'; grid[4][8] = '#334155'; grid[4][9] = '#475569';
      grid[5][6] = '#334155'; grid[5][7] = '#475569'; grid[5][8] = '#64748b';

      // 3. Stepped Tandem Cockpit Canopies (Glossy Cyan - matching Rocketship visor!)
      // Rear cockpit (Gunner, raised: x: 8..9, y: 5..6)
      grid[5][8] = '#00f0ff'; grid[5][9] = '#38bdf8';
      grid[6][8] = '#0284c7'; grid[6][9] = '#38bdf8';
      // Front cockpit (Pilot, forward/stepped down: x: 10..12, y: 6..7)
      grid[6][10] = '#00f0ff'; grid[6][11] = '#38bdf8'; grid[6][12] = '#e0f2fe';
      grid[7][10] = '#0284c7'; grid[7][11] = '#38bdf8'; grid[7][12] = '#00f0ff';

      // 4. Main Armored Cabin & Fuselage (x: 6..14, y: 7..10)
      for (let y = 8; y <= 10; y++) {
        for (let x = 6; x <= 12; x++) {
          grid[y][x] = (y === 10) ? '#1e293b' : '#334155';
        }
      }
      grid[8][7] = '#0f172a'; grid[9][7] = '#0f172a';
      grid[8][10] = '#0f172a'; grid[9][10] = '#0f172a';

      // Team Insignia Decal on mid-fuselage door
      grid[8][8] = 'TEAM'; grid[8][9] = 'TEAM';
      grid[9][8] = 'TEAM'; grid[9][9] = 'TEAM';

      // 5. Forward Nose & Chin FLIR Targeting Turret (+X: x: 13..15, y: 7..8)
      grid[7][13] = '#475569'; grid[7][14] = '#334155';
      grid[8][13] = '#334155'; grid[8][14] = '#1e293b'; grid[8][15] = '#facc15'; // Golden FLIR optical sensor lens

      // 6. Side Stub Wing & Dual Rotary Cannons (Pointing forward +X)
      grid[9][8] = '#475569'; grid[9][9] = '#1e293b';
      // Upper cannon barrel (y: 9, x: 10..15)
      grid[9][10] = '#0f172a'; grid[9][11] = '#334155'; grid[9][12] = '#475569';
      grid[9][13] = '#94a3b8'; grid[9][14] = '#cbd5e1'; grid[9][15] = '#f1f5f9';
      // Lower cannon barrel (y: 10, x: 10..14)
      grid[10][9] = '#0f172a'; grid[10][10] = '#1e293b'; grid[10][11] = '#334155';
      grid[10][12] = '#475569'; grid[10][13] = '#94a3b8'; grid[10][14] = '#cbd5e1';

      // 7. Tubular Landing Skids (y: 11..13, x: 5..14)
      grid[11][7] = '#1e293b'; grid[12][7] = '#334155';
      grid[11][12] = '#1e293b'; grid[12][12] = '#334155';
      for (let x = 6; x <= 13; x++) {
        grid[13][x] = (x === 6 || x === 13) ? '#64748b' : '#94a3b8';
      }
      grid[12][14] = '#cbd5e1';

      if (unitType === 'A9Piranha') {
        for (let x = 1; x <= 15; x++) {
          grid[1][x] = (x === 1 || x === 15) ? '#facc15' : '#1e293b';
        }
      }
    }
    else if (unitType === 'A9Pigeon' || unitType === 'A9_Pigeon' || unitType === 'A9 Pigeon' || unitType === 'A9Pigeon_Hull') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // 🕊️ A9 Pigeon Heavy VTOL Fighter-Bomber (SD 16x16 Fallback — Sleek Dart Profile)
      // 1. Slender Supersonic Fuselage (y: 6..7, x: 3..15)
      grid[5][4] = '#64748b'; grid[5][5] = '#94a3b8'; grid[5][6] = '#cbd5e1';
      // Low Flush Cockpit Canopy (y: 5, x: 7..9)
      grid[5][7] = '#0284c7'; grid[5][8] = '#00f0ff'; grid[5][9] = '#ffffff';
      // Fuselage Centerline
      for (let x = 3; x <= 14; x++) {
        grid[6][x] = (x >= 10 && x <= 13) ? '#14b8a6' : ((x >= 8) ? '#a855f7' : '#475569');
      }
      grid[6][15] = '#f1f5f9'; // Needle tip
      // Lower Fuselage
      for (let x = 3; x <= 13; x++) {
        grid[7][x] = (x >= 10) ? '#0d9488' : ((x >= 8) ? '#7e22ce' : '#1e293b');
      }
      // 2. Far-Back 90° Slender Straight Wings (Far wing straight UP, Near wing straight DOWN with Tip Missile Rail)
      // Far Wing (y: 4, 1px chord x: 5, shortened span)
      grid[4][5] = '#cbd5e1'; grid[4][6] = '#ffffff'; // Far Tip rail
      // Near Main Wing (y: 8..10, 1px chord x: 5 with TEAM insignia & Tip Missile Rail, shortened span)
      grid[8][5]  = '#cbd5e1';
      grid[9][5]  = 'TEAM';
      grid[10][5] = '#cbd5e1'; grid[10][6] = '#ffffff'; // Slender tip missile launcher rail with bullet nose
      // 3. Chin Gatling Gun & Belly Bomb (in composite fallback)
      if (unitType !== 'A9Pigeon_Hull') {
        grid[8][11] = '#334155'; grid[8][12] = '#94a3b8'; grid[8][13] = '#e2e8f0'; grid[8][14] = '#64748b';
        grid[8][8] = '#1e293b'; grid[8][9] = '#facc15';
        grid[6][1] = '#fef08a'; grid[6][2] = '#00f0ff';
        grid[7][1] = '#38bdf8'; grid[7][2] = '#1e293b';
        // Perched wingman pigeon atop canopy rim
        grid[3][8] = '#1e293b'; grid[3][9] = '#334155';
        grid[4][7] = '#64748b'; grid[4][8] = '#2dd4bf'; grid[4][9] = '#f43f5e';
      }
    }
    else if (unitType === 'A9Pigeon_Engine_Horiz' || unitType === 'A9Pigeon_Engine_Diag' || unitType === 'A9Pigeon_Engine_Vert') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      if (unitType === 'A9Pigeon_Engine_Vert') {
        grid[6][3] = '#cbd5e1'; grid[6][4] = '#94a3b8';
        grid[7][2] = '#d97706'; grid[7][3] = '#8b5cf6'; grid[7][4] = '#3b82f6';
        grid[8][2] = '#334155'; grid[8][3] = '#1e293b'; grid[8][4] = '#0f172a';
        grid[9][3] = '#00f0ff'; grid[9][4] = '#00f0ff';
        grid[10][3] = '#ffffff'; grid[10][4] = '#fef08a';
      } else {
        // Horizontal / Diagonal Aft Nozzle with Titanium Temper & Mach Core
        grid[5][2] = '#cbd5e1'; grid[5][3] = '#94a3b8';
        grid[6][1] = '#fef08a'; grid[6][2] = '#00f0ff'; grid[6][3] = '#3b82f6'; grid[6][4] = '#8b5cf6'; grid[6][5] = '#d97706';
        grid[7][1] = '#38bdf8'; grid[7][2] = '#00f0ff'; grid[7][3] = '#334155'; grid[7][4] = '#1e293b'; grid[7][5] = '#0f172a';
      }
    }
    else if (unitType === 'A9Pigeon_Gatling_Spin1' || unitType === 'A9Pigeon_Gatling_Spin2') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      const isSpin2 = unitType.endsWith('2');
      grid[7][10] = '#d97706'; grid[7][11] = '#facc15'; // Brass feed chute
      grid[8][10] = '#1e293b'; grid[8][11] = '#334155';
      grid[8][12] = isSpin2 ? '#ffffff' : '#94a3b8';
      grid[8][13] = isSpin2 ? '#94a3b8' : '#cbd5e1';
      grid[8][14] = '#e2e8f0'; grid[8][15] = '#ffffff'; // Muzzle crown
    }
    else if (unitType === 'A9Pigeon_Bomb') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      grid[9][6] = '#ef4444'; // Red tail beacon
      grid[9][7] = '#facc15'; grid[9][8] = '#cbd5e1'; grid[9][9] = '#f8fafc'; // White dove stencil
      grid[9][10] = '#facc15'; grid[9][11] = '#ffffff'; // Nose fuze probe
      grid[10][6] = '#1e293b'; grid[10][7] = '#334155'; grid[10][8] = '#475569';
      grid[10][9] = '#facc15'; grid[10][10] = '#64748b';
    }
    else if (unitType.startsWith('A9Pigeon_Bird_') || unitType.startsWith('A9Pigeon_Pilot_')) {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // Rock Pigeon markings: White cere, orange eye, iridescent green/purple throat, double dark wing bars, coral feet
      grid[7][6] = '#f8fafc'; // White rump (Bürzel)
      grid[7][7] = '#64748b'; grid[7][8] = '#94a3b8'; grid[7][9] = '#14b8a6'; grid[7][10] = '#334155';
      grid[7][11] = '#f97316'; grid[7][12] = '#f8fafc'; grid[7][13] = '#1e293b'; // Eye, Cere, Beak
      grid[8][6] = '#0f172a'; // Black tail band
      grid[8][7] = '#0f172a'; grid[8][8] = '#cbd5e1'; grid[8][9] = '#a855f7'; grid[8][10] = '#cbd5e1';
      grid[9][8] = '#f43f5e'; // Tucked coral-red feet
      if (unitType.includes('Pilot')) {
        grid[6][10] = '#ca8a04'; grid[6][11] = '#fef08a'; grid[6][12] = '#0f172a'; // Golden aviator goggles
        grid[8][9] = '#ef4444'; grid[8][10] = '#dc2626'; // Fluttering crimson scarf
      }
      if (unitType.endsWith('Perched')) {
        grid[7][4] = '#f8fafc'; grid[8][4] = '#0f172a'; // resting tail
        grid[7][6] = '#0f172a'; grid[8][6] = '#0f172a'; // double wing bars
        grid[9][7] = '#f43f5e'; grid[10][7] = '#0f172a'; // claws gripped to probe
      } else if (unitType.endsWith('Peck')) {
        grid[7][4] = '#f8fafc'; grid[8][4] = '#0f172a'; // resting tail
        grid[7][6] = '#0f172a'; grid[8][6] = '#0f172a'; // double wing bars
        grid[7][11] = null; grid[7][12] = null; grid[7][13] = null; // lower head
        grid[8][11] = '#f97316'; grid[8][12] = '#f8fafc'; grid[9][12] = '#1e293b'; // peck beak down
        grid[9][7] = '#f43f5e'; grid[10][7] = '#0f172a'; // claws gripped to probe
      } else if (unitType.endsWith('Threat1') || unitType.endsWith('Threat_Puff')) {
        grid[7][4] = '#f8fafc'; grid[8][4] = '#0f172a'; // resting tail
        grid[7][6] = '#0f172a'; grid[8][6] = '#0f172a'; // double wing bars
        grid[7][9] = '#14b8a6'; grid[7][10] = '#a855f7'; // puffed iridescent throat
        grid[7][11] = '#f97316'; grid[7][12] = '#f8fafc'; grid[7][13] = '#1e293b'; // eye, cere, beak
        grid[8][10] = '#cbd5e1'; grid[8][11] = '#cbd5e1'; // broad puffed breast
        grid[9][7] = '#f43f5e'; grid[10][7] = '#0f172a'; // claws gripped to canopy
      } else if (unitType.endsWith('Threat2') || unitType.endsWith('Threat_Bob')) {
        grid[8][4] = '#f8fafc'; grid[9][4] = '#0f172a'; // tail pressed down
        grid[7][6] = '#0f172a'; grid[8][6] = '#0f172a'; // double wing bars
        grid[7][11] = null; grid[7][12] = null; grid[7][13] = null;
        grid[8][11] = '#14b8a6'; grid[8][12] = '#f97316'; grid[8][13] = '#f8fafc'; grid[8][14] = '#1e293b'; // head forward, beak open
        grid[9][7] = '#f43f5e'; grid[10][7] = '#0f172a'; // claws gripped to canopy
      } else if (unitType.endsWith('Threat_WingStretch')) {
        grid[4][7] = '#f8fafc'; grid[4][8] = '#0f172a'; grid[5][7] = '#94a3b8'; grid[5][8] = '#0f172a'; // wings stretched high
        grid[6][6] = '#0f172a'; grid[6][7] = '#94a3b8'; grid[7][7] = '#0f172a';
        grid[7][4] = '#f8fafc'; grid[8][4] = '#0f172a'; // tail
        grid[7][11] = '#f97316'; grid[7][12] = '#f8fafc'; grid[7][13] = '#1e293b'; // eye, cere, beak
        grid[8][9] = '#14b8a6'; grid[8][10] = '#cbd5e1'; // forward chest
        grid[9][7] = '#f43f5e'; grid[10][7] = '#0f172a'; // claws gripped to canopy
      } else if (unitType.endsWith('Threat_Alert')) {
        grid[7][4] = '#f8fafc'; grid[8][4] = '#0f172a'; // resting tail
        grid[7][6] = '#0f172a'; grid[8][6] = '#0f172a'; // double wing bars
        grid[7][11] = '#f97316'; grid[7][12] = '#f8fafc'; grid[7][13] = '#1e293b';
        grid[8][9] = '#14b8a6'; grid[8][10] = '#cbd5e1';
        grid[9][7] = '#f43f5e'; grid[10][7] = '#0f172a'; // claws gripped to canopy
      } else if (unitType.includes('BackFlap')) {
        grid[8][5] = '#f8fafc'; grid[9][5] = '#0f172a'; // pitched down braking tail
        grid[10][8] = '#f43f5e'; // extended air-brake feet
        if (unitType.endsWith('1')) {
          grid[4][9] = '#f8fafc'; grid[5][9] = '#0f172a'; grid[5][10] = '#94a3b8'; grid[6][10] = '#0f172a';
        } else if (unitType.endsWith('2')) {
          grid[6][10] = '#f8fafc'; grid[7][10] = '#0f172a'; grid[7][11] = '#94a3b8'; grid[8][11] = '#0f172a';
        } else {
          grid[9][9] = '#0f172a'; grid[9][10] = '#94a3b8'; grid[10][10] = '#f8fafc';
        }
      } else if (unitType.endsWith('1')) {
        grid[5][8] = '#f8fafc'; grid[5][9] = '#0f172a'; grid[6][8] = '#94a3b8'; grid[6][9] = '#0f172a'; // Wings up + bars
      } else if (unitType.endsWith('2')) {
        grid[7][4] = '#f8fafc'; grid[7][5] = '#0f172a'; grid[8][5] = '#0f172a'; // Glide with wing bars
      } else {
        grid[9][9] = '#0f172a'; grid[9][10] = '#94a3b8'; grid[10][9] = '#f8fafc'; // Downstroke
      }
    }
    else if (unitType === 'A9Piranha_Rotor') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      grid[2][8] = '#0f172a'; grid[2][9] = '#94a3b8';
      for (let x = 1; x <= 15; x++) {
        grid[1][x] = (x === 1 || x === 15) ? '#facc15' : '#1e293b';
      }
    }
    else if (unitType === 'Marine_Parachute') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // 🪂 Emergency Ejection Parachute & Suspended Marine Pilot (Side/3/4 View)
      // 1. Wide Billowing Parachute Canopy (y: 0..5, x: 1..14)
      for (let x = 4; x <= 11; x++) grid[0][x] = '#f8fafc';
      for (let x = 2; x <= 13; x++) grid[1][x] = '#e2e8f0';
      for (let x = 1; x <= 14; x++) grid[2][x] = '#cbd5e1';
      for (let x = 1; x <= 14; x++) grid[3][x] = '#94a3b8';
      for (let x = 1; x <= 14; x++) {
        grid[4][x] = (x % 3 === 0) ? '#64748b' : '#94a3b8';
      }
      for (let x = 2; x <= 13; x += 3) {
        grid[5][x] = '#64748b'; grid[5][x + 1] = '#64748b';
      }
      for (let y = 0; y <= 4; y++) {
        grid[y][7] = 'TEAM'; grid[y][8] = 'TEAM';
      }

      // 2. Taut Suspension Rigging Lines (y: 5..8)
      grid[5][2] = '#cbd5e1'; grid[5][13] = '#cbd5e1';
      grid[6][3] = '#94a3b8'; grid[6][6] = '#cbd5e1'; grid[6][9] = '#cbd5e1'; grid[6][12] = '#94a3b8';
      grid[7][4] = '#94a3b8'; grid[7][7] = '#cbd5e1'; grid[7][8] = '#cbd5e1'; grid[7][11] = '#94a3b8';
      grid[8][5] = '#64748b'; grid[8][7] = '#94a3b8'; grid[8][8] = '#94a3b8'; grid[8][10] = '#64748b';

      // 3. Marine Pilot in Combat Harness (y: 9..15, x: 5..11)
      grid[9][6] = '#334155'; grid[9][7] = '#475569'; grid[9][8] = '#475569'; grid[9][9] = '#334155';
      grid[10][6] = '#00f0ff'; grid[10][7] = '#38bdf8'; grid[10][8] = '#00f0ff'; grid[10][9] = '#e0f2fe';
      grid[11][5] = '#1e293b'; grid[11][6] = 'TEAM'; grid[11][7] = 'TEAM'; grid[11][8] = 'TEAM'; grid[11][9] = 'TEAM'; grid[11][10] = '#1e293b';
      grid[12][5] = '#334155'; grid[12][6] = '#475569'; grid[12][7] = '#1e293b'; grid[12][8] = '#1e293b'; grid[12][9] = '#475569'; grid[12][10] = '#334155';
      grid[13][6] = '#0f172a'; grid[13][7] = '#facc15'; grid[13][8] = '#facc15'; grid[13][9] = '#0f172a';
      grid[14][6] = '#1e293b'; grid[14][7] = '#334155'; grid[14][8] = '#334155'; grid[14][9] = '#1e293b';
      grid[15][5] = '#0f172a'; grid[15][6] = '#1e293b'; grid[15][9] = '#1e293b'; grid[15][10] = '#0f172a';
    }
    else if (unitType === 'Shade_Parachute') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // 🪂 Shade Parachute Landing (docs/singleplayer implementation
      // soft-foraging-flamingo.md Phase 2) - same canopy/rigging grid as
      // Marine_Parachute above, Shade's own near-black palette (see the
      // 'Shade' branch further up this file) on the suspended pilot instead
      // of the Marine's blue/khaki combat harness.
      // 1. Wide Billowing Parachute Canopy (y: 0..5, x: 1..14)
      for (let x = 4; x <= 11; x++) grid[0][x] = '#f8fafc';
      for (let x = 2; x <= 13; x++) grid[1][x] = '#e2e8f0';
      for (let x = 1; x <= 14; x++) grid[2][x] = '#cbd5e1';
      for (let x = 1; x <= 14; x++) grid[3][x] = '#94a3b8';
      for (let x = 1; x <= 14; x++) {
        grid[4][x] = (x % 3 === 0) ? '#64748b' : '#94a3b8';
      }
      for (let x = 2; x <= 13; x += 3) {
        grid[5][x] = '#64748b'; grid[5][x + 1] = '#64748b';
      }
      for (let y = 0; y <= 4; y++) {
        grid[y][7] = 'TEAM'; grid[y][8] = 'TEAM';
      }

      // 2. Taut Suspension Rigging Lines (y: 5..8)
      grid[5][2] = '#cbd5e1'; grid[5][13] = '#cbd5e1';
      grid[6][3] = '#94a3b8'; grid[6][6] = '#cbd5e1'; grid[6][9] = '#cbd5e1'; grid[6][12] = '#94a3b8';
      grid[7][4] = '#94a3b8'; grid[7][7] = '#cbd5e1'; grid[7][8] = '#cbd5e1'; grid[7][11] = '#94a3b8';
      grid[8][5] = '#64748b'; grid[8][7] = '#94a3b8'; grid[8][8] = '#94a3b8'; grid[8][10] = '#64748b';

      // 3. Shade Pilot suspended in harness (y: 9..15, x: 5..11)
      grid[9][6] = '#0f172a'; grid[9][7] = '#1e293b'; grid[9][8] = '#1e293b'; grid[9][9] = '#0f172a';
      grid[10][6] = '#020617'; grid[10][7] = '#ef4444'; grid[10][8] = '#ff0033'; grid[10][9] = '#020617';
      grid[11][5] = '#020617'; grid[11][6] = 'TEAM'; grid[11][7] = 'TEAM'; grid[11][8] = 'TEAM'; grid[11][9] = 'TEAM'; grid[11][10] = '#020617';
      grid[12][5] = '#0f172a'; grid[12][6] = '#1e293b'; grid[12][7] = '#090d16'; grid[12][8] = '#090d16'; grid[12][9] = '#1e293b'; grid[12][10] = '#0f172a';
      grid[13][6] = '#020617'; grid[13][7] = '#0f172a'; grid[13][8] = '#0f172a'; grid[13][9] = '#020617';
      grid[14][6] = '#090d16'; grid[14][7] = '#1e293b'; grid[14][8] = '#1e293b'; grid[14][9] = '#090d16';
      grid[15][5] = '#020617'; grid[15][6] = '#090d16'; grid[15][9] = '#090d16'; grid[15][10] = '#020617';
    }
    else if (unitType === 'TheSalvageCompany' ||
             unitType === 'TheSalvageCompany_Chassis' ||
             unitType === 'TheSalvageCompany_Chassis_Roll1' ||
             unitType === 'TheSalvageCompany_Chassis_Roll2' ||
             unitType === 'TheSalvageCompany_Chassis_Roll3') {
      gridSize = 20;
      grid = Array.from({ length: 20 }, () => Array(20).fill(null));
      // 🚜 The Salvage Company Heavy Industrial Salvage Crawler (Side Profile View)
      // Facing right (+X forward; heavily armored prow at right; cargo hopper at left; rolling wheels at bottom)
      let rollFrame = 0;
      if (unitType === 'TheSalvageCompany_Chassis_Roll2') rollFrame = 1;
      else if (unitType === 'TheSalvageCompany_Chassis_Roll3') rollFrame = 2;

      // 1. Heavy Multi-Axle Road Wheels & Tread Lugs (y: 15..18, x: 2..16)
      for (let x = 2; x <= 16; x++) grid[15][x] = '#1c1a18';

      // 4 Heavy Industrial Road Wheels: W1(cx: 3), W2(cx: 7), W3(cx: 11), W4(cx: 15)
      const wheelCenters = [3, 7, 11, 15];
      for (const cx of wheelCenters) {
        grid[16][cx - 1] = '#1c1a18'; grid[16][cx + 1] = '#1c1a18';
        grid[17][cx - 1] = '#262320'; grid[17][cx + 1] = '#262320';
        grid[18][cx - 1] = '#1c1a18'; grid[18][cx + 1] = '#1c1a18';

        if (rollFrame === 0) {
          // Roll 1: Vertical spoke alignment (0 deg)
          grid[16][cx] = '#855b38';
          grid[17][cx] = '#82623a';
          grid[18][cx] = '#6e472b';
          grid[18][cx - 1] = '#38271e';
        } else if (rollFrame === 1) {
          // Roll 2: Diagonal spoke alignment (45 deg)
          grid[16][cx - 1] = '#855b38';
          grid[16][cx] = '#262320';
          grid[17][cx] = '#965935';
          grid[18][cx] = '#1c1a18';
          grid[18][cx + 1] = '#6e472b';
        } else {
          // Roll 3: Horizontal spoke alignment (90 deg)
          grid[16][cx] = '#262320';
          grid[17][cx - 1] = '#855b38';
          grid[17][cx] = '#82623a';
          grid[17][cx + 1] = '#6e472b';
          grid[18][cx] = '#38271e';
        }
      }
      // Rear mudflap & heavy bumper
      grid[15][1] = '#1c1a18'; grid[16][1] = '#262320'; grid[17][1] = '#38271e';

      // 2. Front Heavy Armored Ram Bulldozer Plow (+X forward: x: 16..19, y: 13..18)
      // Row 19 bottom protruding pixels removed per user feedback
      grid[13][16] = '#543926'; grid[13][17] = '#6e472b';
      grid[14][16] = '#38271e'; grid[14][17] = '#543926'; grid[14][18] = '#6e472b';
      grid[15][17] = '#38271e'; grid[15][18] = '#543926'; grid[15][19] = '#855b38';
      grid[16][17] = '#262320'; grid[16][18] = '#6e472b'; grid[16][19] = '#543926';
      grid[17][17] = '#1c1a18'; grid[17][18] = '#38271e'; grid[17][19] = '#6e472b';
      grid[18][17] = '#1c1a18'; grid[18][18] = '#262320'; grid[18][19] = '#38271e';

      // 3. Armored Front Operator Cabin & Visor-Brow (+X mid: x: 11..16, y: 9..14)
      grid[9][12] = '#38271e'; grid[9][13] = '#543926'; grid[9][14] = '#543926'; grid[9][15] = '#38271e';
      // Armored windshield vision slit: Cyan glass shielded behind heavy overhanging steel
      grid[10][12] = '#38271e'; grid[10][13] = '#00d2df'; grid[10][14] = '#0284c7'; grid[10][15] = '#38271e';
      // Sloped front cowl & bolted armor
      grid[11][12] = '#543926'; grid[11][13] = '#6e472b'; grid[11][14] = '#543926'; grid[11][15] = '#6e472b'; grid[11][16] = '#38271e';
      // Amber fog / warning headlights (muted dusty amber)
      grid[12][16] = '#caa152'; grid[12][17] = '#965935';
      // Cab side door with TEAM badge & red salvage tally marks
      grid[12][12] = 'TEAM'; grid[12][13] = 'TEAM'; grid[12][14] = '#543926'; grid[12][15] = '#6e472b';
      grid[13][12] = 'TEAM'; grid[13][13] = 'TEAM'; grid[13][14] = '#c02626'; grid[13][15] = '#c02626';
      grid[14][11] = '#38271e'; grid[14][12] = '#543926'; grid[14][13] = '#6e472b'; grid[14][14] = '#543926'; grid[14][15] = '#38271e';

      // 4. Twin Diesel Exhaust Stacks with charred heat shielding (x: 10, y: 4..8)
      grid[4][10] = '#1c1a18';
      grid[5][10] = '#262320';
      grid[6][10] = '#383532';
      grid[7][10] = '#4c4844';
      grid[8][10] = '#543926';

      // 5. Rotary Turret Pedestal Bearing (x: 8..10, y: 8..9)
      grid[8][8] = '#262320'; grid[8][9] = '#38271e';
      grid[9][8] = '#1c1a18'; grid[9][9] = '#38271e'; grid[9][10] = '#262320'; grid[9][11] = '#38271e';

      // 6. High-Walled Cargo Hopper Bed & Desaturated Rusted Panels (-X rear: x: 1..10, y: 9..14)
      for (let y = 12; y <= 14; y++) {
        for (let x = 1; x <= 10; x++) {
          if (x === 1 || x === 4 || x === 7 || x === 10) {
            grid[y][x] = '#38271e';
          } else if ((x === 3 || x === 8) && y === 13) {
            grid[y][x] = '#78492d';
          } else if (x === 5 && y === 14) {
            grid[y][x] = '#6e472b';
          } else {
            grid[y][x] = (y === 12) ? '#855b38' : '#543926';
          }
        }
      }
      for (let x = 1; x <= 8; x++) {
        grid[11][x] = (x % 2 === 0) ? '#543926' : '#6e472b';
      }

      // 7. Hopper Interior Loaded Scrap Pile (Scrap voxel fragments: x: 2..8, y: 9..11)
      grid[9][2] = '#38271e'; grid[9][3] = '#6b6560'; grid[9][4] = '#855b38';
      grid[10][2] = '#262320'; grid[10][3] = '#caa152'; grid[10][4] = '#38271e'; grid[10][5] = '#82623a'; grid[10][6] = '#6e472b';
      grid[11][4] = '#1c1a18'; grid[11][5] = '#0284c7'; grid[11][6] = '#4c4844'; grid[11][7] = '#855b38';

      if (unitType === 'TheSalvageCompany') {
        grid[7][9] = '#38271e'; grid[7][8] = '#543926'; grid[6][7] = '#6e472b'; grid[6][6] = '#855b38';
        grid[7][7] = '#262320';
        grid[5][6] = '#38271e'; grid[5][5] = '#543926';
        grid[6][5] = '#6e472b'; grid[7][5] = '#855b38';
        grid[8][5] = '#38271e'; grid[8][6] = '#262320';
      }
    }
    else if (unitType === 'TheSalvageCompany_Arm_Rest') {
      gridSize = 20;
      grid = Array.from({ length: 20 }, () => Array(20).fill(null));
      // Locked resting cradle position on chassis
      grid[8][9] = '#38271e'; grid[8][10] = '#543926';
      grid[7][9] = '#543926'; grid[7][8] = '#6e472b'; grid[6][7] = '#855b38'; grid[6][6] = '#543926';
      grid[7][7] = '#262320'; grid[7][6] = '#1c1a18';
      grid[5][6] = '#38271e'; grid[5][5] = '#543926';
      grid[6][5] = '#6e472b'; grid[7][5] = '#855b38';
      grid[8][5] = '#38271e'; grid[8][6] = '#262320';
    }
    else if (unitType === 'TheSalvageCompany_Arm_Reach') {
      gridSize = 20;
      grid = Array.from({ length: 20 }, () => Array(20).fill(null));
      grid[8][9] = '#38271e'; grid[8][10] = '#543926';
      grid[7][10] = '#543926'; grid[7][11] = '#6e472b'; grid[8][12] = '#855b38'; grid[9][13] = '#543926'; grid[10][14] = '#6e472b';
      grid[8][11] = '#262320'; grid[9][12] = '#1c1a18';
      grid[11][14] = '#38271e';
      grid[12][15] = '#543926'; grid[13][16] = '#6e472b'; grid[14][17] = '#855b38'; grid[15][18] = '#543926';
      grid[16][17] = '#262320'; grid[16][19] = '#262320';
      grid[17][16] = '#543926'; grid[17][19] = '#543926';
      grid[18][16] = '#6e472b'; grid[18][19] = '#6e472b';
    }
    else if (unitType === 'TheSalvageCompany_Arm_Grab') {
      gridSize = 20;
      grid = Array.from({ length: 20 }, () => Array(20).fill(null));
      grid[8][9] = '#38271e'; grid[8][10] = '#543926';
      grid[7][10] = '#543926'; grid[7][11] = '#6e472b'; grid[8][12] = '#855b38'; grid[9][13] = '#543926'; grid[10][14] = '#6e472b';
      grid[8][11] = '#262320'; grid[9][12] = '#1c1a18';
      grid[11][14] = '#38271e';
      grid[12][15] = '#543926'; grid[13][16] = '#6e472b'; grid[14][17] = '#855b38'; grid[15][17] = '#543926';
      grid[16][16] = '#38271e'; grid[16][17] = '#543926'; grid[16][18] = '#38271e';
      grid[17][16] = '#6e472b'; grid[17][18] = '#6e472b';
      grid[17][17] = '#caa152';
      grid[18][17] = '#855b38';
      grid[18][18] = '#6b6560';
    }
    else if (unitType === 'TheSalvageCompany_Arm_Lift') {
      gridSize = 20;
      grid = Array.from({ length: 20 }, () => Array(20).fill(null));
      grid[8][9] = '#38271e'; grid[8][10] = '#543926';
      grid[7][10] = '#543926'; grid[6][10] = '#6e472b'; grid[5][11] = '#855b38'; grid[4][11] = '#543926'; grid[3][12] = '#6e472b';
      grid[6][9] = '#262320'; grid[5][10] = '#1c1a18';
      grid[2][12] = '#38271e';
      grid[2][13] = '#543926'; grid[3][14] = '#6e472b'; grid[4][15] = '#855b38';
      grid[5][15] = '#38271e'; grid[5][16] = '#38271e';
      grid[6][14] = '#543926'; grid[6][17] = '#543926';
      grid[6][15] = '#caa152'; grid[6][16] = '#855b38'; grid[7][15] = '#6b6560';
    }
    else if (unitType === 'TheSalvageCompany_Arm_Dump') {
      gridSize = 20;
      grid = Array.from({ length: 20 }, () => Array(20).fill(null));
      grid[8][9] = '#38271e'; grid[8][10] = '#543926';
      grid[7][9] = '#543926'; grid[6][8] = '#6e472b'; grid[5][7] = '#855b38'; grid[4][6] = '#543926';
      grid[6][7] = '#262320';
      grid[3][5] = '#38271e';
      grid[4][5] = '#543926'; grid[5][5] = '#6e472b'; grid[6][5] = '#855b38';
      grid[6][4] = '#543926'; grid[6][6] = '#543926';
      grid[7][3] = '#6e472b'; grid[7][7] = '#6e472b';
      grid[7][5] = '#caa152';
      grid[8][5] = '#855b38';
      grid[9][5] = '#6b6560';
    }
    else if (unitType === 'Orchestra_Chassis' ||
             unitType === 'Orchestra_Chassis_Roll1' ||
             unitType === 'Orchestra_Chassis_Roll2' ||
             unitType === 'Orchestra_Chassis_Roll3' ||
             unitType === 'Orchestra_Chassis_Roll4' ||
             unitType === 'Orchestra_Chassis_Roll5' ||
             unitType === 'Orchestra_Chassis_Roll6') {
      gridSize = 20;
      grid = Array.from({ length: 20 }, () => Array(20).fill(null));
      // 🚚 Orchestra Heavy 8x8 Tactical Missile Transporter Chassis (MAZ-543M / Smerch Class, 20x20)
      // Facing right (+X forward). Elongated low-slung military profile with 4 massive double-axle wheels.
      let rollFrame = 0;
      if (unitType === 'Orchestra_Chassis_Roll2') rollFrame = 1;
      else if (unitType === 'Orchestra_Chassis_Roll3') rollFrame = 2;
      else if (unitType === 'Orchestra_Chassis_Roll4') rollFrame = 3;
      else if (unitType === 'Orchestra_Chassis_Roll5') rollFrame = 4;
      else if (unitType === 'Orchestra_Chassis_Roll6') rollFrame = 5;

      // 1. 4 Massive Heavy Off-road Road Wheels (y: 15..18)
      // Tandem grouping: Rear drive bogies at cx: 3 & 7; Central equipment gap at 9..10; Front steering bogies at cx: 13 & 17
      const wheelCenters = [3, 7, 13, 17];
      for (const cx of wheelCenters) {
        // Heavy rubber tire contour (4px wide, 4px high)
        grid[15][cx - 1] = '#0f172a'; grid[15][cx] = '#0f172a'; grid[15][cx + 1] = '#0f172a';
        grid[16][cx - 1] = '#1e293b'; grid[16][cx + 1] = '#1e293b';
        grid[17][cx - 1] = '#1e293b'; grid[17][cx + 1] = '#1e293b';
        grid[18][cx - 1] = '#0f172a'; grid[18][cx] = '#0f172a'; grid[18][cx + 1] = '#0f172a';

        if (rollFrame === 0) {
          // Roll 1: 12 Uhr
          grid[16][cx] = '#cbd5e1';
          grid[17][cx] = '#94a3b8';
        } else if (rollFrame === 1) {
          // Roll 2: 2 Uhr
          grid[16][cx] = '#94a3b8';
          grid[17][cx] = '#cbd5e1';
          grid[16][cx + 1] = '#cbd5e1';
        } else if (rollFrame === 2) {
          // Roll 3: 4 Uhr
          grid[16][cx] = '#64748b';
          grid[17][cx] = '#cbd5e1';
          grid[17][cx + 1] = '#94a3b8';
        } else if (rollFrame === 3) {
          // Roll 4: 6 Uhr
          grid[16][cx] = '#94a3b8';
          grid[17][cx] = '#cbd5e1';
        } else if (rollFrame === 4) {
          // Roll 5: 8 Uhr
          grid[16][cx] = '#cbd5e1';
          grid[17][cx] = '#94a3b8';
          grid[17][cx - 1] = '#64748b';
        } else {
          // Roll 6: 10 Uhr
          grid[16][cx] = '#cbd5e1';
          grid[17][cx] = '#64748b';
          grid[16][cx - 1] = '#94a3b8';
        }
      }

      // 2. Central Equipment Bay & Hydraulic Outriggers (Between axle groups: x: 9..11, y: 14..17)
      grid[15][9] = '#1e293b'; grid[15][10] = '#334155'; grid[15][11] = '#1e293b';
      grid[16][9] = '#334155'; grid[16][10] = '#475569'; grid[16][11] = '#334155';
      grid[17][10] = '#1e293b'; // Hydraulic jack footpad

      // 3. Lower Chassis Frame Beam & Sloped Wheel Arches (y: 13..14, x: 0..19)
      for (let x = 1; x <= 18; x++) {
        grid[14][x] = '#1e293b';
      }
      for (let x = 2; x <= 18; x++) {
        grid[13][x] = (x === 5 || x === 9 || x === 15) ? '#475569' : '#334155';
      }
      // Rear heavy towing shackles & bumper
      grid[13][0] = '#334155'; grid[14][0] = '#0f172a';
      grid[13][1] = '#1e293b'; grid[14][1] = '#334155';
      // Front heavy towing pintle & bumper (+X front: x: 19)
      grid[13][19] = '#334155'; grid[14][19] = '#0f172a';

      // 4. Low Front Engine Hood / Compartment (+X front: x: 15..19, y: 11..13)
      for (let x = 15; x <= 18; x++) {
        grid[11][x] = '#475569';
        grid[12][x] = '#334155';
      }
      grid[11][19] = '#334155'; grid[12][19] = '#1e293b';
      grid[12][18] = '#1e293b';
      grid[12][19] = '#fef08a';
      grid[13][18] = '#facc15';

      // 5. Armored Driver & Crew Cabin (x: 10..15, y: 8..11)
      for (let y = 8; y <= 10; y++) {
        for (let x = 11; x <= 14; x++) {
          grid[y][x] = (y === 8) ? '#475569' : '#334155';
        }
      }
      grid[9][10] = '#1e293b'; grid[10][10] = '#334155';
      grid[8][14] = '#64748b'; grid[8][15] = '#334155';
      grid[9][13] = '#0284c7'; grid[9][14] = '#00f0ff'; grid[9][15] = '#38bdf8';
      grid[8][13] = '#1e293b';
      grid[6][12] = '#ef4444'; grid[6][13] = '#38bdf8';
      grid[7][11] = '#1e293b'; grid[7][12] = '#475569'; grid[7][13] = '#64748b'; grid[7][14] = '#1e293b';
      grid[10][12] = 'TEAM'; grid[10][13] = 'TEAM';
      grid[11][12] = 'TEAM'; grid[11][13] = 'TEAM';

      // 6. Flatbed Launch Deck & Rear Turntable Pivot Base (x: 1..10, y: 11..12)
      for (let x = 2; x <= 9; x++) {
        grid[12][x] = '#1e293b';
      }
      grid[11][2] = '#0f172a'; grid[11][3] = '#64748b'; grid[11][4] = '#64748b'; grid[11][5] = '#0f172a';
      grid[12][3] = '#334155'; grid[12][4] = '#475569';
      grid[11][7] = '#0f172a'; grid[11][8] = '#cbd5e1';
      grid[8][1] = '#0f172a'; grid[9][1] = '#1e293b'; grid[10][1] = '#334155';
    }
    else if (unitType === 'Orchestra_Launcher') {
      gridSize = 20;
      grid = Array.from({ length: 20 }, () => Array(20).fill(null));
      // 🚀 Orchestra Heavy Cylindrical Rocket Launcher Battery (300mm Smerch Class, 20x20)
      // Horizontal transport profile (folds flat at 0 deg, elevates up to ~38 deg around rear pivot at x: 3.5, y: 11.5)

      // 1. Rear Turntable Pivot Hinge & Hydraulic Ram Arm
      grid[11][2] = '#0f172a'; grid[11][3] = '#475569'; grid[11][4] = '#cbd5e1'; grid[11][5] = '#0f172a';
      grid[12][3] = '#0f172a'; grid[12][4] = '#1e293b';

      // Telescopic Hydraulic Actuator Cylinder (Underneath center: x: 7..9, y: 11..12)
      grid[11][7] = '#cbd5e1'; grid[11][8] = '#94a3b8';
      grid[12][8] = '#64748b';

      // 2. Multi-Tube Cylindrical Launch Pack (Long cluster: x: 2..16, y: 7..10)
      for (let x = 4; x <= 15; x++) {
        grid[7][x] = (x === 6 || x === 10 || x === 14) ? '#cbd5e1' : '#475569';
        grid[8][x] = (x === 6 || x === 10 || x === 14) ? '#94a3b8' : '#334155';
      }
      for (let x = 3; x <= 15; x++) {
        grid[9][x] = (x >= 8 && x <= 11) ? 'TEAM' : ((x === 6 || x === 14) ? '#cbd5e1' : '#334155');
        grid[10][x] = (x >= 8 && x <= 11) ? 'TEAM' : ((x === 6 || x === 14) ? '#94a3b8' : '#1e293b');
      }

      grid[7][6] = '#cbd5e1'; grid[8][6] = '#cbd5e1'; grid[9][6] = '#cbd5e1'; grid[10][6] = '#cbd5e1';
      grid[7][10] = '#cbd5e1'; grid[8][10] = '#cbd5e1';
      grid[7][14] = '#cbd5e1'; grid[8][14] = '#cbd5e1'; grid[9][14] = '#cbd5e1'; grid[10][14] = '#cbd5e1';

      // 3. Rocket Warhead Noses & Tube Muzzle Openings (+X front: x: 16..18, y: 7..10)
      grid[7][16] = '#1e293b'; grid[7][17] = '#ef4444'; grid[7][18] = '#ffffff';
      grid[8][16] = '#1e293b'; grid[8][17] = '#ef4444'; grid[8][18] = '#ffffff';
      grid[9][16] = '#1e293b'; grid[9][17] = '#ef4444'; grid[9][18] = '#ffffff';
      grid[10][16] = '#1e293b'; grid[10][17] = '#ef4444';

      // 4. Rear Blast Exhaust Ports (-X rear: x: 1..3, y: 7..10)
      grid[7][3] = '#0f172a'; grid[7][2] = '#1e293b';
      grid[8][3] = '#0f172a'; grid[8][2] = '#0f172a'; grid[8][1] = '#1e293b';
      grid[9][2] = '#0f172a'; grid[9][1] = '#0f172a';
      grid[10][2] = '#0f172a';
    }
    else if (unitType === 'Orchestra') {
      gridSize = 20;
      grid = Array.from({ length: 20 }, () => Array(20).fill(null));
      // Fully Assembled Orchestra (Chassis + Launcher for low-quality composite blit)
      // 1. 4 Massive Road Wheels
      const wheelCenters = [3, 7, 13, 17];
      for (const cx of wheelCenters) {
        grid[15][cx - 1] = '#0f172a'; grid[15][cx] = '#0f172a'; grid[15][cx + 1] = '#0f172a';
        grid[16][cx - 1] = '#1e293b'; grid[16][cx + 1] = '#1e293b';
        grid[17][cx - 1] = '#1e293b'; grid[17][cx + 1] = '#1e293b';
        grid[18][cx - 1] = '#0f172a'; grid[18][cx] = '#0f172a'; grid[18][cx + 1] = '#0f172a';
        grid[16][cx] = '#cbd5e1';
        grid[17][cx] = '#94a3b8';
      }
      grid[15][9] = '#1e293b'; grid[15][10] = '#334155'; grid[15][11] = '#1e293b';
      grid[16][9] = '#334155'; grid[16][10] = '#475569'; grid[16][11] = '#334155';
      grid[17][10] = '#1e293b';

      for (let x = 1; x <= 18; x++) grid[14][x] = '#1e293b';
      for (let x = 2; x <= 18; x++) grid[13][x] = (x === 5 || x === 9 || x === 15) ? '#475569' : '#334155';
      grid[13][0] = '#334155'; grid[14][0] = '#0f172a';
      grid[13][1] = '#1e293b'; grid[14][1] = '#334155';
      grid[13][19] = '#334155'; grid[14][19] = '#0f172a';

      // 2. Front Hood & Radiator
      for (let x = 15; x <= 18; x++) {
        grid[11][x] = '#475569';
        grid[12][x] = '#334155';
      }
      grid[11][19] = '#334155'; grid[12][19] = '#1e293b';
      grid[12][18] = '#1e293b';
      grid[12][19] = '#fef08a';
      grid[13][18] = '#facc15';

      // 3. Cabin
      for (let y = 8; y <= 10; y++) {
        for (let x = 11; x <= 14; x++) grid[y][x] = (y === 8) ? '#475569' : '#334155';
      }
      grid[9][10] = '#1e293b'; grid[10][10] = '#334155';
      grid[8][14] = '#64748b'; grid[8][15] = '#334155';
      grid[9][13] = '#0284c7'; grid[9][14] = '#00f0ff'; grid[9][15] = '#38bdf8';
      grid[8][13] = '#1e293b';
      grid[6][12] = '#ef4444'; grid[6][13] = '#38bdf8';
      grid[7][11] = '#1e293b'; grid[7][12] = '#475569'; grid[7][13] = '#64748b'; grid[7][14] = '#1e293b';
      grid[10][12] = 'TEAM'; grid[10][13] = 'TEAM';
      grid[11][12] = 'TEAM'; grid[11][13] = 'TEAM';

      // 4. Flatbed & Turntable
      for (let x = 2; x <= 9; x++) grid[12][x] = '#1e293b';
      grid[11][2] = '#0f172a'; grid[11][3] = '#64748b'; grid[11][4] = '#64748b'; grid[11][5] = '#0f172a';
      grid[12][3] = '#334155'; grid[12][4] = '#475569';
      grid[8][1] = '#0f172a'; grid[9][1] = '#1e293b'; grid[10][1] = '#334155';

      // 5. Rocket Launch Tubes
      for (let x = 4; x <= 15; x++) {
        grid[7][x] = (x === 6 || x === 10 || x === 14) ? '#cbd5e1' : '#475569';
        grid[8][x] = (x === 6 || x === 10 || x === 14) ? '#94a3b8' : '#334155';
      }
      for (let x = 3; x <= 15; x++) {
        grid[9][x] = (x >= 8 && x <= 11) ? 'TEAM' : ((x === 6 || x === 14) ? '#cbd5e1' : '#334155');
        grid[10][x] = (x >= 8 && x <= 11) ? 'TEAM' : ((x === 6 || x === 14) ? '#94a3b8' : '#1e293b');
      }
      grid[7][6] = '#cbd5e1'; grid[8][6] = '#cbd5e1'; grid[9][6] = '#cbd5e1'; grid[10][6] = '#cbd5e1';
      grid[7][10] = '#cbd5e1'; grid[8][10] = '#cbd5e1';
      grid[7][14] = '#cbd5e1'; grid[8][14] = '#cbd5e1'; grid[9][14] = '#cbd5e1'; grid[10][14] = '#cbd5e1';
      grid[7][16] = '#1e293b'; grid[7][17] = '#ef4444'; grid[7][18] = '#ffffff';
      grid[8][16] = '#1e293b'; grid[8][17] = '#ef4444'; grid[8][18] = '#ffffff';
      grid[9][16] = '#1e293b'; grid[9][17] = '#ef4444'; grid[9][18] = '#ffffff';
      grid[10][16] = '#1e293b'; grid[10][17] = '#ef4444';
      grid[7][3] = '#0f172a'; grid[7][2] = '#1e293b';
      grid[8][3] = '#0f172a'; grid[8][2] = '#0f172a'; grid[8][1] = '#1e293b';
      grid[9][2] = '#0f172a'; grid[9][1] = '#0f172a';
      grid[10][2] = '#0f172a';
    }
    else if (unitType === 'DeployableCannon_Base') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // 🕷️ Deployable Cannon Base: Articulated Quadruped Mech Spider Legs & Vertical Blast Shields

      // 1. Central Rotary Turret Bearing Collar (y: 8, x: 5..8)
      grid[8][5] = '#1e293b'; grid[8][6] = '#475569'; grid[8][7] = '#64748b'; grid[8][8] = '#1e293b';

      // 2. Front Armored Blast Shields / Breastplates (y: 9..12, x: 4..8)
      // Left shield plate
      grid[9][4] = '#64748b'; grid[9][5] = '#475569';
      grid[10][4] = '#475569'; grid[10][5] = 'TEAM';
      grid[11][4] = '#334155'; grid[11][5] = '#1e293b';
      grid[12][4] = '#1e293b'; grid[12][5] = '#0f172a';

      // Right shield plate (facing forward)
      grid[9][6] = '#94a3b8'; grid[9][7] = '#64748b';
      grid[10][6] = '#64748b'; grid[10][7] = '#475569';
      grid[11][6] = '#475569'; grid[11][7] = '#334155';
      grid[12][6] = '#334155'; grid[12][7] = '#1e293b';
      // Lower telemetry status LED (green sensor light from artwork)
      grid[12][8] = '#22c55e';

      // 3. Articulated Spider Mech Legs
      // Rear Leg (-X)
      grid[10][3] = '#334155';
      grid[11][2] = '#475569'; grid[11][1] = '#64748b';
      grid[12][1] = '#94a3b8';
      grid[13][1] = '#334155';
      grid[14][0] = '#475569'; grid[14][1] = '#1e293b';
      grid[15][0] = '#0f172a'; grid[15][1] = '#64748b';

      // Background Rear Leg (depth perspective)
      grid[12][3] = '#1e293b';
      grid[13][3] = '#0f172a';
      grid[14][2] = '#1e293b';
      grid[15][2] = '#0f172a';

      // Front Leg (+X)
      grid[10][8] = '#334155';
      grid[11][9] = '#475569'; grid[11][10] = '#64748b';
      grid[12][10] = '#64748b'; grid[12][11] = '#94a3b8';
      grid[13][11] = '#475569';
      grid[14][11] = '#334155'; grid[14][12] = '#475569';
      grid[15][12] = '#1e293b'; grid[15][13] = '#64748b';

      // Background Front Leg (depth perspective)
      grid[12][13] = '#1e293b';
      grid[13][14] = '#1e293b';
      grid[14][14] = '#0f172a';
      grid[15][15] = '#334155';
    }
    else if (unitType === 'DeployableCannon_Turret') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // 💥 Deployable Cannon Turret: Sleek Autocannon, Dual Optics & Sensor Pod

      // 1. Radio Antenna Whip (rear)
      grid[1][3] = '#94a3b8';
      grid[2][3] = '#64748b';
      grid[3][3] = '#475569';

      // 2. Sensor Radome Dome (rear top)
      grid[2][4] = '#64748b'; grid[2][5] = '#475569';
      grid[3][4] = '#475569'; grid[3][5] = '#334155';

      // 3. Top Optical Scope (over gun trunnion)
      grid[3][7] = '#64748b'; grid[3][8] = '#94a3b8';
      grid[4][7] = '#334155'; grid[4][8] = '#475569';

      // 4. Sensor Pod & Multi-Spectral Optics (rear/mid)
      grid[4][2] = '#facc15'; // Amber telemetry readout
      grid[4][3] = '#334155'; grid[4][4] = '#1e293b'; grid[4][5] = '#22c55e'; // Green optic lens!
      grid[5][2] = '#334155'; grid[5][3] = '#1e293b'; grid[5][4] = '#0f172a'; grid[5][5] = '#00f0ff'; // Blue optic lens!
      grid[6][3] = '#1e293b'; grid[6][4] = '#334155'; grid[6][5] = '#475569';

      // 5. Gun Breech & Trunnion Housing
      grid[4][6] = '#475569';
      grid[5][6] = 'TEAM'; grid[5][7] = 'TEAM';
      grid[6][6] = '#334155'; grid[6][7] = '#1e293b';

      // 6. Exposed Mechanical Cog/Gear under Breech
      grid[7][4] = '#0f172a'; grid[7][5] = '#475569'; grid[7][6] = '#0f172a'; grid[7][7] = '#334155';

      // 7. Slender Autocannon Barrel (+X forward)
      // Barrel sleeve & collar
      grid[5][8] = '#64748b'; grid[5][9] = '#94a3b8';
      grid[6][8] = '#334155'; grid[6][9] = '#475569';

      // Fluted Barrel Tube
      grid[5][10] = '#cbd5e1'; grid[5][11] = '#94a3b8'; grid[5][12] = '#cbd5e1'; grid[5][13] = '#94a3b8';
      grid[6][10] = '#334155'; grid[6][11] = '#1e293b'; grid[6][12] = '#334155'; grid[6][13] = '#1e293b';

      // Ported Tactical Muzzle Brake / Compensator
      grid[4][14] = '#64748b'; grid[4][15] = '#94a3b8';
      grid[5][14] = '#0f172a'; grid[5][15] = '#cbd5e1';
      grid[6][14] = '#1e293b'; grid[6][15] = '#475569';
    }
    else if (unitType === 'InvasionCapsuleSling' || unitType === 'Invasion_Capsule_Sling' || unitType === 'Invasion Capsule Sling') {
      gridSize = 20;
      grid = Array.from({ length: 20 }, () => Array(20).fill(null));
      // 🛰️ Invasion Capsule Sling (Centrifuge Mass Accelerator):
      // Massive SpinLaunch-style kinetic catapult facility with a circular
      // vacuum rotor chamber, internal spinning truss arm with cyan plasma vortex,
      // tangential multi-stage magnetic launch chimney, and reinforced bunker airlock.

      // 1. Bedrock platform & foundation footings (y: 18..19, x: 2..18)
      for (let x = 2; x <= 18; x++) grid[19][x] = '#1e293b';
      for (let x = 3; x <= 17; x++) grid[18][x] = '#334155';
      grid[19][2] = '#0f172a'; grid[19][18] = '#0f172a';
      grid[18][3] = '#64748b'; grid[18][17] = '#64748b';
      grid[19][6] = '#0f172a'; grid[19][7] = '#0f172a';
      grid[19][12] = '#0f172a'; grid[19][13] = '#0f172a';

      // 2. Fortified blast bunker & airlock entrance (y: 13..18, x: 12..18)
      for (let y = 14; y <= 17; y++) {
        for (let x = 12; x <= 18; x++) grid[y][x] = '#334155';
      }
      grid[13][12] = '#64748b'; grid[13][13] = '#94a3b8'; grid[13][14] = '#94a3b8'; grid[13][15] = '#94a3b8'; grid[13][16] = '#64748b';
      grid[14][12] = '#475569'; grid[14][13] = '#e2e8f0'; grid[14][14] = '#e2e8f0'; grid[14][15] = '#cbd5e1'; grid[14][16] = '#64748b';
      grid[14][17] = '#475569'; grid[14][18] = '#334155';

      // Heavy double blast door with cross-reinforcing seal
      grid[15][13] = '#0f172a'; grid[15][14] = '#1e293b'; grid[15][15] = '#1e293b'; grid[15][16] = '#0f172a';
      grid[16][13] = '#1e293b'; grid[16][14] = '#64748b'; grid[16][15] = '#64748b'; grid[16][16] = '#1e293b';
      grid[17][13] = '#0f172a'; grid[17][14] = '#1e293b'; grid[17][15] = '#1e293b'; grid[17][16] = '#0f172a';

      // Bunker team badge & telemetry status LEDs
      grid[15][17] = 'TEAM'; grid[16][17] = 'TEAM';
      grid[14][16] = '#22c55e'; // Ready beacon
      grid[15][18] = '#fbbf24'; grid[16][18] = '#f97316'; // Capacitor charge status

      // 3. Centrifuge circular drum housing (Center ~ x:7.0, y:11.0, Radius ~ 6.6)
      for (let y = 4; y <= 17; y++) {
        for (let x = 1; x <= 13; x++) {
          const dx = x - 7.0;
          const dy = y - 11.0;
          const dist = Math.hypot(dx, dy);

          if (dist <= 6.6 && dist >= 4.7) {
            if (dy < -2.5 || (dx < -2.5 && dy < 1.5)) {
              grid[y][x] = (dist > 5.8) ? '#e2e8f0' : '#94a3b8'; // Top-left highlight
            } else if (dy > 2.8 || dx > 2.8) {
              grid[y][x] = (dist > 5.8) ? '#1e293b' : '#334155'; // Bottom-right shadow
            } else {
              grid[y][x] = '#64748b'; // Mid tone
            }
          } else if (dist < 4.7) {
            grid[y][x] = (dist < 2.0) ? '#1e293b' : '#090d16'; // Sunken vacuum basin
          }
        }
      }

      // Segmented outer hull plating with top-left notch cutout
      grid[4][6] = '#f1f5f9'; grid[4][7] = '#f8fafc'; grid[4][8] = '#e2e8f0'; grid[4][9] = '#cbd5e1';
      grid[5][4] = null;      grid[5][5] = '#e2e8f0'; grid[5][6] = '#f1f5f9'; // Notch cutout at 10-o'clock
      grid[6][3] = '#f1f5f9'; grid[7][2] = '#e2e8f0'; grid[8][2] = '#cbd5e1';
      grid[9][1] = '#cbd5e1'; grid[10][1] = '#94a3b8'; grid[11][1] = '#94a3b8'; grid[12][1] = '#64748b';
      grid[13][2] = '#475569'; grid[14][2] = '#334155'; grid[15][3] = '#1e293b'; grid[16][4] = '#1e293b';
      grid[17][5] = '#0f172a'; grid[17][6] = '#0f172a';

      // Stator vanes & radial ventilation ribs in the basin floor
      grid[9][3] = '#334155'; grid[10][3] = '#334155';
      grid[13][5] = '#334155'; grid[14][6] = '#334155';
      grid[13][9] = '#334155'; grid[12][10] = '#334155';
      grid[8][5] = '#334155'; grid[7][6] = '#334155';

      // 4. Concentric glowing ionization vortex rings (Cyan Plasma)
      // Outer glowing ring
      grid[6][7] = '#00f0ff'; grid[6][8] = '#67e8f9'; grid[6][9] = '#00f0ff';
      grid[7][5] = '#67e8f9'; grid[7][10] = '#00f0ff';
      grid[8][4] = '#00f0ff'; grid[8][11] = '#0284c7';
      grid[9][3] = '#00f0ff'; grid[9][11] = '#0284c7';
      grid[10][2] = '#67e8f9'; grid[10][11] = '#0369a1';
      grid[11][2] = '#00f0ff'; grid[11][11] = '#0369a1';
      grid[12][3] = '#0284c7'; grid[12][10] = '#0369a1';
      grid[13][4] = '#0369a1'; grid[13][8] = '#0369a1';
      grid[14][5] = '#0369a1'; grid[14][7] = '#0369a1';

      // Inner high-velocity plasma vortex
      grid[8][7] = '#e0f2fe'; grid[8][8] = '#67e8f9';
      grid[9][5] = '#67e8f9'; grid[9][9] = '#00f0ff';
      grid[10][4] = '#00f0ff'; grid[10][9] = '#0284c7';
      grid[11][4] = '#00f0ff'; grid[11][9] = '#0284c7';
      grid[12][5] = '#00f0ff'; grid[12][8] = '#0284c7';
      grid[13][6] = '#0284c7'; grid[13][7] = '#0284c7';

      // 5. Central Hub & High-Speed Rotating Truss Sling Arm
      // Spindle Pivot Hub
      grid[10][7] = '#f1f5f9'; grid[10][8] = '#cbd5e1';
      grid[11][7] = '#64748b'; grid[11][8] = '#334155';
      grid[10][7] = '#ffffff'; // Axle plasma core

      // Diagonal Centrifuge Arm spanning from lower-left to upper-right
      grid[6][11] = '#f8fafc'; grid[6][12] = '#00f0ff'; // Arm tip / capsule cradle facing launch chimney
      grid[7][10] = '#f1f5f9'; grid[7][11] = '#64748b';
      grid[8][9] = '#e2e8f0';  grid[8][10] = '#475569';
      grid[9][8] = '#f1f5f9';  grid[9][9] = '#64748b';
      grid[10][6] = '#cbd5e1'; grid[10][7] = '#f1f5f9';
      grid[11][5] = '#94a3b8'; grid[11][6] = '#cbd5e1';
      grid[12][4] = '#64748b'; grid[12][5] = '#94a3b8';
      grid[13][3] = '#475569'; grid[13][4] = '#64748b';
      grid[14][3] = '#1e293b'; // Counterweight heel

      // 6. Tangential Mass-Driver Launch Tube / Chimney (Upper-Right Mass Accelerator)
      // Stage 1: Drum Tangent Transition & Breech (x:10..13, y:7..10)
      grid[10][11] = '#64748b'; grid[10][12] = '#94a3b8'; grid[10][13] = '#475569';
      grid[9][11] = '#94a3b8';  grid[9][12] = '#cbd5e1';  grid[9][13] = '#64748b'; grid[9][14] = '#334155';
      grid[8][12] = '#cbd5e1';  grid[8][13] = '#e2e8f0';  grid[8][14] = '#94a3b8'; grid[8][15] = '#475569';

      // Stage 2: Induction Coil Pod #1 with Circular Release Port (x:12..17, y:5..8)
      grid[7][13] = '#e2e8f0'; grid[7][14] = '#f1f5f9'; grid[7][15] = '#94a3b8'; grid[7][16] = '#475569';
      grid[6][13] = '#f1f5f9'; grid[6][14] = '#00f0ff'; grid[6][15] = '#0f172a'; grid[6][16] = '#94a3b8'; grid[6][17] = '#475569'; // Coil #1
      grid[5][14] = '#f1f5f9'; grid[5][15] = '#e2e8f0'; grid[5][16] = '#cbd5e1'; grid[5][17] = '#64748b';

      // Stage 3: Induction Coil Pod #2 with Circular Release Port (x:14..19, y:3..6)
      grid[5][15] = '#f1f5f9'; grid[5][16] = '#f8fafc'; grid[5][17] = '#94a3b8'; grid[5][18] = '#475569';
      grid[4][15] = '#f8fafc'; grid[4][16] = '#00f0ff'; grid[4][17] = '#0f172a'; grid[4][18] = '#94a3b8'; grid[4][19] = '#475569'; // Coil #2
      grid[3][16] = '#f8fafc'; grid[3][17] = '#f1f5f9'; grid[3][18] = '#cbd5e1'; grid[3][19] = '#64748b';

      // Stage 4: Induction Coil Pod #3 (x:16..19, y:1..4)
      grid[3][17] = '#f8fafc'; grid[3][18] = '#f1f5f9'; grid[3][19] = '#94a3b8';
      grid[2][16] = '#f8fafc'; grid[2][17] = '#00f0ff'; grid[2][18] = '#0f172a'; grid[2][19] = '#94a3b8'; // Coil #3
      grid[1][17] = '#f8fafc'; grid[1][18] = '#f1f5f9'; grid[1][19] = '#cbd5e1';

      // Stage 5: Muzzle Crown & Loaded Capsule Projectile (x:17..19, y:0..2)
      grid[1][18] = '#f8fafc'; grid[1][19] = '#e2e8f0';
      grid[0][18] = '#f1f5f9'; grid[0][19] = '#fbbf24'; // Ready capsule nose cone
      grid[0][17] = '#e2e8f0';
      grid[1][17] = '#ffffff'; // Muzzle ionization flash

      // Magnetic Accelerator Rail Spine running up the center channel
      grid[9][12] = '#00f0ff';
      grid[8][13] = '#67e8f9';
      grid[7][14] = '#e0f2fe';
      grid[5][15] = '#67e8f9';
      grid[4][16] = '#67e8f9';
      grid[3][17] = '#ffffff';
      grid[2][17] = '#ffffff';
    }
    else if (unitType === 'DeployableCannon' || unitType === 'Deployable_Cannon') {
      gridSize = 16;
      grid = Array.from({ length: 16 }, () => Array(16).fill(null));
      // Full Assembled Model: Base + Turret Combined

      // Base: Legs, Shields, Collar
      grid[8][5] = '#1e293b'; grid[8][6] = '#475569'; grid[8][7] = '#64748b'; grid[8][8] = '#1e293b';
      grid[9][4] = '#64748b'; grid[9][5] = '#475569'; grid[9][6] = '#94a3b8'; grid[9][7] = '#64748b';
      grid[10][4] = '#475569'; grid[10][5] = 'TEAM'; grid[10][6] = '#64748b'; grid[10][7] = '#475569';
      grid[11][4] = '#334155'; grid[11][5] = '#1e293b'; grid[11][6] = '#475569'; grid[11][7] = '#334155';
      grid[12][4] = '#1e293b'; grid[12][5] = '#0f172a'; grid[12][6] = '#334155'; grid[12][7] = '#1e293b';
      grid[12][8] = '#22c55e';

      // Rear Leg
      grid[10][3] = '#334155'; grid[11][2] = '#475569'; grid[11][1] = '#64748b'; grid[12][1] = '#94a3b8';
      grid[13][1] = '#334155'; grid[14][0] = '#475569'; grid[14][1] = '#1e293b'; grid[15][0] = '#0f172a'; grid[15][1] = '#64748b';
      grid[12][3] = '#1e293b'; grid[13][3] = '#0f172a'; grid[14][2] = '#1e293b'; grid[15][2] = '#0f172a';

      // Front Leg
      grid[10][8] = '#334155'; grid[11][9] = '#475569'; grid[11][10] = '#64748b'; grid[12][10] = '#64748b'; grid[12][11] = '#94a3b8';
      grid[13][11] = '#475569'; grid[14][11] = '#334155'; grid[14][12] = '#475569'; grid[15][12] = '#1e293b'; grid[15][13] = '#64748b';
      grid[12][13] = '#1e293b'; grid[13][14] = '#1e293b'; grid[14][14] = '#0f172a'; grid[15][15] = '#334155';

      // Turret: Antenna, Dome, Scope, Optics, Barrel
      grid[1][3] = '#94a3b8'; grid[2][3] = '#64748b'; grid[3][3] = '#475569';
      grid[2][4] = '#64748b'; grid[2][5] = '#475569'; grid[3][4] = '#475569'; grid[3][5] = '#334155';
      grid[3][7] = '#64748b'; grid[3][8] = '#94a3b8'; grid[4][7] = '#334155'; grid[4][8] = '#475569';

      grid[4][2] = '#facc15'; grid[4][3] = '#334155'; grid[4][4] = '#1e293b'; grid[4][5] = '#22c55e';
      grid[5][2] = '#334155'; grid[5][3] = '#1e293b'; grid[5][4] = '#0f172a'; grid[5][5] = '#00f0ff';
      grid[6][3] = '#1e293b'; grid[6][4] = '#334155'; grid[6][5] = '#475569';

      grid[4][6] = '#475569'; grid[5][6] = 'TEAM'; grid[5][7] = 'TEAM'; grid[6][6] = '#334155'; grid[6][7] = '#1e293b';
      grid[7][4] = '#0f172a'; grid[7][5] = '#475569'; grid[7][6] = '#0f172a'; grid[7][7] = '#334155';

      grid[5][8] = '#64748b'; grid[5][9] = '#94a3b8'; grid[6][8] = '#334155'; grid[6][9] = '#475569';
      grid[5][10] = '#cbd5e1'; grid[5][11] = '#94a3b8'; grid[5][12] = '#cbd5e1'; grid[5][13] = '#94a3b8';
      grid[6][10] = '#334155'; grid[6][11] = '#1e293b'; grid[6][12] = '#334155'; grid[6][13] = '#1e293b';

      grid[4][14] = '#64748b'; grid[4][15] = '#94a3b8';
      grid[5][14] = '#0f172a'; grid[5][15] = '#cbd5e1';
      grid[6][14] = '#1e293b'; grid[6][15] = '#475569';
    }
    // ==========================================
    // SUL SWARM BESPOKE 8-BIT RETRO SPRITES
    // ==========================================
    else if (unitType === 'DefectiveLoaf' || unitType === 'Defective_Loaf' || unitType === 'Defective Loaf' || unitType === 'DefectiveLoaf_Front') {
      // 𓏏 PANEL 1: FRONTAL HOVER, SYMMETRICAL ANTLER BOUGHS & COBALT CHITIN BREASTPLATE
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));

      // 1. Antler-like thorny branches radiating upwards & outwards (y: 0-6)
      grid[0][5] = '#facc15'; grid[0][18] = '#facc15';
      grid[1][4] = '#facc15'; grid[1][5] = '#ca8a04'; grid[1][8] = '#facc15';
      grid[1][15] = '#facc15'; grid[1][18] = '#ca8a04'; grid[1][19] = '#facc15';

      grid[2][3] = '#a3e635'; grid[2][4] = '#84cc16'; grid[2][5] = '#65a30d'; grid[2][7] = '#ca8a04'; grid[2][8] = '#84cc16';
      grid[2][11] = '#facc15'; grid[2][12] = '#facc15';
      grid[2][15] = '#84cc16'; grid[2][16] = '#ca8a04'; grid[2][18] = '#65a30d'; grid[2][19] = '#84cc16'; grid[2][20] = '#a3e635';

      grid[3][2] = '#a3e635'; grid[3][3] = '#65a30d'; grid[3][5] = '#4d7c0f'; grid[3][6] = '#65a30d'; grid[3][7] = '#84cc16'; grid[3][8] = '#65a30d';
      grid[3][11] = '#ca8a04'; grid[3][12] = '#ca8a04';
      grid[3][15] = '#65a30d'; grid[3][16] = '#84cc16'; grid[3][17] = '#65a30d'; grid[3][18] = '#4d7c0f'; grid[3][20] = '#65a30d'; grid[3][21] = '#a3e635';

      grid[4][4] = '#65a30d'; grid[4][5] = '#4d7c0f'; grid[4][7] = '#4d7c0f'; grid[4][8] = '#365314'; grid[4][9] = '#ca8a04';
      grid[4][11] = '#84cc16'; grid[4][12] = '#84cc16';
      grid[4][14] = '#ca8a04'; grid[4][15] = '#365314'; grid[4][16] = '#4d7c0f'; grid[4][18] = '#4d7c0f'; grid[4][19] = '#65a30d';

      grid[5][6] = '#4d7c0f'; grid[5][7] = '#365314'; grid[5][8] = '#4d7c0f'; grid[5][9] = '#65a30d';
      grid[5][10] = '#84cc16'; grid[5][11] = '#a3e635'; grid[5][12] = '#a3e635'; grid[5][13] = '#84cc16';
      grid[5][14] = '#65a30d'; grid[5][15] = '#4d7c0f'; grid[5][16] = '#365314'; grid[5][17] = '#4d7c0f';

      // 2. Starburst Eye & Radiant Core (y: 6-10)
      grid[6][7] = '#365314'; grid[6][8] = '#4d7c0f'; grid[6][9] = '#65a30d';
      grid[6][10] = '#a21caf'; grid[6][11] = '#c026d3'; grid[6][12] = '#c026d3'; grid[6][13] = '#a21caf';
      grid[6][14] = '#65a30d'; grid[6][15] = '#4d7c0f'; grid[6][16] = '#365314';

      grid[7][6] = '#ca8a04'; grid[7][7] = '#eab308'; grid[7][8] = '#ca8a04';
      grid[7][9] = '#c026d3'; grid[7][10] = '#e879f9'; grid[7][11] = '#fae8ff'; grid[7][12] = '#fae8ff'; grid[7][13] = '#e879f9'; grid[7][14] = '#c026d3';
      grid[7][15] = '#ca8a04'; grid[7][16] = '#eab308'; grid[7][17] = '#ca8a04';

      grid[8][6] = '#ca8a04'; grid[8][7] = '#fde047'; grid[8][8] = '#eab308';
      grid[8][9] = '#d946ef'; grid[8][10] = '#ffffff'; grid[8][11] = '#ffffff'; grid[8][12] = '#ffffff'; grid[8][13] = '#ffffff'; grid[8][14] = '#d946ef';
      grid[8][15] = '#eab308'; grid[8][16] = '#fde047'; grid[8][17] = '#ca8a04';

      grid[9][6] = '#b45309'; grid[9][7] = '#ca8a04'; grid[9][8] = '#fde047';
      grid[9][9] = '#a21caf'; grid[9][10] = '#d946ef'; grid[9][11] = '#f0abfc'; grid[9][12] = '#f0abfc'; grid[9][13] = '#d946ef'; grid[9][14] = '#a21caf';
      grid[9][15] = '#fde047'; grid[9][16] = '#ca8a04'; grid[9][17] = '#b45309';

      grid[10][7] = '#ca8a04'; grid[10][8] = '#eab308'; grid[10][9] = '#fde047';
      grid[10][10] = '#701a75'; grid[10][11] = '#a21caf'; grid[10][12] = '#a21caf'; grid[10][13] = '#701a75';
      grid[10][14] = '#fde047'; grid[10][15] = '#eab308'; grid[10][16] = '#ca8a04';

      // 3. Cobalt-Blue Chitin Carapace Shield with Gold V-Collar (y: 11-16)
      grid[11][7] = '#1e40af'; grid[11][8] = '#2563eb'; grid[11][9] = '#3b82f6';
      grid[11][10] = '#60a5fa'; grid[11][11] = '#93c5fd'; grid[11][12] = '#93c5fd'; grid[11][13] = '#60a5fa';
      grid[11][14] = '#3b82f6'; grid[11][15] = '#2563eb'; grid[11][16] = '#1e40af';

      grid[12][7] = '#1e3a8a'; grid[12][8] = 'TEAM'; grid[12][9] = '#2563eb';
      grid[12][10] = '#3b82f6'; grid[12][11] = '#60a5fa'; grid[12][12] = '#60a5fa'; grid[12][13] = '#3b82f6';
      grid[12][14] = '#2563eb'; grid[12][15] = 'TEAM'; grid[12][16] = '#1e3a8a';

      // Inscribed Semicircle Bread Loaf Glyph 𓏏 (y: 13-14) - CRITICAL TEST INVARIANTS:
      // grid[13][11] === '#0f172a' && grid[14][11] === '#facc15'
      grid[13][7] = '#1e3a8a'; grid[13][8] = '#1e40af'; grid[13][9] = '#1d4ed8';
      grid[13][10] = '#0f172a'; grid[13][11] = '#0f172a'; grid[13][12] = '#0f172a'; grid[13][13] = '#0f172a';
      grid[13][14] = '#1d4ed8'; grid[13][15] = '#1e40af'; grid[13][16] = '#1e3a8a';

      grid[14][8] = '#1e3a8a'; grid[14][9] = '#1e40af';
      grid[14][10] = '#0f172a'; grid[14][11] = '#facc15'; grid[14][12] = '#facc15'; grid[14][13] = '#0f172a';
      grid[14][14] = '#1e40af'; grid[14][15] = '#1e3a8a';

      grid[15][8] = '#0f172a'; grid[15][9] = '#1e3a8a';
      grid[15][10] = '#1e40af'; grid[15][11] = '#1d4ed8'; grid[15][12] = '#1d4ed8'; grid[15][13] = '#1e40af';
      grid[15][14] = '#1e3a8a'; grid[15][15] = '#0f172a';

      grid[16][7] = '#991b1b'; grid[16][8] = '#7f1d1d'; grid[16][9] = '#1e3a8a';
      grid[16][10] = '#4338ca'; grid[16][11] = '#6366f1'; grid[16][12] = '#6366f1'; grid[16][13] = '#4338ca';
      grid[16][14] = '#1e3a8a'; grid[16][15] = '#7f1d1d'; grid[16][16] = '#991b1b';

      // 4. Coiled Proboscis Base & Root Skirt Anchors (y: 17-20)
      grid[17][6] = '#7f1d1d'; grid[17][7] = '#b45309'; grid[17][8] = '#365314';
      grid[17][10] = '#6366f1'; grid[17][11] = '#818cf8'; grid[17][12] = '#818cf8'; grid[17][13] = '#6366f1';
      grid[17][15] = '#365314'; grid[17][16] = '#b45309'; grid[17][17] = '#7f1d1d';

      grid[18][6] = '#450a0a'; grid[18][7] = '#78350f';
      grid[18][10] = '#4338ca'; grid[18][11] = '#a855f7'; grid[18][12] = '#c084fc'; grid[18][13] = '#818cf8';
      grid[18][16] = '#78350f'; grid[18][17] = '#450a0a';

      grid[19][7] = '#ca8a04';
      grid[19][11] = '#7c3aed'; grid[19][12] = '#f0abfc';
      grid[19][16] = '#ca8a04';

      grid[20][7] = '#365314'; grid[20][16] = '#365314';
    }
    else if (unitType === 'DefectiveLoaf_Drift') {
      // 𓏏 PANEL 2: 3/4 FLIGHT DRIFT PROFILE, SWEPT-BACK BOUGHS & STREAMLINED CARAPACE
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));

      // 1. Swept-Back Antler Boughs (Streamlined backwards against airflow, x: 1-13, y: 0-7)
      grid[0][4] = '#facc15'; grid[0][7] = '#facc15';
      grid[1][3] = '#facc15'; grid[1][4] = '#ca8a04'; grid[1][6] = '#ca8a04'; grid[1][7] = '#84cc16';
      grid[2][2] = '#a3e635'; grid[2][3] = '#84cc16'; grid[2][5] = '#65a30d'; grid[2][6] = '#84cc16'; grid[2][9] = '#facc15';
      grid[3][2] = '#84cc16'; grid[3][4] = '#65a30d'; grid[3][5] = '#4d7c0f'; grid[3][8] = '#ca8a04'; grid[3][9] = '#84cc16';
      grid[4][3] = '#65a30d'; grid[4][5] = '#4d7c0f'; grid[4][6] = '#365314'; grid[4][8] = '#65a30d'; grid[4][11] = '#facc15';
      grid[5][4] = '#4d7c0f'; grid[5][6] = '#365314'; grid[5][7] = '#4d7c0f'; grid[5][10] = '#ca8a04'; grid[5][11] = '#84cc16';
      grid[6][5] = '#365314'; grid[6][7] = '#4d7c0f'; grid[6][8] = '#65a30d'; grid[6][9] = '#84cc16'; grid[6][10] = '#a3e635';

      // 2. 3/4 Starburst Eye Cavity (Leading forward, x: 12-16, y: 5-8)
      grid[5][12] = '#c026d3'; grid[5][13] = '#fae8ff'; grid[5][14] = '#c026d3';
      grid[6][11] = '#d946ef'; grid[6][12] = '#ffffff'; grid[6][13] = '#ffffff'; grid[6][14] = '#d946ef';
      grid[7][11] = '#a21caf'; grid[7][12] = '#f0abfc'; grid[7][13] = '#ffffff'; grid[7][14] = '#f0abfc'; grid[7][15] = '#a21caf';
      grid[8][12] = '#701a75'; grid[8][13] = '#a21caf'; grid[8][14] = '#c026d3';

      // 3. Golden Collar Rim swept back (x: 8-12, y: 8-10)
      grid[8][8] = '#ca8a04'; grid[8][9] = '#eab308'; grid[8][10] = '#fde047'; grid[8][11] = '#ca8a04';
      grid[9][7] = '#ca8a04'; grid[9][8] = '#fde047'; grid[9][9] = '#eab308'; grid[9][10] = '#ca8a04';
      grid[10][8] = '#ca8a04'; grid[10][9] = '#ca8a04';

      // 4. Aerodynamic 3/4 Cobalt Carapace (Leaning forward, x: 9-17, y: 9-16)
      grid[8][15] = '#3b82f6'; grid[8][16] = '#60a5fa';
      grid[9][11] = '#2563eb'; grid[9][12] = 'TEAM'; grid[9][13] = '#3b82f6'; grid[9][14] = '#60a5fa'; grid[9][15] = '#93c5fd'; grid[9][16] = '#3b82f6';
      grid[10][10] = '#1d4ed8'; grid[10][11] = '#2563eb'; grid[10][12] = '#3b82f6'; grid[10][13] = '#60a5fa'; grid[10][14] = '#60a5fa'; grid[10][15] = '#2563eb';
      grid[11][9] = '#1e40af'; grid[11][10] = '#1d4ed8'; grid[11][11] = '#2563eb'; grid[11][12] = '#3b82f6'; grid[11][13] = '#2563eb'; grid[11][14] = '#1d4ed8';

      // 3/4 Bread Loaf Glyph 𓏏 on flank
      grid[12][9] = '#1e3a8a'; grid[12][10] = '#0f172a'; grid[12][11] = '#0f172a'; grid[12][12] = '#0f172a'; grid[12][13] = '#1d4ed8';
      grid[13][9] = '#1e3a8a'; grid[13][10] = '#0f172a'; grid[13][11] = '#facc15'; grid[13][12] = '#0f172a'; grid[13][13] = '#1e40af';

      grid[14][9] = '#0f172a'; grid[14][10] = '#1e40af'; grid[14][11] = '#1d4ed8'; grid[14][12] = '#1d4ed8'; grid[14][13] = '#1e3a8a';
      grid[15][10] = '#1e3a8a'; grid[15][11] = '#1e40af'; grid[15][12] = '#1e3a8a';

      // 5. Proboscis Base aiming forward-downward (x: 13-17, y: 14-17)
      grid[14][14] = '#4338ca'; grid[14][15] = '#6366f1';
      grid[15][13] = '#4338ca'; grid[15][14] = '#818cf8'; grid[15][15] = '#a855f7'; grid[15][16] = '#6366f1';
      grid[16][14] = '#6366f1'; grid[16][15] = '#c084fc'; grid[16][16] = '#f0abfc'; grid[16][17] = '#818cf8';
      grid[17][15] = '#7c3aed'; grid[17][16] = '#e879f9';

      // 6. Root Filaments trailing back in slipstream (x: 4-10, y: 15-20)
      grid[15][7] = '#991b1b'; grid[15][8] = '#7f1d1d';
      grid[16][5] = '#991b1b'; grid[16][6] = '#7f1d1d'; grid[16][7] = '#b45309'; grid[16][8] = '#365314';
      grid[17][4] = '#450a0a'; grid[17][5] = '#78350f'; grid[17][6] = '#365314';
      grid[18][3] = '#450a0a'; grid[18][4] = '#ca8a04';
      grid[19][3] = '#365314';
    }
    else if (unitType === 'DefectiveLoaf_Attack') {
      // 𓏏 PANEL 3: ATTACK LOCK-ON, RADIATING STARBURST FLARE & DILATED EMITTER NOZZLE
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));

      // 1. Bristling Energized Antlers (y: 0-5)
      grid[0][4] = '#ffffff'; grid[0][5] = '#facc15'; grid[0][18] = '#facc15'; grid[0][19] = '#ffffff';
      grid[1][3] = '#ffffff'; grid[1][4] = '#fde047'; grid[1][5] = '#ca8a04'; grid[1][8] = '#ffffff';
      grid[1][15] = '#ffffff'; grid[1][18] = '#ca8a04'; grid[1][19] = '#fde047'; grid[1][20] = '#ffffff';

      grid[2][2] = '#a3e635'; grid[2][3] = '#84cc16'; grid[2][4] = '#facc15'; grid[2][7] = '#fde047'; grid[2][8] = '#84cc16';
      grid[2][11] = '#ffffff'; grid[2][12] = '#ffffff';
      grid[2][15] = '#84cc16'; grid[2][16] = '#fde047'; grid[2][19] = '#facc15'; grid[2][20] = '#84cc16'; grid[2][21] = '#a3e635';

      grid[3][3] = '#65a30d'; grid[3][5] = '#84cc16'; grid[3][6] = '#a3e635'; grid[3][7] = '#facc15';
      grid[3][11] = '#fae8ff'; grid[3][12] = '#fae8ff';
      grid[3][16] = '#facc15'; grid[3][17] = '#a3e635'; grid[3][18] = '#84cc16'; grid[3][20] = '#65a30d';

      // 2. Exploding Starburst Corona (8 Radial Energy Spikes around Core)
      // Top spike:
      grid[4][11] = '#ffffff'; grid[4][12] = '#ffffff';
      grid[5][11] = '#fae8ff'; grid[5][12] = '#fae8ff';
      // Diagonal upper spikes:
      grid[5][7] = '#ffffff'; grid[5][8] = '#fae8ff'; grid[6][9] = '#f0abfc';
      grid[5][15] = '#fae8ff'; grid[5][16] = '#ffffff'; grid[6][14] = '#f0abfc';
      // Horizontal lateral spikes:
      grid[8][4] = '#ffffff'; grid[8][5] = '#fae8ff'; grid[8][6] = '#f0abfc'; grid[8][7] = '#e879f9';
      grid[8][16] = '#e879f9'; grid[8][17] = '#f0abfc'; grid[8][18] = '#fae8ff'; grid[8][19] = '#ffffff';

      // Supernova Starburst Core (y: 6-10, x: 8-15)
      grid[6][10] = '#c026d3'; grid[6][11] = '#ffffff'; grid[6][12] = '#ffffff'; grid[6][13] = '#c026d3';
      grid[7][8] = '#f0abfc'; grid[7][9] = '#e879f9'; grid[7][10] = '#ffffff'; grid[7][11] = '#ffffff'; grid[7][12] = '#ffffff'; grid[7][13] = '#ffffff'; grid[7][14] = '#e879f9'; grid[7][15] = '#f0abfc';
      grid[8][8] = '#d946ef'; grid[8][9] = '#ffffff'; grid[8][10] = '#ffffff'; grid[8][11] = '#ffffff'; grid[8][12] = '#ffffff'; grid[8][13] = '#ffffff'; grid[8][14] = '#ffffff'; grid[8][15] = '#d946ef';
      grid[9][8] = '#a21caf'; grid[9][9] = '#e879f9'; grid[9][10] = '#ffffff'; grid[9][11] = '#ffffff'; grid[9][12] = '#ffffff'; grid[9][13] = '#ffffff'; grid[9][14] = '#e879f9'; grid[9][15] = '#a21caf';
      grid[10][9] = '#701a75'; grid[10][10] = '#c026d3'; grid[10][11] = '#ffffff'; grid[10][12] = '#ffffff'; grid[10][13] = '#c026d3'; grid[10][14] = '#701a75';

      // 3. Flared Chitin Carapace & Dilated Emitter
      grid[11][6] = '#1e40af'; grid[11][7] = 'TEAM'; grid[11][8] = '#2563eb'; grid[11][9] = '#60a5fa';
      grid[11][10] = '#93c5fd'; grid[11][11] = '#ffffff'; grid[11][12] = '#ffffff'; grid[11][13] = '#93c5fd';
      grid[11][14] = '#60a5fa'; grid[11][15] = '#2563eb'; grid[11][16] = 'TEAM'; grid[11][17] = '#1e40af';

      grid[12][6] = '#1e3a8a'; grid[12][7] = '#1e40af'; grid[12][8] = '#2563eb'; grid[12][9] = '#3b82f6';
      grid[12][10] = '#60a5fa'; grid[12][11] = '#93c5fd'; grid[12][12] = '#93c5fd'; grid[12][13] = '#60a5fa';
      grid[12][14] = '#3b82f6'; grid[12][15] = '#2563eb'; grid[12][16] = '#1e40af'; grid[12][17] = '#1e3a8a';

      // Glowing Bread Loaf Glyph 𓏏
      grid[13][7] = '#1e3a8a'; grid[13][8] = '#1e40af'; grid[13][9] = '#1d4ed8';
      grid[13][10] = '#0f172a'; grid[13][11] = '#0f172a'; grid[13][12] = '#0f172a'; grid[13][13] = '#0f172a';
      grid[13][14] = '#1d4ed8'; grid[13][15] = '#1e40af'; grid[13][16] = '#1e3a8a';

      grid[14][8] = '#1e3a8a'; grid[14][9] = '#1e40af';
      grid[14][10] = '#0f172a'; grid[14][11] = '#facc15'; grid[14][12] = '#facc15'; grid[14][13] = '#0f172a';
      grid[14][14] = '#1e40af'; grid[14][15] = '#1e3a8a';

      // 4. Emitter Nozzle Aperture (y: 15-18)
      grid[15][9] = '#1e3a8a'; grid[15][10] = '#4338ca'; grid[15][11] = '#701a75'; grid[15][12] = '#701a75'; grid[15][13] = '#4338ca'; grid[15][14] = '#1e3a8a';
      grid[16][9] = '#4338ca'; grid[16][10] = '#c026d3'; grid[16][11] = '#e879f9'; grid[16][12] = '#e879f9'; grid[16][13] = '#c026d3'; grid[16][14] = '#4338ca';
      grid[17][10] = '#a855f7'; grid[17][11] = '#f0abfc'; grid[17][12] = '#f0abfc'; grid[17][13] = '#a855f7';
      grid[18][11] = '#ffffff'; grid[18][12] = '#ffffff';

      // 5. Tense Root Anchors
      grid[16][6] = '#991b1b'; grid[16][7] = '#7f1d1d'; grid[16][16] = '#7f1d1d'; grid[16][17] = '#991b1b';
      grid[17][5] = '#7f1d1d'; grid[17][6] = '#b45309'; grid[17][17] = '#b45309'; grid[17][18] = '#7f1d1d';
      grid[18][5] = '#450a0a'; grid[18][18] = '#450a0a';
    }
    else if (unitType === 'DefectiveLoaf_Rupture') {
      // 𓏏 PANEL 4: RUPTURED CHITIN KOKON, COLLAPSED CORE & CAUSTIC ACID SPILL
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));

      // 1. Broken, withered antler fragments slumped downward (y: 7-11)
      grid[7][3] = '#4d7c0f'; grid[7][4] = '#65a30d'; grid[7][5] = '#ca8a04';
      grid[8][2] = '#365314'; grid[8][3] = '#4d7c0f'; grid[8][6] = '#ca8a04'; grid[8][7] = '#84cc16';
      grid[9][3] = '#365314'; grid[9][4] = '#4d7c0f';
      grid[8][17] = '#ca8a04'; grid[8][18] = '#4d7c0f'; grid[8][19] = '#365314';
      grid[9][18] = '#365314';

      // 2. Shattered Carapace Halves & Hollow Breached Crater (y: 10-17)
      // Left fractured carapace shell
      grid[11][4] = '#1e40af'; grid[11][5] = '#2563eb'; grid[11][6] = '#1d4ed8'; grid[11][7] = '#0f172a';
      grid[12][3] = '#1e3a8a'; grid[12][4] = '#1e40af'; grid[12][5] = '#2563eb'; grid[12][6] = '#1d4ed8'; grid[12][7] = '#0f172a';
      grid[13][4] = '#0f172a'; grid[13][5] = '#1e3a8a'; grid[13][6] = '#1e40af'; grid[13][7] = '#0f172a';
      grid[14][5] = '#0f172a'; grid[14][6] = '#1e3a8a'; grid[14][7] = '#0f172a';

      // Hollow breached central void
      grid[10][10] = '#020617'; grid[10][11] = '#020617'; grid[10][12] = '#020617'; grid[10][13] = '#020617';
      grid[11][9] = '#020617'; grid[11][10] = '#0f172a'; grid[11][11] = '#020617'; grid[11][12] = '#020617'; grid[11][13] = '#0f172a'; grid[11][14] = '#020617';
      grid[12][9] = '#020617'; grid[12][10] = '#1e1b4b'; grid[12][11] = '#020617'; grid[12][12] = '#020617'; grid[12][13] = '#1e1b4b'; grid[12][14] = '#020617';
      grid[13][9] = '#020617'; grid[13][10] = '#0f172a'; grid[13][11] = '#020617'; grid[13][12] = '#020617'; grid[13][13] = '#0f172a'; grid[13][14] = '#020617';

      // Right fractured carapace shell
      grid[11][16] = '#0f172a'; grid[11][17] = '#1d4ed8'; grid[11][18] = '#2563eb'; grid[11][19] = '#1e40af';
      grid[12][16] = '#0f172a'; grid[12][17] = '#1d4ed8'; grid[12][18] = '#2563eb'; grid[12][19] = '#1e40af'; grid[12][20] = '#1e3a8a';
      grid[13][16] = '#0f172a'; grid[13][17] = '#1e40af'; grid[13][18] = '#1e3a8a'; grid[13][19] = '#0f172a';
      grid[14][16] = '#0f172a'; grid[14][17] = '#1e3a8a'; grid[14][18] = '#0f172a';

      // 3. Spilling Corrosive Bio-Acid Puddle (Fluorescent Greens, Purples & Foam) (y: 16-22)
      grid[16][10] = '#581c87'; grid[16][11] = '#7e22ce'; grid[16][12] = '#7e22ce'; grid[16][13] = '#581c87';

      grid[17][8] = '#365314'; grid[17][9] = '#4d7c0f'; grid[17][10] = '#7e22ce'; grid[17][11] = '#a855f7'; grid[17][12] = '#c026d3'; grid[17][13] = '#7e22ce'; grid[17][14] = '#4d7c0f'; grid[17][15] = '#365314';

      grid[18][5] = '#365314'; grid[18][6] = '#65a30d'; grid[18][7] = '#84cc16'; grid[18][8] = '#a3e635'; grid[18][9] = '#a855f7'; grid[18][10] = '#c026d3'; grid[18][11] = '#fae8ff'; grid[18][12] = '#f0abfc'; grid[18][13] = '#c026d3'; grid[18][14] = '#a855f7'; grid[18][15] = '#a3e635'; grid[18][16] = '#84cc16'; grid[18][17] = '#65a30d'; grid[18][18] = '#365314';

      grid[19][3] = '#4d7c0f'; grid[19][4] = '#84cc16'; grid[19][5] = '#a3e635'; grid[19][6] = '#4ade80'; grid[19][7] = '#ecfccb'; grid[19][8] = '#a3e635'; grid[19][9] = '#c026d3'; grid[19][10] = '#e879f9'; grid[19][11] = '#ffffff'; grid[19][12] = '#ffffff'; grid[19][13] = '#e879f9'; grid[19][14] = '#c026d3'; grid[19][15] = '#a3e635'; grid[19][16] = '#4ade80'; grid[19][17] = '#a3e635'; grid[19][18] = '#84cc16'; grid[19][19] = '#4d7c0f';

      grid[20][4] = '#365314'; grid[20][5] = '#65a30d'; grid[20][6] = '#84cc16'; grid[20][7] = '#a3e635'; grid[20][8] = '#4ade80'; grid[20][9] = '#84cc16'; grid[20][10] = '#7e22ce'; grid[20][11] = '#a855f7'; grid[20][12] = '#a855f7'; grid[20][13] = '#7e22ce'; grid[20][14] = '#84cc16'; grid[20][15] = '#4ade80'; grid[20][16] = '#a3e635'; grid[20][17] = '#84cc16'; grid[20][18] = '#65a30d'; grid[20][19] = '#365314';

      grid[21][6] = '#365314'; grid[21][7] = '#4d7c0f'; grid[21][8] = '#65a30d'; grid[21][9] = '#84cc16'; grid[21][10] = '#a3e635'; grid[21][11] = '#84cc16'; grid[21][12] = '#84cc16'; grid[21][13] = '#a3e635'; grid[21][14] = '#84cc16'; grid[21][15] = '#65a30d'; grid[21][16] = '#4d7c0f'; grid[21][17] = '#365314';

      // Dead limp root remnants dissolved in the puddle
      grid[17][6] = '#450a0a'; grid[17][7] = '#7f1d1d'; grid[17][16] = '#7f1d1d'; grid[17][17] = '#450a0a';
    }
    else if (unitType === 'RejectedLoaf_Drift') {
      // 𓏏 PANEL 2: 3/4 FLIGHT PROFILE SKULL & SWEPT-BACK SPIKY CROWN (Facing right)
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));

      // 1. Swept-Back Thorny Crown (Spines trailing back & up against airflow)
      grid[1][12] = '#c084fc'; grid[2][11] = '#a855f7'; grid[3][10] = '#7e22ce';
      grid[1][15] = '#c084fc'; grid[2][14] = '#a855f7'; grid[3][13] = '#7e22ce';
      grid[2][17] = '#c084fc'; grid[3][16] = '#a855f7'; grid[4][15] = '#7e22ce';
      grid[2][8] = '#c084fc'; grid[3][7] = '#a855f7'; grid[4][8] = '#7e22ce'; grid[5][9] = '#581c87';
      grid[4][4] = '#c084fc'; grid[5][5] = '#a855f7'; grid[6][6] = '#7e22ce'; grid[6][7] = '#581c87';
      grid[7][2] = '#c084fc'; grid[7][3] = '#a855f7'; grid[8][4] = '#7e22ce'; grid[8][5] = '#581c87'; grid[8][6] = '#3b0764';
      grid[10][3] = '#a855f7'; grid[10][4] = '#7e22ce'; grid[11][5] = '#581c87'; grid[11][6] = '#3b0764';
      grid[13][4] = '#7e22ce'; grid[13][5] = '#581c87'; grid[14][6] = '#3b0764';
      grid[4][18] = '#c084fc'; grid[5][17] = '#a855f7';

      // 2. 3/4 Profile Ivory Bone Skull Core (Tilted forward into wind)
      grid[4][12] = '#e2e8f0'; grid[4][13] = '#f8fafc'; grid[4][14] = '#ffffff';
      grid[5][10] = '#cbd5e1'; grid[5][11] = '#e2e8f0'; grid[5][12] = '#f8fafc'; grid[5][13] = '#ffffff'; grid[5][14] = '#ffffff'; grid[5][15] = '#f8fafc';
      grid[6][9] = '#94a3b8'; grid[6][10] = '#cbd5e1'; grid[6][11] = 'TEAM'; grid[6][12] = 'TEAM'; grid[6][13] = '#ffffff'; grid[6][14] = '#ffffff'; grid[6][15] = '#ffffff'; grid[6][16] = '#f8fafc';
      grid[7][9] = '#64748b'; grid[7][10] = '#94a3b8'; grid[7][11] = '#cbd5e1'; grid[7][12] = '#e2e8f0'; grid[7][13] = '#f8fafc'; grid[7][14] = '#ffffff'; grid[7][15] = '#cbd5e1'; grid[7][16] = '#f8fafc';
      grid[8][8] = '#475569'; grid[8][9] = '#64748b'; grid[8][10] = '#94a3b8'; grid[8][11] = '#cbd5e1'; grid[8][12] = '#0f172a'; grid[8][13] = '#0f172a'; grid[8][14] = '#0f172a'; grid[8][15] = '#cbd5e1'; grid[8][16] = '#e2e8f0';
      grid[9][8] = '#334155'; grid[9][9] = '#475569'; grid[9][10] = '#64748b'; grid[9][11] = '#94a3b8'; grid[9][12] = '#0f172a'; grid[9][13] = '#1e1b4b'; grid[9][14] = '#0f172a'; grid[9][15] = '#94a3b8'; grid[9][16] = '#cbd5e1';
      grid[10][9] = '#334155'; grid[10][10] = '#475569'; grid[10][11] = '#64748b'; grid[10][12] = '#94a3b8'; grid[10][13] = '#0f172a'; grid[10][14] = '#0f172a'; grid[10][15] = '#cbd5e1'; grid[10][16] = '#f8fafc';
      grid[11][9] = '#1e293b'; grid[11][10] = '#334155'; grid[11][11] = '#475569'; grid[11][12] = '#64748b'; grid[11][13] = '#94a3b8'; grid[11][14] = '#cbd5e1'; grid[11][15] = '#f8fafc';
      grid[12][10] = '#1e293b'; grid[12][11] = '#334155'; grid[12][12] = '#475569'; grid[12][13] = '#0f172a'; grid[12][14] = '#cbd5e1'; grid[12][15] = '#e2e8f0';
      grid[13][11] = '#1e293b'; grid[13][12] = '#334155'; grid[13][13] = '#475569'; grid[13][14] = '#0f172a'; grid[13][15] = '#cbd5e1';

      // 3. Trailing Bulb & Filament Root Anchors
      grid[14][8] = '#581c87'; grid[14][9] = '#7e22ce'; grid[14][10] = '#581c87'; grid[14][11] = '#7e22ce'; grid[14][12] = '#581c87'; grid[14][13] = '#7e22ce';
      grid[15][8] = '#7e22ce'; grid[15][9] = '#a855f7'; grid[15][10] = '#7e22ce'; grid[15][11] = '#a855f7';
    }
    else if (unitType === 'RejectedLoaf_Shield') {
      // 𓏏 PANEL 4: CRYSTALLINE CHITIN SHIELD & FLARED THORNS (Hit & Damage Reflection)
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));

      // 1. Lengthened, Bristling & Flaring Energy Thorns
      grid[0][11] = '#ffffff'; grid[0][12] = '#ffffff'; grid[1][11] = '#f5d0fe'; grid[1][12] = '#f5d0fe'; grid[2][11] = '#c084fc'; grid[2][12] = '#c084fc'; grid[3][11] = '#a855f7'; grid[3][12] = '#a855f7';
      grid[0][6] = '#ffffff'; grid[1][7] = '#f5d0fe'; grid[2][8] = '#c084fc'; grid[3][9] = '#a855f7'; grid[4][10] = '#7e22ce';
      grid[0][17] = '#ffffff'; grid[1][16] = '#f5d0fe'; grid[2][15] = '#c084fc'; grid[3][14] = '#a855f7'; grid[4][13] = '#7e22ce';
      grid[4][2] = '#ffffff'; grid[5][3] = '#f5d0fe'; grid[6][4] = '#c084fc'; grid[6][5] = '#a855f7'; grid[7][6] = '#7e22ce';
      grid[4][21] = '#ffffff'; grid[5][20] = '#f5d0fe'; grid[6][19] = '#c084fc'; grid[6][18] = '#a855f7'; grid[7][17] = '#7e22ce';
      grid[8][1] = '#ffffff'; grid[8][2] = '#f5d0fe'; grid[8][3] = '#c084fc'; grid[8][4] = '#a855f7'; grid[8][5] = '#7e22ce';
      grid[8][22] = '#ffffff'; grid[8][21] = '#f5d0fe'; grid[8][20] = '#c084fc'; grid[8][19] = '#a855f7'; grid[8][18] = '#7e22ce';
      grid[11][2] = '#f5d0fe'; grid[11][3] = '#c084fc'; grid[12][4] = '#a855f7'; grid[12][5] = '#7e22ce';
      grid[11][21] = '#f5d0fe'; grid[11][20] = '#c084fc'; grid[12][19] = '#a855f7'; grid[12][18] = '#7e22ce';
      grid[14][3] = '#c084fc'; grid[14][4] = '#a855f7'; grid[15][5] = '#7e22ce';
      grid[14][20] = '#c084fc'; grid[14][19] = '#a855f7'; grid[15][18] = '#7e22ce';

      // 2. Faceted Crystalline Chitin Plates across Skull
      grid[4][11] = '#ffffff'; grid[4][12] = '#ffffff';
      grid[5][10] = '#f5d0fe'; grid[5][11] = '#ffffff'; grid[5][12] = '#ffffff'; grid[5][13] = '#f5d0fe';
      grid[6][9] = '#c084fc'; grid[6][10] = '#f5d0fe'; grid[6][11] = '#e9d5ff'; grid[6][12] = '#e9d5ff'; grid[6][13] = '#f5d0fe'; grid[6][14] = '#c084fc';
      grid[7][8] = '#a855f7'; grid[7][9] = '#c084fc'; grid[7][10] = '#e9d5ff'; grid[7][11] = 'TEAM'; grid[7][12] = 'TEAM'; grid[7][13] = '#e9d5ff'; grid[7][14] = '#c084fc'; grid[7][15] = '#a855f7';

      // 3. Piercing Soul-Flame in Eye Sockets (Faceted Diamond Fire)
      grid[8][8] = '#7e22ce'; grid[8][9] = '#f472b6'; grid[8][10] = '#ffffff'; grid[8][11] = '#c084fc'; grid[8][12] = '#c084fc'; grid[8][13] = '#ffffff'; grid[8][14] = '#f472b6'; grid[8][15] = '#7e22ce';
      grid[9][8] = '#581c87'; grid[9][9] = '#f472b6'; grid[9][10] = '#facc15'; grid[9][11] = '#e9d5ff'; grid[9][12] = '#e9d5ff'; grid[9][13] = '#facc15'; grid[9][14] = '#f472b6'; grid[9][15] = '#581c87';

      // 4. Lower Crystalline Cheeks & Chin Carapace
      grid[10][8] = '#581c87'; grid[10][9] = '#7e22ce'; grid[10][10] = '#a855f7'; grid[10][11] = '#c084fc'; grid[10][12] = '#c084fc'; grid[10][13] = '#a855f7'; grid[10][14] = '#7e22ce'; grid[10][15] = '#581c87';
      grid[11][9] = '#581c87'; grid[11][10] = '#7e22ce'; grid[11][11] = '#e9d5ff'; grid[11][12] = '#e9d5ff'; grid[11][13] = '#7e22ce'; grid[11][14] = '#581c87';
      grid[12][10] = '#7e22ce'; grid[12][11] = '#a855f7'; grid[12][12] = '#a855f7'; grid[12][13] = '#7e22ce';
      grid[13][10] = '#a855f7'; grid[13][11] = '#c084fc'; grid[13][12] = '#c084fc'; grid[13][13] = '#a855f7';

      // 5. Shield Energy Root Matrix
      grid[14][9] = '#581c87'; grid[14][10] = '#7e22ce'; grid[14][11] = '#a855f7'; grid[14][12] = '#a855f7'; grid[14][13] = '#7e22ce'; grid[14][14] = '#581c87';
      grid[15][10] = '#7e22ce'; grid[15][11] = '#c084fc'; grid[15][12] = '#c084fc'; grid[15][13] = '#7e22ce';
    }
    else if (unitType === 'RejectedLoaf' || unitType === 'Rejected_Loaf' || unitType === 'Rejected Loaf' || unitType === 'RejectedLoaf_Front') {
      // 𓏏 PANEL 1: CANONICAL FRONTAL HOVER SKULL & RADIATING CORONAL SPIKY HALO
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));

      // 1. Radiating Coronal Thorny Halo (Outwards from center x: 11-12, y: 8)
      grid[0][11] = '#c084fc'; grid[0][12] = '#c084fc';
      grid[1][11] = '#c084fc'; grid[2][11] = '#a855f7'; grid[3][11] = '#7e22ce';
      grid[1][12] = '#c084fc'; grid[2][12] = '#a855f7'; grid[3][12] = '#7e22ce';
      grid[1][8] = '#a855f7'; grid[2][9] = '#7e22ce'; grid[3][10] = '#581c87';
      grid[1][15] = '#a855f7'; grid[2][14] = '#7e22ce'; grid[3][13] = '#581c87';
      grid[2][6] = '#c084fc'; grid[3][7] = '#a855f7'; grid[4][8] = '#581c87';
      grid[2][17] = '#c084fc'; grid[3][16] = '#a855f7'; grid[4][15] = '#581c87';

      // Left lateral spikes
      grid[4][4] = '#c084fc'; grid[5][5] = '#a855f7'; grid[6][6] = '#7e22ce'; grid[6][7] = '#581c87';
      grid[7][2] = '#c084fc'; grid[7][3] = '#a855f7'; grid[8][4] = '#7e22ce'; grid[8][5] = '#581c87'; grid[8][6] = '#3b0764';
      grid[10][3] = '#a855f7'; grid[10][4] = '#7e22ce'; grid[11][5] = '#581c87'; grid[11][6] = '#3b0764';
      grid[13][4] = '#7e22ce'; grid[13][5] = '#581c87'; grid[14][6] = '#3b0764';

      // Right lateral spikes
      grid[4][19] = '#c084fc'; grid[5][18] = '#a855f7'; grid[6][17] = '#7e22ce'; grid[6][16] = '#581c87';
      grid[7][21] = '#c084fc'; grid[7][20] = '#a855f7'; grid[8][19] = '#7e22ce'; grid[8][18] = '#581c87'; grid[8][17] = '#3b0764';
      grid[10][20] = '#a855f7'; grid[10][19] = '#7e22ce'; grid[11][18] = '#581c87'; grid[11][17] = '#3b0764';
      grid[13][19] = '#7e22ce'; grid[13][18] = '#581c87'; grid[14][17] = '#3b0764';

      // 2. Pale Ivory Bone Skull Core (y: 4 to 13, x: 7 to 16)
      grid[4][10] = '#f8fafc'; grid[4][11] = '#ffffff'; grid[4][12] = '#ffffff'; grid[4][13] = '#f8fafc';
      grid[5][9] = '#f8fafc'; grid[5][10] = '#ffffff'; grid[5][11] = '#ffffff'; grid[5][12] = '#ffffff'; grid[5][13] = '#ffffff'; grid[5][14] = '#f8fafc';
      grid[6][8] = '#e2e8f0'; grid[6][9] = '#f8fafc'; grid[6][10] = '#ffffff'; grid[6][11] = '#ffffff'; grid[6][12] = '#ffffff'; grid[6][13] = '#f8fafc'; grid[6][14] = '#e2e8f0'; grid[6][15] = '#cbd5e1';

      // Brow & Forehead (with Team Badge)
      grid[7][8] = '#cbd5e1'; grid[7][9] = '#e2e8f0'; grid[7][10] = '#f8fafc'; grid[7][11] = '#ffffff'; grid[7][12] = '#ffffff'; grid[7][13] = '#f8fafc'; grid[7][14] = '#e2e8f0'; grid[7][15] = '#94a3b8';
      grid[7][11] = 'TEAM'; grid[7][12] = 'TEAM';

      // Deep Hollow Eye Sockets (CRITICAL for test: [8][9] and [8][13] are #0f172a)
      grid[8][8] = '#94a3b8'; grid[8][9] = '#0f172a'; grid[8][10] = '#0f172a'; grid[8][11] = '#cbd5e1'; grid[8][12] = '#cbd5e1'; grid[8][13] = '#0f172a'; grid[8][14] = '#0f172a'; grid[8][15] = '#64748b';
      grid[9][8] = '#64748b'; grid[9][9] = '#0f172a'; grid[9][10] = '#1e1b4b'; grid[9][11] = '#e2e8f0'; grid[9][12] = '#e2e8f0'; grid[9][13] = '#1e1b4b'; grid[9][14] = '#0f172a'; grid[9][15] = '#475569';

      // Nasal aperture & Cheekbones
      grid[10][8] = '#64748b'; grid[10][9] = '#94a3b8'; grid[10][10] = '#cbd5e1'; grid[10][11] = '#0f172a'; grid[10][12] = '#0f172a'; grid[10][13] = '#cbd5e1'; grid[10][14] = '#94a3b8'; grid[10][15] = '#475569';
      grid[11][9] = '#64748b'; grid[11][10] = '#94a3b8'; grid[11][11] = '#0f172a'; grid[11][12] = '#0f172a'; grid[11][13] = '#94a3b8'; grid[11][14] = '#64748b';

      // Teeth / Jaw cleft
      grid[12][10] = '#cbd5e1'; grid[12][11] = '#0f172a'; grid[12][12] = '#0f172a'; grid[12][13] = '#cbd5e1';
      grid[13][10] = '#f8fafc'; grid[13][11] = '#475569'; grid[13][12] = '#475569'; grid[13][13] = '#f8fafc';

      // 3. Spore Filament Bulb Anchors (y: 14 to 15)
      grid[14][9] = '#581c87'; grid[14][10] = '#7e22ce'; grid[14][11] = '#581c87'; grid[14][12] = '#581c87'; grid[14][13] = '#7e22ce'; grid[14][14] = '#581c87';
      grid[15][8] = '#7e22ce'; grid[15][9] = '#a855f7'; grid[15][10] = '#581c87'; grid[15][11] = '#7e22ce'; grid[15][12] = '#7e22ce'; grid[15][13] = '#581c87'; grid[15][14] = '#a855f7'; grid[15][15] = '#7e22ce';
    }
    else if (unitType === 'ApostateLoaf_Eruption' || unitType === 'ApostateLoaf_SporeBurst') {
      // 𓏏 PANEL 4: CAP CLAMSHELL SPLIT & VOLCANIC SPORE MORTAR ERUPTION!
      // Matches Bottom-Right of example apostate loaf.jpeg reference.
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));

      // 1. Volcanic Spore Mortar Plume (y: 0 to 2)
      // Test anchor: grid[0][11] === '#c084fc'
      grid[0][9] = '#c026d3'; grid[0][10] = '#a855f7'; grid[0][11] = '#c084fc'; grid[0][12] = '#c084fc'; grid[0][13] = '#a855f7'; grid[0][14] = '#c026d3';
      grid[0][7] = '#7e22ce'; grid[0][8] = '#c084fc'; grid[0][15] = '#c084fc'; grid[0][16] = '#7e22ce';
      grid[1][8] = '#a855f7'; grid[1][9] = '#c084fc'; grid[1][10] = '#ffffff'; grid[1][11] = '#00f0ff'; grid[1][12] = '#00f0ff'; grid[1][13] = '#ffffff'; grid[1][14] = '#c084fc'; grid[1][15] = '#a855f7';
      grid[1][6] = '#c026d3'; grid[1][7] = '#e879f9'; grid[1][16] = '#e879f9'; grid[1][17] = '#c026d3';
      grid[2][6] = '#7e22ce'; grid[2][7] = '#a855f7'; grid[2][8] = '#00f0ff'; grid[2][9] = '#67e8f9'; grid[2][10] = '#ffffff'; grid[2][11] = '#ffffff'; grid[2][12] = '#ffffff'; grid[2][13] = '#ffffff'; grid[2][14] = '#67e8f9'; grid[2][15] = '#00f0ff'; grid[2][16] = '#a855f7'; grid[2][17] = '#7e22ce';

      // 2. Split Clamshell Cap Wings (curling outward to sides)
      // Left Wing (x: 2 to 6, y: 3 to 7)
      grid[3][3] = '#6e594d'; grid[3][4] = '#856a5d'; grid[3][5] = '#6e594d';
      grid[4][2] = '#504040'; grid[4][3] = '#6e594d'; grid[4][4] = '#856a5d'; grid[4][5] = '#382e29';
      grid[5][2] = '#382e29'; grid[5][3] = '#504040'; grid[5][4] = '#6e594d'; grid[5][5] = '#06b6d4';
      grid[6][2] = '#241e1a'; grid[6][3] = '#382e29'; grid[6][4] = '#0891b2'; grid[6][5] = '#00f0ff';
      grid[7][3] = '#1a1512'; grid[7][4] = '#0891b2'; grid[7][5] = '#00f0ff';

      // Right Wing (x: 17 to 21, y: 3 to 7)
      grid[3][18] = '#6e594d'; grid[3][19] = '#856a5d'; grid[3][20] = '#6e594d';
      grid[4][18] = '#382e29'; grid[4][19] = '#856a5d'; grid[4][20] = '#6e594d'; grid[4][21] = '#504040';
      grid[5][18] = '#06b6d4'; grid[5][19] = '#6e594d'; grid[5][20] = '#504040'; grid[5][21] = '#382e29';
      grid[6][18] = '#00f0ff'; grid[6][19] = '#0891b2'; grid[6][20] = '#382e29'; grid[6][21] = '#241e1a';
      grid[7][18] = '#00f0ff'; grid[7][19] = '#0891b2'; grid[7][20] = '#1a1512';

      // 3. Central Exposed Bioluminescent Spore Sponge (Inside opened crater x: 6 to 17, y: 3 to 7)
      // Test anchor: grid[4][11] === '#ffffff'
      grid[3][8] = '#06b6d4'; grid[3][9] = '#00f0ff'; grid[3][10] = '#ffffff'; grid[3][11] = '#ffffff'; grid[3][12] = '#ffffff'; grid[3][13] = '#ffffff'; grid[3][14] = '#00f0ff'; grid[3][15] = '#06b6d4';
      grid[4][7] = '#00f0ff'; grid[4][8] = '#22d3ee'; grid[4][9] = '#67e8f9'; grid[4][10] = '#ffffff'; grid[4][11] = '#ffffff'; grid[4][12] = '#ffffff'; grid[4][13] = '#ffffff'; grid[4][14] = '#67e8f9'; grid[4][15] = '#22d3ee'; grid[4][16] = '#00f0ff';
      grid[5][7] = '#06b6d4'; grid[5][8] = '#00f0ff'; grid[5][9] = '#22d3ee'; grid[5][10] = '#67e8f9'; grid[5][11] = '#ffffff'; grid[5][12] = '#ffffff'; grid[5][13] = '#67e8f9'; grid[5][14] = '#22d3ee'; grid[5][15] = '#00f0ff'; grid[5][16] = '#06b6d4';
      grid[6][7] = '#0891b2'; grid[6][8] = '#06b6d4'; grid[6][9] = '#00f0ff'; grid[6][10] = '#22d3ee'; grid[6][11] = '#22d3ee'; grid[6][12] = '#22d3ee'; grid[6][13] = '#22d3ee'; grid[6][14] = '#00f0ff'; grid[6][15] = '#06b6d4'; grid[6][16] = '#0891b2';
      grid[7][8] = '#155e75'; grid[7][9] = '#0891b2'; grid[7][10] = '#06b6d4'; grid[7][11] = 'TEAM'; grid[7][12] = 'TEAM'; grid[7][13] = '#06b6d4'; grid[7][14] = '#0891b2'; grid[7][15] = '#155e75';

      // 4. THE PROMINENT CHITIN TORSO / "STAMM" (Rows 8 to 11)
      // Absorbing volcanic mortar recoil with ribbed chitin trunk
      grid[8][7]  = '#241e1a'; grid[8][8]  = '#4a3d35'; grid[8][9]  = '#7a5848'; grid[8][10] = '#5a4035';
      grid[8][11] = '#9a6e58'; grid[8][12] = '#9a6e58';
      grid[8][13] = '#5a4035'; grid[8][14] = '#7a5848'; grid[8][15] = '#4a3d35'; grid[8][16] = '#241e1a';

      grid[9][7]  = '#1a1512'; grid[9][8]  = '#5a4035'; grid[9][9]  = '#3b2d26'; grid[9][10] = '#856a5d';
      grid[9][11] = '#856a5d'; grid[9][12] = '#856a5d';
      grid[9][13] = '#856a5d'; grid[9][14] = '#3b2d26'; grid[9][15] = '#5a4035'; grid[9][16] = '#1a1512';

      grid[10][8]  = '#241e1a'; grid[10][9]  = '#3b2d26'; grid[10][10] = '#5a4035';
      grid[10][11] = '#7a5848'; grid[10][12] = '#7a5848';
      grid[10][13] = '#5a4035'; grid[10][14] = '#3b2d26'; grid[10][15] = '#241e1a';

      grid[11][9]  = '#1a1512'; grid[11][10] = '#3b2d26';
      grid[11][11] = '#06b6d4'; grid[11][12] = '#06b6d4';
      grid[11][13] = '#3b2d26'; grid[11][14] = '#1a1512';

      // 5. Suspended Spine & Head (hanging below torso)
      grid[12][10] = '#00f0ff'; grid[12][11] = '#22d3ee'; grid[12][12] = '#22d3ee'; grid[12][13] = '#00f0ff';
      grid[13][10] = '#0284c7'; grid[13][11] = '#ffffff'; grid[13][12] = '#00f0ff'; grid[13][13] = '#0284c7';
      grid[14][10] = '#0f172a'; grid[14][11] = '#0284c7'; grid[14][12] = '#0284c7'; grid[14][13] = '#0f172a';
      grid[15][11] = '#00f0ff'; grid[15][12] = '#06b6d4';
      grid[16][11] = '#0891b2'; grid[16][12] = '#0891b2';

      // 6. Six Braced Stilt Legs (Absorbing mortar recoil with warm chitin shading, reaching row 23)
      _addApostateOuterRearLeg(grid, true,  5, 2, 12, 0, 23, 1);
      _addApostateOuterRearLeg(grid, false, 5, 21, 12, 23, 23, 22);

      _addApostateMidLeg(grid, true,  6, 4, 14, 2, 23, 5);
      _addApostateMidLeg(grid, false, 6, 19, 14, 21, 23, 18);

      _addApostateFrontLeg(grid, true,  7, 6, 23, 7);
      _addApostateFrontLeg(grid, false, 7, 17, 23, 16);
    }
    else if (unitType === 'ApostateLoaf_Attack' || unitType === 'ApostateLoaf_Charge') {
      // 𓏏 PANEL 3: ATTACK CHARGE / RADIANT CYAN HEAD AURA & SPORE MOTES
      // Matches Bottom-Left of example apostate loaf.jpeg reference.
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));

      // 1. Spore sparkles floating around cap (y: 0 to 4)
      grid[0][11] = '#c084fc'; grid[0][12] = '#c084fc';
      grid[1][7] = '#e879f9'; grid[1][16] = '#e879f9';
      grid[2][5] = '#38bdf8'; grid[2][18] = '#38bdf8';
      grid[4][3] = '#c084fc'; grid[4][20] = '#c084fc';

      // 2. Dome Cap (Wide segmented umbrella dome)
      grid[1][9]  = '#382e29'; grid[1][10] = '#504040'; grid[1][11] = '#6e594d'; grid[1][12] = '#856a5d'; grid[1][13] = '#6e594d'; grid[1][14] = '#504040'; grid[1][15] = '#382e29';
      grid[2][6]  = '#241e1a'; grid[2][7]  = '#382e29'; grid[2][8]  = '#504040'; grid[2][9]  = '#6e594d'; grid[2][10] = '#856a5d';
      grid[2][11] = '#6e594d'; grid[2][12] = '#856a5d'; grid[2][13] = '#6e594d'; grid[2][14] = '#856a5d'; grid[2][15] = '#6e594d';
      grid[2][16] = '#504040'; grid[2][17] = '#382e29'; grid[2][18] = '#241e1a';

      grid[3][4]  = '#1a1512'; grid[3][5]  = '#2e2520'; grid[3][6]  = '#382e29'; grid[3][7]  = '#504040'; grid[3][8]  = '#6e594d';
      grid[3][9]  = '#856a5d'; grid[3][10] = '#382e29'; grid[3][11] = '#856a5d'; grid[3][12] = '#856a5d'; grid[3][13] = '#382e29';
      grid[3][14] = '#6e594d'; grid[3][15] = '#856a5d'; grid[3][16] = '#504040'; grid[3][17] = '#382e29'; grid[3][18] = '#2e2520'; grid[3][19] = '#1a1512';

      grid[4][3]  = '#1a1512'; grid[4][4]  = '#241e1a'; grid[4][5]  = '#382e29'; grid[4][6]  = '#504040'; grid[4][7]  = '#6e594d';
      grid[4][8]  = '#504040'; grid[4][9]  = '#382e29'; grid[4][10] = '#504040'; grid[4][11] = '#6e594d'; grid[4][12] = '#6e594d';
      grid[4][13] = '#504040'; grid[4][14] = '#382e29'; grid[4][15] = '#504040'; grid[4][16] = '#6e594d'; grid[4][17] = '#504040';
      grid[4][18] = '#382e29'; grid[4][19] = '#241e1a'; grid[4][20] = '#1a1512';

      grid[5][3]  = '#151210'; grid[5][4]  = '#1a1512'; grid[5][5]  = '#241e1a'; grid[5][6]  = '#382e29'; grid[5][7]  = '#241e1a';
      grid[5][8]  = '#382e29'; grid[5][9]  = '#1a1512'; grid[5][10] = 'TEAM';   grid[5][11] = 'TEAM';   grid[5][12] = '#1a1512';
      grid[5][13] = '#382e29'; grid[5][14] = '#241e1a'; grid[5][15] = '#382e29'; grid[5][16] = '#241e1a'; grid[5][17] = '#1a1512';
      grid[5][2]  = '#3b322a'; grid[5][21] = '#3b322a';

      // 3. Bioluminescent Gills (Rows 6-7)
      grid[6][4]  = '#0891b2'; grid[6][5]  = '#06b6d4'; grid[6][6]  = '#22d3ee'; grid[6][7]  = '#06b6d4';
      grid[6][8]  = '#00f0ff'; grid[6][9]  = '#22d3ee'; grid[6][10] = '#00f0ff'; grid[6][11] = '#06b6d4';
      grid[6][12] = '#06b6d4'; grid[6][13] = '#00f0ff'; grid[6][14] = '#22d3ee'; grid[6][15] = '#00f0ff';
      grid[6][16] = '#06b6d4'; grid[6][17] = '#22d3ee'; grid[6][18] = '#06b6d4'; grid[6][19] = '#0891b2';

      // 4. THE PROMINENT CHITIN TORSO / "STAMM" (Rows 7 to 11)
      // Chitin trunk anchoring legs while gathering radiant attack power
      grid[7][7]  = '#1a1512'; grid[7][8]  = '#2e2520'; grid[7][9]  = '#4a3d35'; grid[7][10] = '#6e594d';
      grid[7][11] = '#5a4035'; grid[7][12] = '#5a4035';
      grid[7][13] = '#6e594d'; grid[7][14] = '#4a3d35'; grid[7][15] = '#2e2520'; grid[7][16] = '#1a1512';

      grid[8][7]  = '#241e1a'; grid[8][8]  = '#4a3d35'; grid[8][9]  = '#7a5848'; grid[8][10] = '#5a4035';
      grid[8][11] = '#9a6e58'; grid[8][12] = '#9a6e58';
      grid[8][13] = '#5a4035'; grid[8][14] = '#7a5848'; grid[8][15] = '#4a3d35'; grid[8][16] = '#241e1a';

      grid[9][7]  = '#1a1512'; grid[9][8]  = '#5a4035'; grid[9][9]  = '#3b2d26'; grid[9][10] = '#856a5d';
      grid[9][11] = '#856a5d'; grid[9][12] = '#856a5d';
      grid[9][13] = '#856a5d'; grid[9][14] = '#3b2d26'; grid[9][15] = '#5a4035'; grid[9][16] = '#1a1512';

      grid[10][8]  = '#241e1a'; grid[10][9]  = '#3b2d26'; grid[10][10] = '#5a4035';
      grid[10][11] = '#7a5848'; grid[10][12] = '#7a5848';
      grid[10][13] = '#5a4035'; grid[10][14] = '#3b2d26'; grid[10][15] = '#241e1a';

      grid[11][9]  = '#1a1512'; grid[11][10] = '#3b2d26';
      grid[11][11] = '#06b6d4'; grid[11][12] = '#06b6d4';
      grid[11][13] = '#3b2d26'; grid[11][14] = '#1a1512';

      // 5. Suspended Head & Radiant Cyan Attack Charge Sphere
      grid[12][8] = '#38bdf8'; grid[12][9] = '#00f0ff'; grid[12][10] = '#e0f2fe';
      grid[12][11] = '#ffffff'; grid[12][12] = '#ffffff';
      grid[12][13] = '#e0f2fe'; grid[12][14] = '#00f0ff'; grid[12][15] = '#38bdf8';

      grid[13][7] = '#00f0ff'; grid[13][8] = '#22d3ee'; grid[13][9] = '#e0f2fe';
      grid[13][10] = '#ffffff'; grid[13][11] = '#ffffff'; grid[13][12] = '#ffffff'; grid[13][13] = '#ffffff';
      grid[13][14] = '#e0f2fe'; grid[13][15] = '#22d3ee'; grid[13][16] = '#00f0ff';

      grid[14][8] = '#0284c7'; grid[14][9] = '#00f0ff'; grid[14][10] = '#38bdf8';
      grid[14][11] = '#e0f2fe'; grid[14][12] = '#e0f2fe';
      grid[14][13] = '#38bdf8'; grid[14][14] = '#00f0ff'; grid[14][15] = '#0284c7';

      grid[15][9] = '#0891b2'; grid[15][10] = '#0284c7'; grid[15][11] = '#00f0ff'; grid[15][12] = '#00f0ff';
      grid[15][13] = '#0284c7'; grid[15][14] = '#0891b2';

      grid[16][10] = '#0891b2'; grid[16][11] = '#00f0ff'; grid[16][12] = '#00f0ff'; grid[16][13] = '#0891b2';

      // 6. Six Braced Arachnid Stilt Legs (Feet reach row 23, longer than head!)
      _addApostateOuterRearLeg(grid, true,  5, 2, 12, 0, 23, 1);
      _addApostateOuterRearLeg(grid, false, 5, 21, 12, 23, 23, 22);

      _addApostateMidLeg(grid, true,  6, 4, 14, 2, 23, 5);
      _addApostateMidLeg(grid, false, 6, 19, 14, 21, 23, 18);

      _addApostateFrontLeg(grid, true,  7, 6, 23, 7);
      _addApostateFrontLeg(grid, false, 7, 17, 23, 16);
    }
    else if (unitType === 'ApostateLoaf_Walk1' || unitType === 'ApostateLoaf_Drift') {
      // 𓏏 INSECT WALKCYCLE — PHASE 1: TRIPOD A CONTACT (L1, R2, L3 forward), TRIPOD B PUSH REAR (R1, L2, R3 back)
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));
      _buildApostateBody(grid);

      // ── TRIPOD A (L1, R2, L3) FORWARD CONTACT at row 23
      _addApostateOuterRearLeg(grid, true,  5, 2, 11, 0, 23, 2);  // L3 forward contact
      _addApostateMidLeg(grid, false, 6, 19, 13, 21, 23, 17);    // R2 forward contact
      _addApostateFrontLeg(grid, true, 7, 6, 23, 6);              // L1 forward contact

      // ── TRIPOD B (R1, L2, R3) PUSH BACK at row 23
      _addApostateOuterRearLeg(grid, false, 5, 22, 13, 23, 23, 23); // R3 pushed back
      _addApostateMidLeg(grid, true, 6, 4, 15, 2, 23, 4);          // L2 pushed back
      _addApostateFrontLeg(grid, false, 7, 17, 23, 17);            // R1 pushed back
    }
    else if (unitType === 'ApostateLoaf_Walk2') {
      // 𓏏 INSECT WALKCYCLE — PHASE 2: TRIPOD B HIGH LIFT & STEP (R1, L2, R3 LIFTED IN AIR!)
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));
      _buildApostateBody(grid);

      // ── TRIPOD A (L1, R2, L3) STANCE (Planted on ground at row 23)
      _addApostateOuterRearLeg(grid, true,  5, 2, 12, 0, 23, 1);   // L3 stance
      _addApostateMidLeg(grid, false, 6, 19, 14, 21, 23, 18);     // R2 stance
      _addApostateFrontLeg(grid, true, 7, 6, 23, 7);               // L1 stance

      // ── TRIPOD B (R1, L2, R3) HIGH LIFT SWING (Lifting knees & claws 4-5px in air!)
      _addApostateOuterRearLeg(grid, false, 4, 21, 10, 22, 19, 22); // R3 high lift in air!
      _addApostateMidLeg(grid, true, 5, 3, 11, 2, 18, 3);          // L2 high lift in air!
      _addApostateFrontLeg(grid, false, 6, 16, 18, 15);            // R1 high lift in air!
    }
    else if (unitType === 'ApostateLoaf_Walk3') {
      // 𓏏 INSECT WALKCYCLE — PHASE 3: TRIPOD B CONTACT (R1, L2, R3 forward), TRIPOD A PUSH REAR (L1, R2, L3 back)
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));
      _buildApostateBody(grid);

      // ── TRIPOD B (R1, L2, R3) FORWARD CONTACT at row 23
      _addApostateOuterRearLeg(grid, false, 5, 21, 11, 23, 23, 21); // R3 forward contact
      _addApostateMidLeg(grid, true, 6, 4, 13, 2, 23, 6);          // L2 forward contact
      _addApostateFrontLeg(grid, false, 7, 17, 23, 16);            // R1 forward contact

      // ── TRIPOD A (L1, R2, L3) PUSH BACK at row 23
      _addApostateOuterRearLeg(grid, true,  5, 1, 13, 0, 23, 0);   // L3 pushed back
      _addApostateMidLeg(grid, false, 6, 19, 15, 21, 23, 20);      // R2 pushed back
      _addApostateFrontLeg(grid, true, 7, 6, 23, 8);               // L1 pushed back
    }
    else if (unitType === 'ApostateLoaf_Walk4') {
      // 𓏏 INSECT WALKCYCLE — PHASE 4: TRIPOD A HIGH LIFT & STEP (L1, R2, L3 LIFTED IN AIR!)
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));
      _buildApostateBody(grid);

      // ── TRIPOD B (R1, L2, R3) STANCE (Planted on ground at row 23)
      _addApostateOuterRearLeg(grid, false, 5, 21, 12, 23, 23, 22); // R3 stance
      _addApostateMidLeg(grid, true, 6, 4, 14, 2, 23, 5);          // L2 stance
      _addApostateFrontLeg(grid, false, 7, 17, 23, 16);            // R1 stance

      // ── TRIPOD A (L1, R2, L3) HIGH LIFT SWING (Lifting knees & claws 4-5px in air!)
      _addApostateOuterRearLeg(grid, true,  4, 2, 10, 1, 19, 1);   // L3 high lift in air!
      _addApostateMidLeg(grid, false, 5, 20, 11, 21, 18, 20);      // R2 high lift in air!
      _addApostateFrontLeg(grid, true, 6, 7, 18, 8);               // L1 high lift in air!
    }
    else if (unitType === 'ApostateLoaf' || unitType === 'Apostate_Loaf' || unitType === 'Apostate Loaf' || unitType === 'ApostateLoaf_Front') {
      // 𓏏 CANONICAL FRONTAL STRIDER STANCE (Idle Resting Hexapod Pose)
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));
      _buildApostateBody(grid);

      // ── SIX THICK, HIGH-ARCHED ARACHNID STILT LEGS (Feet reach row 23, longer than head!)
      _addApostateOuterRearLeg(grid, true,  5, 2, 12, 0, 23, 1);
      _addApostateOuterRearLeg(grid, false, 5, 21, 12, 23, 23, 22);

      _addApostateMidLeg(grid, true,  6, 4, 14, 2, 23, 5);
      _addApostateMidLeg(grid, false, 6, 19, 14, 21, 23, 18);

      _addApostateFrontLeg(grid, true,  7, 6, 23, 7);
      _addApostateFrontLeg(grid, false, 7, 17, 23, 16);
    }
    else if (unitType === 'ForgottenLoaf_Walk1') {
      // 𓏏 ARACHNID TRIPOD GAIT — PHASE 1: TRIPOD A CONTACT (rear-left, mid-right, front-left forward), TRIPOD B PUSH (rear-right, mid-left, front-right back)
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));
      _addForgottenLoafLeg(grid, true,  10, 7,  4, 2,  21, 1,   2, 0, false); // Rear-left (Tripod A forward contact)
      _addForgottenLoafLeg(grid, false, 10, 16, 4, 21, 21, 22, -2, 0, false); // Rear-right (Tripod B push back)
      _buildForgottenLoafBody(grid);
      _addForgottenLoafLeg(grid, true,  11, 6,  5, 1,  23, 0,  -1, 0, true);  // Mid-left (Tripod B push back)
      _addForgottenLoafLeg(grid, false, 11, 17, 5, 22, 23, 23,  1, 0, true);  // Mid-right (Tripod A forward contact)
      _addForgottenLoafLeg(grid, true,  12, 8,  7, 4,  23, 6,   2, 0, true);  // Front-left (Tripod A forward contact)
      _addForgottenLoafLeg(grid, false, 12, 15, 7, 19, 23, 17, -2, 0, true);  // Front-right (Tripod B push back)
    }
    else if (unitType === 'ForgottenLoaf_Walk2') {
      // 𓏏 ARACHNID TRIPOD GAIT — PHASE 2: TRIPOD A STANCE, TRIPOD B HIGH LIFT & STEP (rear-right, mid-left, front-right in air)
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));
      _addForgottenLoafLeg(grid, true,  10, 7,  4, 2,  21, 1,   0, 0, false); // Rear-left (Tripod A stance)
      _addForgottenLoafLeg(grid, false, 10, 16, 4, 21, 21, 22,  0, 3, false); // Rear-right (Tripod B lifted)
      _buildForgottenLoafBody(grid);
      _addForgottenLoafLeg(grid, true,  11, 6,  5, 1,  23, 0,   1, 4, true);  // Mid-left (Tripod B lifted high)
      _addForgottenLoafLeg(grid, false, 11, 17, 5, 22, 23, 23,  0, 0, true);  // Mid-right (Tripod A stance)
      _addForgottenLoafLeg(grid, true,  12, 8,  7, 4,  23, 6,   0, 0, true);  // Front-left (Tripod A stance)
      _addForgottenLoafLeg(grid, false, 12, 15, 7, 19, 23, 17,  1, 4, true);  // Front-right (Tripod B lifted high)
    }
    else if (unitType === 'ForgottenLoaf_Walk3') {
      // 𓏏 ARACHNID TRIPOD GAIT — PHASE 3: TRIPOD B CONTACT (rear-right, mid-left, front-right forward), TRIPOD A PUSH (rear-left, mid-right, front-left back)
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));
      _addForgottenLoafLeg(grid, true,  10, 7,  4, 2,  21, 1,  -2, 0, false); // Rear-left (Tripod A push back)
      _addForgottenLoafLeg(grid, false, 10, 16, 4, 21, 21, 22,  2, 0, false); // Rear-right (Tripod B forward contact)
      _buildForgottenLoafBody(grid);
      _addForgottenLoafLeg(grid, true,  11, 6,  5, 1,  23, 0,   2, 0, true);  // Mid-left (Tripod B forward contact)
      _addForgottenLoafLeg(grid, false, 11, 17, 5, 22, 23, 23, -2, 0, true);  // Mid-right (Tripod A push back)
      _addForgottenLoafLeg(grid, true,  12, 8,  7, 4,  23, 6,  -2, 0, true);  // Front-left (Tripod A push back)
      _addForgottenLoafLeg(grid, false, 12, 15, 7, 19, 23, 17,  2, 0, true);  // Front-right (Tripod B forward contact)
    }
    else if (unitType === 'ForgottenLoaf_Walk4') {
      // 𓏏 ARACHNID TRIPOD GAIT — PHASE 4: TRIPOD B STANCE, TRIPOD A HIGH LIFT & STEP (rear-left, mid-right, front-left in air)
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));
      _addForgottenLoafLeg(grid, true,  10, 7,  4, 2,  21, 1,   0, 3, false); // Rear-left (Tripod A lifted)
      _addForgottenLoafLeg(grid, false, 10, 16, 4, 21, 21, 22,  0, 0, false); // Rear-right (Tripod B stance)
      _buildForgottenLoafBody(grid);
      _addForgottenLoafLeg(grid, true,  11, 6,  5, 1,  23, 0,   0, 0, true);  // Mid-left (Tripod B stance)
      _addForgottenLoafLeg(grid, false, 11, 17, 5, 22, 23, 23, -1, 4, true);  // Mid-right (Tripod A lifted high)
      _addForgottenLoafLeg(grid, true,  12, 8,  7, 4,  23, 6,   1, 4, true);  // Front-left (Tripod A lifted high)
      _addForgottenLoafLeg(grid, false, 12, 15, 7, 19, 23, 17,  0, 0, true);  // Front-right (Tripod B stance)
    }
    else if (unitType === 'ForgottenLoaf' || unitType === 'Forgotten_Loaf' || unitType === 'Forgotten Loaf' || unitType === 'ForgottenLoaf_Front') {
      // 𓏏 SUL FORGOTTEN LOAF (24x24): SPIDER MINE & GLOWING LIQUID TANK (Resting Hexapod Pose)
      // - Segmented chitinous Sul Rumpf (torso/chassis) with bioluminescent vent fissures
      // - Compact spherical bio-fluid tank resting securely in dorsal cradle mount
      // - 6 long spider stilt legs with clear 2-segment articulation (Oberschenkel + Unterschenkel)
      //   and prominent amber knee knuckles
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));

      // 1. Background Spider Legs (Far legs, darker chitin #1c0d09, #27130d)
      // Oberschenkel arches from torso coxa up to high knee (row 4); Unterschenkel plunges to ground
      _addForgottenLoafLeg(grid, true,  10,  7, 4,  2, 21,  1, 0, 0, false); // Rear-left (bg)
      _addForgottenLoafLeg(grid, false, 10, 16, 4, 21, 21, 22, 0, 0, false); // Rear-right (bg)

      // 2. Compact Glass Sphere & Sul Rumpf Chassis
      _buildForgottenLoafBody(grid);

      // 3. Foreground Spider Legs (Mid & Front Legs, articulated with high-arched knees)
      // Mid legs: wide stance, high knee at row 5, sharp plunge to row 23
      _addForgottenLoafLeg(grid, true,  11, 6,   5,  1, 23,  0, 0, 0, true);  // Mid-left
      _addForgottenLoafLeg(grid, false, 11, 17,  5, 22, 23, 23, 0, 0, true);  // Mid-right

      // Front legs: lunging forward, knee at row 7, sharp plunge to row 23
      _addForgottenLoafLeg(grid, true,  12, 8,   7,  4, 23,  6, 0, 0, true);  // Front-left
      _addForgottenLoafLeg(grid, false, 12, 15,  7, 19, 23, 17, 0, 0, true);  // Front-right
    }
    else if (unitType === 'DelvingPillar_Drift') {
      // 𓉐 DELVING PILLAR (DRIFT): AERODYNAMIC CRUISE PROFILE (Facing RIGHT)
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));

      // 1. Sleek Streamlined Carapace (Dorsal shell flattened along airflow)
      grid[2][8] = '#2f4f34'; grid[2][9] = '#3e6344'; grid[2][10] = '#56825c'; grid[2][11] = '#85ab8c';
      grid[2][12] = '#56825c'; grid[2][13] = '#3e6344'; grid[2][14] = '#2f4f34';

      grid[3][6] = '#1f3723'; grid[3][7] = '#2f4f34'; grid[3][8] = '#56825c'; grid[3][9] = '#85ab8c';
      grid[3][10] = '#56825c'; grid[3][11] = '#3e6344'; grid[3][12] = '#2f4f34'; grid[3][13] = '#56825c';
      grid[3][14] = '#85ab8c'; grid[3][15] = '#3e6344'; grid[3][16] = '#1f3723';

      grid[4][4] = '#142417'; grid[4][5] = '#2f4f34'; grid[4][6] = 'TEAM'; grid[4][7] = 'TEAM';
      grid[4][8] = '#56825c'; grid[4][9] = '#2f4f34'; grid[4][10] = '#ca8a04'; grid[4][11] = '#facc15';
      grid[4][12] = '#fef08a'; grid[4][13] = '#142417'; grid[4][14] = '#3e6344'; grid[4][15] = '#56825c';
      grid[4][16] = '#3e6344'; grid[4][17] = '#1f3723'; grid[4][18] = '#142417';

      grid[5][4] = '#142417'; grid[5][5] = 'TEAM'; grid[5][6] = 'TEAM'; grid[5][7] = '#3e6344';
      grid[5][8] = '#142417'; grid[5][9] = '#ca8a04'; grid[5][10] = '#facc15'; grid[5][11] = '#142417';
      grid[5][12] = '#2f4f34'; grid[5][13] = '#56825c'; grid[5][14] = '#3e6344'; grid[5][15] = '#1f3723';
      grid[5][16] = '#3f0808'; grid[5][17] = '#6b1212'; grid[5][18] = '#3f0808';

      // 2. Thrusting Snout & Forward Ocular Array (Piercing airflow to the RIGHT)
      grid[6][5] = '#142417'; grid[6][6] = '#3f0808'; grid[6][7] = '#6b1212'; grid[6][8] = '#991b1b';
      grid[6][9] = '#6b1212'; grid[6][10] = '#3f0808'; grid[6][11] = '#6b1212'; grid[6][12] = '#ca8a04';
      grid[6][13] = '#facc15'; grid[6][14] = '#fef08a'; grid[6][15] = '#991b1b'; grid[6][16] = '#facc15';
      grid[6][17] = '#fef08a'; grid[6][18] = '#ffffff'; grid[6][19] = '#facc15'; grid[6][20] = '#3f0808';

      grid[7][5] = '#3f0808'; grid[7][6] = '#6b1212'; grid[7][7] = '#991b1b'; grid[7][8] = '#b91c1c';
      grid[7][9] = '#ca8a04'; grid[7][10] = '#facc15'; grid[7][11] = '#fef08a'; grid[7][12] = '#ca8a04';
      grid[7][13] = '#dc2626'; grid[7][14] = '#facc15'; grid[7][15] = '#ffffff'; grid[7][16] = '#facc15';
      grid[7][17] = '#fef08a'; grid[7][18] = '#ffffff'; grid[7][19] = '#facc15'; grid[7][20] = '#6b1212'; grid[7][21] = '#3f0808';

      grid[8][5] = '#3f0808'; grid[8][6] = '#6b1212'; grid[8][7] = '#991b1b'; grid[8][8] = '#dc2626';
      grid[8][9] = '#facc15'; grid[8][10] = '#ffffff'; grid[8][11] = '#facc15'; grid[8][12] = '#991b1b';
      grid[8][13] = '#facc15'; grid[8][14] = '#ffffff'; grid[8][15] = '#facc15'; grid[8][16] = '#ca8a04';
      grid[8][17] = '#facc15'; grid[8][18] = '#fef08a'; grid[8][19] = '#ca8a04'; grid[8][20] = '#6b1212'; grid[8][21] = '#3f0808';

      grid[9][6] = '#3f0808'; grid[9][7] = '#6b1212'; grid[9][8] = '#991b1b'; grid[9][9] = '#ca8a04';
      grid[9][10] = '#facc15'; grid[9][11] = '#fef08a'; grid[9][12] = '#ca8a04'; grid[9][13] = '#6b1212';
      grid[9][14] = '#ca8a04'; grid[9][15] = '#facc15'; grid[9][16] = '#ffffff'; grid[9][17] = '#facc15';
      grid[9][18] = '#ca8a04'; grid[9][19] = '#6b1212'; grid[9][20] = '#3f0808';

      grid[10][6] = '#0f172a'; grid[10][7] = '#3f0808'; grid[10][8] = '#6b1212'; grid[10][9] = '#991b1b';
      grid[10][10] = '#6b1212'; grid[10][11] = '#3f0808'; grid[10][12] = '#6b1212'; grid[10][13] = '#ca8a04';
      grid[10][14] = '#facc15'; grid[10][15] = '#ca8a04'; grid[10][16] = '#6b1212'; grid[10][17] = '#3f0808';

      // 3. Swept-Back Muscle Sinew & Conduit Cuffs (At rear-left)
      grid[11][3] = '#0f172a'; grid[11][4] = '#1e293b'; grid[11][5] = '#334155'; grid[11][6] = '#1e293b';
      grid[11][7] = '#0f172a'; grid[11][8] = '#3f0808'; grid[11][9] = '#6b1212'; grid[11][10] = '#991b1b';
      grid[11][11] = '#6b1212'; grid[11][12] = '#3f0808';

      grid[12][2] = '#0f172a'; grid[12][3] = '#1e293b'; grid[12][4] = '#334155'; grid[12][5] = '#1e293b';
      grid[12][6] = '#0f172a'; grid[12][7] = '#3f0808'; grid[12][8] = '#7f1d1d'; grid[12][9] = '#b91c1c';
      grid[12][10] = '#7f1d1d'; grid[12][11] = '#3f0808';

      grid[13][3] = '#0f172a'; grid[13][4] = '#1e293b'; grid[13][5] = '#0f172a';
      grid[13][6] = '#450a0a'; grid[13][7] = '#991b1b'; grid[13][8] = '#dc2626'; grid[13][9] = '#991b1b'; grid[13][10] = '#450a0a';

      grid[14][4] = '#450a0a'; grid[14][5] = '#7f1d1d'; grid[14][6] = '#991b1b'; grid[14][7] = '#7f1d1d'; grid[14][8] = '#450a0a';
    }
    else if (unitType === 'DelvingPillar_Attack' || unitType === 'DelvingPillar_Scan') {
      // 𓉐 DELVING PILLAR (ATTACK / SCAN OVERDRIVE): WHITE-HOT SENSORY OVERCLOCK (Facing RIGHT)
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));

      // 1. Flaring Carapace Plates & Exposed Glowing Vents (Top & Rear)
      grid[1][8] = '#3e6344'; grid[1][9] = '#56825c'; grid[1][10] = '#85ab8c'; grid[1][11] = '#ffffff';
      grid[1][12] = '#ffffff'; grid[1][13] = '#85ab8c'; grid[1][14] = '#56825c'; grid[1][15] = '#3e6344';

      grid[2][6] = '#2f4f34'; grid[2][7] = '#3e6344'; grid[2][8] = '#85ab8c'; grid[2][9] = '#ffffff';
      grid[2][10] = '#ffffff'; grid[2][11] = '#ffffff'; grid[2][12] = '#ffffff'; grid[2][13] = '#85ab8c';
      grid[2][14] = '#3e6344'; grid[2][15] = '#2f4f34'; grid[2][16] = '#1f3723';

      grid[3][4] = '#1f3723'; grid[3][5] = '#2f4f34'; grid[3][6] = '#56825c'; grid[3][7] = '#facc15';
      grid[3][8] = '#ffffff'; grid[3][9] = '#ffffff'; grid[3][10] = '#ffffff'; grid[3][11] = '#ffffff';
      grid[3][12] = '#facc15'; grid[3][13] = '#56825c'; grid[3][14] = '#2f4f34'; grid[3][15] = '#3e6344';
      grid[3][16] = '#56825c'; grid[3][17] = '#3e6344'; grid[3][18] = '#1f3723';

      grid[4][3] = '#142417'; grid[4][4] = '#2f4f34'; grid[4][5] = 'TEAM'; grid[4][6] = 'TEAM';
      grid[4][7] = '#56825c'; grid[4][8] = '#facc15'; grid[4][9] = '#ffffff'; grid[4][10] = '#ffffff';
      grid[4][11] = '#ffffff'; grid[4][12] = '#facc15'; grid[4][13] = '#2f4f34'; grid[4][14] = '#3e6344';
      grid[4][15] = '#56825c'; grid[4][16] = '#85ab8c'; grid[4][17] = '#3e6344'; grid[4][18] = '#142417';

      grid[5][3] = '#142417'; grid[5][4] = 'TEAM'; grid[5][5] = 'TEAM'; grid[5][6] = '#3e6344';
      grid[5][7] = '#142417'; grid[5][8] = '#fef08a'; grid[5][9] = '#ffffff'; grid[5][10] = '#fef08a';
      grid[5][11] = '#142417'; grid[5][12] = '#2f4f34'; grid[5][13] = '#3e6344'; grid[5][14] = '#2f4f34';
      grid[5][15] = '#3f0808'; grid[5][16] = '#6b1212'; grid[5][17] = '#facc15'; grid[5][18] = '#ffffff'; grid[5][19] = '#3f0808';

      // 2. White-Hot Radiant Compound Ocular Array (Snout facing RIGHT)
      grid[6][3] = '#142417'; grid[6][4] = '#1f3723'; grid[6][5] = '#3f0808'; grid[6][6] = '#6b1212';
      grid[6][7] = '#991b1b'; grid[6][8] = '#dc2626'; grid[6][9] = '#ffffff'; grid[6][10] = '#ffffff';
      grid[6][11] = '#dc2626'; grid[6][12] = '#6b1212'; grid[6][13] = '#991b1b'; grid[6][14] = '#facc15';
      grid[6][15] = '#ffffff'; grid[6][16] = '#ffffff'; grid[6][17] = '#ffffff'; grid[6][18] = '#facc15';
      grid[6][19] = '#ef4444'; grid[6][20] = '#ffffff'; grid[6][21] = '#facc15'; grid[6][22] = '#3f0808';

      grid[7][4] = '#3f0808'; grid[7][5] = '#6b1212'; grid[7][6] = '#991b1b'; grid[7][7] = '#dc2626';
      grid[7][8] = '#ffffff'; grid[7][9] = '#ffffff'; grid[7][10] = '#ef4444'; grid[7][11] = '#ffffff';
      grid[7][12] = '#ffffff'; grid[7][13] = '#dc2626'; grid[7][14] = '#ffffff'; grid[7][15] = '#ffffff';
      grid[7][16] = '#ffffff'; grid[7][17] = '#ffffff'; grid[7][18] = '#ffffff'; grid[7][19] = '#facc15';
      grid[7][20] = '#ffffff'; grid[7][21] = '#fef08a'; grid[7][22] = '#6b1212'; grid[7][23] = '#3f0808';

      grid[8][4] = '#3f0808'; grid[8][5] = '#6b1212'; grid[8][6] = '#991b1b'; grid[8][7] = '#dc2626';
      grid[8][8] = '#ffffff'; grid[8][9] = '#ffffff'; grid[8][10] = '#ffffff'; grid[8][11] = '#ffffff';
      grid[8][12] = '#ef4444'; grid[8][13] = '#ffffff'; grid[8][14] = '#ffffff'; grid[8][15] = '#ffffff';
      grid[8][16] = '#ffffff'; grid[8][17] = '#ffffff'; grid[8][18] = '#ffffff'; grid[8][19] = '#ef4444';
      grid[8][20] = '#ffffff'; grid[8][21] = '#ffffff'; grid[8][22] = '#facc15'; grid[8][23] = '#6b1212';

      grid[9][4] = '#3f0808'; grid[9][5] = '#6b1212'; grid[9][6] = '#991b1b'; grid[9][7] = '#ef4444';
      grid[9][8] = '#ffffff'; grid[9][9] = '#ffffff'; grid[9][10] = '#ffffff'; grid[9][11] = '#ffffff';
      grid[9][12] = '#ef4444'; grid[9][13] = '#ffffff'; grid[9][14] = '#ffffff'; grid[9][15] = '#ffffff';
      grid[9][16] = '#ffffff'; grid[9][17] = '#ffffff'; grid[9][18] = '#ffffff'; grid[9][19] = '#ffffff';
      grid[9][20] = '#facc15'; grid[9][21] = '#fef08a'; grid[9][22] = '#6b1212';

      grid[10][4] = '#3f0808'; grid[10][5] = '#6b1212'; grid[10][6] = '#991b1b'; grid[10][7] = '#dc2626';
      grid[10][8] = '#ffffff'; grid[10][9] = '#ffffff'; grid[10][10] = '#ffffff'; grid[10][11] = '#dc2626';
      grid[10][12] = '#991b1b'; grid[10][13] = '#6b1212'; grid[10][14] = '#dc2626'; grid[10][15] = '#ffffff';
      grid[10][16] = '#ffffff'; grid[10][17] = '#ffffff'; grid[10][18] = '#facc15'; grid[10][19] = '#991b1b';
      grid[10][20] = '#6b1212'; grid[10][21] = '#3f0808';

      grid[11][3] = '#0f172a'; grid[11][4] = '#3f0808'; grid[11][5] = '#6b1212'; grid[11][6] = '#991b1b';
      grid[11][7] = '#dc2626'; grid[11][8] = '#facc15'; grid[11][9] = '#ffffff'; grid[11][10] = '#ffffff';
      grid[11][11] = '#facc15'; grid[11][12] = '#6b1212'; grid[11][13] = '#991b1b'; grid[11][14] = '#dc2626';
      grid[11][15] = '#ffffff'; grid[11][16] = '#facc15'; grid[11][17] = '#6b1212'; grid[11][18] = '#3f0808';

      // 3. Supercharged Visceral Muscle & Tentacle Cuffs (Rear-Left)
      grid[12][2] = '#0f172a'; grid[12][3] = '#1e293b'; grid[12][4] = '#334155'; grid[12][5] = '#1e293b';
      grid[12][6] = '#0f172a'; grid[12][7] = '#6b1212'; grid[12][8] = '#991b1b'; grid[12][9] = '#dc2626';
      grid[12][10] = '#ef4444'; grid[12][11] = '#dc2626'; grid[12][12] = '#991b1b'; grid[12][13] = '#6b1212';
      grid[12][14] = '#3f0808';

      grid[13][2] = '#0f172a'; grid[13][3] = '#1e293b'; grid[13][4] = '#334155'; grid[13][5] = '#475569';
      grid[13][6] = '#334155'; grid[13][7] = '#1e293b'; grid[13][8] = '#6b1212'; grid[13][9] = '#991b1b';
      grid[13][10] = '#dc2626'; grid[13][11] = '#b91c1c'; grid[13][12] = '#6b1212'; grid[13][13] = '#3f0808';

      grid[14][2] = '#0f172a'; grid[14][3] = '#1e293b'; grid[14][4] = '#334155'; grid[14][5] = '#1e293b';
      grid[14][6] = '#0f172a'; grid[14][7] = '#6b1212'; grid[14][8] = '#b91c1c'; grid[14][9] = '#ef4444';
      grid[14][10] = '#dc2626'; grid[14][11] = '#b91c1c'; grid[14][12] = '#6b1212';

      grid[15][3] = '#0f172a'; grid[15][4] = '#1e293b'; grid[15][5] = '#0f172a';
      grid[15][6] = '#7f1d1d'; grid[15][7] = '#dc2626'; grid[15][8] = '#f87171'; grid[15][9] = '#ef4444';
      grid[15][10] = '#dc2626'; grid[15][11] = '#7f1d1d';

      grid[16][4] = '#7f1d1d'; grid[16][5] = '#b91c1c'; grid[16][6] = '#dc2626'; grid[16][7] = '#b91c1c';
      grid[16][8] = '#7f1d1d';
    }
    else if (unitType === 'DelvingPillar' || unitType === 'Delving_Pillar' || unitType === 'Delving Pillar' || unitType === 'DelvingPillar_Hover') {
      // 𓉐 DELVING PILLAR: TIER 2 UNCOMMON SUL AIR SENTINEL & DETECTOR BEHEMOTH
      // 3/4 Directional Profile (Facing RIGHT: carapace dorsal/top-rear, snout & golden ocular array at front-right)
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));

      // 1. Dorsal Segmented Carapace (Olive-Metallic Chitin Shell, arched over top and rear)
      grid[1][7] = '#1f3723'; grid[1][8] = '#2f4f34'; grid[1][9] = '#3e6344'; grid[1][10] = '#56825c';
      grid[1][11] = '#85ab8c'; grid[1][12] = '#56825c'; grid[1][13] = '#3e6344'; grid[1][14] = '#2f4f34'; grid[1][15] = '#1f3723';

      grid[2][5] = '#142417'; grid[2][6] = '#1f3723'; grid[2][7] = '#2f4f34'; grid[2][8] = '#3e6344';
      grid[2][9] = '#56825c'; grid[2][10] = '#85ab8c'; grid[2][11] = '#85ab8c'; grid[2][12] = '#56825c';
      grid[2][13] = '#3e6344'; grid[2][14] = '#2f4f34'; grid[2][15] = '#3e6344'; grid[2][16] = '#56825c'; grid[2][17] = '#2f4f34'; grid[2][18] = '#142417';

      // Row 3: Carapace plate ridges & dorsal sensory dome lens
      grid[3][4] = '#142417'; grid[3][5] = '#1f3723'; grid[3][6] = '#2f4f34'; grid[3][7] = '#3e6344';
      grid[3][8] = '#142417'; // Plate seam
      grid[3][9] = '#ca8a04'; grid[3][10] = '#facc15'; grid[3][11] = '#fef08a'; grid[3][12] = '#ca8a04'; // Dorsal shell eye dome
      grid[3][13] = '#142417'; grid[3][14] = '#3e6344'; grid[3][15] = '#56825c'; grid[3][16] = '#85ab8c';
      grid[3][17] = '#56825c'; grid[3][18] = '#3e6344'; grid[3][19] = '#1f3723'; grid[3][20] = '#142417';

      // Row 4: Carapace flank with TEAM identification & snout brow rim
      grid[4][3] = '#142417'; grid[4][4] = '#1f3723'; grid[4][5] = 'TEAM'; grid[4][6] = 'TEAM'; grid[4][7] = 'TEAM';
      grid[4][8] = '#2f4f34'; grid[4][9] = '#713f12'; grid[4][10] = '#eab308'; grid[4][11] = '#facc15';
      grid[4][12] = '#713f12'; grid[4][13] = '#142417'; grid[4][14] = '#2f4f34'; grid[4][15] = '#3e6344';
      grid[4][16] = '#56825c'; grid[4][17] = '#3e6344'; grid[4][18] = '#2f4f34'; grid[4][19] = '#1f3723'; grid[4][20] = '#142417';

      // Row 5: Lower carapace rim, second TEAM marking, brow ridge curving into snout
      grid[5][3] = '#142417'; grid[5][4] = '#2f4f34'; grid[5][5] = 'TEAM'; grid[5][6] = 'TEAM';
      grid[5][7] = '#3e6344'; grid[5][8] = '#1f3723'; grid[5][9] = '#142417'; grid[5][10] = '#ca8a04';
      grid[5][11] = '#facc15'; grid[5][12] = '#142417'; grid[5][13] = '#1f3723'; grid[5][14] = '#2f4f34';
      grid[5][15] = '#3e6344'; grid[5][16] = '#2f4f34'; grid[5][17] = '#1f3723'; grid[5][18] = '#142417';
      grid[5][19] = '#3f0808'; grid[5][20] = '#6b1212'; grid[5][21] = '#3f0808';

      // Row 6: Transition from rear chitin shell to visceral crimson tissue & forward snout eyes
      grid[6][3] = '#142417'; grid[6][4] = '#1f3723'; grid[6][5] = '#2f4f34'; grid[6][6] = '#1f3723';
      grid[6][7] = '#142417'; grid[6][8] = '#3f0808'; grid[6][9] = '#6b1212'; grid[6][10] = '#991b1b';
      grid[6][11] = '#6b1212'; grid[6][12] = '#3f0808'; grid[6][13] = '#6b1212'; grid[6][14] = '#ca8a04';
      grid[6][15] = '#facc15'; grid[6][16] = '#fef08a'; grid[6][17] = '#ca8a04'; grid[6][18] = '#991b1b';
      grid[6][19] = '#dc2626'; grid[6][20] = '#facc15'; grid[6][21] = '#fef08a'; grid[6][22] = '#991b1b'; grid[6][23] = '#3f0808';

      // Row 7: Striated crimson neck & primary compound snout ocular array
      grid[7][4] = '#142417'; grid[7][5] = '#3f0808'; grid[7][6] = '#6b1212'; grid[7][7] = '#991b1b';
      grid[7][8] = '#7f1d1d'; grid[7][9] = '#3f0808'; grid[7][10] = '#6b1212'; grid[7][11] = '#ca8a04';
      grid[7][12] = '#facc15'; grid[7][13] = '#ca8a04'; grid[7][14] = '#991b1b'; grid[7][15] = '#facc15';
      grid[7][16] = '#ffffff'; grid[7][17] = '#fef08a'; grid[7][18] = '#facc15'; grid[7][19] = '#dc2626';
      grid[7][20] = '#fef08a'; grid[7][21] = '#ffffff'; grid[7][22] = '#facc15'; grid[7][23] = '#3f0808';

      // Row 8: Dense compound eyes & fibrous muscle tendons
      grid[8][4] = '#3f0808'; grid[8][5] = '#6b1212'; grid[8][6] = '#991b1b'; grid[8][7] = '#b91c1c';
      grid[8][8] = '#6b1212'; grid[8][9] = '#3f0808'; grid[8][10] = '#6b1212'; grid[8][11] = '#facc15';
      grid[8][12] = '#fef08a'; grid[8][13] = '#facc15'; grid[8][14] = '#991b1b'; grid[8][15] = '#facc15';
      grid[8][16] = '#ffffff'; grid[8][17] = '#facc15'; grid[8][18] = '#ca8a04'; grid[8][19] = '#facc15';
      grid[8][20] = '#ffffff'; grid[8][21] = '#facc15'; grid[8][22] = '#ca8a04'; grid[8][23] = '#6b1212';

      // Row 9: Main central detector row — CRITICAL TEST INVARIANT:
      // grid[9][10] === '#facc15' and grid[9][11] === '#ffffff'
      grid[9][4] = '#3f0808'; grid[9][5] = '#6b1212'; grid[9][6] = '#991b1b'; grid[9][7] = '#dc2626';
      grid[9][8] = '#991b1b'; grid[9][9] = '#6b1212'; grid[9][10] = '#facc15'; grid[9][11] = '#ffffff';
      grid[9][12] = '#fef08a'; grid[9][13] = '#facc15'; grid[9][14] = '#991b1b'; grid[9][15] = '#ca8a04';
      grid[9][16] = '#facc15'; grid[9][17] = '#ffffff'; grid[9][18] = '#facc15'; grid[9][19] = '#eab308';
      grid[9][20] = '#facc15'; grid[9][21] = '#ca8a04'; grid[9][22] = '#3f0808';

      // Row 10: Lower snout curvature & forward beak
      grid[10][4] = '#3f0808'; grid[10][5] = '#6b1212'; grid[10][6] = '#991b1b'; grid[10][7] = '#b91c1c';
      grid[10][8] = '#991b1b'; grid[10][9] = '#6b1212'; grid[10][10] = '#ca8a04'; grid[10][11] = '#facc15';
      grid[10][12] = '#fef08a'; grid[10][13] = '#ca8a04'; grid[10][14] = '#6b1212'; grid[10][15] = '#991b1b';
      grid[10][16] = '#facc15'; grid[10][17] = '#ffffff'; grid[10][18] = '#facc15'; grid[10][19] = '#991b1b';
      grid[10][20] = '#6b1212'; grid[10][21] = '#3f0808';

      // Row 11: Under-snout curvature & lower ocular sensors
      grid[11][3] = '#0f172a'; grid[11][4] = '#3f0808'; grid[11][5] = '#6b1212'; grid[11][6] = '#991b1b';
      grid[11][7] = '#7f1d1d'; grid[11][8] = '#6b1212'; grid[11][9] = '#3f0808'; grid[11][10] = '#6b1212';
      grid[11][11] = '#ca8a04'; grid[11][12] = '#facc15'; grid[11][13] = '#ca8a04'; grid[11][14] = '#6b1212';
      grid[11][15] = '#991b1b'; grid[11][16] = '#facc15'; grid[11][17] = '#ca8a04'; grid[11][18] = '#6b1212';
      grid[11][19] = '#3f0808';

      // Row 12: Ventral throat, cybernetic cable collars & snout cleft
      grid[12][2] = '#0f172a'; grid[12][3] = '#1e293b'; grid[12][4] = '#334155'; grid[12][5] = '#1e293b';
      grid[12][6] = '#0f172a'; grid[12][7] = '#3f0808'; grid[12][8] = '#6b1212'; grid[12][9] = '#991b1b';
      grid[12][10] = '#6b1212'; grid[12][11] = '#3f0808'; grid[12][12] = '#6b1212'; grid[12][13] = '#ca8a04';
      grid[12][14] = '#facc15'; grid[12][15] = '#6b1212'; grid[12][16] = '#3f0808'; grid[12][17] = '#142417';

      // Row 13: Conduit collars & muscle roots (Swept back toward rear)
      grid[13][2] = '#0f172a'; grid[13][3] = '#1e293b'; grid[13][4] = '#334155'; grid[13][5] = '#475569';
      grid[13][6] = '#334155'; grid[13][7] = '#1e293b'; grid[13][8] = '#0f172a'; grid[13][9] = '#3f0808';
      grid[13][10] = '#6b1212'; grid[13][11] = '#991b1b'; grid[13][12] = '#b91c1c'; grid[13][13] = '#7f1d1d';
      grid[13][14] = '#3f0808'; grid[13][15] = '#142417';

      // Row 14: Tentacle base root cuffs (Rear/bottom left)
      grid[14][2] = '#0f172a'; grid[14][3] = '#1e293b'; grid[14][4] = '#334155'; grid[14][5] = '#1e293b';
      grid[14][6] = '#0f172a'; grid[14][7] = '#3f0808'; grid[14][8] = '#6b1212'; grid[14][9] = '#991b1b';
      grid[14][10] = '#dc2626'; grid[14][11] = '#991b1b'; grid[14][12] = '#6b1212'; grid[14][13] = '#3f0808';

      // Row 15: Tentacle socket rings & root tendons
      grid[15][2] = '#0f172a'; grid[15][3] = '#1e293b'; grid[15][4] = '#0f172a';
      grid[15][5] = '#3f0808'; grid[15][6] = '#6b1212'; grid[15][7] = '#991b1b'; grid[15][8] = '#dc2626';
      grid[15][9] = '#991b1b'; grid[15][10] = '#6b1212'; grid[15][11] = '#3f0808';

      // Row 16: Ventral tentacle roots
      grid[16][3] = '#0f172a'; grid[16][4] = '#0f172a';
      grid[16][5] = '#3f0808'; grid[16][6] = '#6b1212'; grid[16][7] = '#991b1b'; grid[16][8] = '#b91c1c';
      grid[16][9] = '#6b1212'; grid[16][10] = '#3f0808';

      // Row 17: Tapering root tips
      grid[17][4] = '#3f0808'; grid[17][5] = '#6b1212'; grid[17][6] = '#991b1b'; grid[17][7] = '#7f1d1d'; grid[17][8] = '#3f0808';
    }
    else if (unitType === 'HarvestingPillar_Stalk') {
      const pData = _buildHarvestingPillarGrid('stalk');
      grid = pData.grid;
      gridSize = pData.gridSize;
    }
    else if (unitType === 'HarvestingPillar_Harvest') {
      const pData = _buildHarvestingPillarGrid('harvest');
      grid = pData.grid;
      gridSize = pData.gridSize;
    }
    else if (unitType === 'HarvestingPillar_Harvest2') {
      const pData = _buildHarvestingPillarGrid('harvest2');
      grid = pData.grid;
      gridSize = pData.gridSize;
    }
    else if (unitType === 'HarvestingPillar' || unitType === 'Harvesting_Pillar' || unitType === 'Harvesting Pillar' || unitType === 'HarvestingPillar_Idle') {
      const pData = _buildHarvestingPillarGrid('idle');
      grid = pData.grid;
      gridSize = pData.gridSize;
    }
    else if (unitType === 'ProcessingPillar_Charge') {
      const pData = _buildProcessingPillarGrid('charge');
      grid = pData.grid;
      gridSize = pData.gridSize;
    }
    else if (unitType === 'ProcessingPillar_Attack') {
      const pData = _buildProcessingPillarGrid('attack');
      grid = pData.grid;
      gridSize = pData.gridSize;
    }
    else if (unitType === 'ProcessingPillar_Head') {
      const pData = _buildProcessingPillarGrid('head');
      grid = pData.grid;
      gridSize = pData.gridSize;
    }
    else if (unitType === 'ProcessingPillar_Body') {
      const pData = _buildProcessingPillarGrid('body');
      grid = pData.grid;
      gridSize = pData.gridSize;
    }
    else if (unitType === 'ProcessingPillar' || unitType === 'Processing_Pillar' || unitType === 'Processing Pillar' || unitType === 'ProcessingPillar_Idle') {
      const pData = _buildProcessingPillarGrid('idle');
      grid = pData.grid;
      gridSize = pData.gridSize;
    }
    else if (unitType === 'ClaimingPillar' || unitType === 'Claiming_Pillar' || unitType === 'Claiming Pillar' || unitType === 'ClaimingPillar_Idle') {
      const pData = _buildClaimingPillarGrid();
      grid = pData.grid;
      gridSize = pData.gridSize;
    }
    else if (unitType === 'MonitorLock' || unitType === 'Monitor_Lock' || unitType === 'Monitor Lock') {
      // 𓉐 SUL MONITOR LOCK: Giant serpentine guardian worm emerging from earth crater.
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));
      _buildMonitorLock(grid);
    }
    else if (unitType === 'MonitorLock_Crater') {
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));
      _buildMonitorLockCrater(grid);
    }
    else if (unitType === 'MonitorLock_Crater_Front') {
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));
      _buildMonitorLockCraterFront(grid);
    }
    else if (unitType === 'MonitorLock_Body') {
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));
      _buildMonitorLockBody(grid);
    }
    else if (unitType === 'MonitorLock_Head') {
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));
      _buildMonitorLockHead(grid);
    }
    else if (unitType === 'MonitorLock_Head_Bite') {
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));
      _buildMonitorLockHead(grid);
      _addMonitorLockOpenJaw(grid);
    }
    else if (unitType === 'MonitorLock_Head_Face') {
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));
      _buildMonitorLockHead(grid, false);
    }
    else if (unitType === 'MonitorLock_Head_Bite_Face') {
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));
      _buildMonitorLockHead(grid, false);
      _addMonitorLockOpenJaw(grid);
    }
    else if (unitType === 'PreserverLock' || unitType === 'Preserver_Lock' || unitType === 'Preserver Lock') {
      // Six-legged biomechanical tank: horn-suspended plasma orb, armored
      // tail carapace, elephantine tripod-gait stance. This combined,
      // neutral-pose bitmap is kept as a fallback/thumbnail sprite (card
      // showroom, deck builder); the animated battlefield render instead
      // composites 'PreserverLock_Body' on top of one of the
      // 'PreserverLock_Legs_*' frames below (js/engine/render/units.js) so
      // the legs can cycle independently of the body.
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));
      _buildPreserverLockBody(grid);
      // Feet spread wide apart in x per pair (8/4/0 and mirrored 15/19/23) so
      // all 6 legs stay visually distinct at 24x24 instead of collapsing
      // into one thick blob - front pair angled forward, rear pair trailing
      // back near the tail plate, mid pair dropping straight down.
      _addPreserverLockLeg(grid, true,  12, 9,  16, 9,  20, 8);
      _addPreserverLockLeg(grid, false, 12, 14, 16, 14, 20, 15);
      _addPreserverLockLeg(grid, true,  14, 6,  18, 5,  22, 4);
      _addPreserverLockLeg(grid, false, 14, 17, 18, 18, 22, 19);
      _addPreserverLockLeg(grid, true,  16, 3,  19, 1,  22, 0);
      _addPreserverLockLeg(grid, false, 16, 20, 19, 22, 22, 23);
    }
    else if (unitType === 'PreserverLock_Body' || unitType.startsWith('PreserverLock_Body_') ||
             unitType === 'Preserver_Lock_Body' || unitType.startsWith('Preserver_Lock_Body_')) {
      // Body-only layer (horn, orb, cable, torso, tail) - no legs, so
      // units.js can composite it with its own independent bob/roll on top
      // of whichever leg frame is currently planted.
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));
      _buildPreserverLockBody(grid);
    }
    else if (unitType === 'PreserverLock_Legs' || unitType === 'PreserverLock_Legs_Stand' ||
             unitType === 'PreserverLock_Legs_Walk1' || unitType === 'PreserverLock_Legs_Walk2' ||
             unitType === 'PreserverLock_Legs_Walk3' || unitType === 'PreserverLock_Legs_Walk4') {
      // Legs-only undercarriage, animated as an uneven tripod gait: Group A
      // (front-left, mid-right, rear-left) and Group B (front-right,
      // mid-left, rear-right) swing in alternation. Each stride is
      // deliberately asymmetric - +2 forward & lifted vs. -1 trailing &
      // grounded - so the gait reads as a slight stagger ("torkeln") rather
      // than a perfectly smooth machine walk. Walk2/Walk4 are the neutral
      // passing pose between strides (same as Stand).
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));
      let strideA = 0, liftA = 0, strideB = 0, liftB = 0;
      if (unitType === 'PreserverLock_Legs_Walk1') { strideA = 2; liftA = 2; strideB = -1; liftB = 0; }
      else if (unitType === 'PreserverLock_Legs_Walk3') { strideB = 2; liftB = 2; strideA = -1; liftA = 0; }

      _addPreserverLockLeg(grid, true,  12, 9,  16, 9,  20, 8,  strideA, liftA); // front-left  (A)
      _addPreserverLockLeg(grid, false, 12, 14, 16, 14, 20, 15, strideB, liftB); // front-right (B)
      _addPreserverLockLeg(grid, true,  14, 6,  18, 5,  22, 4,  strideB, liftB); // mid-left    (B)
      _addPreserverLockLeg(grid, false, 14, 17, 18, 18, 22, 19, strideA, liftA); // mid-right   (A)
      _addPreserverLockLeg(grid, true,  16, 3,  19, 1,  22, 0,  strideA, liftA); // rear-left   (A)
      _addPreserverLockLeg(grid, false, 16, 20, 19, 22, 22, 23, strideB, liftB); // rear-right  (B)
    }
    else if (unitType === 'SentinalLock' || unitType === 'Sentinal_Lock' || unitType === 'Sentinal Lock') {
      // Combined fallback pose (card showroom / deck builder thumbnail,
      // never the animated battlefield render): the static body with one
      // wing panel baked on in its neutral, undistorted "pointing up" pose,
      // just so the silhouette reads as whole without the live per-frame
      // squash animation units.js applies on the battlefield.
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));
      _buildSentinalLockBody(grid);
      _addSentinalLockWingStrutPanel(grid, 11, 12, -Math.PI / 2, 2, 8, 5, SENTINAL_LOCK_WING_STRUT_COLOR, SENTINAL_LOCK_WING_PANEL_COLORS, SENTINAL_LOCK_WING_EDGE_COLOR);
    }
    else if (unitType === 'SentinalLock_Roll1' || unitType === 'SentinalLock_Roll2' || unitType === 'SentinalLock_Roll3' || unitType === 'SentinalLock_Roll4') {
      const rollIdx = parseInt(unitType.slice(-1), 10) - 1;
      gridSize = 24;
      grid = _buildSentinalLockRoll(rollIdx);
    }
    else if (unitType === 'SentinalLock_Body') {
      // Static fuselage - head, torso, tail, claws. NEVER rotated; see the
      // file comment on _buildSentinalLockBody for why a barrel roll around
      // the flight axis must leave this alone.
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));
      _buildSentinalLockBody(grid);
    }
    else if (unitType === 'SentinalLock_WingPanel') {
      // One wing, neutral pose (rooted at the bottom-center, pointing
      // straight up) - units.js draws three instances of this per frame,
      // each with its own live vertical squash to fake the barrel roll.
      gridSize = 24;
      grid = Array.from({ length: 24 }, () => Array(24).fill(null));
      _buildSentinalLockWingPanel(grid);
    }
    else if (unitType === 'EnforcerVulture' || unitType === 'Enforcer_Vulture' || unitType === 'Enforcer Vulture' || unitType === 'EnforcerVulture_Body' || unitType === 'EnforcerVulture_Head') {
      const hdData = getHDSpriteGrid(unitType) || getHDSpriteGrid('EnforcerVulture');
      if (hdData) {
        grid = hdData.grid;
        gridSize = hdData.gridSize;
      }
    }
    else if (unitType === 'LootingVulture' || unitType === 'Looting_Vulture' || unitType === 'Looting Vulture' || unitType === 'LootingVulture_Body' || unitType === 'LootingVulture_Pyramid' || unitType === 'LootingVulture_Head' || unitType === 'LootingVulture_Tail') {
      const hdData = getHDSpriteGrid(unitType) || getHDSpriteGrid('LootingVulture');
      if (hdData) {
        grid = hdData.grid;
        gridSize = hdData.gridSize;
      }
    }
    else if (unitType.startsWith('ParasiteVulture') || unitType === 'Parasite_Vulture' || unitType === 'Parasite Vulture') {
      const hdData = getHDSpriteGrid(unitType) || getHDSpriteGrid('ParasiteVulture');
      if (hdData) {
        grid = hdData.grid;
        gridSize = hdData.gridSize;
      }
    }
    else if (hasHDSprite(unitType)) {
      const hdData = getHDSpriteGrid(unitType);
      if (hdData) {
        grid = hdData.grid;
        gridSize = hdData.gridSize;
      }
    }
    else if (unitType.startsWith('Panzer')) {
      const hdData = getHDSpriteGrid('Panzer');
      if (hdData) { grid = hdData.grid; gridSize = hdData.gridSize; }
    }
    else if (unitType.startsWith('HyperPanzer') || unitType.startsWith('Hyper_Panzer')) {
      const hdData = getHDSpriteGrid('HyperPanzer');
      if (hdData) { grid = hdData.grid; gridSize = hdData.gridSize; }
    }
    else if (unitType.startsWith('Orchestra')) {
      const hdData = getHDSpriteGrid('Orchestra');
      if (hdData) { grid = hdData.grid; gridSize = hdData.gridSize; }
    }
    else if (unitType.startsWith('TheSalvageCompany')) {
      const hdData = getHDSpriteGrid('TheSalvageCompany');
      if (hdData) { grid = hdData.grid; gridSize = hdData.gridSize; }
    }
    else if (unitType.startsWith('A9Grizzly')) {
      const hdData = getHDSpriteGrid('A9Grizzly');
      if (hdData) { grid = hdData.grid; gridSize = hdData.gridSize; }
    }
    else {
      gridSize = 12;
      grid = Array.from({ length: 12 }, () => Array(12).fill(null));
      for (let y = 3; y <= 8; y++) for (let x = 3; x <= 8; x++) grid[y][x] = '#64748b';
      grid[5][5] = 'TEAM';
      grid[4][9] = '#facc15'; grid[5][9] = '#facc15';
    }

    const spriteData = { grid, gridSize };
    globalSpriteCache[cacheKey] = spriteData;
    return spriteData;
}

// ==========================================
// PRE-RENDERED SPRITE BITMAPS
// ==========================================
// renderUnit() used to redraw every unit's procedural grid pixel-by-pixel,
// every frame - up to 18x18=324 fillRect calls per unit. Every (unitType,
// race, team) combination looks identical from one instance to the next, so
// we bake each one to an offscreen canvas exactly once and blit it with a
// single drawImage() afterwards. This is also where the pixel-art passes
// that only need to happen once live: integer-snapped cells, a baked 1px
// dark silhouette outline, and directional (top-lit / bottom-shadowed)
// shading instead of the old per-frame "brighten any pixel with empty space
// above it" hack.
//
// Trade-off: the old loop also animated individual leg/wing pixel rows and
// flickered thruster-colored pixels between two colors every frame. Baking
// the sprite to a bitmap means those two per-pixel animations are dropped -
// the whole-sprite bounce/hover/recoil motion (still computed live in
// renderUnit) remains as the movement feedback instead.
const spriteBitmapCache = new Map();
const SPRITE_PIXEL_UNIT = 4; // px per grid cell in the canonical baked bitmap

function drawSilhouetteOutline(ctx, resolved, gridSize, color) {
  ctx.fillStyle = color;
  for (let gy = 0; gy < gridSize; gy++) {
    for (let gx = 0; gx < gridSize; gx++) {
      if (!resolved[gy][gx]) continue;
      const neighbors = [[-1, 0], [1, 0], [0, -1], [0, 1]];
      for (const [ox, oy] of neighbors) {
        const ngx = gx + ox, ngy = gy + oy;
        const neighborFilled = ngy >= 0 && ngy < gridSize && ngx >= 0 && ngx < gridSize && resolved[ngy][ngx];
        if (!neighborFilled) {
          ctx.fillRect(gx * SPRITE_PIXEL_UNIT + ox, gy * SPRITE_PIXEL_UNIT + oy, SPRITE_PIXEL_UNIT, SPRITE_PIXEL_UNIT);
        }
      }
    }
  }
}

if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('ucg_hd_mode_changed', () => {
    clearSpriteBitmapCache();
    clearAuraBitmapCache();
    clearStatueBitmapCache();
  });
}

export function clearSpriteBitmapCache() {
  for (const entry of spriteBitmapCache.values()) {
    if (!entry) continue;
    if (typeof entry._release === 'function') {
      entry._release();
    } else if (entry.normal) {
      entry.normal.width = 0;
      entry.normal.height = 0;
    }
  }
  spriteBitmapCache.clear();
  _spriteFast[0].clear(); _spriteFast[1].clear(); _spriteFast[2].clear(); _spriteFast[3].clear();
  if (_thermalHeatStrip) {
    _thermalHeatStrip.width = 0;
    _thermalHeatStrip.height = 0;
    _thermalHeatStrip = null;
  }
}

// Allocation-free front cache for buildSpriteBitmap(): the renderer asks for
// the same (unitType, race, team) bitmaps several times per unit per frame,
// and the canonical string key (`${type}_${race}_${team}_hd`) cost a fresh
// string + hash on every one of those lookups. Indexed by the HD/UHD mode
// bits (so a runtime mode toggle never serves a stale entry), then nested
// Maps type -> race -> team. Entries are only ever added together with the
// canonical cache and dropped together with it.
const _spriteFast = [new Map(), new Map(), new Map(), new Map()];

export function buildSpriteBitmap(unitType, race, team) {
  const modeBits = (isHDEnabled() ? 1 : 0) | (isUHDEnabled() ? 2 : 0);
  const fastRoot = _spriteFast[modeBits];
  const byRace = fastRoot.get(unitType);
  if (byRace !== undefined) {
    const byTeam = byRace.get(race);
    if (byTeam !== undefined) {
      const hit = byTeam.get(team);
      if (hit !== undefined) return hit;
    }
  }
  const entry = _buildSpriteBitmapSlow(unitType, race, team);
  if (entry) {
    let r = fastRoot.get(unitType);
    if (r === undefined) { r = new Map(); fastRoot.set(unitType, r); }
    let t = r.get(race);
    if (t === undefined) { t = new Map(); r.set(race, t); }
    t.set(team, entry);
  }
  return entry;
}

function _buildSpriteBitmapSlow(unitType, race, team) {
  const isHD = isHDEnabled() && hasHDSprite(unitType);
  const key = `${unitType}_${race}_${team}_${isHD ? 'hd' : 'std'}`;
  const cached = spriteBitmapCache.get(key);
  if (cached) return cached;
  if (typeof document === 'undefined' && typeof OffscreenCanvas === 'undefined') {
    return null;
  }

  const spriteData = (isHD ? getHDSpriteGrid(unitType) : null) || getSpriteGrid(unitType, race);
  if (!spriteData || !spriteData.grid) return null;
  const { grid, gridSize } = spriteData;
  const isWestern = team === 'western';
  const teamBadgeColor = isWestern ? '#00f0ff' : '#ff003c';
  const size = gridSize * SPRITE_PIXEL_UNIT;

  // ProcessingPillar and BastionLock use articulated multi-layer kinematic rigs in units.js
  // that apply `ctx.scale(dir, 1)` to handle horizontal facing. Pre-baking a horizontal mirror
  // here would cause eastern units to be flipped twice (inverted head/god rays/shields).
  const isRigFlipped = unitType.startsWith('ProcessingPillar') ||
    unitType.startsWith('BastionLock_Shield') ||
    unitType.startsWith('BastionLock_Body') ||
    unitType.startsWith('BastionLock_Legs');

  // Resolve each cell's final color once: team-badge substitution + the
  // per-team horizontal mirror both baked directly into the bitmap.
  const resolved = [];
  for (let gy = 0; gy < gridSize; gy++) {
    const row = [];
    for (let gx = 0; gx < gridSize; gx++) {
      const col = (isWestern || isRigFlipped) ? gx : (gridSize - 1 - gx);
      let c = grid[gy][col];
      if (c === 'TEAM') c = teamBadgeColor;
      row.push(c || null);
    }
    resolved.push(row);
  }

  const normal = document.createElement('canvas');
  normal.width = size;
  normal.height = size;
  const nctx = normal.getContext('2d');

  drawSilhouetteOutline(nctx, resolved, gridSize, '#05070b');

  for (let gy = 0; gy < gridSize; gy++) {
    for (let gx = 0; gx < gridSize; gx++) {
      const c = resolved[gy][gx];
      if (!c) continue;
      const px = gx * SPRITE_PIXEL_UNIT, py = gy * SPRITE_PIXEL_UNIT;
      nctx.fillStyle = c;
      nctx.fillRect(px, py, SPRITE_PIXEL_UNIT, SPRITE_PIXEL_UNIT);

      // Directional light (fixed upper-left source): brighten cells exposed
      // at the top, darken the underside of cells exposed at the bottom.
      const aboveFilled = gy > 0 && resolved[gy - 1][gx];
      const belowFilled = gy < gridSize - 1 && resolved[gy + 1][gx];
      if (!aboveFilled) {
        nctx.fillStyle = 'rgba(255, 255, 255, 0.30)';
        nctx.fillRect(px, py, SPRITE_PIXEL_UNIT, Math.max(1, SPRITE_PIXEL_UNIT * 0.4));
      }
      if (!belowFilled) {
        nctx.fillStyle = 'rgba(0, 0, 0, 0.28)';
        nctx.fillRect(px, py + SPRITE_PIXEL_UNIT * 0.65, SPRITE_PIXEL_UNIT, SPRITE_PIXEL_UNIT * 0.35);
      }
    }
  }

  let _flash = null;
  let _rim = null;
  const entry = {
    normal,
    size,
    get flash() {
      if (_flash) return _flash;
      if (typeof document === 'undefined' && typeof OffscreenCanvas === 'undefined') return null;
      _flash = (typeof document !== 'undefined' && document.createElement)
        ? document.createElement('canvas')
        : new OffscreenCanvas(size, size);
      _flash.width = size;
      _flash.height = size;
      const fctx = _flash.getContext('2d');
      drawSilhouetteOutline(fctx, resolved, gridSize, '#05070b');
      // 1. High-specular base texture: preserves internal mechanical contours under intense flash
      fctx.drawImage(normal, 0, 0);
      fctx.globalCompositeOperation = 'source-atop';
      fctx.fillStyle = 'rgba(255, 255, 255, 0.72)';
      fctx.fillRect(0, 0, size, size);
      fctx.globalCompositeOperation = 'source-over';
      // 2. Gleaming incandescent white on outer rim, armor peaks & weapon edges
      fctx.fillStyle = '#ffffff';
      for (let gy = 0; gy < gridSize; gy++) {
        for (let gx = 0; gx < gridSize; gx++) {
          if (!resolved[gy][gx]) continue;
          const isTop = gy === 0 || !resolved[gy - 1][gx];
          const isLeft = gx === 0 || !resolved[gy][gx - 1];
          const isRight = gx === gridSize - 1 || !resolved[gy][gx + 1];
          if (isTop || isLeft || isRight) {
            fctx.fillRect(gx * SPRITE_PIXEL_UNIT, gy * SPRITE_PIXEL_UNIT, SPRITE_PIXEL_UNIT, SPRITE_PIXEL_UNIT);
          }
        }
      }
      return _flash;
    },
    get rim() {
      if (_rim) return _rim;
      if (typeof document === 'undefined' && typeof OffscreenCanvas === 'undefined') return null;
      _rim = (typeof document !== 'undefined' && document.createElement)
        ? document.createElement('canvas')
        : new OffscreenCanvas(size, size);
      _rim.width = size;
      _rim.height = size;
      const rctx = _rim.getContext('2d');
      rctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      for (let gy = 0; gy < gridSize; gy++) {
        for (let gx = 0; gx < gridSize; gx++) {
          if (!resolved[gy][gx]) continue;
          const isTop = gy === 0 || !resolved[gy - 1][gx];
          const isLeft = gx === 0 || !resolved[gy][gx - 1];
          const isRight = gx === gridSize - 1 || !resolved[gy][gx + 1];
          if (isTop || isLeft || isRight) {
            rctx.fillRect(gx * SPRITE_PIXEL_UNIT, gy * SPRITE_PIXEL_UNIT, SPRITE_PIXEL_UNIT, Math.max(1, SPRITE_PIXEL_UNIT * 0.45));
          }
        }
      }
      return _rim;
    },
    _release() {
      if (normal) { normal.width = 0; normal.height = 0; }
      if (_flash) { _flash.width = 0; _flash.height = 0; _flash = null; }
      if (_rim) { _rim.width = 0; _rim.height = 0; _rim = null; }
    }
  };
  spriteBitmapCache.set(key, entry);
  return entry;
}

// ==========================================
// PRE-BAKED 24-CARAT GOLDEN STATUE BITMAP
// ==========================================
// Transmutes any unit's base sprite bitmap into a solid polished gold statue
// with antique dark-gold crevice shading and specular light reflections.
// Baked once per (unitType, race, team) and cached (zero CPU cost on render loop).
const statueBitmapCache = new Map();

export function clearStatueBitmapCache() {
  for (const entry of statueBitmapCache.values()) {
    if (!entry) continue;
    if (entry.normal) {
      entry.normal.width = 0;
      entry.normal.height = 0;
    }
    if (entry.flash && entry.flash !== entry.normal) {
      entry.flash.width = 0;
      entry.flash.height = 0;
    }
  }
  statueBitmapCache.clear();
}

export function buildGoldenStatueBitmap(unitType, race, team) {
  if (typeof document === 'undefined' && typeof OffscreenCanvas === 'undefined') {
    return null;
  }
  const baseBitmap = buildSpriteBitmap(unitType, race, team);
  if (!baseBitmap || !baseBitmap.normal) return baseBitmap;
  const isHD = isHDEnabled() && hasHDSprite(unitType);
  const key = `statue_${unitType}_${race}_${team}_${isHD ? 'hd' : 'std'}`;
  const cached = statueBitmapCache.get(key);
  if (cached) return cached;

  const size = baseBitmap.size || (24 * SPRITE_PIXEL_UNIT);
  let normalCanvas = null;
  if (typeof document !== 'undefined' && document.createElement) {
    normalCanvas = document.createElement('canvas');
    normalCanvas.width = size;
    normalCanvas.height = size;
    const ctx = normalCanvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(baseBitmap.normal, 0, 0);
      try {
        const imgData = ctx.getImageData(0, 0, size, size);
        const data = imgData.data;
        for (let i = 0; i < data.length; i += 4) {
          const a = data[i + 3];
          if (a < 20) continue;
          const r = data[i], g = data[i + 1], b = data[i + 2];
          const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
          let gr, gg, gb;
          if (lum < 0.18) {
            // Antique burnished bronze crevice shading
            const t = lum / 0.18;
            gr = 68 + t * 80; gg = 38 + t * 54; gb = 10 + t * 6;
          } else if (lum < 0.55) {
            // Warm gold transitioning into imperial 24k gold
            const t = (lum - 0.18) / 0.37;
            gr = 148 + t * 80; gg = 92 + t * 70; gb = 16 + t * 6;
          } else if (lum < 0.82) {
            // Brilliant lustrous solar yellow-gold
            const t = (lum - 0.55) / 0.27;
            gr = 228 + t * 27; gg = 162 + t * 56; gb = 22 + t * 32;
          } else {
            // Radiant white-gold specular edge sheen
            const t = (lum - 0.82) / 0.18;
            gr = 255; gg = 218 + t * 34; gb = 54 + t * 151;
          }
          data[i] = Math.min(255, Math.round(gr));
          data[i + 1] = Math.min(255, Math.round(gg));
          data[i + 2] = Math.min(255, Math.round(gb));
        }
        ctx.putImageData(imgData, 0, 0);
      } catch (_) {
        ctx.globalCompositeOperation = 'source-atop';
        ctx.fillStyle = 'rgba(250, 204, 21, 0.85)';
        ctx.fillRect(0, 0, size, size);
      }
    }
  }

  const result = { normal: normalCanvas || baseBitmap.normal, flash: normalCanvas || baseBitmap.flash, size };
  statueBitmapCache.set(key, result);
  return result;
}

// ==========================================
// PRE-RENDERED 3-FRAME PIXEL FLAME AURA
// ==========================================
// Pre-bakes a 3-frame animated pixel flame aura for any unit with an equipped
// aura cosmetic (or innate hero/melt aura). Dilation + directional upward flame
// tongues + detached rising wisps + 2-tone rim contouring are baked into
// offscreen canvas bitmaps once per (unitType, race, team, auraHex, frameIndex),
// costing exactly ONE drawImage() call per unit on the render loop (60 FPS solid).
const auraBitmapCache = new Map();

export function clearAuraBitmapCache() {
  for (const entry of auraBitmapCache.values()) {
    if (entry?.canvas) {
      entry.canvas.width = 0;
      entry.canvas.height = 0;
    }
  }
  auraBitmapCache.clear();
}

function parseAuraHexColor(hex) {
  if (!hex || typeof hex !== 'string') return { r: 0, g: 240, b: 255 };
  let clean = hex.replace('#', '').trim();
  if (clean.length === 3) {
    clean = clean[0] + clean[0] + clean[1] + clean[1] + clean[2] + clean[2];
  } else if (clean.length === 8) {
    clean = clean.slice(0, 6);
  }
  const num = parseInt(clean, 16);
  if (isNaN(num)) return { r: 0, g: 240, b: 255 };
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255
  };
}

export function buildAuraBitmap(unitType, race, team, auraHex = '#00f0ff', frameIndex = 0) {
  const normFrame = Math.abs(frameIndex | 0) % 3;
  const isHD = isHDEnabled() && hasHDSprite(unitType);
  const key = `${unitType}_${race}_${team}_${auraHex}_${normFrame}_${isHD ? 'hd' : 'std'}`;
  const cached = auraBitmapCache.get(key);
  if (cached) return cached;

  const spriteData = (isHD ? getHDSpriteGrid(unitType) : null) || getSpriteGrid(unitType, race);
  if (!spriteData || !spriteData.grid) return null;
  const { grid, gridSize } = spriteData;
  if (!grid || !gridSize) return null;

  const isWestern = team === 'western';
  const isStickmanAura = isHD && (unitType === 'Stickman' || unitType.startsWith('Stickman'));
  const isPulpAura = isHD && (unitType === 'Pulp' || unitType.startsWith('Pulp'));
  const isHotHandAura = isHD && (unitType === 'HotHand' || unitType.startsWith('HotHand') || unitType === 'Hot Hand');
  const isVehicleAura = unitType === 'Orchestra' || unitType.startsWith('Orchestra') ||
                        unitType === 'Panzer' || unitType.startsWith('Panzer') ||
                        unitType === 'HyperPanzer' ||
                        unitType === 'A9Grizzly' ||
                        unitType === 'TheSalvageCompany' ||
                        unitType === 'A9Piranha' ||
                        unitType === 'A9Pigeon' || unitType.startsWith('A9Pigeon');
  const scale = isHD ? (gridSize / 24) : 1;
  const padX = isHD ? Math.round(5 * scale) : 3;
  const padTop = isHD ? Math.round(10 * scale) : 6;
  const padBottom = isHD ? Math.round(3 * scale) : 2;
  const auraGridW = gridSize + padX * 2;
  const auraGridH = gridSize + padTop + padBottom;

  // 1. Resolve occupied cells with team mirroring
  const occupied = Array.from({ length: gridSize }, () => new Uint8Array(gridSize));
  let minGx = gridSize, maxGx = 0, minGy = gridSize, maxGy = 0;
  let hasAny = false;

  if (isStickmanAura) {
    // 🦾 Articulated Stickman walker aura:
    // Unifies the animated digitigrade stride (Stand + Walk1..8), chassis, torso, and lance breech core.
    // Bridges the core torso and pelvis to prevent hollow cutouts, ghost standing limbs, or static weapon silhouettes.
    const stickmanParts = [
      'Stickman_Torso',
      'Stickman_Chassis',
      'Stickman_Legs',
      'Stickman_Legs_Walk1',
      'Stickman_Legs_Walk2',
      'Stickman_Legs_Walk3',
      'Stickman_Legs_Walk4',
      'Stickman_Legs_Walk5',
      'Stickman_Legs_Walk6',
      'Stickman_Legs_Walk7',
      'Stickman_Legs_Walk8'
    ];
    for (const partKey of stickmanParts) {
      const segData = getHDSpriteGrid(partKey);
      if (!segData?.grid) continue;
      for (let gy = 0; gy < gridSize; gy++) {
        for (let gx = 0; gx < gridSize; gx++) {
          const col = isWestern ? gx : (gridSize - 1 - gx);
          if (segData.grid[gy][col]) {
            occupied[gy][gx] = 1;
            if (gx < minGx) minGx = gx;
            if (gx > maxGx) maxGx = gx;
            if (gy < minGy) minGy = gy;
            if (gy > maxGy) maxGy = gy;
            hasAny = true;
          }
        }
      }
    }

    // Heavy Lance Cannon: Heavy breech and receiver core (col <= 26) attached to the torso mount.
    // Omits the slender 20px extending barrel (col > 26) which elevates up to 30° dynamically in combat,
    // avoiding a frozen horizontal weapon needle in empty space.
    const cannonData = getHDSpriteGrid('Stickman_Cannon');
    if (cannonData?.grid) {
      for (let gy = 0; gy < gridSize; gy++) {
        for (let col = 0; col <= 26; col++) {
          if (cannonData.grid[gy][col]) {
            const gx = isWestern ? col : (gridSize - 1 - col);
            for (let dy = -2; dy <= 2; dy++) {
              const ty = gy + dy;
              if (ty >= 0 && ty < gridSize) {
                occupied[ty][gx] = 1;
                if (gx < minGx) minGx = gx;
                if (gx > maxGx) maxGx = gx;
                if (ty < minGy) minGy = ty;
                if (ty > maxGy) maxGy = ty;
                hasAny = true;
              }
            }
          }
        }
      }
    }

    // Cohesive torso and pelvic core bridge (y: 12..28): fills hollow voids between chassis and weapon
    for (let gy = 12; gy <= 28; gy++) {
      let rowMinGx = gridSize, rowMaxGx = -1;
      for (let gx = 0; gx < gridSize; gx++) {
        if (occupied[gy][gx]) {
          if (gx < rowMinGx) rowMinGx = gx;
          if (gx > rowMaxGx) rowMaxGx = gx;
        }
      }
      if (rowMinGx < rowMaxGx) {
        for (let gx = rowMinGx; gx <= rowMaxGx; gx++) occupied[gy][gx] = 1;
      }
    }

    // Pelvic & thigh bridge (y: 29..36): smooth transition between chassis and stride envelope
    for (let gy = 29; gy <= 36; gy++) {
      let rowMinGx = gridSize, rowMaxGx = -1;
      for (let gx = 0; gx < gridSize; gx++) {
        if (occupied[gy][gx]) {
          if (gx < rowMinGx) rowMinGx = gx;
          if (gx > rowMaxGx) rowMaxGx = gx;
        }
      }
      if (rowMinGx < rowMaxGx) {
        for (let gx = rowMinGx; gx <= rowMaxGx; gx++) occupied[gy][gx] = 1;
      }
    }
  } else if (isPulpAura) {
    // 🤡 Articulated Sul Pulp Moon-Hop Aura:
    // Unifies the clown mask, jester bells, scepter, mortar, and all 6 hopping stride & squash leg phases.
    const pulpParts = [
      'Pulp_Body',
      'Pulp_Head',
      'Pulp_Weapon',
      'Pulp_Legs_Walk1',
      'Pulp_Legs_Walk2',
      'Pulp_Legs_Walk3',
      'Pulp_Legs_Walk4',
      'Pulp_Legs_Walk5',
      'Pulp_Legs_Walk6'
    ];
    for (const partKey of pulpParts) {
      const segData = getHDSpriteGrid(partKey);
      if (!segData?.grid) continue;
      for (let gy = 0; gy < gridSize; gy++) {
        for (let gx = 0; gx < gridSize; gx++) {
          const col = isWestern ? gx : (gridSize - 1 - gx);
          if (segData.grid[gy][col]) {
            occupied[gy][gx] = 1;
            if (gx < minGx) minGx = gx;
            if (gx > maxGx) maxGx = gx;
            if (gy < minGy) minGy = gy;
            if (gy > maxGy) maxGy = gy;
            hasAny = true;
          }
        }
      }
    }
  } else if (isHotHandAura) {
    // 🔥 Articulated Hot Hand sprinter aura:
    // Unifies the athletic forward-leaning torso, flowing slipstream hair (modes 0, 1, 2),
    // reactive molten gauntlet reach (Guard, Jab, Uppercut, Slam), and all 6 explosive sprint stride phases.
    // Bridges the pelvic core and applies dynamic forward-lean envelope to eliminate static vertical standing leg imprints.
    const hotHandParts = [
      'HotHand_Torso',
      'HotHand_Hair',
      'HotHand_Hair_Sprint1',
      'HotHand_Hair_Sprint2',
      'HotHand_Arm_Rear',
      'HotHand_Arm_Guard',
      'HotHand_Arm_Jab',
      'HotHand_Arm_Uppercut',
      'HotHand_Arm_Slam',
      'HotHand_Legs',
      'HotHand_Legs_Sprint1',
      'HotHand_Legs_Sprint2',
      'HotHand_Legs_Sprint3',
      'HotHand_Legs_Sprint4',
      'HotHand_Legs_Sprint5',
      'HotHand_Legs_Sprint6'
    ];
    for (const partKey of hotHandParts) {
      const segData = getHDSpriteGrid(partKey);
      if (!segData?.grid) continue;
      for (let gy = 0; gy < gridSize; gy++) {
        for (let gx = 0; gx < gridSize; gx++) {
          const col = isWestern ? gx : (gridSize - 1 - gx);
          if (segData.grid[gy][col]) {
            occupied[gy][gx] = 1;
            if (gx < minGx) minGx = gx;
            if (gx > maxGx) maxGx = gx;
            if (gy < minGy) minGy = gy;
            if (gy > maxGy) maxGy = gy;
            hasAny = true;
          }
        }
      }
    }

    // Dynamic forward athletic lean: upper body & hair (rows 2..12) tilt forward by ~0.22 rad (shift +1..+2 in facing dir)
    for (const partKey of ['HotHand_Torso', 'HotHand_Hair_Sprint1', 'HotHand_Hair_Sprint2', 'HotHand_Arm_Guard', 'HotHand_Arm_Jab']) {
      const segData = getHDSpriteGrid(partKey);
      if (!segData?.grid) continue;
      for (let gy = 2; gy <= 12; gy++) {
        for (let gx = 0; gx < gridSize; gx++) {
          const col = isWestern ? gx : (gridSize - 1 - gx);
          if (segData.grid[gy][col]) {
            for (const shift of [1, 2]) {
              const shiftedGx = isWestern ? (gx + shift) : (gx - shift);
              if (shiftedGx >= 0 && shiftedGx < gridSize) {
                occupied[gy][shiftedGx] = 1;
                if (shiftedGx < minGx) minGx = shiftedGx;
                if (shiftedGx > maxGx) maxGx = shiftedGx;
              }
            }
          }
        }
      }
    }

    // Torso core bridge (gy: 4..12): bridges hair to torso to ensure solid upper body
    for (let gy = 4; gy <= 12; gy++) {
      let rowMinGx = gridSize, rowMaxGx = -1;
      for (let gx = 0; gx < gridSize; gx++) {
        if (occupied[gy][gx]) {
          if (gx < rowMinGx) rowMinGx = gx;
          if (gx > rowMaxGx) rowMaxGx = gx;
        }
      }
      if (rowMinGx < rowMaxGx) {
        for (let gx = rowMinGx; gx <= rowMaxGx; gx++) occupied[gy][gx] = 1;
      }
    }

    // Pelvic & hip bridge (gy: 13..17): eliminates hollow void or vertical two-leg silhouette
    for (let gy = 13; gy <= 17; gy++) {
      let rowMinGx = gridSize, rowMaxGx = -1;
      for (let gx = 0; gx < gridSize; gx++) {
        if (occupied[gy][gx]) {
          if (gx < rowMinGx) rowMinGx = gx;
          if (gx > rowMaxGx) rowMaxGx = gx;
        }
      }
      if (rowMinGx < rowMaxGx) {
        for (let gx = rowMinGx; gx <= rowMaxGx; gx++) occupied[gy][gx] = 1;
      }
    }
  } else {
    for (let gy = 0; gy < gridSize; gy++) {
      for (let gx = 0; gx < gridSize; gx++) {
        const col = isWestern ? gx : (gridSize - 1 - gx);
        if (grid[gy][col]) {
          occupied[gy][gx] = 1;
          if (gx < minGx) minGx = gx;
          if (gx > maxGx) maxGx = gx;
          if (gy < minGy) minGy = gy;
          if (gy > maxGy) maxGy = gy;
          hasAny = true;
        }
      }
    }
  }
  if (!hasAny) return null;

  // Mask dimensions: 0: empty, 1: outer rim contour, 2: mid flame, 3: bright inner flame
  const mask = Array.from({ length: auraGridH }, () => new Uint8Array(auraGridW));

  // Find topmost occupied row per column for upward flame tongues
  const topGyPerCol = new Int8Array(gridSize).fill(-1);
  for (let gx = 0; gx < gridSize; gx++) {
    for (let gy = 0; gy < gridSize; gy++) {
      if (occupied[gy][gx]) {
        topGyPerCol[gx] = gy;
        break;
      }
    }
  }

  if (isVehicleAura) {
    // Chamfer steep 90° vehicle drop-offs (max slope 1.5 * scale cells per column)
    // Eliminates sheer vertical cliff walls and blocky rectangular step corners at hull edges
    const maxSlope = Math.max(1.5, Math.round(1.5 * scale));
    for (let gx = 1; gx < gridSize; gx++) {
      if (topGyPerCol[gx - 1] !== -1 && topGyPerCol[gx] !== -1) {
        if (topGyPerCol[gx] > topGyPerCol[gx - 1] + maxSlope) {
          topGyPerCol[gx] = Math.round(topGyPerCol[gx - 1] + maxSlope);
        }
      }
    }
    for (let gx = gridSize - 2; gx >= 0; gx--) {
      if (topGyPerCol[gx + 1] !== -1 && topGyPerCol[gx] !== -1) {
        if (topGyPerCol[gx] > topGyPerCol[gx + 1] + maxSlope) {
          topGyPerCol[gx] = Math.round(topGyPerCol[gx + 1] + maxSlope);
        }
      }
    }

    // Chamfer occupied grid so base dilation also follows the smooth slope without cliff walls
    for (let gx = 0; gx < gridSize; gx++) {
      const chamferTop = topGyPerCol[gx];
      if (chamferTop !== -1) {
        for (let gy = chamferTop; gy < gridSize; gy++) {
          if (occupied[gy][gx]) break;
          occupied[gy][gx] = 1;
        }
      }
    }
  }

  // 2. Base dilation (scaled to unit resolution)
  const dRad = isHD ? Math.max(2, Math.round(2 * scale)) : 1;
  const flareDist = isHD ? Math.round(3 * scale) : 2;
  const maxDistSq = isHD ? Math.round(5 * scale * scale) : 2;
  for (let gy = 0; gy < gridSize; gy++) {
    for (let gx = 0; gx < gridSize; gx++) {
      if (!occupied[gy][gx]) continue;
      const ox = gx + padX;
      const oy = gy + padTop;

      mask[oy][ox] = 3;

      for (let dy = -dRad; dy <= dRad; dy++) {
        for (let dx = -dRad; dx <= dRad; dx++) {
          const distSq = dx * dx + dy * dy;
          if (distSq > maxDistSq) continue;
          const my = oy + dy, mx = ox + dx;
          if (my >= 0 && my < auraGridH && mx >= 0 && mx < auraGridW) {
            if (mask[my][mx] < 2) mask[my][mx] = 2;
          }
        }
      }

      // Directional flares
      for (const [dx, dy] of [
        [-flareDist, 0],
        [flareDist, 0],
        [-flareDist, -1],
        [flareDist, -1],
        [0, -flareDist]
      ]) {
        const my = oy + dy, mx = ox + dx;
        if (my >= 0 && my < auraGridH && mx >= 0 && mx < auraGridW) {
          if (mask[my][mx] < 2) mask[my][mx] = 2;
        }
      }
    }
  }

  // 3. Upward flame tongues / spikes on top of unit
  for (let gx = 0; gx < gridSize; gx++) {
    const topGy = topGyPerCol[gx];
    if (topGy === -1) continue;

    const ox = gx + padX;
    const oy = topGy + padTop;

    const colRel = (gx - minGx) / Math.max(1, (maxGx - minGx));
    const centerBias = 1 - Math.abs(colRel - 0.5) * 1.5;

    const fPhase = normFrame * 2.094; // 2 * Math.PI / 3

    if (isVehicleAura) {
      // 🚗 Anti-Box Vehicle Corona Shaping:
      // Broad vehicle hulls have wide flat roofs that dilation would turn into solid rectangular blocks.
      // We shape the corona with an arched parabolic crown, multi-harmonic licking tongues, and tapered reach.
      const arch = Math.sin(colRel * Math.PI) * (2.2 * scale);
      const wave = Math.sin(gx * (0.45 / scale) + fPhase) * 1.2
                 + Math.sin(gx * (1.1 / scale) + fPhase * 1.5) * 0.9
                 + Math.sin(gx * (2.2 / scale) + fPhase * 0.7) * 0.6;
      const tongueReach = Math.max(2, Math.round((2.8 + arch + wave * 1.8) * scale));
      const innerHotReach = Math.round(2.2 * scale);

      for (let reach = 1; reach <= tongueReach; reach++) {
        const ty = oy - reach;
        if (ty >= 0 && ty < auraGridH) {
          mask[ty][ox] = (reach <= innerHotReach) ? 3 : 2;
          // Tapered side reach: narrow towards tongue tips to form distinct licking flames rather than a flat brick
          const curSideReach = Math.max(1, Math.round((1 - (reach / tongueReach) * 0.7) * 1.8 * scale));
          for (let s = 1; s <= curSideReach; s++) {
            if (ox - s >= 0 && mask[ty][ox - s] < 2) mask[ty][ox - s] = 2;
            if (ox + s < auraGridW && mask[ty][ox + s] < 2) mask[ty][ox + s] = 2;
          }
        }
      }
    } else {
      const wave = isHD
        ? Math.sin(gx * (0.9 / scale) + fPhase) * 0.7 + Math.sin(gx * (1.6 / scale) + fPhase * 1.4) * 0.5
        : Math.sin(gx * 1.6 + fPhase);

      const tongueReach = isHD
        ? Math.max(2, Math.round((3.5 + centerBias * 3.8 + wave * 2.6) * scale))
        : Math.max(1, Math.round(2.0 + centerBias * 2.2 + wave * 1.6));

      const innerHotReach = isHD ? Math.round(3 * scale) : 1;
      const sideReach = isHD ? Math.round(4 * scale) : 2;

      for (let reach = 1; reach <= tongueReach; reach++) {
        const ty = oy - reach;
        if (ty >= 0 && ty < auraGridH) {
          mask[ty][ox] = (reach <= innerHotReach) ? 3 : 2;
          if (reach <= sideReach) {
            if (ox > 0 && mask[ty][ox - 1] < 2) mask[ty][ox - 1] = 2;
            if (ox < auraGridW - 1 && mask[ty][ox + 1] < 2) mask[ty][ox + 1] = 2;
          }
        }
      }
    }
  }

  // 4. Detached rising flame wisps / motes above the head
  const headCenterX = Math.round((minGx + maxGx) / 2) + padX;
  const headTopY = minGy + padTop;

  if (isHD) {
    const s = scale;
    if (normFrame === 0) {
      [[Math.round(-7 * s), Math.round(-3 * s)], [Math.round(-8 * s), Math.round(-3 * s)], [Math.round(-5 * s), Math.round(4 * s)], [Math.round(-9 * s), Math.round(1 * s)]].forEach(([dy, dx]) => {
        const wy = headTopY + dy, wx = headCenterX + dx;
        if (wy >= 0 && wy < auraGridH && wx >= 0 && wx < auraGridW) mask[wy][wx] = 2;
      });
    } else if (normFrame === 1) {
      [[Math.round(-9 * s), Math.round(-4 * s)], [Math.round(-6 * s), Math.round(4 * s)], [Math.round(-7 * s), Math.round(4 * s)], [Math.round(-4 * s), Math.round(-1 * s)], [Math.round(-5 * s), Math.round(-1 * s)]].forEach(([dy, dx]) => {
        const wy = headTopY + dy, wx = headCenterX + dx;
        if (wy >= 0 && wy < auraGridH && wx >= 0 && wx < auraGridW) mask[wy][wx] = 2;
      });
    } else {
      [[Math.round(-6 * s), Math.round(-2 * s)], [Math.round(-7 * s), Math.round(-2 * s)], [Math.round(-8 * s), Math.round(3 * s)], [Math.round(-9 * s), Math.round(3 * s)], [Math.round(-4 * s), Math.round(5 * s)]].forEach(([dy, dx]) => {
        const wy = headTopY + dy, wx = headCenterX + dx;
        if (wy >= 0 && wy < auraGridH && wx >= 0 && wx < auraGridW) mask[wy][wx] = 2;
      });
    }
  } else {
    if (normFrame === 0) {
      const w1y = headTopY - 4, w1x = headCenterX - 2;
      if (w1y >= 0 && w1y < auraGridH && w1x >= 0 && w1x < auraGridW) {
        mask[w1y][w1x] = 2;
        if (w1y - 1 >= 0) mask[w1y - 1][w1x] = 2;
      }
      const w2y = headTopY - 3, w2x = headCenterX + 3;
      if (w2y >= 0 && w2y < auraGridH && w2x >= 0 && w2x < auraGridW) {
        mask[w2y][w2x] = 2;
      }
    } else if (normFrame === 1) {
      const w1y = headTopY - 5, w1x = headCenterX - 3;
      if (w1y >= 0 && w1y < auraGridH && w1x >= 0 && w1x < auraGridW) {
        mask[w1y][w1x] = 2;
      }
      const w2y = headTopY - 4, w2x = headCenterX + 3;
      if (w2y >= 0 && w2y < auraGridH && w2x >= 0 && w2x < auraGridW) {
        mask[w2y][w2x] = 2;
        if (w2y - 1 >= 0) mask[w2y - 1][w2x] = 2;
      }
      const w3y = headTopY - 3, w3x = headCenterX;
      if (w3y >= 0 && w3y < auraGridH && w3x >= 0 && w3x < auraGridW) {
        mask[w3y][w3x] = 2;
      }
    } else {
      const w1y = headTopY - 4, w1x = headCenterX - 1;
      if (w1y >= 0 && w1y < auraGridH && w1x >= 0 && w1x < auraGridW) {
        mask[w1y][w1x] = 2;
        if (w1y - 1 >= 0) mask[w1y - 1][w1x] = 2;
      }
      const w2y = headTopY - 5, w2x = headCenterX + 2;
      if (w2y >= 0 && w2y < auraGridH && w2x >= 0 && w2x < auraGridW) {
        mask[w2y][w2x] = 2;
      }
    }
  }

  // 5. Border Detection: Any filled cell touching empty 4-neighbor becomes rim (1)
  const finalMask = Array.from({ length: auraGridH }, () => new Uint8Array(auraGridW));
  for (let y = 0; y < auraGridH; y++) {
    for (let x = 0; x < auraGridW; x++) {
      const v = mask[y][x];
      if (v === 0) continue;
      const isRim = (
        x === 0 || x === auraGridW - 1 || y === 0 || y === auraGridH - 1 ||
        mask[y - 1][x] === 0 || mask[y + 1][x] === 0 ||
        mask[y][x - 1] === 0 || mask[y][x + 1] === 0
      );
      finalMask[y][x] = isRim ? 1 : v;
    }
  }

  const canvasW = auraGridW * SPRITE_PIXEL_UNIT;
  const canvasH = auraGridH * SPRITE_PIXEL_UNIT;

  let canvas = null;
  if (typeof document !== 'undefined') {
    canvas = document.createElement('canvas');
    canvas.width = canvasW;
    canvas.height = canvasH;
    const ctx = canvas.getContext('2d');

    const { r, g, b } = parseAuraHexColor(auraHex);
    const rimColor = isHD
      ? `rgba(${Math.floor(r * 0.50)}, ${Math.floor(g * 0.50)}, ${Math.floor(b * 0.65)}, 0.88)`
      : `rgba(${Math.floor(r * 0.55)}, ${Math.floor(g * 0.55)}, ${Math.floor(b * 0.65)}, 0.92)`;
    const midColor = `rgba(${Math.floor(r * 0.85)}, ${Math.floor(g * 0.85)}, ${Math.floor(b * 0.90)}, 0.86)`;
    const coreColor = `rgba(${r}, ${g}, ${b}, 0.82)`;
    const hotColor = `rgba(${Math.min(255, r + 75)}, ${Math.min(255, g + 75)}, ${Math.min(255, b + 75)}, 0.92)`;

    for (let y = 0; y < auraGridH; y++) {
      for (let x = 0; x < auraGridW; x++) {
        const v = finalMask[y][x];
        if (v === 0) continue;
        const px = x * SPRITE_PIXEL_UNIT;
        const py = y * SPRITE_PIXEL_UNIT;

        let col = coreColor;
        if (v === 1) {
          col = rimColor;
        } else if (v === 2) {
          col = isHD ? midColor : coreColor;
        } else if (v === 3) {
          col = hotColor;
        }

        ctx.fillStyle = col;
        ctx.fillRect(px, py, SPRITE_PIXEL_UNIT, SPRITE_PIXEL_UNIT);
      }
    }
  }

  const entry = {
    canvas,
    width: canvasW,
    height: canvasH,
    padX,
    padTop,
    padBottom,
    gridSize,
    auraGridW,
    auraGridH,
    isHD,
    mask: finalMask
  };
  auraBitmapCache.set(key, entry);
  return entry;
}


// Pre-baked Look-Up Table (LUT) for Damped Harmonic Oscillator weapon recoil
const RECOIL_LUT_SIZE = 128;
const RECOIL_LUT = new Float32Array(RECOIL_LUT_SIZE);
for (let i = 0; i < RECOIL_LUT_SIZE; i++) {
  const tau = 1.0 - (i / (RECOIL_LUT_SIZE - 1));
  RECOIL_LUT[i] = Math.exp(-4.4 * tau) * Math.cos(Math.PI * 3.4 * tau);
}

// Damped Harmonic Oscillator helper for mechanical weapon recoil bounce & secondary motion (O(1) LUT accelerated)
export function dampedSpringRecoil(progress) {
  if (progress <= 0) return 0;
  if (progress >= 1) return RECOIL_LUT[RECOIL_LUT_SIZE - 1];
  return RECOIL_LUT[(progress * (RECOIL_LUT_SIZE - 1)) | 0];
}

// Pre-baked 64x4 1D/2D Gradient Look-Up Strip for Thermal Weapon Barrel Glow
let _thermalHeatStrip = null;
export function getThermalHeatStrip() {
  if (_thermalHeatStrip) return _thermalHeatStrip;
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 4;
  const ctx = canvas.getContext('2d');
  const grad = ctx.createLinearGradient(0, 0, 64, 0);
  grad.addColorStop(0, 'rgba(220, 38, 38, 0.35)');
  grad.addColorStop(0.55, 'rgba(249, 115, 22, 0.90)');
  grad.addColorStop(1, 'rgba(254, 240, 138, 1.00)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 64, 4);
  _thermalHeatStrip = canvas;
  return _thermalHeatStrip;
}
